import React, { useState, useEffect, useContext, createContext, useCallback, useRef, useMemo } from "react";
import { BrowserRouter, Routes, Route, Link, NavLink, useParams, useNavigate, Navigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { toastSuccess, toastError, toastLoading, pickErrorMsg } from "./lib/feedback";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowRight, ArrowUpRight, TrendingUp, Clock, CheckCircle2, Package,
  Calendar, MapPin, User, Download, QrCode, Truck, Hammer, Ruler,
  PenTool, Sparkles, Phone, Mail, LogOut, Home, Building2, Users,
  HardHat, Wallet, FileText, Image as ImageIcon, AlertCircle, Plus,
  Archive, X, Settings, Layers, MessageSquare, Edit3, Pencil, CheckSquare, AlertTriangle,
  ShieldCheck, Droplets, Flame, Ban, Zap, Check, Bell, Folder, Trash2, Copy, Trophy, Medal, Award, Camera
} from "lucide-react";
import "./App.css";
// Componentes extraídos para arquivos próprios (refactor)
import { ObraMedicoesPanel } from "./components/MedicoesPanel";
import {
  SBGaleriaAmbientePanel, SBMateriaisAprovadosPanel,
  SBAceiteCamasaPanel, SBTermoMedicaoPanel
} from "./components/SBPanels";
import { NPSPublicForm, NPSAdminPage, ObraNPSCard } from "./components/NPSPanels";
import RetrabalhosDashboard from "./components/RetrabalhosDashboard";
import SerieFotosAdminModal from "./components/SerieFotosAdminModal";
import ParceiroPortal, { ParceiroObraDetail } from "./components/ParceiroPortal";
import ParceiroV2Portal from "./components/ParceiroV2Portal";
import { ModoCampoHome, ModoCampoFoto, ModoCampoEtapa, ModoCampoAprovacoes } from "./components/ModoCampoAdmin";
import FotosPendentesPanel from "./components/FotosPendentesPanel";
import { SobrasPanel, SobrasGlobalPage, SobrasSugestoes, SobrasAlertaCliente } from "./components/SobrasPanel";
import { ObraHealthCard, ObrasAtencaoCard } from "./components/HealthPanel";
import { SBWhatsAppBox } from "./components/SBWhatsAppBox";
import { CashflowPage } from "./components/CashflowPage";
import RastreioPublic from "./components/RastreioPublic";import FrentistaPanel from "./components/FrentistaPanel";
import FretesPanel from "./components/FretesPanel";
import ServicosPanel, { ReciboPrintView, ReciboPublicView } from "./components/ServicosPanel";
import { CadastroRapidoModal, BtnCadastroRapido } from "./components/CadastroRapido";
import ObraMateriaisItens from "./components/ObraMateriaisItens";
import { SBCronologiaMidia, SBFlag } from "./components/SBCronologiaMidia";
import { SBNovidadesPanel } from "./components/SBNovidadesPanel";
import { MidiasLoteBar, SelecaoCheck } from "./components/MidiasLote";
import { SBAditivos } from "./components/SBAditivos";
import { SBCronogramaPagamentos, SBTransformacao, SBFichasTecnicas, SBValorizacao, SBAvaliacao } from "./components/SBExtras";
import { SBExtrasAdmin } from "./components/SBExtrasAdmin";
import OcorrenciasPanel from "./components/OcorrenciasPanel";
import AditivosPanel from "./components/AditivosPanel";
import BrindesPanel from "./components/BrindesPanel";
import LucroRealPanel from "./components/LucroRealPanel";
import FotoWizardEtapa, { getFotosObrigatorias, WIZARD_ETAPA_CONFIG, resolveEtapaConfig } from "./components/FotoWizardEtapa";
// M1: helpers de formatação centralizados (fonte única em ./lib/fmt.js via ./lib)
import { brl, fmt, fmtDataHora, fmtDataCurta, fmtRelativo, fmtMoedaOuTraco } from "./lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (u) => !u ? "" : (u.startsWith("http") ? u : `${BACKEND}${u}`);

// P2 fix 2026-02: JWT httpOnly cookie
// - Envia cookies em toda request (same-origin: sem CORS drama).
// - `getCsrfCookie()` lê `cps_csrf` (non-httpOnly, double-submit) para o header
//   X-CSRF-Token em mutações.
// - Magic-link (cliente/arquiteto/terceirizado) continua em localStorage.
axios.defaults.withCredentials = true;

function getCsrfCookie() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)cps_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : "";
  } catch { return ""; }
}

// Interceptor global: anexa X-CSRF-Token em mutações. Backend só valida quando
// auth veio via cookie (Bearer bypassa CSRF por design), mas enviar sempre é
// inofensivo e cobre TODOS os axios.post/put/patch/delete espalhados sem precisar
// alterar cada call site individualmente.
axios.interceptors.request.use((config) => {
  // Sessão por cookie: vários call sites montam `Bearer ${token}` com token nulo.
  const auth = config.headers?.Authorization;
  if (auth === "Bearer null" || auth === "Bearer undefined" || auth === "Bearer ") {
    delete config.headers.Authorization;
  }
  const method = (config.method || "get").toLowerCase();
  if (method === "post" || method === "put" || method === "patch" || method === "delete") {
    const csrf = getCsrfCookie();
    if (csrf) {
      config.headers = config.headers || {};
      if (!config.headers["X-CSRF-Token"]) config.headers["X-CSRF-Token"] = csrf;
    }
  }
  return config;
});


/* ========== AUTH CONTEXT ========== */
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  // `token` continua existindo por 2 motivos: (a) magic-link (cliente/arquiteto/
  // terceirizado) SEGUE em localStorage, (b) backward-compat de sessões antigas
  // criadas antes da migração para cookie. Login normal (admin/gestor/financeiro)
  // NÃO grava mais no localStorage — a sessão vive no cookie httpOnly `cps_auth`.
  const [token, setToken] = useState(() => localStorage.getItem("cps_token"));

  useEffect(() => {
    // Tenta /auth/me — o backend aceita cookie (fluxo novo) OU Bearer (legacy/magic).
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/auth/me`, { headers })
      .then(r => setUser(r.data))
      .catch(() => {
        // Se falhou, limpa qualquer resquício de sessão
        if (token) { localStorage.removeItem("cps_token"); setToken(null); }
        setUser(null);
      });
  }, [token]);

  const login = async (email, password) => {
    // Backend seta cookie httpOnly + cookie CSRF automaticamente.
    // NÃO salvamos mais o JWT no localStorage nesse fluxo (evita XSS).
    const r = await axios.post(`${API}/auth/login`, { email, password });
    // Limpa qualquer token legacy que ainda esteja no localStorage.
    localStorage.removeItem("cps_token");
    setToken(null);
    setUser(r.data.user);
    return r.data.user;
  };
  const magicLogin = async (magicToken) => {
    // Magic-link (sem senha, sessão longa) — continua em localStorage por design.
    const r = await axios.post(`${API}/auth/magic`, { token: magicToken });
    localStorage.setItem("cps_token", r.data.token);
    setToken(r.data.token); setUser(r.data.user);
    return r.data.user;
  };
  const logout = async () => {
    try {
      // Chama backend para limpar cookies (best-effort — falha não bloqueia UI)
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { "X-CSRF-Token": getCsrfCookie() },
      });
    } catch { /* ignore */ }
    localStorage.removeItem("cps_token");
    setToken(null); setUser(null);
  };

  const api = useCallback((method, path, body) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const m = (method || "").toLowerCase();
    // Cache-bust em GETs pra impedir browser/CDN servir stale após mutação.
    if (m === "get") {
      headers["Cache-Control"] = "no-cache";
      headers["Pragma"] = "no-cache";
    }
    // Double-submit CSRF em mutações (só relevante quando auth vem de cookie;
    // backend ignora quando auth vem de Bearer). Enviar sempre é inofensivo.
    if (m === "post" || m === "put" || m === "patch" || m === "delete") {
      const csrf = getCsrfCookie();
      if (csrf) headers["X-CSRF-Token"] = csrf;
    }
    const config = { method, url: `${API}${path}`, data: body, headers };
    return axios(config).then(r => r.data).catch(err => {
      // 401 global: sessão expirada → limpa e redireciona
      if (err?.response?.status === 401) {
        localStorage.removeItem("cps_token");
        // Pequeno delay pra o stack desfazer antes do redirect
        setTimeout(() => { window.location.href = "/login?expired=1"; }, 200);
      }
      throw err;
    });
  }, [token]);

  return <AuthCtx.Provider value={{ user, token, login, magicLogin, logout, api }}>{children}</AuthCtx.Provider>;
}

/* ========== HELPERS ========== */
// M1: brl, fmt e outras formatações vêm de ./lib (fonte única em ./lib/fmt.js).
// Bug-fix timezone resolvido na fonte (parseData ancora YYYY-MM-DD ao meio-dia local).

/**
 * obraCpsCode — código CPS público da obra:
 *   • Se obra.numero_orcamento estiver preenchido → "CPS-6567"
 *   • Senão, usa obra.codigo (interno, ex: "CPS-0001")
 *   • Fallback final (raro): "CPS-[sem código]"
 */
const obraCpsCode = (obra) => {
  const num = (obra?.numero_orcamento || "").toString().trim();
  if (num) return `CPS-${num}`;
  if (obra?.codigo) return obra.codigo;
  return "CPS-[sem código]";
};

const ROLE_LBL = {
  admin: "Administrador", financeiro: "Financeiro", gestor: "Gestor de Obras",
  terceirizado: "Terceirizado", cliente: "Cliente", arquiteto: "Arquiteto"
};
const ESP_LBL = {
  medicao: "Medição", projeto: "Projeto", producao: "Produção",
  acabamento: "Acabamento", frete: "Frete", instalacao: "Instalação"
};
const MAT_CAT = {
  marmore: "Mármore", quartzito: "Quartzito", granito: "Granito",
  quartzo_sinterizado: "Quartzo / Composto", travertino: "Travertino",
  onix: "Ônix", pedra: "Pedra", industrializado: "Industrializado"
};
const TERC_TIPOS = [{ v: "marmoraria", l: "Marmoraria" }, { v: "fornecedor", l: "Fornecedor" }, { v: "frentista", l: "Frentista" }, { v: "outros", l: "Outros" }];
// Formulários mínimos do cadastro rápido (mesmas rotas POST do cadastro normal)
const CADASTRO_RAPIDO = {
  arquiteto: { endpoint: "arquitetos", titulo: "arquiteto", fields: [
    { k: "nome", l: "Nome", req: true }, { k: "escritorio", l: "Escritório" },
    { k: "email", l: "Email", type: "email" }, { k: "telefone", l: "Telefone" }] },
  prestador: { endpoint: "prestadores", titulo: "prestador", fields: [
    { k: "nome", l: "Nome", req: true },
    { k: "especialidade", l: "Especialidade / Função", req: true, opts: Object.entries(ESP_LBL).map(([v, l]) => ({ v, l })) },
    { k: "telefone", l: "Telefone" }] },
  terceirizado: { endpoint: "terceirizados", titulo: "terceirizado", fields: [
    { k: "nome", l: "Nome", req: true }, { k: "empresa", l: "Empresa", req: true },
    { k: "tipo", l: "Tipo", req: true, opts: TERC_TIPOS }, { k: "telefone", l: "Telefone" }] },
  material: { endpoint: "materiais", titulo: "material", fields: [
    { k: "nome", l: "Nome", req: true },
    { k: "categoria", l: "Categoria", req: true, opts: Object.entries(MAT_CAT).map(([v, l]) => ({ v, l })) },
    { k: "fornecedor", l: "Fornecedor" }] },
};
const STATUS_OBRA = {
  em_andamento: { l: "Em andamento", c: "run" },
  concluida: { l: "Concluída", c: "done" },
  arquivada: { l: "Arquivada", c: "hold" },
  cancelada: { l: "Cancelada", c: "hold" },
  pausada: { l: "Pausada", c: "hold" },
  rascunho: { l: "Rascunho", c: "hold" },
};

function Tricolore({ thin }) {
  return <div className={"tricolore" + (thin ? " thin" : "")}>
    <span className="v" /><span className="b" /><span className="r" />
  </div>;
}

/* ========== LOGIN ========== */
function LoginPage() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const expired = new URLSearchParams(location.search).get("expired") === "1";

  useEffect(() => { if (user) nav("/"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await login(email, password); nav("/"); }
    catch (e) {
      // Task 1 (2026-02-19): 429 = lockout por brute force. Backend retorna
      // "Muitas tentativas de login. Tente novamente em X min." — exibimos como-is.
      if (e.response?.status === 429) {
        setErr(e.response?.data?.detail || "Muitas tentativas de login. Aguarde alguns minutos e tente novamente.");
      } else {
        setErr(e.response?.data?.detail || "Erro ao entrar");
      }
    }
    finally { setLoading(false); }
  };

  return (
    <div className="login-wrap fade-in">
      <div className="login-art">
        <div className="login-brand">
          <Tricolore />
          <div className="login-brand-top" style={{ marginTop: 20 }}>CAMASA</div>
          <div className="login-brand-sub">Process System</div>
        </div>
        <div className="login-art-text">
          <h2>Controle <em>absoluto</em><br />sobre cada obra.</h2>
          <p>Sistema proprietário CAMASA para gestão integrada de processos — da seleção da matéria-prima à entrega, com a elegância italiana que sua marca merece.</p>
        </div>
        <div className="login-art-quote">
          "Il vero lusso è raro come un sogno."
          <small>— Filosofia CAMASA · 2026</small>
        </div>
      </div>

      <div className="login-form">
        <div className="login-card">
          <div className="login-eyebrow">Acesso restrito</div>
          <h1 className="login-title">Entre no <em>CPS</em></h1>
          <p className="login-desc">Sistema de processo, controle e rastreabilidade da obra.</p>

          {err && (() => {
            const isLockout = /muitas tentativas|tente novamente em/i.test(err);
            return (
              <div
                className="login-err"
                data-testid={isLockout ? "login-lockout" : "login-error"}
                style={isLockout ? {
                  borderColor: "#c67a3b", color: "#f0b784",
                  background: "rgba(198, 122, 59, 0.08)",
                  padding: "14px 16px",
                } : undefined}
              >
                {isLockout ? "⏱ " : ""}{err}
                {isLockout && (
                  <div style={{ fontSize: 12, marginTop: 6, opacity: 0.8 }}>
                    Por segurança, bloqueamos temporariamente após várias tentativas erradas.
                  </div>
                )}
              </div>
            );
          })()}
          {expired && !err && (
            <div className="login-err" data-testid="login-expired" style={{ borderColor: "#b8935a", color: "#d4b27a" }}>
              ⏱ Sua sessão expirou. Faça login novamente para continuar.
            </div>
          )}

          <form onSubmit={submit}>
            <div className="field">
              <label>Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div className="field">
              <label>Senha</label>
              <input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
            </div>
            <button data-testid="login-submit" className="btn btn-primary" style={{ width: "100%", padding: "16px" }} disabled={loading}>
              {loading ? "Entrando…" : "Entrar no sistema"}
            </button>
          </form>

          <div style={{ marginTop: 16, textAlign: "center" }}>
            <Link
              to="/forgot-password"
              data-testid="link-forgot-password"
              style={{
                color: "#b8935a", fontSize: 13, textDecoration: "none",
                borderBottom: "1px dashed rgba(184, 147, 90, 0.4)",
                paddingBottom: 2,
              }}
            >
              Esqueci minha senha
            </Link>
          </div>

          <div style={{ marginTop: 24, textAlign: "center" }}>
            <Tricolore thin />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========== FORGOT PASSWORD ========== */
function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (e) {
      if (e.response?.status === 429) {
        setErr("Muitas solicitações. Aguarde alguns minutos e tente novamente.");
      } else {
        setErr(e.response?.data?.detail || "Erro ao processar. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap fade-in">
      <div className="login-art">
        <div className="login-brand">
          <Tricolore />
          <div className="login-brand-top" style={{ marginTop: 20 }}>CAMASA</div>
          <div className="login-brand-sub">Process System</div>
        </div>
        <div className="login-art-text">
          <h2>Recupere seu <em>acesso</em>.</h2>
          <p>Enviaremos um link seguro para o seu e-mail cadastrado — válido por 1 hora e utilizável uma única vez.</p>
        </div>
      </div>

      <div className="login-form">
        <div className="login-card">
          <div className="login-eyebrow">Redefinir senha</div>
          <h1 className="login-title">Esqueceu a <em>senha?</em></h1>
          <p className="login-desc">Informe seu e-mail. Se ele estiver cadastrado, você receberá o link em instantes.</p>

          {err && <div className="login-err" data-testid="forgot-error">{err}</div>}

          {sent ? (
            <div
              data-testid="forgot-success"
              style={{
                background: "rgba(58, 90, 74, 0.15)",
                border: "1px solid #3a5a4a",
                color: "#a8d1ba",
                padding: "18px 20px",
                borderRadius: 6,
                lineHeight: 1.6,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Verifique seu e-mail</div>
              Se o e-mail <strong>{email}</strong> estiver cadastrado, um link de redefinição chegará em instantes.
              <div style={{ fontSize: 12, marginTop: 10, opacity: 0.85 }}>
                Não recebeu? Verifique a pasta de spam ou tente novamente em alguns minutos.
              </div>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="field">
                <label>Email cadastrado</label>
                <input
                  data-testid="forgot-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="voce@camasa.com.br"
                />
              </div>
              <button
                data-testid="forgot-submit"
                className="btn btn-primary"
                style={{ width: "100%", padding: "16px" }}
                disabled={loading || !email}
              >
                {loading ? "Enviando…" : "Enviar link de redefinição"}
              </button>
            </form>
          )}

          <div style={{ marginTop: 20, textAlign: "center" }}>
            <Link
              to="/login"
              data-testid="link-back-to-login"
              style={{
                color: "#b8935a", fontSize: 13, textDecoration: "none",
                borderBottom: "1px dashed rgba(184, 147, 90, 0.4)",
                paddingBottom: 2,
              }}
            >
              ← Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========== RESET PASSWORD ========== */
function ResetPasswordPage() {
  const location = useLocation();
  const nav = useNavigate();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("A senha precisa ter no mínimo 8 caracteres."); return; }
    if (pw !== pw2) { setErr("As senhas não conferem."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || "Não foi possível redefinir. Solicite um novo link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap fade-in">
      <div className="login-art">
        <div className="login-brand">
          <Tricolore />
          <div className="login-brand-top" style={{ marginTop: 20 }}>CAMASA</div>
          <div className="login-brand-sub">Process System</div>
        </div>
        <div className="login-art-text">
          <h2>Nova senha, <em>novo começo</em>.</h2>
          <p>Escolha uma senha forte com pelo menos 8 caracteres. Após redefinir, você poderá entrar imediatamente.</p>
        </div>
      </div>

      <div className="login-form">
        <div className="login-card">
          <div className="login-eyebrow">Nova senha</div>
          <h1 className="login-title">Redefinir <em>senha</em></h1>
          <p className="login-desc">Escolha uma senha segura para sua conta.</p>

          {!token && (
            <div className="login-err" data-testid="reset-notoken">
              Link inválido. <Link to="/forgot-password" style={{ color: "#d4b27a" }}>Solicite um novo</Link>.
            </div>
          )}
          {err && <div className="login-err" data-testid="reset-error">{err}</div>}

          {done ? (
            <div
              data-testid="reset-success"
              style={{
                background: "rgba(58, 90, 74, 0.15)",
                border: "1px solid #3a5a4a",
                color: "#a8d1ba",
                padding: "18px 20px",
                borderRadius: 6,
                lineHeight: 1.6,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Senha redefinida com sucesso ✓</div>
              Redirecionando para o login…
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="field">
                <label>Nova senha (mínimo 8 caracteres)</label>
                <input
                  data-testid="reset-pw"
                  type="password"
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  required
                  minLength={8}
                  disabled={!token}
                />
              </div>
              <div className="field">
                <label>Confirme a nova senha</label>
                <input
                  data-testid="reset-pw2"
                  type="password"
                  value={pw2}
                  onChange={e => setPw2(e.target.value)}
                  required
                  minLength={8}
                  disabled={!token}
                />
              </div>
              <button
                data-testid="reset-submit"
                className="btn btn-primary"
                style={{ width: "100%", padding: "16px" }}
                disabled={loading || !token || !pw || !pw2}
              >
                {loading ? "Redefinindo…" : "Redefinir senha"}
              </button>
            </form>
          )}

          <div style={{ marginTop: 20, textAlign: "center" }}>
            <Link
              to="/login"
              data-testid="link-back-to-login-2"
              style={{
                color: "#b8935a", fontSize: 13, textDecoration: "none",
                borderBottom: "1px dashed rgba(184, 147, 90, 0.4)",
                paddingBottom: 2,
              }}
            >
              ← Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function MagicLogin() {
  const { token } = useParams();
  const { magicLogin, logout } = useAuth();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  useEffect(() => {
    magicLogin(token).then(() => nav("/")).catch(e => {
      logout();  // limpa sessão antiga pra não confundir
      setErr(e.response?.data?.detail || "Link inválido");
    });
  }, [token, magicLogin, logout, nav]);
  return <div className="container" style={{ textAlign: "center", paddingTop: 160 }}>
    {err ? (
      <>
        <h2 className="page-title"><em>Link inválido</em> ou <em>expirado</em></h2>
        <p className="page-sub" style={{ maxWidth: 520, margin: "14px auto 0" }}>
          Este link mágico não é mais válido. Isso pode acontecer quando o administrador rotacionou o link por segurança, o terceirizado foi removido, ou o link já foi substituído por outro.
        </p>
        <p className="page-sub" style={{ maxWidth: 520, margin: "10px auto 0", fontSize: 12, opacity: 0.7 }}>
          Motivo técnico: {err}
        </p>
        <div style={{ marginTop: 28, display: "flex", gap: 12, justifyContent: "center" }}>
          <button className="btn" onClick={() => nav("/login")} data-testid="magic-err-login">Ir para o login administrativo</button>
        </div>
        <p className="page-sub" style={{ maxWidth: 520, margin: "24px auto 0", fontSize: 12, opacity: 0.6 }}>
          Se você é terceirizado, cliente ou arquiteto, entre em contato com a CAMASA para receber um novo link.
        </p>
      </>
    ) : (
      <p className="page-sub">Autenticando…</p>
    )}
  </div>;
}

/* ========== PROTECTED ROUTE ========== */
function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <div className="container" style={{ paddingTop: 120 }}><p className="page-sub">Carregando…</p></div>;
  if (!user) return <Navigate to="/login" replace />;
  // Frentista só pode acessar /frentista — redireciona qualquer outra rota
  if (user.role === "frentista" && !location.pathname.startsWith("/frentista")) {
    return <Navigate to="/frentista" replace />;
  }
  // Terceirizado (parceiro) só pode acessar /parceiro — portal próprio sem admin
  if (user.role === "terceirizado" && !location.pathname.startsWith("/parceiro")) {
    return <Navigate to="/parceiro" replace />;
  }
  return children;
}

/* ========== NAV ========== */
function Nav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isAdmin = ["admin", "financeiro", "gestor"].includes(user?.role);
  // Modo Campo é experiência standalone mobile-first → esconder o nav admin
  if (location.pathname.startsWith("/campo") || location.pathname === "/aprovar") return null;
  return (
    <nav className="nav">
      <div className="nav-inner">
        <Link to="/" className="brand" style={{ display: "flex", alignItems: "center" }}>
          <img src="/cps-logo.jpg" alt="CAMASA Process System" style={{
            height: 64,
            width: "auto",
            maxWidth: 280,
            objectFit: "contain",
            display: "block",
            filter: "drop-shadow(0 0 16px rgba(184,147,90,0.25))",
          }} />
        </Link>
        <div className="nav-links">
          <NavLink to="/" end data-testid="nav-dash" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Painel</NavLink>
          <NavLink to="/obras" data-testid="nav-obras" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Obras</NavLink>
          {isAdmin && <NavLink to="/prestadores" data-testid="nav-prestadores" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Prestadores</NavLink>}
          {isAdmin && <NavLink to="/arquitetos" data-testid="nav-arquitetos" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Arquitetos</NavLink>}
          {isAdmin && <NavLink to="/terceirizados" data-testid="nav-terceirizados" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Terceirizados</NavLink>}
          {isAdmin && <NavLink to="/materiais" data-testid="nav-materiais" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Materiais</NavLink>}
          {(user?.role === "admin" || user?.role === "gestor") && <NavLink to="/aprovacao" data-testid="nav-aprovacao" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Aprovação</NavLink>}
          {(user?.role === "admin" || user?.role === "gestor") && <FotosPendentesNavLink />}
          {user?.role === "admin" && <NavLink to="/auditoria" data-testid="nav-auditoria" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Auditoria</NavLink>}
          {(user?.role === "admin" || user?.role === "financeiro") && <NavLink to="/financeiro" data-testid="nav-fin" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Financeiro</NavLink>}
          {(user?.role === "admin" || user?.role === "financeiro") && <NavLink to="/cashflow" data-testid="nav-cashflow" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Caixa</NavLink>}
          {(user?.role === "admin" || user?.role === "financeiro") && <NavLink to="/dre" data-testid="nav-dre" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>DRE</NavLink>}
          {isAdmin && <NavLink to="/retrabalhos" data-testid="nav-retrabalhos" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Retrabalhos</NavLink>}
          {(user?.role === "admin" || user?.role === "gestor" || user?.role === "financeiro") && <NavLink to="/ocorrencias" data-testid="nav-ocorrencias" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Ocorrências</NavLink>}
          {(user?.role === "admin" || user?.role === "financeiro") && <NavLink to="/nps" data-testid="nav-nps" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>NPS</NavLink>}
          {(user?.role === "admin" || user?.role === "financeiro" || user?.role === "gestor") && <NavLink to="/sobras" data-testid="nav-sobras" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Sobras</NavLink>}
          {(user?.role === "admin" || user?.role === "gestor" || user?.role === "financeiro") && <NavLink to="/conversas-ai" data-testid="nav-ai" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Concierge AI</NavLink>}
          {(user?.role === "admin" || user?.role === "gestor") && <NavLink to="/pos-obra-insights" data-testid="nav-pos-obra" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>Pós-obra</NavLink>}
        </div>
        <div className="nav-user">
          <NotifBell />
          <div>
            <div className="nav-user-name">{user?.name}</div>
            <div className="nav-user-role">{ROLE_LBL[user?.role]}</div>
          </div>
          <button className="nav-logout" onClick={logout} data-testid="nav-logout"><LogOut size={12} /></button>
        </div>
      </div>
    </nav>
  );
}

function FotosPendentesNavLink() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem("cps_token");
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/fotos-pendentes?status=pendente`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCount((r.data.items || []).length);
      } catch {}
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <NavLink to="/admin/fotos-pendentes" data-testid="nav-fotos-pendentes" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
      Fotos {count > 0 && <span style={{ marginLeft: 4, padding: "1px 7px", background: "var(--gold)", color: "#0a0a0a", fontSize: 10, fontWeight: 700, borderRadius: 8 }} data-testid="fotos-pendentes-badge">{count}</span>}
    </NavLink>
  );
}


function NotifBell() {
  const { api, user } = useAuth();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);

  const refresh = useCallback(() => {
    if (!user) return;
    api("get", "/notifications/unread-count").then(r => setCount(r.count || 0)).catch(() => { });
  }, [api, user]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000); // polling 30s
    return () => clearInterval(t);
  }, [refresh]);

  const openPanel = () => {
    api("get", "/notifications?limit=20").then(setItems).catch(() => { });
    setOpen(true);
  };

  const readAll = async () => {
    await api("post", "/notifications/read-all", {});
    setItems(items.map(i => ({ ...i, read: true })));
    setCount(0);
  };
  const deleteOne = async (id) => {
    await api("delete", `/notifications/${id}`);
    setItems(items.filter(i => i.id !== id));
    refresh();
  };
  const deleteAll = async () => {
    if (!window.confirm("Apagar TODAS as notificações? Esta ação é irreversível.")) return;
    await api("delete", "/notifications");
    setItems([]); setCount(0);
  };

  return (
    <div className="notif-wrap">
      <button className="notif-bell" onClick={openPanel} data-testid="notif-bell" title="Notificações">
        <Bell size={16} />
        {count > 0 && <span className="notif-badge">{count > 9 ? "9+" : count}</span>}
      </button>
      {open && (
        <>
          <div className="notif-back" onClick={() => { setOpen(false); refresh(); }} />
          <div className="notif-panel">
            <div className="notif-head">
              <div>
                <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--gold)", textTransform: "uppercase" }}>Inbox</div>
                <h4 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, margin: "4px 0 0", color: "var(--ink)", fontWeight: 400 }}>Notificações</h4>
              </div>
              {count > 0 && <button className="btn btn-ghost btn-sm" onClick={readAll}>Marcar todas</button>}
              {items.length > 0 && <button className="btn btn-ghost btn-sm" onClick={deleteAll} style={{ color: "#c47a7a", marginLeft: 6 }} data-testid="notif-delete-all">Limpar</button>}
            </div>
            <div className="notif-list">
              {items.length === 0 ? (
                <div className="empty" style={{ padding: 40 }}><h3 style={{ fontSize: 18 }}>Nada por enquanto</h3><p style={{ fontSize: 13 }}>As notificações aparecerão aqui.</p></div>
              ) : items.map(n => (
                <div key={n.id} className={"notif-item " + (n.read ? "read" : "")}>
                  <div className="notif-dot" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, letterSpacing: ".22em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 4 }}>{n.eyebrow}</div>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: 15, color: "var(--ink)", marginBottom: 4 }}>{n.title}</div>
                    <div style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".08em" }}>{new Date(n.created_at).toLocaleString("pt-BR")}</div>
                    {n.cta_url && <a href={n.cta_url} className="btn btn-ghost btn-sm" style={{ marginTop: 8 }}>Abrir →</a>}
                  </div>
                  <button onClick={() => deleteOne(n.id)} title="Apagar" data-testid={`notif-delete-${n.id}`} style={{ background: "transparent", border: "none", color: "var(--ink-3)", cursor: "pointer", fontSize: 16, padding: "4px 8px", marginLeft: 6 }}>×</button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ========== DASHBOARD ========== */
function Dashboard() {
  const { user } = useAuth();
  // Home dedicada para papéis não-administrativos (muito mais clara)
  if (user?.role === "terceirizado") return <TerceirizadoHome />;
  if (user?.role === "cliente") return <ClienteHome />;
  if (user?.role === "arquiteto") return <ArquitetoHome />;
  return <DashboardAdmin />;
}
/**
 * HowToBanner — banner colapsável "Como usar esta tela"
 * Props:
 *  - id: chave única (salva estado de fechado/aberto no localStorage)
 *  - title: título opcional (padrão "Como usar")
 *  - steps: [{num, title, desc}]
 * O banner aparece fechado por padrão e expande quando o usuário clica.
 * Uma vez fechado, lembra a preferência (não chateia usuários veteranos).
 */
function HowToBanner({ id, title = "Como usar esta tela", steps = [] }) {
  const key = `cps_howto_${id}`;
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(key) !== "closed"; } catch { return true; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(key, next ? "open" : "closed"); } catch {}
  };
  return (
    <div className={"howto-banner " + (open ? "open" : "closed")} data-testid={`howto-${id}`}>
      <button className="howto-banner-head" onClick={toggle} data-testid={`howto-toggle-${id}`}>
        <span className="howto-banner-eyebrow">📘 {title}</span>
        <span className="howto-banner-arrow">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="howto-banner-body">
          <div className="howto-steps" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))` }}>
            {steps.map((s, i) => (
              <div key={i} className="howto-step">
                <div className="howto-num">{String(s.num || i + 1).padStart(2, "0")}</div>
                <div className="howto-body">
                  <div className="howto-title">{s.title}</div>
                  <div className="howto-desc" dangerouslySetInnerHTML={{ __html: s.desc }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * InstallPwaBanner — orienta usuário a instalar o CPS como app no tablet/celular.
 * Detecta iOS vs Android e mostra passo-a-passo visual.
 * Não aparece se já está rodando como PWA (standalone) ou se foi dispensado.
 */
function InstallPwaBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("cps_pwa_dismissed") === "1"; } catch { return false; }
  });
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const isStandalone = typeof window !== "undefined" && (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);

  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallPromptEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);

  if (isStandalone || dismissed) return null;
  // Só mostra em tablet/mobile (desktop não precisa)
  const isMobile = isIOS || isAndroid || window.innerWidth < 900;
  if (!isMobile) return null;

  const close = () => {
    setDismissed(true);
    try { localStorage.setItem("cps_pwa_dismissed", "1"); } catch {}
  };
  const installAndroid = async () => {
    if (!installPromptEvt) return;
    installPromptEvt.prompt();
    await installPromptEvt.userChoice;
    setInstallPromptEvt(null);
    close();
  };

  return (
    <div className="pwa-banner" data-testid="pwa-banner">
      <button className="pwa-close" onClick={close} aria-label="Fechar" data-testid="pwa-close">×</button>
      <div className="pwa-icon"><img src="/icon-192.png" alt="CPS" /></div>
      <div className="pwa-body">
        <div className="pwa-title">Instale o <em>CPS</em> no seu {isIOS ? "iPad / iPhone" : "tablet / celular"}</div>
        <div className="pwa-desc">
          {isIOS ? (
            <>Toque em <strong>Compartilhar</strong> (ícone <span className="pwa-kbd">⬆</span>) e depois em <strong>"Adicionar à Tela de Início"</strong>. Pronto — vai aparecer um ícone CPS na tela inicial que abre direto sem precisar de link.</>
          ) : (
            <>Toque no menu do navegador (três pontos <span className="pwa-kbd">⋮</span>) e escolha <strong>"Instalar aplicativo"</strong> ou <strong>"Adicionar à tela inicial"</strong>. Depois é só tocar no ícone CPS e entrar direto.</>
          )}
        </div>
        {installPromptEvt && isAndroid && (
          <button className="btn btn-sm" onClick={installAndroid} style={{ marginTop: 10 }} data-testid="pwa-install-btn">
            Instalar agora
          </button>
        )}
      </div>
    </div>
  );
}


function TerceirizadoHome() {
  const { api, user } = useAuth();
  const [obras, setObras] = useState(null);
  useEffect(() => { api("get", "/obras").then(setObras).catch(() => setObras([])); }, [api]);
  return (
    <>
      <div className="container">
        <InstallPwaBanner />
        <div className="partner-hero fade-up">
          <div className="eyebrow"><Tricolore thin /> Portal do Terceirizado</div>
          <h1 className="page-title" style={{ marginTop: 12 }}>Bem-vindo, <em>{user?.nome || "terceirizado"}</em></h1>
          <p className="page-sub" style={{ maxWidth: 720, marginTop: 14 }}>
            Este é o seu espaço para <strong style={{ color: "var(--gold)" }}>acompanhar as obras</strong> em que você está vinculado e <strong style={{ color: "var(--gold)" }}>enviar fotos e vídeos</strong> direto do canteiro. Toda mídia passa por uma curadoria rápida da CAMASA antes de aparecer para o cliente.
          </p>
        </div>

        {/* Como usar (instruções passo-a-passo) */}
        <div className="partner-howto fade-up">
          <div className="howto-eyebrow">Como funciona</div>
          <div className="howto-steps">
            <div className="howto-step">
              <div className="howto-num">01</div>
              <div className="howto-body">
                <div className="howto-title">Escolha uma obra</div>
                <div className="howto-desc">Abaixo você vê todas as obras em que a CAMASA te vinculou. Clique para entrar.</div>
              </div>
            </div>
            <div className="howto-step">
              <div className="howto-num">02</div>
              <div className="howto-body">
                <div className="howto-title">Abra a aba <em>Mídias</em></div>
                <div className="howto-desc">Na obra, use o menu lateral esquerdo. Clique em <strong>MÍDIAS</strong>.</div>
              </div>
            </div>
            <div className="howto-step">
              <div className="howto-num">03</div>
              <div className="howto-body">
                <div className="howto-title">Envie fotos ou vídeos</div>
                <div className="howto-desc">Arraste os arquivos para a área pontilhada ou use o botão <strong>"Enviar mídias"</strong>. Fotos vão para a fila de aprovação da CAMASA.</div>
              </div>
            </div>
            <div className="howto-step">
              <div className="howto-num">04</div>
              <div className="howto-body">
                <div className="howto-title">Consulte <em>Documentos</em></div>
                <div className="howto-desc">Plantas, medições e fichas técnicas da pedra ficam na aba <strong>DOCUMENTOS</strong> de cada obra.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Obras vinculadas */}
        <div className="partner-obras fade-up">
          <div className="howto-eyebrow">Suas obras · {obras?.length ?? "…"}</div>
          {obras === null ? (
            <p className="page-sub">Carregando…</p>
          ) : obras.length === 0 ? (
            <div className="empty" data-testid="partner-empty">
              <Building2 size={32} color="var(--gold)" style={{ margin: "0 auto 14px", display: "block", opacity: 0.6 }} />
              <h3>Você ainda não está vinculado a nenhuma obra</h3>
              <p>Assim que a CAMASA vincular você a uma obra, ela aparecerá aqui. Entre em contato com a equipe caso precise de acesso urgente.</p>
            </div>
          ) : (
            <div className="partner-obra-grid">
              {obras.map(o => (
                <Link to={`/obras/${o.id}`} key={o.id} className="partner-obra-card" data-testid={`partner-obra-${o.id}`}>
                  <div className="partner-obra-media" style={{ backgroundImage: `url(${fileUrl(o.imagem_capa) || "https://images.unsplash.com/photo-1615873968403-89e068629265?w=800&q=80"})` }}>
                    <div className="partner-obra-code">{o.codigo}</div>
                  </div>
                  <div className="partner-obra-body">
                    <div className="partner-obra-name">{o.nome}</div>
                    <div className="partner-obra-meta">{o.cliente_nome} · {o.cidade}/{o.estado}</div>
                    <div className="partner-obra-cta">
                      Entrar e enviar mídias <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

function ClienteHome() {
  const { api, user } = useAuth();
  const [obras, setObras] = useState(null);
  useEffect(() => { api("get", "/obras").then(setObras).catch(() => setObras([])); }, [api]);
  const obra = obras?.[0];
  return (
    <>
      <div className="container">
        <InstallPwaBanner />
        <div className="partner-hero fade-up">
          <div className="eyebrow"><Tricolore thin /> Cliente CAMASA</div>
          <h1 className="page-title" style={{ marginTop: 12 }}>Olá, <em>{user?.nome || obra?.cliente_nome || "cliente"}</em></h1>
          <p className="page-sub" style={{ maxWidth: 720, marginTop: 14 }}>
            Acompanhe em tempo real a <strong style={{ color: "var(--gold)" }}>evolução da sua obra</strong>, consulte documentos, veja as fotos aprovadas e acompanhe o <strong style={{ color: "var(--gold)" }}>rastreio do frete</strong> estilo Mercado Livre.
          </p>
        </div>

        {/* Como usar */}
        <div className="partner-howto fade-up">
          <div className="howto-eyebrow">O que você pode fazer por aqui</div>
          <div className="howto-steps">
            <div className="howto-step"><div className="howto-num">01</div><div className="howto-body">
              <div className="howto-title">Acompanhe as <em>etapas</em></div>
              <div className="howto-desc">Entre na sua obra abaixo e veja a aba <strong>Etapas</strong> para conferir o % de progresso em tempo real.</div>
            </div></div>
            <div className="howto-step"><div className="howto-num">02</div><div className="howto-body">
              <div className="howto-title">Veja as <em>fotos</em></div>
              <div className="howto-desc">Na aba <strong>Mídias</strong>, confira fotos e vídeos do canteiro já aprovados pela curadoria CAMASA.</div>
            </div></div>
            <div className="howto-step"><div className="howto-num">03</div><div className="howto-body">
              <div className="howto-title">Consulte <em>Documentos</em></div>
              <div className="howto-desc">Contrato, orçamento, notas fiscais e fichas técnicas da pedra ficam na aba <strong>Documentos</strong>.</div>
            </div></div>
            <div className="howto-step"><div className="howto-num">04</div><div className="howto-body">
              <div className="howto-title">Rastreie o <em>frete</em></div>
              <div className="howto-desc">Na <strong>Visão Geral</strong>, acompanhe a pedra saindo da marmoraria até chegar na sua obra.</div>
            </div></div>
          </div>
        </div>

        {obras === null ? <p className="page-sub">Carregando…</p> :
          !obra ? <div className="empty"><h3>Nenhuma obra vinculada ao seu acesso</h3></div> :
            <div className="partner-obras fade-up">
              <div className="howto-eyebrow">Sua obra</div>
              <div className="partner-obra-grid" style={{ marginTop: 18 }}>
                <Link to={`/obras/${obra.id}`} className="partner-obra-card" data-testid={`client-obra-${obra.id}`}>
                  <div className="partner-obra-media" style={{ backgroundImage: `url(${fileUrl(obra.imagem_capa) || "https://images.unsplash.com/photo-1615873968403-89e068629265?w=800&q=80"})` }}>
                    <div className="partner-obra-code">{obra.codigo}</div>
                  </div>
                  <div className="partner-obra-body">
                    <div className="partner-obra-name">{obra.nome}</div>
                    <div className="partner-obra-meta">{obra.cidade}/{obra.estado}</div>
                    <div className="partner-obra-cta">Acompanhar obra <ArrowRight size={12} /></div>
                  </div>
                </Link>
              </div>
            </div>}
      </div>
      <Footer />
      {obra && <CamasaAIChat obraId={obra.id} obraNome={obra.nome} clienteNome={user?.nome || obra.cliente_nome} authToken={localStorage.getItem("cps_token")} obraStatus={obra.status} />}
    </>
  );
}

function ArquitetoHome() {
  const { api, user } = useAuth();
  const [obras, setObras] = useState(null);
  useEffect(() => { api("get", "/obras").then(setObras).catch(() => setObras([])); }, [api]);
  return (
    <>
      <div className="container">
        <InstallPwaBanner />
        <div className="partner-hero fade-up">
          <div className="eyebrow"><Tricolore thin /> Portal do Arquiteto</div>
          <h1 className="page-title" style={{ marginTop: 12 }}>Olá, <em>{user?.nome || "arquiteto"}</em></h1>
          <p className="page-sub" style={{ maxWidth: 720, marginTop: 14 }}>
            Aqui você <strong style={{ color: "var(--gold)" }}>acompanha as obras dos seus clientes</strong>, consulta plantas, medições técnicas e acompanha cada etapa em tempo real.
          </p>
        </div>

        {/* Como usar */}
        <div className="partner-howto fade-up">
          <div className="howto-eyebrow">Como aproveitar o Portal</div>
          <div className="howto-steps">
            <div className="howto-step"><div className="howto-num">01</div><div className="howto-body">
              <div className="howto-title">Selecione a <em>obra</em></div>
              <div className="howto-desc">Abaixo você vê todas as obras dos seus clientes. Clique em uma para abrir.</div>
            </div></div>
            <div className="howto-step"><div className="howto-num">02</div><div className="howto-body">
              <div className="howto-title">Consulte <em>Documentos</em></div>
              <div className="howto-desc">Na aba <strong>Documentos</strong>, baixe plantas, medições, fichas técnicas e laudos da pedra.</div>
            </div></div>
            <div className="howto-step"><div className="howto-num">03</div><div className="howto-body">
              <div className="howto-title">Acompanhe as <em>Etapas</em></div>
              <div className="howto-desc">Veja o progresso de cada fase — da seleção do material à instalação final.</div>
            </div></div>
            <div className="howto-step"><div className="howto-num">04</div><div className="howto-body">
              <div className="howto-title">Veja <em>Mídias</em> e <em>Frete</em></div>
              <div className="howto-desc">Fotos aprovadas do canteiro e rastreio do frete para não perder nenhum detalhe.</div>
            </div></div>
          </div>
        </div>

        {obras === null ? <p className="page-sub">Carregando…</p> :
          obras.length === 0 ? <div className="empty"><h3>Nenhuma obra vinculada a você ainda</h3><p>Entre em contato com a CAMASA para ser vinculado.</p></div> :
            <div className="partner-obras fade-up">
              <div className="howto-eyebrow">Obras dos seus clientes · {obras.length}</div>
              <div className="partner-obra-grid" style={{ marginTop: 18 }}>
                {obras.map(o => (
                  <Link to={`/obras/${o.id}`} key={o.id} className="partner-obra-card">
                    <div className="partner-obra-media" style={{ backgroundImage: `url(${fileUrl(o.imagem_capa) || "https://images.unsplash.com/photo-1615873968403-89e068629265?w=800&q=80"})` }}>
                      <div className="partner-obra-code">{o.codigo}</div>
                    </div>
                    <div className="partner-obra-body">
                      <div className="partner-obra-name">{o.nome}</div>
                      <div className="partner-obra-meta">{o.cliente_nome} · {o.cidade}/{o.estado}</div>
                      <div className="partner-obra-cta">Abrir obra <ArrowRight size={12} /></div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>}
      </div>
      <Footer />
    </>
  );
}

/**
 * Alerta de margem baixa/negativa no Painel — só admin/financeiro.
 * Mostra obras ativas com margem_projetada_pct < 20% ou margem < 0 como cards de atenção.
 */
function GargalosCard({ gargalos, obrasComAlerta }) {
  const total = gargalos.reduce((acc, g) => acc + g.count, 0);
  const max = Math.max(...gargalos.map(g => g.count), 1);
  return (
    <div className="container" style={{ marginTop: 24, marginBottom: 16 }}>
      <div data-testid="gargalos-card" style={{
        border: "1px solid var(--line)",
        background: "linear-gradient(135deg, rgba(184,147,90,0.05) 0%, rgba(13,13,15,0.6) 100%)",
        padding: "22px 26px",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Layers size={16} style={{ color: "#b8935a" }} />
            <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--ink-3)", textTransform: "uppercase", fontWeight: 600 }}>
              Gargalos · etapas em execução
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 11, color: "var(--ink-3)" }}>
            <span><strong style={{ color: "var(--ink)" }}>{total}</strong> execuções</span>
            {obrasComAlerta > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#c47a7a" }} data-testid="gargalos-alerta-count">
                <AlertTriangle size={12} /> <strong>{obrasComAlerta}</strong> parada{obrasComAlerta === 1 ? "" : "s"} &gt; 7d
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {gargalos.map(g => {
            const pct = (g.count / max) * 100;
            return (
              <div key={g.etapa} data-testid={`gargalo-${g.etapa}`} style={{ display: "grid", gridTemplateColumns: "240px 1fr 50px", alignItems: "center", gap: 14 }}>
                <div style={{ fontSize: 12, color: "var(--ink-2)", textTransform: "uppercase", letterSpacing: ".15em", fontWeight: 500 }}>
                  {g.etapa}
                </div>
                <div style={{ height: 8, background: "rgba(184,147,90,0.08)", borderRadius: 0, position: "relative", overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${pct}%`,
                    background: "linear-gradient(90deg, #b8935a 0%, #d4b27a 100%)",
                    transition: "width 0.6s ease-out",
                  }} />
                </div>
                <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, color: "var(--ink)", textAlign: "right" }}>
                  {g.count}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function MarginAlertCard() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("camasa_margin_alert_dismissed") === new Date().toDateString(); }
    catch { return false; }
  });

  useEffect(() => {
    api("get", "/dre/consolidado").then(setData).catch(() => { });
  }, [api]);

  if (!data || dismissed) return null;

  // Filtra: margem projetada <20% ou < 0 — só obras ativas/em andamento (ignora concluídas e sem receita)
  const critical = (data.obras || []).filter(o =>
    o.status === "em_andamento" &&
    (o.custo_total > 0 || o.receita_realizada > 0) &&  // ignora obras vazias/de teste
    (o.margem_projetada_pct < 20 || o.margem_projetada < 0)
  );

  if (critical.length === 0) return null;

  const dismiss = () => {
    try { localStorage.setItem("camasa_margin_alert_dismissed", new Date().toDateString()); } catch { }
    setDismissed(true);
  };

  return (
    <div className="container" style={{ marginTop: 24, marginBottom: 16 }}>
      <div data-testid="margin-alert" style={{
        border: "1px solid rgba(196,122,122,0.4)",
        background: "linear-gradient(135deg, rgba(196,122,122,0.08) 0%, rgba(196,122,122,0.02) 100%)",
        padding: "22px 26px",
        position: "relative",
      }}>
        <button onClick={dismiss} data-testid="margin-alert-dismiss" style={{
          position: "absolute", top: 10, right: 14, background: "none", border: "none",
          color: "var(--ink-3)", fontSize: 18, cursor: "pointer",
        }} title="Ocultar até amanhã">×</button>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <div style={{ width: 10, height: 10, background: "#c47a7a", borderRadius: "50%", animation: "pulse 2s ease-in-out infinite" }} />
          <div style={{ fontSize: 10, letterSpacing: ".3em", color: "#c47a7a", textTransform: "uppercase", fontWeight: 600 }}>
            Alerta de margem · {critical.length} obra{critical.length === 1 ? "" : "s"}
          </div>
        </div>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 22, color: "var(--ink)", margin: "0 0 14px", lineHeight: 1.2 }}>
          {critical.length === 1 ? "Uma obra está" : "Obras estão"} com margem abaixo de 20%.
        </h3>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10, marginBottom: 16 }}>
          {critical.slice(0, 4).map(o => {
            const isNeg = o.margem_projetada < 0;
            return (
              <div key={o.id} data-testid={`margin-alert-obra-${o.id}`} style={{
                padding: "12px 14px",
                background: "rgba(13,13,15,0.4)",
                border: "1px solid rgba(196,122,122,0.25)",
                borderLeft: `3px solid ${isNeg ? "#c47a7a" : "#d4b57e"}`,
              }}>
                <div style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-3)", textTransform: "uppercase", marginBottom: 4 }}>{o.codigo}</div>
                <div style={{ color: "var(--ink)", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>{o.nome}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, color: "var(--ink-3)" }}>Margem projetada</span>
                  <span style={{ fontFamily: "'Fraunces', serif", fontSize: 18, color: isNeg ? "#c47a7a" : "#d4b57e", fontVariantNumeric: "tabular-nums" }}>
                    {o.margem_projetada_pct}%
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                  {fmtBRL(o.margem_projetada)} · custo {fmtBRL(o.custo_total)}
                </div>
              </div>
            );
          })}
        </div>

        {critical.length > 4 && (
          <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 14 }}>
            + {critical.length - 4} outra{critical.length - 4 === 1 ? "" : "s"} obra{critical.length - 4 === 1 ? "" : "s"} abaixo de 20%.
          </div>
        )}

        <Link to="/dre" className="btn btn-ghost btn-sm" data-testid="margin-alert-open-dre" style={{ borderColor: "rgba(196,122,122,0.4)", color: "#c47a7a" }}>
          Ver DRE detalhado →
        </Link>
      </div>
    </div>
  );
}



function BackupExportCard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', text }

  const exportar = async () => {
    setLoading(true); setFeedback(null);
    try {
      const res = await axios.get(`${API}/admin/backup-export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      // Força download no navegador
      const today = new Date().toISOString().slice(0, 10);
      const filename = `cps_backup_${today}.zip`;
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setFeedback({ type: "success", text: `Backup exportado com sucesso (${(blob.size / 1024).toFixed(0)} KB)` });
      setTimeout(() => setFeedback(null), 5000);
    } catch (e) {
      setFeedback({ type: "error", text: e?.response?.data?.detail || e?.message || "Erro ao gerar backup" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ marginBottom: 24 }} data-testid="backup-export-card">
      <div style={{
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        padding: "16px 22px",
        background: "rgba(184,147,90,0.04)",
        border: "1px solid rgba(184,147,90,0.18)",
        borderLeft: "3px solid rgba(184,147,90,0.55)",
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 4 }}>
            Cópia de segurança
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 }}>
            Exporta o banco inteiro em um ZIP (JSON por coleção). Faça backup mensal pra garantir.
          </div>
          {feedback && (
            <div data-testid={`backup-feedback-${feedback.type}`} style={{
              marginTop: 8, fontSize: 12,
              color: feedback.type === "success" ? "#7bbf86" : "#c47a7a",
            }}>
              {feedback.type === "success" ? "✓ " : "⚠ "}{feedback.text}
            </div>
          )}
        </div>
        <button
          className="btn btn-primary"
          onClick={exportar}
          disabled={loading}
          data-testid="backup-export-btn"
          style={{ opacity: loading ? 0.7 : 1, minWidth: 200 }}
        >
          {loading ? "Gerando backup…" : "⬇ Exportar Backup"}
        </button>
      </div>
    </div>
  );
}


function DashboardAdmin() {
  const { api, user } = useAuth();
  const [s, setS] = useState(null);
  const [obras, setObras] = useState([]);
  const isAdmin = ["admin", "financeiro", "gestor"].includes(user?.role);
  const canSeeFin = ["admin", "financeiro", "cliente", "arquiteto"].includes(user?.role);

  const reload = useCallback(() => {
    api("get", "/dashboard/summary").then(setS).catch(() => { });
    api("get", "/obras").then(setObras).catch(() => { });
  }, [api]);
  useEffect(() => { reload(); }, [reload]);

  if (!s) return <div className="container"><p className="page-sub">Carregando painel…</p></div>;

  return (
    <>
      <section className="hero-v2 fade-up">
        <div className="hero-v2-logo">
          <img src="/cps-logo.jpg" alt="CAMASA Process System" style={{ height: 140, width: "auto", maxWidth: 560, objectFit: "contain", filter: "drop-shadow(0 0 28px rgba(184,147,90,0.35))" }} />
        </div>
        <div className="eyebrow" style={{ justifyContent: "center" }}><Tricolore thin /> Minhas Obras <Tricolore thin /></div>
        <h1 className="hero-v2-title">Controle <em>absoluto</em><br />sobre cada obra.</h1>
        <p className="hero-v2-sub">
          Da <em style={{ fontStyle: "italic" }}>seleção da matéria-prima</em> à entrega — produção, frete, instalação e financeiro em um só lugar, com a elegância italiana que sua marca merece.
        </p>
        <div className="hero-v2-cta">
          <Link to="/obras" className="btn btn-primary" data-testid="btn-ver-obras">Ver Minhas Obras <ArrowRight size={13} /></Link>
          <Link to="/signature/fabio-fukuda" className="btn btn-ghost">Signature Book <ArrowUpRight size={13} /></Link>
        </div>
        {canSeeFin && s.valor_sob_gestao != null && (
          <div className="hero-v2-metric">
            <div className="hero-metric-big shimmer">{brl(s.valor_sob_gestao)}</div>
            <div className="hero-metric-lbl">Valor sob gestão · {s.obras_total} obras · {s.valor_sob_gestao ? Math.round(s.recebido / s.valor_sob_gestao * 100) : 0}% recebido</div>
          </div>
        )}
      </section>

      <div className="kpis-wrap">
        <div className="kpis stagger">
          <div className="kpi"><div className="kpi-label">Obras ativas</div><div className="kpi-value">{String(s.obras_ativas).padStart(2, "0")}</div><div className="kpi-delta"><TrendingUp size={11} />pipeline</div></div>
          <div className="kpi"><div className="kpi-label">Concluídas</div><div className="kpi-value">{String(s.obras_concluidas).padStart(2, "0")}</div><div className="kpi-delta"><CheckCircle2 size={11} />entregues</div></div>
          <div className="kpi">
            <div className="kpi-label">A receber</div>
            {s.a_receber != null ? <><div className="kpi-value">{brl(s.a_receber)}</div><div className="kpi-delta"><Clock size={11} />previsão</div></> : <div className="kpi-hidden">restrito</div>}
          </div>
          <div className="kpi"><div className="kpi-label">Prestadores</div><div className="kpi-value">{String(s.prestadores_ativos).padStart(2, "0")}</div><div className="kpi-delta"><TrendingUp size={11} />ativos</div></div>
        </div>
      </div>

      {(user?.role === "admin" || user?.role === "financeiro") && <MarginAlertCard />}

      {user?.role === "admin" && <BackupExportCard />}

      {(user?.role === "admin" || user?.role === "gestor" || user?.role === "financeiro") &&
        s.gargalos && s.gargalos.length > 0 && <GargalosCard gargalos={s.gargalos} obrasComAlerta={s.obras_com_alerta} />}

      <div className="container">
        {(user?.role === "admin" || user?.role === "gestor" || user?.role === "financeiro") && <ObrasAtencaoCard />}
        <HowToBanner id="dashboard-admin" title="Início Rápido — CPS em 6 passos" steps={[
          { num: 1, title: "Cadastre Arquitetos e Terceirizados", desc: "No menu superior, entre em <strong>Arquitetos</strong> e <strong>Terceirizados</strong> e cadastre quem vai colaborar na obra." },
          { num: 2, title: "Cadastre Materiais do catálogo", desc: "Em <strong>Materiais</strong>, suba fotos reais das pedras (mármores, quartzitos) que você vai usar nas obras." },
          { num: 3, title: "Crie a Obra", desc: "Em <strong>Obras → Nova Obra</strong>, preencha cliente, endereço, valor e datas. O código CPS-XXXX é gerado automático." },
          { num: 4, title: "Vincule tudo à obra", desc: "Dentro da obra, clique em <strong>Editar</strong> e marque: arquiteto, materiais, prestadores e terceirizados que vão atuar." },
          { num: 5, title: "Compartilhe Magic Links", desc: "Na Visão Geral use <strong>🔗 Link do Cliente</strong> e <strong>🔗 Link do Arquiteto</strong> para dar acesso sem senha." },
          { num: 6, title: "Opere o dia-a-dia", desc: "Atualize <strong>Etapas</strong>, suba mídias, aprove fotos enviadas pelos terceirizados na <strong>Aprovação</strong>, atualize o rastreio do frete e lance o financeiro. Tudo aparece em tempo real para todos." },
        ]} />
      </div>

      <section className="section">
        <div className="section-head">
          <div><div className="eyebrow"><Tricolore thin /> Pipeline</div><h2 className="section-title">Obras em <em>curso</em></h2></div>
          <Link to="/obras" className="btn btn-ghost">Ver todas <ArrowRight size={13} /></Link>
        </div>
        {obras.length === 0 ? <div className="empty"><h3>Nenhuma obra ainda</h3><p>Crie sua primeira obra para começar.</p></div> :
          <div className="obras-grid stagger">{obras.slice(0, 6).map(o => <ObraCard key={o.id} o={o} onAction={reload} />)}</div>}
      </section>

      <Footer />
    </>
  );
}

function ObraCard({ o, onAction }) {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const canEdit = ["admin", "gestor"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const etapasConcluidas = (o.etapas || []).filter(e => e.status === "concluida").length;
  const total = (o.etapas || []).length || 8;
  const pct = Math.round(etapasConcluidas / total * 100);
  const status = STATUS_OBRA[o.status] || STATUS_OBRA.em_andamento;
  const etapaAtual = (o.etapas || []).find(e => e.status === "em_andamento")?.nome || (o.status === "concluida" ? "Entregue" : "Aguardando");

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const openCard = () => navigate(`/obras/${o.id}`);
  const arquivar = async (e) => {
    stop(e);
    if (!window.confirm(`Arquivar "${o.nome}"? Pode ser restaurada depois.`)) return;
    await api("post", `/obras/${o.id}/arquivar`); onAction && onAction();
  };
  const restaurar = async (e) => {
    stop(e);
    await api("post", `/obras/${o.id}/restaurar`); onAction && onAction();
  };
  const excluir = async (e) => {
    stop(e);
    const c = window.prompt(`EXCLUSÃO PERMANENTE de "${o.nome}".\n\nApaga MongoDB + todos arquivos do disco — IRREVERSÍVEL.\n\nDigite o código (${o.codigo}) para confirmar:`);
    if (!c || c.trim() !== o.codigo) { if (c !== null) alert("Código incorreto. Operação cancelada."); return; }
    if (!window.confirm("Confirma DEFINITIVAMENTE a exclusão?")) return;
    await api("delete", `/obras/${o.id}?hard=true`); onAction && onAction();
  };

  return (
    <div className="obra-card-wrap" style={{ position: "relative" }}>
      <div role="link" tabIndex={0} onClick={openCard} onKeyDown={e => { if (e.key === "Enter") openCard(); }} className="obra-card" data-testid={`obra-card-${o.id}`} style={{ cursor: "pointer" }}>
        <div className="obra-img" style={{ backgroundImage: `url(${fileUrl(o.imagem_capa) || "https://images.unsplash.com/photo-1615873968403-89e068629265?w=1000&q=85"})` }}>
          <div className={`obra-badge ${status.c}`}>{status.l}</div>
          {(o._alertas || []).length > 0 && (
            <div
              data-testid={`obra-alert-${o.id}`}
              title={(o._alertas || []).filter(a => a.tipo === "etapa_parada").map(a => `${a.etapa} parada há ${a.dias} dias`).join(" · ") || "Documentação atrasada"}
              style={{
                position: "absolute", top: 12, right: 12,
                background: "rgba(196,122,122,0.92)",
                color: "#0d0d0f",
                padding: "5px 10px",
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".05em",
                display: (o._alertas || []).some(a => a.tipo === "etapa_parada") ? "inline-flex" : "none",
                alignItems: "center",
                gap: 5,
                boxShadow: "0 2px 12px rgba(196,122,122,0.4)",
                animation: "pulse 2s ease-in-out infinite",
              }}>
              <AlertTriangle size={12} />
              {(() => {
                const ep = (o._alertas || []).find(a => a.tipo === "etapa_parada");
                return ep ? `${ep.etapa} · ${ep.dias}d` : "";
              })()}
            </div>
          )}
          {(o._alertas || []).some(a => a.tipo === "gap_documentacao") && (
            <div
              data-testid={`obra-gap-doc-${o.id}`}
              title="Documentação atrasada em relação ao avanço da obra"
              style={{
                position: "absolute",
                top: (o._alertas || []).some(a => a.tipo === "etapa_parada") ? 48 : 12,
                right: 12,
                background: "rgba(212,178,122,0.92)",
                color: "#0d0d0f",
                padding: "5px 10px",
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".05em",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                boxShadow: "0 2px 10px rgba(212,178,122,0.4)",
              }}>
              <AlertTriangle size={11} />
              {(() => {
                const g = (o._alertas || []).find(a => a.tipo === "gap_documentacao");
                return g ? `DOC ${g.gap_pct}pp` : "";
              })()}
            </div>
          )}
        </div>
        <div className="obra-body">
          <div className="obra-client">{o.codigo} · {o.cidade}</div>
          <h3 className="obra-name">{o.nome}</h3>
          <div style={{ marginTop: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--ink-3)", letterSpacing: ".2em", textTransform: "uppercase", marginBottom: 8 }}>
              <span>{etapaAtual}</span><span>{pct}%</span>
            </div>
            <div className="progress"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
          </div>
          {o.valor_total != null && <div className="obra-row"><span className="l">Valor</span><span className="v">{brl(o.valor_total)}</span></div>}
          <div className="obra-row" style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}><span className="l">Cliente</span><span className="v">{o.cliente_nome}</span></div>
          {canEdit && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)", display: "flex", gap: 6, flexWrap: "wrap" }} onClick={stop}>
              <Link to={`/obras/${o.id}`} className="btn btn-ghost btn-sm" style={{ padding: "5px 10px", fontSize: 10 }} data-testid={`card-edit-${o.id}`}>Abrir / Editar</Link>
              {o.status === "arquivada" ? (
                <button className="btn btn-ghost btn-sm" style={{ padding: "5px 10px", fontSize: 10 }} onClick={restaurar} data-testid={`card-restore-${o.id}`}>Restaurar</button>
              ) : (
                <button className="btn btn-ghost btn-sm" style={{ padding: "5px 10px", fontSize: 10 }} onClick={arquivar} data-testid={`card-arch-${o.id}`}>Arquivar</button>
              )}
              {isAdmin && <button className="btn btn-ghost btn-sm" style={{ padding: "5px 10px", fontSize: 10, color: "#c47a7a", borderColor: "#4a2a2a" }} onClick={excluir} data-testid={`card-del-${o.id}`}>Excluir</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ========== OBRAS LIST ========== */
function ObrasList() {
  const { api, user } = useAuth();
  const [obras, setObras] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    api("get", `/obras?include_arquivadas=${showArchived}`).then(setObras).catch(() => { });
  }, [api, showArchived]);
  useEffect(() => { load(); }, [load]);

  const canCreate = ["admin", "gestor"].includes(user?.role);

  return (
    <>
      <div className="container">
        <div className="page-head fade-up">
          <div>
            <div className="eyebrow"><Tricolore thin /> Portfólio</div>
            <h1 className="page-title"><em>Obras</em></h1>
            <p className="page-sub">Gestão integrada — produção, logística, instalação e pós-entrega.</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className={"btn btn-sm " + (showArchived ? "btn-primary" : "btn-ghost")} onClick={() => setShowArchived(v => !v)} data-testid="toggle-archived">
              <Archive size={12} /> {showArchived ? "Ocultar arquivadas" : "Ver arquivadas"}
            </button>
            {canCreate && <button className="btn" onClick={() => setShowNew(true)} data-testid="btn-nova-obra"><Plus size={13} /> Nova Obra</button>}
          </div>
        </div>

        {canCreate && (
          <HowToBanner id="obras-list" title="Como gerenciar Obras" steps={[
            { num: 1, title: "Crie uma nova obra", desc: "Clique em <strong>Nova Obra</strong> no topo e preencha: cliente, endereço, datas e valor. O código CPS-XXXX é gerado automaticamente." },
            { num: 2, title: "Vincule pessoas e materiais", desc: "Dentro da obra, use <strong>Editar</strong> para vincular: arquiteto responsável, materiais (mármores/pedras) e terceirizados (marmoraria, fretes etc)." },
            { num: 3, title: "Compartilhe acompanhamento", desc: "Na Visão Geral, use <strong>🔗 Link do Cliente</strong> e <strong>🔗 Link do Arquiteto</strong> — eles entram sem senha e acompanham em tempo real." },
            { num: 4, title: "Atualize semanalmente", desc: "Marque etapas como concluídas, suba fotos/vídeos do canteiro, atualize o rastreio do frete e anexe contratos em <strong>Documentos</strong>." },
          ]} />
        )}
        {obras.length === 0 ? <div className="empty"><h3>Nenhuma obra cadastrada</h3></div> :
          <div className="obras-grid stagger">{obras.map(o => <ObraCard key={o.id} o={o} onAction={load} />)}</div>}
      </div>
      {showNew && <NovaObraModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      <Footer />
    </>
  );
}

function NovaObraModal({ onClose, onSaved }) {
  const { api } = useAuth();
  const [arquitetos, setArquitetos] = useState([]);
  const [form, setForm] = useState({ numero_orcamento: "", nome: "", cliente_nome: "", cidade: "São Paulo", valor_total: 0, arquiteto_id: "" });
  const [loading, setLoading] = useState(false);
  const [novoArq, setNovoArq] = useState(false);
  useEffect(() => { api("get", "/arquitetos").then(setArquitetos).catch(() => { }); }, [api]);
  const save = async () => {
    setLoading(true);
    try {
      await api("post", "/obras", {
        ...form,
        valor_total: parseFloat(form.valor_total) || 0,
        arquiteto_id: form.arquiteto_id || null,
        numero_orcamento: (form.numero_orcamento || "").trim() || null,
      });
      onSaved();
    } catch (e) {
      // Mensagem útil em vez do genérico "Erro ao salvar"
      let msg;
      if (e?.response?.data?.detail) {
        msg = typeof e.response.data.detail === "string" ? e.response.data.detail : JSON.stringify(e.response.data.detail);
      } else if (e?.response?.status) {
        msg = `Servidor respondeu ${e.response.status} ${e.response.statusText || ""}`;
      } else if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
        msg = "Tempo esgotado. A obra pode ter sido criada — recarregue a lista e confira antes de tentar de novo.";
      } else if (e?.message?.includes("Network")) {
        msg = "Sem conexão com o servidor. Verifique sua internet e tente de novo.";
      } else {
        msg = e?.message || "Erro desconhecido";
      }
      alert("Não foi possível criar a obra:\n\n" + msg);
    }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <h3 className="modal-title">Nova <em>obra</em></h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* Campo Nº do Orçamento — primeiro do formulário, bem visível. */}
          <div className="field">
            <label>Nº do Orçamento</label>
            <input
              data-testid="form-numero-orcamento"
              value={form.numero_orcamento}
              onChange={e => setForm({ ...form, numero_orcamento: e.target.value })}
              placeholder="Ex: 6567"
              style={{ fontSize: 14 }}
            />
          </div>
          <div className="field"><label>Nome da obra</label><input data-testid="form-nome" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="Residência…" /></div>
          <div className="field"><label>Cliente</label><input data-testid="form-cliente" value={form.cliente_nome} onChange={e => setForm({ ...form, cliente_nome: e.target.value })} /></div>
          <div className="field"><label>Cidade</label><input value={form.cidade} onChange={e => setForm({ ...form, cidade: e.target.value })} /></div>
          <div className="field"><label>Valor total (R$)</label><input data-testid="form-valor" type="number" value={form.valor_total} onChange={e => setForm({ ...form, valor_total: e.target.value })} /></div>
          <div className="field">
            <label>Arquiteto (opcional)</label>
            <select value={form.arquiteto_id} onChange={e => setForm({ ...form, arquiteto_id: e.target.value })} data-testid="form-arquiteto">
              <option value="">— nenhum —</option>
              {arquitetos.map(a => <option key={a.id} value={a.id}>{a.nome} {a.escritorio && `— ${a.escritorio}`}</option>)}
            </select>
            <BtnCadastroRapido label="Novo Arquiteto" onClick={() => setNovoArq(true)} testid="nova-obra-novo-arquiteto" />
          </div>
          {novoArq && (
            <CadastroRapidoModal cfg={CADASTRO_RAPIDO.arquiteto} api={api} onClose={() => setNovoArq(false)}
              onCreated={a => { setArquitetos(l => [...l, a]); setForm(f => ({ ...f, arquiteto_id: a.id })); setNovoArq(false); }} />
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={loading || !form.nome || !form.cliente_nome} data-testid="form-save">{loading ? "Salvando…" : "Criar obra"}</button>
        </div>
      </div>
    </div>
  );
}

/* ========== OBRA DETAIL — WORKSPACE UNIFICADO ========== */

/**
 * NumeroOrcamentoEditor — mostra/edita o número do orçamento humano
 * (substitui CPS-0001 por CPS-{numero} nos materiais voltados ao cliente).
 */
function NumeroOrcamentoEditor({ obra, api, onReload }) {
  const [editing, setEditing] = useState(false);
  const [valor, setValor] = useState(obra.numero_orcamento || "");
  const [busy, setBusy] = useState(false);
  const salvar = async () => {
    setBusy(true);
    try {
      await api("patch", `/obras/${obra.id}`, { numero_orcamento: (valor || "").trim() || null });
      setEditing(false);
      onReload?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setBusy(false); }
  };
  if (!editing) {
    return (
      <div style={{ marginTop: 6, fontSize: 10, letterSpacing: ".15em", color: "var(--ink-3)", textTransform: "uppercase", display: "flex", gap: 6, alignItems: "center" }}>
        <span>Nº Orçamento:</span>
        <strong style={{ color: "var(--gold)" }} data-testid="ws-side-numero-orcamento">{obra.numero_orcamento || "—"}</strong>
        <button
          type="button"
          onClick={() => { setValor(obra.numero_orcamento || ""); setEditing(true); }}
          data-testid="btn-edit-numero-orcamento"
          style={{ background: "transparent", border: "1px solid var(--line)", color: "var(--gold)", fontSize: 9, padding: "1px 6px", letterSpacing: ".15em", cursor: "pointer" }}
        >Editar</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6, display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }} data-testid="numero-orcamento-editor">
      <input
        className="input"
        value={valor}
        onChange={e => setValor(e.target.value)}
        placeholder="Ex: 6567"
        autoFocus
        data-testid="input-numero-orcamento"
        style={{ flex: 1, minWidth: 80, fontSize: 11, fontFamily: "monospace" }}
      />
      <button className="btn btn-sm btn-primary" onClick={salvar} disabled={busy} data-testid="save-numero-orcamento" style={{ padding: "4px 8px", fontSize: 10 }}>{busy ? "…" : "Salvar"}</button>
      <button className="btn btn-sm btn-ghost" onClick={() => setEditing(false)} disabled={busy} style={{ padding: "4px 8px", fontSize: 10 }}>×</button>
    </div>
  );
}

function ObraDetail() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const nav = useNavigate();
  const [obra, setObra] = useState(null);
  const [tab, setTab] = useState("visao");

  const load = useCallback(() => {
    api("get", `/obras/${id}`).then(setObra).catch(() => { setObra(null); nav("/obras"); });
  }, [api, id, nav]);
  // B1 fix: limpa estado da obra anterior ao trocar de :id para evitar state leak.
  useEffect(() => { setObra(null); setTab("visao"); load(); }, [id, load]);

  const canEdit = ["admin", "gestor"].includes(user?.role);
  const canUpload = ["admin", "gestor", "terceirizado"].includes(user?.role);  // terceirizado pode só subir mídia (vai pra fila de aprovação)
  const canFin = ["admin", "financeiro"].includes(user?.role);
  const seeFin = ["admin", "financeiro", "cliente", "arquiteto"].includes(user?.role);
  const canMedicoes = ["admin", "gestor", "financeiro"].includes(user?.role);

  if (!obra) return <div className="container"><p className="page-sub">Carregando obra…</p></div>;

  const etapasConc = (obra.etapas || []).filter(e => e.status === "concluida").length;
  const pct = Math.round(etapasConc / (obra.etapas?.length || 1) * 100);

  const tabs = [
    { id: "visao", label: "Visão Geral", icon: Home },
    { id: "etapas", label: "Etapas", icon: Layers },
    { id: "materiais", label: "Materiais", icon: Package },
    { id: "midias", label: "Mídias", icon: ImageIcon },
    { id: "documentos", label: "Documentos", icon: Folder },
    { id: "equipe", label: "Equipe", icon: Users },
    ...(seeFin ? [{ id: "financeiro", label: "Financeiro", icon: Wallet }] : []),
    ...(["admin", "gestor", "financeiro"].includes(user?.role) ? [{ id: "lucro-real", label: "Lucro Real", icon: TrendingUp }] : []),
    ...(canMedicoes ? [{ id: "medicoes", label: "Medições", icon: Ruler }] : []),
    ...(["admin", "gestor", "financeiro"].includes(user?.role) ? [{ id: "fretes", label: "Fretes", icon: Truck }] : []),
    ...(["admin", "gestor", "financeiro"].includes(user?.role) ? [{ id: "servicos", label: "Serviços", icon: Hammer }] : []),
    ...(["admin", "gestor", "financeiro"].includes(user?.role) ? [{ id: "ocorrencias", label: "Ocorrências", icon: AlertTriangle }] : []),
    { id: "sb", label: "Signature Book", icon: FileText },
  ];

  return (
    <div className="ws">
      <aside className="ws-side">
        <div className="ws-side-head">
          <button className="ws-side-back" onClick={() => nav("/obras")}>← Voltar</button>
          <div className="ws-side-title">{obra.nome}</div>
          <div className="ws-side-code">{obraCpsCode(obra)} · {obra.cidade}</div>
          {/* Nº do orçamento — editável apenas para admin/gestor */}
          {canEdit && (
            <NumeroOrcamentoEditor obra={obra} api={api} onReload={load} />
          )}
        </div>
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} className={"ws-tab" + (tab === t.id ? " active" : "")} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}>
              <span className="ws-tab-icon"><Icon size={14} /></span>{t.label}
            </button>
          );
        })}
      </aside>

      <main className="ws-main">
        {tab === "visao" && <ObraVisao obra={obra} pct={pct} seeFin={seeFin} canEdit={canEdit} onReload={load} setTab={setTab} />}
        {tab === "etapas" && <ObraEtapas obra={obra} canEdit={canEdit} onReload={load} api={api} />}
        {tab === "materiais" && <ObraMateriais obra={obra} canEdit={canEdit} onReload={load} />}
        {tab === "midias" && <ObraMidias obra={obra} canEdit={canEdit} canUpload={canUpload} onReload={load} api={api} />}
        {tab === "documentos" && <ObraDocumentos obra={obra} canEdit={canEdit} onReload={load} api={api} />}
        {tab === "equipe" && <ObraEquipe obra={obra} />}
        {tab === "financeiro" && seeFin && <ObraFinanceiro obra={obra} canFin={canFin} api={api} onReload={load} />}
        {tab === "medicoes" && canMedicoes && <ObraMedicoesPanel obra={obra} api={api} onReload={load} />}
        {tab === "lucro-real" && ["admin", "gestor", "financeiro"].includes(user?.role) && <LucroRealPanel obraId={obra.id} api={api} />}
        {tab === "fretes" && ["admin", "gestor", "financeiro"].includes(user?.role) && <FretesPanel obraId={obra.id} obraNome={obra.nome} />}
        {tab === "servicos" && ["admin", "gestor", "financeiro"].includes(user?.role) && <ServicosPanel obraId={obra.id} userRole={user?.role} />}
        {tab === "ocorrencias" && ["admin", "gestor", "financeiro"].includes(user?.role) && <OcorrenciasPanel obraId={obra.id} />}
        {tab === "sb" && <ObraSignaturePanel obra={obra} api={api} onReload={load} />}
      </main>
    </div>
  );
}

/* ========== Seção pública do Termo de Medição Final (dentro do SB público) ========== */
function SBPublicTermoSection({ medicoes, publicToken, publicView }) {
  const termo = medicoes.termo_final;
  const aceite = medicoes.termo_aceite_cliente;
  const [showAceite, setShowAceite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", observacao: "" });
  const [localAceite, setLocalAceite] = useState(aceite);

  const submeter = async () => {
    if (!form.nome.trim() || form.nome.trim().length < 3) {
      alert("Informe seu nome completo (mín. 3 caracteres).");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/medicao/${publicToken}/aceite`, {
        nome: form.nome.trim(),
        email: form.email.trim() || null,
        observacao: form.observacao.trim() || null,
      });
      setLocalAceite(r.data.aceite);
      setShowAceite(false);
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ marginTop: 24, padding: 22, border: "1px solid var(--gold-3)", background: "rgba(184,147,90,0.04)" }} data-testid="sb-public-termo-medicao">
      <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 8 }}>Termo de Medição Final</div>
      <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontStyle: "italic", color: "var(--ink)", marginBottom: 6 }}>
        Comparativo Orçado · Realizado
      </div>
      <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.7, marginBottom: 18 }}>
        Documento formal de fechamento financeiro da obra, com comparativo detalhado entre o orçamento aprovado e o que foi efetivamente realizado, ambiente por ambiente.
      </p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <a href={fileUrl(termo.url)} target="_blank" rel="noreferrer" className="btn btn-primary" data-testid="sb-public-termo-view">
          <FileText size={13} /> Visualizar Termo (PDF)
        </a>
        <span style={{ fontSize: 11, color: "var(--ink-3)" }}>v{termo.version} · {Math.round((termo.size_bytes || 0) / 1024)}KB</span>
      </div>

      {localAceite ? (
        <div style={{ marginTop: 18, padding: 14, background: "rgba(95,168,108,0.10)", border: "1px solid var(--verde-2)", display: "flex", alignItems: "center", gap: 12 }} data-testid="sb-public-termo-aceito">
          <CheckCircle2 size={24} color="var(--verde-2)" />
          <div>
            <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--verde-2)", textTransform: "uppercase" }}>Termo aceito</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 18, color: "var(--ink)", marginTop: 4 }}>{localAceite.nome}</div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4, letterSpacing: ".08em" }}>
              {fmt(localAceite.data)} · {new Date(localAceite.data).toLocaleTimeString("pt-BR")} · IP {localAceite.ip || "—"}
            </div>
          </div>
        </div>
      ) : publicView && (
        <div style={{ marginTop: 18, padding: 14, border: "1px dashed var(--gold-3)", background: "rgba(184,147,90,0.03)" }}>
          <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 6 }}>Aceite separado</div>
          {!showAceite ? (
            <>
              <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.6, margin: "0 0 12px" }}>
                Após revisar o Termo, registre seu aceite formal abaixo. Este aceite é independente do aceite geral da obra e refere-se especificamente à conformidade dos valores medidos.
              </p>
              <button className="btn btn-primary" onClick={() => setShowAceite(true)} data-testid="sb-public-termo-aceitar-btn">
                <ShieldCheck size={13} /> Aceitar Termo de Medição
              </button>
            </>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                className="form-input"
                placeholder="Seu nome completo *"
                value={form.nome}
                onChange={e => setForm({ ...form, nome: e.target.value })}
                data-testid="sb-public-termo-nome"
              />
              <input
                className="form-input"
                type="email"
                placeholder="Seu e-mail (opcional)"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
              <textarea
                className="form-input"
                placeholder="Observação (opcional)"
                value={form.observacao}
                onChange={e => setForm({ ...form, observacao: e.target.value })}
                rows={2}
                style={{ resize: "vertical" }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-primary" onClick={submeter} disabled={submitting} data-testid="sb-public-termo-confirmar">
                  {submitting ? "Registrando…" : "Confirmar Aceite"}
                </button>
                <button className="btn btn-ghost" onClick={() => setShowAceite(false)} disabled={submitting}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MagicLinkButtons({ obra, api }) {
  const gen = async (audience, force = false) => {
    if (force && !window.confirm(`Rotacionar magic link do ${audience}? O link antigo será invalidado.`)) return;
    try {
      const r = await api("post", `/obras/${obra.id}/magic-link?audience=${audience}${force ? "&force=true" : ""}`);
      const verb = r.rotated ? "🔄 NOVO link" : "🔗 Link atual";
      const msg = `${verb} para ${audience.toUpperCase()} (${r.nome}):\n\n${r.magic_url}\n\n(copiado para a área de transferência)`;
      try { await navigator.clipboard.writeText(r.magic_url); } catch {}
      alert(msg);
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };
  return (
    <>
      <button className="btn btn-ghost btn-sm" style={{ borderColor: "var(--gold)", color: "var(--gold)" }} onClick={() => gen("cliente")} data-testid="btn-magic-cliente" title="Gera link para o cliente acompanhar a obra">
        🔗 Link do Cliente
      </button>
      {obra.arquiteto_id && (
        <button className="btn btn-ghost btn-sm" style={{ borderColor: "var(--gold)", color: "var(--gold)" }} onClick={() => gen("arquiteto")} data-testid="btn-magic-arquiteto" title="Gera link para o arquiteto acompanhar a obra">
          🔗 Link do Arquiteto
        </button>
      )}
    </>
  );
}

const FRETE_STEPS = [
  { k: "aguardando", l: "Aguardando coleta", icon: "⏳" },
  { k: "coletado", l: "Coletado", icon: "📦" },
  { k: "em_transito", l: "Em trânsito", icon: "🚚" },
  { k: "entregue", l: "Entregue no canteiro", icon: "✅" },
];

function FretePanel({ obra, canEdit, api, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const frete = obra.frete;
  const curIdx = frete ? FRETE_STEPS.findIndex(s => s.k === frete.status) : -1;
  const [form, setForm] = useState({
    status: frete?.status || "aguardando",
    localizacao: frete?.localizacao || "",
    previsao: frete?.previsao || "",
    motorista: frete?.motorista || "",
    nota: "",
  });
  const save = async () => {
    try {
      await api("post", `/obras/${obra.id}/frete`, form);
      setShowForm(false);
      onReload && onReload();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };
  return (
    <div className="panel" style={{ marginTop: 32 }}>
      <div className="panel-head">
        <div><div className="panel-eyebrow">Logística</div><h3 className="panel-title">Rastreio do <em>frete</em></h3></div>
        {canEdit && <button className="btn btn-sm" onClick={() => setShowForm(v => !v)} data-testid="btn-frete-update">
          {frete ? "Atualizar status" : "Iniciar rastreio"}
        </button>}
      </div>

      {/* Timeline */}
      <div className="frete-timeline" data-testid="frete-timeline">
        {FRETE_STEPS.map((s, i) => {
          const done = curIdx >= 0 && i <= curIdx;
          const current = i === curIdx;
          return (
            <div key={s.k} className={"frete-step " + (done ? "done " : "") + (current ? "current" : "")}>
              <div className="frete-dot">{s.icon}</div>
              <div className="frete-lbl">{s.l}</div>
            </div>
          );
        })}
      </div>

      {frete && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginTop: 22, padding: 20, background: "var(--bg-3)", border: "1px solid var(--line)" }}>
          {frete.localizacao && <div><div className="meta-row" style={{ padding: 0 }}><span className="l">📍 Localização</span></div><div style={{ color: "var(--ink)", fontSize: 14, marginTop: 4 }}>{frete.localizacao}</div></div>}
          {frete.previsao && <div><div className="meta-row" style={{ padding: 0 }}><span className="l">📅 Previsão</span></div><div style={{ color: "var(--gold)", fontFamily: "'Fraunces',serif", fontSize: 16, marginTop: 4 }}>{frete.previsao}</div></div>}
          {frete.motorista && <div><div className="meta-row" style={{ padding: 0 }}><span className="l">🚗 Motorista</span></div><div style={{ color: "var(--ink)", fontSize: 14, marginTop: 4 }}>{frete.motorista}</div></div>}
          {frete.atualizado_em && <div><div className="meta-row" style={{ padding: 0 }}><span className="l">🕒 Atualizado</span></div><div style={{ color: "var(--ink-dim)", fontSize: 12, marginTop: 4 }}>{fmt(frete.atualizado_em)}</div></div>}
        </div>
      )}

      {/* Histórico */}
      {frete?.historico?.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div className="panel-eyebrow" style={{ marginBottom: 10 }}>Histórico</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[...frete.historico].reverse().slice(0, 8).map((h, i) => {
              const step = FRETE_STEPS.find(s => s.k === h.status);
              return (
                <div key={i} style={{ display: "flex", gap: 10, padding: "8px 12px", background: "var(--bg-2)", fontSize: 12, color: "var(--ink-dim)" }}>
                  <span style={{ color: "var(--gold)", minWidth: 130 }}>{step?.icon} {step?.l}</span>
                  {h.localizacao && <span>{h.localizacao}</span>}
                  {h.nota && <span style={{ fontStyle: "italic", opacity: 0.75 }}>· {h.nota}</span>}
                  <span style={{ marginLeft: "auto", opacity: 0.6 }}>{fmt(h.data)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Form de atualização */}
      {showForm && canEdit && (
        <div style={{ marginTop: 22, padding: 22, background: "var(--bg-3)", border: "1px solid var(--gold)" }}>
          <div className="field">
            <label>Novo status</label>
            <select className="inp" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} data-testid="frete-select-status">
              {FRETE_STEPS.map(s => <option key={s.k} value={s.k}>{s.icon} {s.l}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div className="field">
              <label>Localização atual</label>
              <input className="inp" type="text" placeholder="Ex: Saiu de Belo Horizonte · Rod. Fernão Dias KM 112" value={form.localizacao} onChange={e => setForm(f => ({ ...f, localizacao: e.target.value }))} data-testid="frete-inp-loc" />
            </div>
            <div className="field">
              <label>Previsão de chegada</label>
              <input className="inp" type="text" placeholder="Ex: Hoje às 18h · Amanhã cedo" value={form.previsao} onChange={e => setForm(f => ({ ...f, previsao: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div className="field">
              <label>Motorista / Transportadora</label>
              <input className="inp" type="text" placeholder="Ex: João Silva · (31) 98877-6655" value={form.motorista} onChange={e => setForm(f => ({ ...f, motorista: e.target.value }))} />
            </div>
            <div className="field">
              <label>Observação (opcional)</label>
              <input className="inp" type="text" placeholder="Ex: Chuva no trajeto" value={form.nota} onChange={e => setForm(f => ({ ...f, nota: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}>Cancelar</button>
            <button className="btn btn-sm" onClick={save} data-testid="btn-frete-save">Salvar atualização</button>
          </div>
        </div>
      )}
    </div>
  );
}


function CronologiaCompletude({ obra }) {
  // Considera etapas com wizard configurado (normal) OU kind=retrabalho
  const etapasComWizard = (obra.etapas || []).filter(
    e => WIZARD_ETAPA_CONFIG[e.nome] || e.kind === "retrabalho"
  );
  const total = etapasComWizard.length;
  if (total === 0) return null;

  // Para cada etapa, checa quantas SÉRIES distintas existem (cada série = unidade documentada)
  // Considera FOTOS + VÍDEOS antigos + NOVAS series_fotos (status="aprovada").
  const seriesFotosAprov = (obra.series_fotos || []).filter(s => s.status === "aprovada");
  const detalhe = etapasComWizard.map(e => {
    const cfg = resolveEtapaConfig(e);
    if (!cfg) return null;
    const fotos = (obra.fotos || []).filter(f => f.etapa === e.nome && f.aprovada !== false);
    const videos = (obra.videos || []).filter(v => v.etapa === e.nome && v.aprovada !== false);
    const midias = [...fotos, ...videos];
    const series = new Set(midias.map(m => m.serie_nome || "_geral").filter(Boolean));
    const serieCount = series.size;
    const seriesCompletas = Array.from(series).filter(sn => {
      const midiasDaSerie = midias.filter(m => (m.serie_nome || "_geral") === sn);
      return midiasDaSerie.length >= cfg.shots.length;
    }).length;
    // Novo: cada série aprovada em obra.series_fotos com etapa_nome também conta
    // como uma "série completa" (parceiro ou admin já produziu o conjunto de 3).
    const seriesFotosDaEtapa = seriesFotosAprov.filter(s => s.etapa_nome === e.nome).length;
    const totalSeriesCompletas = seriesCompletas + seriesFotosDaEtapa;
    return {
      nome: e.nome,
      isRetrabalho: e.kind === "retrabalho",
      serieLabel: cfg.serieLabel,
      serieCount: serieCount + seriesFotosDaEtapa,
      seriesCompletas: totalSeriesCompletas,
      shotsPorSerie: cfg.shots.length,
      documentada: totalSeriesCompletas > 0,
    };
  }).filter(Boolean);

  const documentadas = detalhe.filter(d => d.documentada).length;
  const pct = Math.round((documentadas / total) * 100);

  // Cor da barra baseada em pct
  const corBarra =
    pct >= 80 ? "linear-gradient(90deg, #5fa86c 0%, #7bbf86 100%)" :
    pct >= 40 ? "linear-gradient(90deg, #b8935a 0%, #d4b27a 100%)" :
                "linear-gradient(90deg, #c47a7a 0%, #d49595 100%)";

  return (
    <div className="panel" data-testid="cronologia-completude">
      <div className="panel-head">
        <div>
          <div className="panel-eyebrow">Documentação</div>
          <h3 className="panel-title">Acompanhamento <em>da obra</em></h3>
        </div>
        <div style={{
          fontFamily: "'Fraunces', serif",
          fontSize: 34,
          color: pct >= 80 ? "#5fa86c" : pct >= 40 ? "#b8935a" : "#c47a7a",
          fontStyle: "italic",
          lineHeight: 1,
        }} data-testid="cronologia-pct">{pct}%</div>
      </div>

      <p style={{ color: "var(--ink-2)", fontSize: 12, lineHeight: 1.6, marginBottom: 14 }}>
        <strong style={{ color: "var(--ink)" }}>{documentadas} de {total}</strong> etapas
        com pelo menos uma série completa. Identifica lacunas antes da entrega
        do <em>Signature Book</em>.
      </p>

      <div style={{
        height: 6,
        background: "rgba(184,147,90,0.1)",
        borderRadius: 3,
        overflow: "hidden",
        marginBottom: 18,
      }}>
        <div style={{
          height: "100%",
          width: `${pct}%`,
          background: corBarra,
          transition: "width 0.6s ease-out",
        }} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {detalhe.map(d => (
          <div key={d.nome} className="meta-row" style={{ padding: "7px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }} data-testid={`cronologia-row-${d.nome}`}>
            <span className="l" style={{
              fontSize: 11,
              color: d.documentada ? "var(--ink)" : "var(--ink-3)",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{
                display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                background: d.documentada ? "#5fa86c" : (d.isRetrabalho ? "rgba(196,122,122,0.3)" : "rgba(184,147,90,0.2)"),
                flexShrink: 0,
              }} />
              {d.isRetrabalho && <span style={{ color: "#c47a7a" }}>🔧</span>}
              {d.nome}
            </span>
            <span style={{
              fontSize: 10,
              color: d.documentada ? "var(--gold)" : "var(--ink-3)",
              letterSpacing: ".1em",
              textTransform: "uppercase",
              fontFeatureSettings: '"tnum"',
            }}>
              {d.serieCount === 0
                ? "—"
                : `${d.seriesCompletas}/${d.serieCount} ${d.serieLabel.toLowerCase()}${d.serieCount === 1 ? "" : "s"} ✓`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AvancoCronograma({ obra }) {
  const etapas = obra.etapas || [];
  const total = etapas.length;
  if (total === 0) return null;

  const concluidas = etapas.filter(e => e.status === "concluida").length;
  const emCurso = etapas.filter(e => e.status === "em_andamento").length;
  const pct = Math.round((concluidas / total) * 100);

  // Identifica próxima etapa (primeira não-concluída)
  const proximaIdx = etapas.findIndex(e => e.status !== "concluida");
  const proxima = proximaIdx >= 0 ? etapas[proximaIdx] : null;

  return (
    <div className="panel" data-testid="avanco-cronograma">
      <div className="panel-head">
        <div>
          <div className="panel-eyebrow">Cronograma</div>
          <h3 className="panel-title">Avanço <em>cronograma</em></h3>
        </div>
        <div style={{
          fontFamily: "'Fraunces', serif",
          fontSize: 34,
          color: "#b8935a",
          fontStyle: "italic",
          lineHeight: 1,
        }} data-testid="avanco-pct">{pct}%</div>
      </div>

      <p style={{ color: "var(--ink-2)", fontSize: 12, lineHeight: 1.6, marginBottom: 14 }}>
        <strong style={{ color: "var(--ink)" }}>{concluidas} de {total}</strong> etapas concluídas
        {emCurso > 0 && <> · <span style={{ color: "#d4b27a" }}>{emCurso} em curso</span></>}.
        Reflete o status manual das etapas (não inclui documentação fotográfica).
      </p>

      <div style={{
        height: 6,
        background: "rgba(184,147,90,0.1)",
        borderRadius: 3,
        overflow: "hidden",
        marginBottom: 18,
      }}>
        <div style={{
          height: "100%",
          width: `${pct}%`,
          background: "linear-gradient(90deg, #b8935a 0%, #d4b27a 100%)",
          transition: "width 0.6s ease-out",
        }} data-testid="avanco-bar" />
      </div>

      {proxima ? (
        <div style={{
          padding: "10px 12px",
          background: "rgba(184,147,90,0.06)",
          border: "1px solid rgba(184,147,90,0.18)",
          fontSize: 11,
          color: "var(--ink-2)",
        }} data-testid="avanco-proxima">
          <div style={{
            fontSize: 9, letterSpacing: ".2em", textTransform: "uppercase",
            color: "#8a857a", marginBottom: 4,
          }}>Próxima etapa</div>
          <div style={{ color: "var(--ink)", fontFamily: "'Fraunces', serif", fontSize: 14 }}>
            {String(proximaIdx + 1).padStart(2, "0")} · {proxima.nome}
          </div>
          <div style={{ fontSize: 10, color: "#8a857a", marginTop: 4, letterSpacing: ".05em" }}>
            {proxima.status === "em_andamento" ? "Em curso" : "Aguardando início"}
          </div>
        </div>
      ) : (
        <div style={{
          padding: "10px 12px",
          background: "rgba(95,168,108,0.08)",
          border: "1px solid rgba(95,168,108,0.25)",
          fontSize: 12,
          color: "#7bbf86",
          textAlign: "center",
          letterSpacing: ".05em",
        }} data-testid="avanco-completo">
          ✓ Todas as etapas concluídas
        </div>
      )}
    </div>
  );
}





function computeGapDocumentacao(obra) {
  const etapas = obra.etapas || [];
  const total = etapas.length;
  if (total === 0) return { avanco: 0, completude: 0, gap: 0, alerta: false };
  const concluidas = etapas.filter(e => e.status === "concluida").length;
  const avanco = Math.round((concluidas / total) * 100);

  const etapasDoc = etapas.filter(e => WIZARD_ETAPA_CONFIG[e.nome] || e.kind === "retrabalho");
  const totalDoc = etapasDoc.length;
  if (totalDoc === 0) return { avanco, completude: 0, gap: avanco, alerta: avanco >= 30 };

  let documentadas = 0;
  for (const e of etapasDoc) {
    const cfg = resolveEtapaConfig(e);
    if (!cfg) continue;
    const shotsN = (cfg.shots || []).length;
    if (shotsN === 0) continue;
    const fotos = (obra.fotos || []).filter(f => f.etapa === e.nome && f.aprovada !== false);
    const videos = (obra.videos || []).filter(v => v.etapa === e.nome && v.aprovada !== false);
    const midias = [...fotos, ...videos];
    const series = {};
    midias.forEach(m => {
      const sn = m.serie_nome || "_geral";
      series[sn] = (series[sn] || 0) + 1;
    });
    if (Object.values(series).some(c => c >= shotsN)) documentadas++;
  }
  const completude = Math.round((documentadas / totalDoc) * 100);
  const gap = Math.max(avanco - completude, 0);
  return { avanco, completude, gap, alerta: gap >= 30 && avanco > 0 };
}

function GapDocumentacaoBanner({ obra, onGoToEtapas }) {
  const { avanco, completude, gap, alerta } = computeGapDocumentacao(obra);
  if (!alerta) return null;
  return (
    <div
      data-testid="gap-documentacao-banner"
      style={{
        marginTop: 18,
        padding: "18px 22px",
        background: "linear-gradient(135deg, rgba(212,178,122,0.10) 0%, rgba(184,147,90,0.08) 100%)",
        border: "1px solid rgba(212,178,122,0.45)",
        borderLeft: "3px solid #d4b27a",
        display: "flex",
        gap: 16,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <div style={{
        fontSize: 28, lineHeight: 1, color: "#d4b27a", flexShrink: 0,
      }} aria-hidden>⚠️</div>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{
          fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase",
          color: "#d4b27a", fontWeight: 600, marginBottom: 4,
        }}>
          Atenção · Gap de documentação ({gap}pp)
        </div>
        <div style={{ color: "var(--ink)", fontSize: 13, lineHeight: 1.5 }}>
          A obra está <strong>{avanco}% avançada</strong> mas a documentação
          fotográfica está em apenas <strong>{completude}%</strong>. Registre as
          fotos das etapas concluídas antes de gerar o <em>Signature Book</em>.
        </div>
      </div>
      <button
        className="btn btn-sm"
        onClick={onGoToEtapas}
        data-testid="gap-go-etapas"
        style={{
          background: "#d4b27a", color: "#0d0d0f", borderColor: "#d4b27a",
          flexShrink: 0, fontWeight: 600,
        }}
      >
        Ir para Etapas
      </button>
    </div>
  );
}



function ObraVisao({ obra, pct, seeFin, canEdit, onReload, setTab }) {
  const { api, user } = useAuth();
  const [showEdit, setShowEdit] = useState(false);
  const status = STATUS_OBRA[obra.status] || STATUS_OBRA.em_andamento;
  const arquivar = async () => {
    if (!window.confirm("Arquivar esta obra?")) return;
    await api("post", `/obras/${obra.id}/arquivar`); onReload();
  };
  const cancelar = async () => {
    const motivo = window.prompt("Motivo do cancelamento (obrigatório):");
    if (!motivo) return;
    await api("post", `/obras/${obra.id}/cancelar`, { motivo }); onReload();
  };
  const concluir = async () => {
    await api("patch", `/obras/${obra.id}`, { status: "concluida", data_entrega_real: new Date().toISOString().slice(0, 10) }); onReload();
  };
  const nav = useNavigate();
  const duplicar = async () => {
    if (!window.confirm(`Duplicar "${obra.nome}"?\n\nA cópia herdará: cliente, endereço, arquiteto, equipe, materiais.\nSerão zerados: lançamentos financeiros, mídias, medições, fretes e etapas.`)) return;
    try {
      const novo = await api("post", `/obras/${obra.id}/duplicar`);
      if (novo && novo.id) {
        nav(`/obras/${novo.id}`);
      }
    } catch (e) {
      alert("Erro ao duplicar: " + (e?.response?.data?.detail || e.message));
    }
  };
  const excluirPermanente = async () => {
    const n1 = window.prompt(`ATENÇÃO: exclusão permanente.\n\nIsto apaga esta obra, fotos, vídeos e todos os arquivos do disco — SEM POSSIBILIDADE DE RECUPERAÇÃO.\n\nDigite o código da obra (${obra.codigo}) para confirmar:`);
    if (!n1 || n1.trim() !== obra.codigo) { if (n1 !== null) alert("Código incorreto. Operação cancelada."); return; }
    if (!window.confirm("Confirma DEFINITIVAMENTE a exclusão permanente?")) return;
    await api("delete", `/obras/${obra.id}?hard=true`);
    nav("/obras");
  };
  return (
    <div className="fade-up">
      <div className="ws-head">
        <div>
          <div className="eyebrow"><Tricolore thin /> {obra.codigo}</div>
          <h1><em>{obra.nome}</em></h1>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span className={"pill " + status.c} style={{ padding: "8px 14px", fontSize: 11 }}>{status.l}</span>
          {canEdit && <button className="btn btn-sm" onClick={() => setShowEdit(true)} data-testid="btn-edit-obra"><Edit3 size={12} /> Editar</button>}
          {canEdit && <button className="btn btn-sm" onClick={duplicar} data-testid="btn-duplicar-obra"><Copy size={12} /> Duplicar</button>}
          {canEdit && obra.status === "em_andamento" && <button className="btn btn-sm" onClick={concluir} data-testid="btn-concluir-obra"><CheckCircle2 size={12} /> Concluir</button>}          {canEdit && obra.status !== "arquivada" && <button className="btn btn-ghost btn-sm" onClick={arquivar}><Archive size={12} /> Arquivar</button>}
          {canEdit && obra.status === "em_andamento" && <button className="btn btn-danger btn-sm" onClick={cancelar}>Cancelar</button>}
          {canEdit && user?.role === "admin" && <button className="btn btn-ghost btn-sm" onClick={excluirPermanente} style={{ color: "#c47a7a", borderColor: "#4a2a2a" }} data-testid="btn-excluir-obra">Excluir permanente</button>}
          {canEdit && <MagicLinkButtons obra={obra} api={api} />}
        </div>
      </div>

      {canEdit && <ObraHealthCard obra={obra} onReload={onReload} />}

      {canEdit && <SobrasAlertaCliente obra={obra} />}

      {canEdit && <HowToBanner id="obra-visao" title="Como operar a Visão Geral" steps={[
        { num: 1, title: "Edite dados da obra", desc: "Clique em <strong>Editar</strong> no topo para alterar cliente, endereço, valor, datas, arquiteto, materiais e prestadores vinculados." },
        { num: 2, title: "Gere Magic Links", desc: "Use <strong>🔗 Link do Cliente</strong> e <strong>🔗 Link do Arquiteto</strong> para compartilhar acesso sem senha. A URL é copiada automaticamente." },
        { num: 3, title: "Atualize o rastreio do frete", desc: "No painel <strong>Rastreio do Frete</strong> abaixo, clique em <strong>Atualizar status</strong> para informar onde a pedra está: Aguardando → Coletado → Em trânsito → Entregue no canteiro." },
        { num: 4, title: "Marque como Concluída", desc: "Quando tudo acabar (instalação incluída), clique em <strong>Concluir</strong> no topo. Isso libera o Signature Book para assinatura do cliente." },
      ]} />}

      {canEdit && obra.data_entrega_real && <ObraNPSCard obraId={obra.id} />}

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 32, marginBottom: 40 }}>
        <div style={{ backgroundImage: `url(${fileUrl(obra.imagem_capa) || "https://images.unsplash.com/photo-1615873968403-89e068629265?w=1400&q=85"})`, backgroundSize: "cover", backgroundPosition: "center", minHeight: 360, border: "1px solid var(--line)" }} />
        <div style={{ padding: "8px 0" }}>
          <div className="meta-row"><span className="l"><User size={10} />Cliente</span><span className="v">{obra.cliente_nome}</span></div>
          {obra.cliente_email && <div className="meta-row"><span className="l">Email</span><span className="v">{obra.cliente_email}</span></div>}
          {obra.arquiteto && <div className="meta-row"><span className="l"><PenTool size={10} />Arquiteto</span><span className="v">{obra.arquiteto.nome}{obra.arquiteto.escritorio ? ` · ${obra.arquiteto.escritorio}` : ""}</span></div>}
          <div className="meta-row"><span className="l"><MapPin size={10} />Local</span><span className="v">{obra.cidade}, {obra.estado}</span></div>
          {obra.endereco && <div className="meta-row"><span className="l">Endereço</span><span className="v">{obra.endereco}</span></div>}
          <div className="meta-row"><span className="l"><Calendar size={10} />Início</span><span className="v">{fmt(obra.data_inicio)}</span></div>
          <div className="meta-row"><span className="l">Entrega prevista</span><span className="v">{fmt(obra.data_entrega_prevista)}</span></div>
          {obra.data_entrega_real && <div className="meta-row"><span className="l">Entrega real</span><span className="v gold-text">{fmt(obra.data_entrega_real)}</span></div>}
          {seeFin && obra.valor_total != null && <div className="meta-row"><span className="l">Valor total</span><span className="v gold-text" style={{ fontFamily: "'Fraunces',serif", fontSize: 16 }}>{brl(obra.valor_total)}</span></div>}
          <div className="meta-row"><span className="l">Progresso</span><span className="v">{pct}%</span></div>
          {obra.motivo_cancelamento && <div className="meta-row"><span className="l" style={{ color: "#c47a7a" }}>Cancelamento</span><span className="v" style={{ color: "#c47a7a" }}>{obra.motivo_cancelamento}</span></div>}
          {obra.aceite_cliente && <div className="meta-row"><span className="l" style={{ color: "var(--verde-2)" }}>✓ Aceite</span><span className="v emerald-text">{obra.aceite_cliente.nome} · {fmt(obra.aceite_cliente.data)}</span></div>}
        </div>
      </div>

      <div className="panels">
        <div className="panel">
          <div className="panel-head"><div><div className="panel-eyebrow">Pipeline</div><h3 className="panel-title">Etapas da <em>obra</em></h3></div></div>
          {(obra.etapas || []).map((e, i) => (
            <div key={i} className="meta-row" style={{ padding: "10px 0" }}>
              <span className="l">{String(i + 1).padStart(2, "0")} · {e.nome}</span>
              <span className={"pill " + (e.status === "concluida" ? "done" : e.status === "em_andamento" ? "run" : "hold")}>
                {e.status === "concluida" ? "Concluída" : e.status === "em_andamento" ? "Em curso" : "Aguardando"}
              </span>
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 18 }} data-testid="progresso-grid">
          <AvancoCronograma obra={obra} />
          <CronologiaCompletude obra={obra} />
        </div>

        <GapDocumentacaoBanner obra={obra} onGoToEtapas={() => setTab("etapas")} />

        <div className="panel">
          <div className="panel-head"><div><div className="panel-eyebrow">Rastreabilidade</div><h3 className="panel-title">Link <em>público</em></h3></div></div>
          <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.7 }}>Compartilhe este link com cliente ou arquiteto para visualização do Signature Book e aceite digital:</p>
          {/* URL humanizada com slug do cliente (fallback: token) */}
          <div style={{ padding: 14, background: "var(--bg-3)", border: "1px solid var(--line)", fontFamily: "monospace", fontSize: 11, color: "var(--gold)", wordBreak: "break-all", marginTop: 10 }} data-testid="sb-public-url">
            {(typeof window !== "undefined" ? window.location.origin : "")}/sb/{obra.cliente_slug || obra.public_token}
          </div>
          {/* Editor inline do cliente_slug */}
          {canEdit && (
            <ClienteSlugEditor obra={obra} api={api} onReload={onReload} />
          )}
          <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <ShareBox url={(typeof window !== "undefined" ? window.location.origin : "") + "/sb/" + (obra.cliente_slug || obra.public_token)} obra={obra} compact />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => window.dispatchEvent(new CustomEvent("camasa:notificar-cliente", { detail: { obraId: obra.id, motivo: "manual" } }))}
              data-testid="btn-notificar-cliente"
              style={{ borderColor: "#25d366", background: "#25d366", color: "#0a0a0a", fontWeight: 600 }}
            >
              <MessageSquare size={12} /> Notificar Cliente
            </button>
          </div>
          <div style={{ marginTop: 14 }}>
            <Link to={`/sb/${obra.cliente_slug || obra.public_token}`} className="btn btn-ghost btn-sm"><QrCode size={12} />Abrir Signature Book</Link>
          </div>
        </div>
      </div>

      <FretePanel obra={obra} canEdit={canEdit} api={api} onReload={onReload} />

      {canEdit && <SobrasSugestoes obraId={obra.id} />}
      {canEdit && <SobrasPanel obra={obra} canEdit={canEdit} />}

      {showEdit && <EditObraModal obra={obra} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); onReload(); }} />}
    </div>
  );
}


/**
 * MateriaisPicker — Seletor de materiais agrupado por família (grupo do catálogo).
 * Essencial para lidar com os 900+ materiais do catálogo CAMASA sem poluir a tela.
 * Features: busca por nome, grupos colapsáveis, contador de selecionados por grupo.
 */
function MateriaisPicker({ materiais, selecionados, onToggle }) {
  const [query, setQuery] = useState("");
  const [openGroups, setOpenGroups] = useState({});

  // Normaliza string para busca acento-insensível
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // Agrupa materiais por "grupo" (famílias dos PDFs); fallback para categoria
  const grouped = useMemo(() => {
    const q = norm(query.trim());
    const g = {};
    (materiais || []).forEach(m => {
      if (q && !norm(m.nome).includes(q)) return;
      const key = m.grupo || MAT_CAT[m.categoria] || "Outros";
      (g[key] = g[key] || []).push(m);
    });
    // Ordem definida: nacionais -> importados -> granitos -> compostos -> outros -> demais
    const order = [
      "Mármores Nacionais", "Mármores Importados",
      "Granitos", "Materiais Compostos", "Outros Materiais",
    ];
    const keys = Object.keys(g).sort((a, b) => {
      const ai = order.indexOf(a), bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b, "pt-BR");
    });
    return keys.map(k => ({ grupo: k, items: g[k].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")) }));
  }, [materiais, query]);

  const selSet = new Set(selecionados || []);
  const totalSel = selSet.size;

  // Auto-expande grupos que têm selecionados (quando abrir o form de uma obra existente)
  useEffect(() => {
    const next = {};
    grouped.forEach(({ grupo, items }) => {
      if (items.some(m => selSet.has(m.id))) next[grupo] = true;
    });
    // Se está buscando, abre todos
    if (query.trim()) grouped.forEach(({ grupo }) => { next[grupo] = true; });
    setOpenGroups(prev => ({ ...prev, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materiais.length, query]);

  const toggleGroup = (g) => setOpenGroups(prev => ({ ...prev, [g]: !prev[g] }));

  return (
    <div data-testid="mat-picker">
      <input
        type="search"
        placeholder={`Buscar entre ${materiais.length} materiais (ex: Calacatta, Taj Mahal, Silestone...)`}
        value={query}
        onChange={e => setQuery(e.target.value)}
        data-testid="mat-picker-search"
        style={{ width: "100%", padding: "10px 12px", marginBottom: 10, background: "var(--bg-2)", border: "1px solid var(--line)", color: "var(--ink)", fontSize: 13 }}
      />
      <div style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 10 }}>
        {totalSel} selecionado{totalSel === 1 ? "" : "s"} · {grouped.length} grupo{grouped.length === 1 ? "" : "s"}
        {query && ` · buscando "${query}"`}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 460, overflowY: "auto", border: "1px solid var(--line)", padding: 10, background: "rgba(13,13,15,0.5)" }}>
        {grouped.length === 0 && (
          <div style={{ color: "var(--ink-3)", textAlign: "center", padding: 30, fontSize: 13 }}>Nenhum material encontrado</div>
        )}
        {grouped.map(({ grupo, items }) => {
          const selectedCount = items.filter(m => selSet.has(m.id)).length;
          const isOpen = !!openGroups[grupo];
          return (
            <div key={grupo} data-testid={`mat-group-${grupo}`} style={{ border: "1px solid var(--line)", background: "var(--bg-2)" }}>
              <button
                type="button"
                onClick={() => toggleGroup(grupo)}
                data-testid={`mat-group-toggle-${grupo}`}
                style={{
                  width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 14px", background: selectedCount > 0 ? "rgba(184,147,90,0.08)" : "transparent",
                  border: "none", cursor: "pointer", color: "var(--ink)",
                  borderLeft: `3px solid ${selectedCount > 0 ? "var(--gold)" : "transparent"}`,
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontFamily: "'Fraunces', serif", fontSize: 14 }}>{grupo}</span>
                  <span style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-3)" }}>{items.length} itens</span>
                  {selectedCount > 0 && (
                    <span style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--gold)", fontWeight: 600 }}>· {selectedCount} selecionado{selectedCount === 1 ? "" : "s"}</span>
                  )}
                </span>
                <span style={{ color: "var(--ink-3)", fontSize: 12 }}>{isOpen ? "▾" : "▸"}</span>
              </button>
              {isOpen && (
                <div className="chip-grid" style={{ padding: 10, borderTop: "1px solid var(--line)", maxHeight: 280, overflowY: "auto" }}>
                  {items.map(m => {
                    const on = selSet.has(m.id);
                    return (
                      <button key={m.id} type="button" onClick={() => onToggle(m.id)}
                        data-testid={`mat-chip-${m.id}`}
                        className={"chip " + (on ? "active" : "")}
                        title={m.acabamento ? `${m.nome} · ${m.acabamento}` : m.nome}
                      >
                        {on && <Check size={10} />}{m.nome}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function EditObraModal({ obra, onClose, onSaved }) {
  const { api, token } = useAuth();
  const [form, setForm] = useState({
    nome: obra.nome || "", numero_orcamento: obra.numero_orcamento || "", cliente_nome: obra.cliente_nome || "",
    cliente_email: obra.cliente_email || "", cliente_telefone: obra.cliente_telefone || "",
    cidade: obra.cidade || "", estado: obra.estado || "SP", endereco: obra.endereco || "",
    data_inicio: obra.data_inicio || "", data_entrega_prevista: obra.data_entrega_prevista || "",
    data_entrega_real: obra.data_entrega_real || "",
    valor_total: obra.valor_total || 0,
    arquiteto_id: obra.arquiteto_id || "",
    imagem_capa: obra.imagem_capa || "",
    observacoes: obra.observacoes || "",
    // Redesign 2026: fluxo de etapas
    beneficiamento_superficie: obra.beneficiamento_superficie || false,
    projeto_origem: obra.projeto_origem || "",
    materiais_ids: obra.materiais_ids || [],
    prestadores_ids: obra.prestadores_ids || [],
    terceirizados_ids: obra.terceirizados_ids || obra.parceiros_ids || [],
  });
  const [arquitetos, setArquitetos] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [terceirizados, setTerceirizados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [novoTipo, setNovoTipo] = useState(null); // arquiteto | prestador | terceirizado | material
  const aoCriar = (a) => {
    const alvo = {
      arquiteto: [setArquitetos, () => setForm(f => ({ ...f, arquiteto_id: a.id }))],
      prestador: [setPrestadores, () => setForm(f => ({ ...f, prestadores_ids: [...f.prestadores_ids, a.id] }))],
      terceirizado: [setTerceirizados, () => setForm(f => ({ ...f, terceirizados_ids: [...f.terceirizados_ids, a.id] }))],
      material: [setMateriais, () => setForm(f => ({ ...f, materiais_ids: [...f.materiais_ids, a.id] }))],
    }[novoTipo];
    alvo[0](l => [...l, a]);
    alvo[1]();
    setNovoTipo(null);
  };
  const [uploadingCapa, setUploadingCapa] = useState(false);
  const capaRef = useRef(null);

  const handleCapaUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(jpe?g|png|webp|heic)$/i.test(f.name)) {
      toastError("Formato não aceito", { description: "Envie uma foto em JPG, PNG, WEBP ou HEIC." });
      e.target.value = ""; return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toastError("Imagem muito grande", { description: `${(f.size / 1024 / 1024).toFixed(1)} MB — o limite é 25 MB. Tire uma foto menor ou reduza.` });
      e.target.value = ""; return;
    }
    if (f.size < 20 * 1024) {
      toastError("Imagem sem qualidade suficiente", { description: `Só ${Math.round(f.size / 1024)} KB — a capa é a foto principal da obra, envie uma imagem com qualidade (mín. 20 KB).` });
      e.target.value = ""; return;
    }
    setUploadingCapa(true);
    const loading = toastLoading("Enviando a foto de capa…");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/obras/${obra.id}/capa`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setForm(prev => ({ ...prev, imagem_capa: r.data?.imagem_capa || r.data?.url || prev.imagem_capa }));
      loading.dismiss();
      toastSuccess("Foto de capa salva!", "Já está disponível pra todo mundo ver.");
    } catch (err) {
      loading.dismiss();
      const doRetry = () => handleCapaUpload(e);
      toastError("Não conseguimos salvar a foto", { description: pickErrorMsg(err), onRetry: doRetry });
    } finally {
      setUploadingCapa(false);
      e.target.value = "";
    }
  };

  const removerCapa = () => {
    if (!form.imagem_capa) return;
    if (!window.confirm("Remover a imagem de capa?")) return;
    setForm(prev => ({ ...prev, imagem_capa: "" }));
  };

  useEffect(() => {
    api("get", "/arquitetos").then(setArquitetos).catch(() => { });
    api("get", "/materiais").then(setMateriais).catch(() => { });
    api("get", "/prestadores").then(setPrestadores).catch(() => { });
    api("get", "/terceirizados").then(setTerceirizados).catch(() => { });
  }, [api]);

  const toggleArr = (k, id) => {
    setForm(f => ({ ...f, [k]: f[k].includes(id) ? f[k].filter(x => x !== id) : [...f[k], id] }));
  };

  const save = async () => {
    setLoading(true);
    try {
      await api("patch", `/obras/${obra.id}`, {
        ...form,
        valor_total: parseFloat(form.valor_total) || 0,
        arquiteto_id: form.arquiteto_id || null,
      });
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <h3 className="modal-title">Editar <em>obra</em></h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="field"><label>Nome da obra</label><input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} /></div>
            <div className="field"><label>Nº do orçamento</label><input value={form.numero_orcamento} onChange={e => setForm({ ...form, numero_orcamento: e.target.value })} data-testid="edit-obra-numero-orcamento" /></div>
            <div className="field"><label>Cliente</label><input value={form.cliente_nome} onChange={e => setForm({ ...form, cliente_nome: e.target.value })} /></div>
            <div className="field"><label>Email do cliente</label><input type="email" value={form.cliente_email} onChange={e => setForm({ ...form, cliente_email: e.target.value })} /></div>
            <div className="field"><label>Telefone</label><input value={form.cliente_telefone} onChange={e => setForm({ ...form, cliente_telefone: e.target.value })} /></div>
            <div className="field"><label>Cidade</label><input value={form.cidade} onChange={e => setForm({ ...form, cidade: e.target.value })} /></div>
            <div className="field"><label>Estado</label><input value={form.estado} onChange={e => setForm({ ...form, estado: e.target.value })} /></div>
            <div className="field span2"><label>Endereço</label><input value={form.endereco} onChange={e => setForm({ ...form, endereco: e.target.value })} /></div>
            <div className="field"><label>Data início</label><input type="date" value={form.data_inicio} onChange={e => setForm({ ...form, data_inicio: e.target.value })} /></div>
            <div className="field"><label>Entrega prevista</label><input type="date" value={form.data_entrega_prevista} onChange={e => setForm({ ...form, data_entrega_prevista: e.target.value })} /></div>
            <div className="field"><label>Entrega real</label><input type="date" value={form.data_entrega_real} onChange={e => setForm({ ...form, data_entrega_real: e.target.value })} /></div>
            <div className="field"><label>Valor total (R$)</label><input type="number" step="0.01" value={form.valor_total} onChange={e => setForm({ ...form, valor_total: e.target.value })} /></div>
            <div className="field span2" style={{ background: "rgba(184,147,90,0.05)", border: "1px solid rgba(184,147,90,0.2)", padding: 14, borderRadius: 4 }}>
              <label style={{ display: "block", marginBottom: 10 }}>Fluxo da obra</label>
              <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
                <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }} data-testid="form-beneficiamento-toggle">
                  <input
                    type="checkbox"
                    checked={form.beneficiamento_superficie}
                    onChange={e => setForm({ ...form, beneficiamento_superficie: e.target.checked })}
                  />
                  <span style={{ fontSize: 13, color: "#cdc8be" }}>Esta obra envolve <strong style={{ color: "#f5f0e8" }}>beneficiamento de superfície</strong> (Etapa "Frete para Beneficiamento" será adicionada)</span>
                </label>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: "#cdc8be" }}>Projeto para medição in loco:</span>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }} data-testid="form-projeto-camasa">
                  <input
                    type="radio"
                    name="projeto_origem"
                    value="camasa"
                    checked={form.projeto_origem === "camasa"}
                    onChange={e => setForm({ ...form, projeto_origem: e.target.value })}
                  />
                  <span style={{ fontSize: 13 }}>CAMASA faz medição</span>
                </label>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }} data-testid="form-projeto-cliente">
                  <input
                    type="radio"
                    name="projeto_origem"
                    value="cliente"
                    checked={form.projeto_origem === "cliente"}
                    onChange={e => setForm({ ...form, projeto_origem: e.target.value })}
                  />
                  <span style={{ fontSize: 13 }}>Cliente fornece projeto</span>
                </label>
              </div>
            </div>
            <div className="field span2">
              <label>Arquiteto</label>
              <select value={form.arquiteto_id} onChange={e => setForm({ ...form, arquiteto_id: e.target.value })} data-testid="edit-obra-arquiteto">
                <option value="">— nenhum —</option>
                {arquitetos.map(a => <option key={a.id} value={a.id}>{a.nome}{a.escritorio ? ` — ${a.escritorio}` : ""}</option>)}
              </select>
              <BtnCadastroRapido label="Novo Arquiteto" onClick={() => setNovoTipo("arquiteto")} testid="edit-obra-novo-arquiteto" />
            </div>
            <div className="field span2">
              <label>Imagem de capa</label>
              <input ref={capaRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleCapaUpload} data-testid="edit-capa-input" />
              {form.imagem_capa ? (
                <div style={{ display: "flex", gap: 12, alignItems: "stretch", marginTop: 4 }}>
                  <div style={{ width: 180, height: 110, backgroundImage: `url(${fileUrl(form.imagem_capa)})`, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--gold-3)", flexShrink: 0 }} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" }}>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => capaRef.current?.click()} disabled={uploadingCapa} data-testid="edit-capa-replace">
                      {uploadingCapa ? "Enviando…" : "Substituir capa"}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ color: "#c47a7a", borderColor: "#4a2a2a" }} onClick={removerCapa} data-testid="edit-capa-remove">
                      Remover
                    </button>
                    <small style={{ color: "var(--ink-3)", fontSize: 11, fontStyle: "italic" }}>Min. 400×400px · 20KB</small>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => !uploadingCapa && capaRef.current?.click()}
                  style={{
                    padding: "28px 16px", textAlign: "center", border: "2px dashed var(--gold-3)",
                    background: "rgba(184,147,90,0.04)", cursor: uploadingCapa ? "wait" : "pointer",
                    transition: "background .2s", marginTop: 4
                  }}
                  data-testid="edit-capa-dropzone"
                >
                  <ImageIcon size={28} color="var(--gold)" strokeWidth={1.4} style={{ display: "block", margin: "0 auto 8px" }} />
                  <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 4 }}>
                    {uploadingCapa ? "Enviando capa…" : "Clique para selecionar a imagem de capa"}
                  </div>
                  <small style={{ color: "var(--ink-3)", fontSize: 11 }}>JPG, PNG, WEBP ou HEIC · Min. 400×400px · 20KB</small>
                </div>
              )}
            </div>
            <div className="field span2"><label>Observações</label><textarea rows={3} value={form.observacoes} onChange={e => setForm({ ...form, observacoes: e.target.value })} /></div>
          </div>

          <div style={{ marginTop: 20 }}>
            <div className="field"><label>Materiais utilizados ({form.materiais_ids.length})</label></div>
            <MateriaisPicker
              materiais={materiais}
              selecionados={form.materiais_ids}
              onToggle={(id) => toggleArr("materiais_ids", id)}
            />
            <BtnCadastroRapido label="Novo Material" onClick={() => setNovoTipo("material")} testid="edit-obra-novo-material" />
          </div>

          <div style={{ marginTop: 20 }}>
            <div className="field"><label>Prestadores alocados ({form.prestadores_ids.length})</label></div>
            <div className="chip-grid">
              {prestadores.map(p => (
                <button key={p.id} type="button" onClick={() => toggleArr("prestadores_ids", p.id)}
                  className={"chip " + (form.prestadores_ids.includes(p.id) ? "active" : "")}
                  data-testid={`chip-prestador-${p.id}`}>
                  {form.prestadores_ids.includes(p.id) && <Check size={10} />}{p.nome} <span style={{ opacity: 0.6 }}>· {ESP_LBL[p.especialidade]}</span>
                </button>
              ))}
            </div>
            <BtnCadastroRapido label="Novo Prestador" onClick={() => setNovoTipo("prestador")} testid="edit-obra-novo-prestador" />
          </div>

          <div style={{ marginTop: 20 }}>
            <div className="field"><label>Terceirizados vinculados ({form.terceirizados_ids.length}) <span style={{ opacity: 0.6, fontSize: 11 }}>— terão acesso via magic-link ao Portal</span></label></div>
            <div className="chip-grid">
              {terceirizados.map(p => (
                <button key={p.id} type="button" onClick={() => toggleArr("terceirizados_ids", p.id)}
                  className={"chip " + (form.terceirizados_ids.includes(p.id) ? "active" : "")}
                  data-testid={`chip-terceirizado-${p.id}`}>
                  {form.terceirizados_ids.includes(p.id) && <Check size={10} />}{p.nome} <span style={{ opacity: 0.6 }}>· {p.tipo}</span>
                </button>
              ))}
            </div>
            <BtnCadastroRapido label="Novo Terceirizado" onClick={() => setNovoTipo("terceirizado")} testid="edit-obra-novo-terceirizado" />
          </div>
          {novoTipo && <CadastroRapidoModal cfg={CADASTRO_RAPIDO[novoTipo]} api={api} onClose={() => setNovoTipo(null)} onCreated={aoCriar} />}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={loading} data-testid="btn-save-edit">{loading ? "Salvando…" : "Salvar alterações"}</button>
        </div>
      </div>
    </div>
  );
}

function ObraEtapas({ obra, canEdit, onReload, api }) {
  const { user } = useAuth();
  const canUploadFotos = ["admin", "gestor", "terceirizado"].includes(user?.role);
  const [editing, setEditing] = useState(null); // idx da etapa em edição
  const [addingNome, setAddingNome] = useState("");
  const [addingBusy, setAddingBusy] = useState(false);
  const [addingMsg, setAddingMsg] = useState(""); // feedback inline (sucesso/erro)
  const [showRetrabalho, setShowRetrabalho] = useState(false);
  const [savingIdx, setSavingIdx] = useState(null); // idx atualmente sendo salvo
  const [saveMsg, setSaveMsg] = useState(""); // toast global de salvamento
  const [wizardEtapa, setWizardEtapa] = useState(null); // nome da etapa cujo wizard fotográfico está aberto
  const [prestadores, setPrestadores] = useState([]);
  useEffect(() => { api("get", "/prestadores").then(setPrestadores).catch(() => { }); }, [api]);
  const nomePrestador = (id) => (prestadores.find(p => p.id === id) || {}).nome;

  const saveEtapa = async (idx, patch) => {
    setSavingIdx(idx);
    setSaveMsg("");
    try {
      const res = await api("post", `/obras/${obra.id}/etapa/${idx}`, patch);
      // Só fecha o editor e recarrega se o backend confirmou
      if (!res || res.ok !== true) {
        throw new Error("Resposta inesperada do servidor — alteração não confirmada");
      }
      setEditing(null);
      await onReload();
      setSaveMsg(`✓ Etapa "${res.etapa?.nome || ""}" salva no servidor`);
      setTimeout(() => setSaveMsg(""), 3000);
      // Auto-dispara modal de notificação WhatsApp se a etapa virou "concluida"
      if (res.etapa?.status === "concluida") {
        window.dispatchEvent(new CustomEvent("camasa:notificar-cliente", {
          detail: { obraId: obra.id, motivo: "etapa_concluida" },
        }));
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Erro ao salvar etapa";
      if (status === 401) {
        setSaveMsg("⚠ Sessão expirada — recarregando para fazer login novamente…");
        setTimeout(() => { window.location.href = "/login"; }, 1500);
      } else {
        setSaveMsg(`⚠ Falha ao salvar: ${detail}`);
      }
    } finally {
      setSavingIdx(null);
    }
  };
  const removeEtapa = async (idx, nome) => {
    if (!window.confirm(`Remover a etapa "${nome}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api("delete", `/obras/${obra.id}/etapa/${idx}`);
      await onReload();
      setSaveMsg(`✓ Etapa "${nome}" removida`);
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Erro ao remover etapa";
      setSaveMsg(`⚠ Falha ao remover: ${detail}`);
    }
  };
  const moveEtapa = async (idx, dir) => {
    const order = (obra.etapas || []).map((_, i) => i);
    const target = idx + dir;
    if (target < 0 || target >= order.length) return;
    [order[idx], order[target]] = [order[target], order[idx]];
    try {
      await api("post", `/obras/${obra.id}/etapas/reorder`, { order });
      await onReload();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Erro ao reordenar";
      setSaveMsg(`⚠ Falha ao reordenar: ${detail}`);
    }
  };
  const addEtapa = async () => {
    const nome = addingNome.trim();
    if (!nome) {
      setAddingMsg("Digite o nome da etapa antes de adicionar.");
      return;
    }
    setAddingBusy(true);
    setAddingMsg("");
    try {
      await api("post", `/obras/${obra.id}/etapas`, { nome });
      setAddingNome("");
      setAddingMsg(`✓ Etapa "${nome}" adicionada.`);
      await onReload();
      // Limpa msg de sucesso após 2.5s
      setTimeout(() => setAddingMsg(""), 2500);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Erro ao adicionar etapa";
      setAddingMsg(`⚠ ${detail}`);
    } finally {
      setAddingBusy(false);
    }
  };

  return (
    <div className="fade-up">
      <div className="ws-head">
        <div><div className="eyebrow"><Tricolore thin /> Ciclo CPS</div><h1>Etapas da <em>obra</em></h1></div>
        {canEdit && (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                className="input"
                placeholder="Nome da nova etapa"
                value={addingNome}
                onChange={e => { setAddingNome(e.target.value); if (addingMsg) setAddingMsg(""); }}
                onKeyDown={e => { if (e.key === "Enter") addEtapa(); }}
                style={{ width: 220, fontSize: 12 }}
                disabled={addingBusy}
                data-testid="etapa-add-input"
              />
              <button
                className="btn btn-sm"
                onClick={addEtapa}
                disabled={addingBusy || !addingNome.trim()}
                data-testid="etapa-add-btn"
              >
                {addingBusy ? "Adicionando…" : "+ Adicionar"}
              </button>
              <button
                className="btn btn-sm"
                onClick={() => setShowRetrabalho(true)}
                data-testid="etapa-retrabalho-btn"
                style={{ borderColor: "#c47a7a", color: "#c47a7a" }}
                title="Adicionar etapa especial de retrabalho"
              >
                🔧 Retrabalho
              </button>
            </div>
            {addingMsg && (
              <div
                data-testid="etapa-add-msg"
                style={{
                  width: "100%",
                  fontSize: 11,
                  letterSpacing: ".05em",
                  color: addingMsg.startsWith("✓") ? "#5fa86c" : "#c47a7a",
                  textAlign: "right",
                  marginTop: 4,
                }}
              >
                {addingMsg}
              </div>
            )}
          </div>
        )}
      </div>
      {canEdit && <HowToBanner id="obra-etapas" title="Como gerenciar Etapas" steps={[
        { num: 1, title: "Clique na etapa para editar", desc: "Troque o status entre <strong>Aguardando → Em curso → Concluída</strong> conforme a obra avança." },
        { num: 2, title: "Reordene com as setas ↑ ↓", desc: "Ajuste a sequência do processo para refletir a realidade da produção (cada obra pode ter ordem diferente)." },
        { num: 3, title: "Adicione etapas customizadas", desc: "Digite o nome no campo acima e clique em <strong>+ Adicionar</strong> para incluir passos específicos desta obra." },
        { num: 4, title: "Regra de ouro", desc: "Atualize <strong>toda sexta-feira</strong>. O % de progresso que o cliente vê no portal dele vem daqui. Disciplina = transparência." },
      ]} />}
      <div className="stages">
        {(obra.etapas || []).map((e, i) => {
          const isEditing = editing === i;
          // Wizard config: etapas normais via map OU retrabalho (sempre tem wizard)
          const temWizard = !!resolveEtapaConfig(e);
          // Conta séries fotográficas existentes nesta etapa (legado: fotos + nova: series_fotos)
          const seriesNomes = new Set(
            (obra.fotos || [])
              .filter(f => f.etapa === e.nome && f.serie_nome)
              .map(f => f.serie_nome),
          );
          const seriesFotosCount = (obra.series_fotos || [])
            .filter(s => s.etapa_nome === e.nome && s.status === "aprovada")
            .length;
          const totalSeries = seriesNomes.size + seriesFotosCount;
          return (
            <div key={i} className={"stage " + (e.status || "aguardando")} data-testid={`etapa-card-${i}`} style={e.kind === "retrabalho" ? { borderTop: "3px solid #c47a7a", background: "rgba(196,122,122,0.06)" } : undefined}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span className="stage-n">{String(i + 1).padStart(2, "0")}</span>
                {e.kind === "retrabalho" && (
                  <span style={{
                    fontSize: 9, letterSpacing: ".15em", color: "#c47a7a", textTransform: "uppercase",
                    background: "rgba(196,122,122,0.12)", padding: "2px 6px",
                    border: "1px solid rgba(196,122,122,0.3)", fontWeight: 600,
                  }} title="Etapa de retrabalho">🔧 RETRABALHO</span>
                )}
                {e.status === "concluida" && <CheckCircle2 size={15} color="#5a7a6a" />}
              </div>
              {isEditing ? (
                <EtapaEditor etapa={e} prestadores={prestadores} saving={savingIdx === i} onSave={patch => saveEtapa(i, patch)} onCancel={() => setEditing(null)} />
              ) : (
                <>
                  <div className="stage-name" onDoubleClick={() => canEdit && setEditing(i)} title={canEdit ? "Clique duplo pra editar" : ""}>{e.nome}</div>
                  <div className="stage-date">
                    {e.status === "concluida" ? `Concluída ${fmt(e.data_fim || e.data_conclusao) || ""}` :
                     e.status === "em_andamento" ? `Em curso ${e.data_inicio ? "desde " + fmt(e.data_inicio) : ""}` :
                     "Aguardando"}
                  </div>
                  {e.responsavel_id && nomePrestador(e.responsavel_id) && (
                    <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }} data-testid={`etapa-responsavel-${i}`}>Responsável: <span style={{ color: "var(--ink-2)" }}>{nomePrestador(e.responsavel_id)}</span></div>
                  )}
                  {e.kind === "retrabalho" && (
                    <div style={{ marginTop: 8, fontSize: 11, color: "#cdc8be", lineHeight: 1.5 }} data-testid={`retrabalho-info-${i}`}>
                      {e.motivo && (
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ color: "#8a857a" }}>Motivo:</span> {e.motivo}
                        </div>
                      )}
                      {e.responsavel_custo && (
                        <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "#c47a7a", fontWeight: 600 }}>
                          Custo: {e.responsavel_custo}{typeof e.custo === "number" && e.custo > 0 ? ` · R$ ${e.custo.toLocaleString("pt-BR", {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : ""}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Observações da etapa (admin) — visíveis também ao cliente no SB */}
                  <EtapaObservacoesBlock observacoes={e.observacoes} compact testId={`etapa-obs-admin-${i}`} />
                  {canUploadFotos && (
                    <button
                      className="btn btn-sm"
                      style={{
                        marginTop: 10,
                        width: "100%",
                        background: totalSeries > 0 ? "rgba(95,168,108,0.10)" : "var(--gold)",
                        color: totalSeries > 0 ? "#7bbf86" : "#0a0a0a",
                        borderColor: totalSeries > 0 ? "rgba(95,168,108,0.4)" : "var(--gold)",
                        fontWeight: 600,
                        fontSize: 11,
                        letterSpacing: ".1em",
                        textTransform: "uppercase",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                      }}
                      onClick={() => setWizardEtapa(e.nome)}
                      data-testid={`etapa-fotos-${i}`}
                      title="Registrar séries fotográficas (3 fotos por item) desta etapa"
                    >
                      <Camera size={13} />
                      {totalSeries > 0
                        ? `${totalSeries} série${totalSeries > 1 ? "s" : ""} · adicionar`
                        : "Registrar fotos"}
                    </button>
                  )}
                  {canEdit && (
                    <div style={{ marginTop: 12, display: "flex", gap: 4, flexWrap: "wrap" }}>
                      <button className="btn btn-ghost btn-sm" style={{ padding: "4px 8px", fontSize: 9 }} onClick={() => setEditing(i)} data-testid={`etapa-edit-${i}`}><Pencil size={9} /> Editar</button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: "4px 8px", fontSize: 9 }} onClick={() => moveEtapa(i, -1)} disabled={i === 0} title="Subir">↑</button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: "4px 8px", fontSize: 9 }} onClick={() => moveEtapa(i, +1)} disabled={i === (obra.etapas.length - 1)} title="Descer">↓</button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: "4px 8px", fontSize: 9, color: "#b8534a" }} onClick={() => removeEtapa(i, e.nome)} data-testid={`etapa-remove-${i}`}>×</button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      {showRetrabalho && (
        <RetrabalhoModal
          obra={obra}
          onClose={() => setShowRetrabalho(false)}
          onCreated={async () => {
            setShowRetrabalho(false);
            await onReload?.();
            setSaveMsg("✓ Retrabalho criado");
            setTimeout(() => setSaveMsg(""), 3000);
          }}
          api={api}
        />
      )}
      {wizardEtapa && (
        <SerieFotosAdminModal
          obra={obra}
          etapaNome={wizardEtapa}
          onClose={() => setWizardEtapa(null)}
          onReload={() => { onReload?.(); }}
        />
      )}
      {saveMsg && (
        <div
          data-testid="etapa-save-toast"
          style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 9999,
            padding: "14px 22px", maxWidth: 380,
            background: saveMsg.startsWith("✓") ? "rgba(95,168,108,0.15)" : "rgba(196,122,122,0.15)",
            border: `1px solid ${saveMsg.startsWith("✓") ? "#5fa86c" : "#c47a7a"}`,
            color: saveMsg.startsWith("✓") ? "#7bbf86" : "#d49595",
            fontSize: 13, letterSpacing: ".02em",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            backdropFilter: "blur(10px)",
          }}
        >
          {saveMsg}
        </div>
      )}
    </div>
  );
}

function RetrabalhoModal({ obra, onClose, onCreated, api }) {
  const [nome, setNome] = useState("");
  const [motivo, setMotivo] = useState("");
  const [responsavel, setResponsavel] = useState("camasa");
  const [prestadorId, setPrestadorId] = useState("");
  const [custo, setCusto] = useState("");
  const [afterIdx, setAfterIdx] = useState(((obra.etapas || []).length - 1).toString());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const prestadores = (obra.terceirizados || []).map(t => ({
    id: t.id || t.terceirizado_id, nome: t.nome,
  })).filter(p => p.id && p.nome);

  const submit = async () => {
    setErr("");
    if (!nome.trim()) { setErr("Dê um nome descritivo para esta etapa."); return; }
    if (!motivo.trim()) { setErr("O motivo do retrabalho é obrigatório."); return; }
    setBusy(true);
    try {
      const body = {
        nome: nome.trim(),
        kind: "retrabalho",
        motivo: motivo.trim(),
        responsavel_custo: responsavel,
        after_idx: parseInt(afterIdx, 10),
      };
      if (prestadorId) body.prestador_id = prestadorId;
      if (custo !== "" && !isNaN(parseFloat(custo))) body.custo = parseFloat(custo);
      await api("post", `/obras/${obra.id}/etapas`, body);
      await onCreated?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Erro ao criar retrabalho");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-back" onClick={onClose} data-testid="retrabalho-modal">
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="modal-head" style={{ borderBottom: "2px solid #c47a7a" }}>
          <h3 style={{ color: "#c47a7a" }}>🔧 Nova etapa de retrabalho</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="field">
            <label>Nome descritivo *</label>
            <input
              className="input" value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ex: Retrabalho — Bancada Quebrada"
              data-testid="retrabalho-nome"
              maxLength={80}
              autoFocus
            />
          </div>
          <div className="field">
            <label>Motivo do retrabalho *</label>
            <textarea
              className="input" value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Descreva o problema que motivou o retrabalho…"
              data-testid="retrabalho-motivo"
              rows={3}
              maxLength={500}
              style={{ resize: "vertical", minHeight: 80 }}
            />
          </div>
          <div className="field">
            <label>Responsável pelo custo *</label>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {[{ v: "camasa", l: "CAMASA" }, { v: "cliente", l: "Cliente" }, { v: "terceiro", l: "Terceiro" }].map(opt => (
                <label key={opt.v} style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer", fontSize: 13 }}>
                  <input
                    type="radio" name="resp_custo"
                    value={opt.v}
                    checked={responsavel === opt.v}
                    onChange={e => setResponsavel(e.target.value)}
                    data-testid={`retrabalho-resp-${opt.v}`}
                  />
                  {opt.l}
                </label>
              ))}
            </div>
          </div>
          {prestadores.length > 0 && (
            <div className="field">
              <label>Prestador responsável (opcional)</label>
              <select
                className="input" value={prestadorId}
                onChange={e => setPrestadorId(e.target.value)}
                data-testid="retrabalho-prestador"
              >
                <option value="">— Nenhum específico —</option>
                {prestadores.map(p => (<option key={p.id} value={p.id}>{p.nome}</option>))}
              </select>
            </div>
          )}
          <div className="field">
            <label>Custo estimado (R$) — opcional</label>
            <input
              className="input" value={custo}
              onChange={e => setCusto(e.target.value.replace(/[^0-9.,]/g, "").replace(",", "."))}
              placeholder="Ex: 850.00"
              inputMode="decimal"
              data-testid="retrabalho-custo"
            />
            <div style={{ fontSize: 10, color: "#8a857a", marginTop: 4, letterSpacing: ".05em" }}>
              Usado no Dashboard de Retrabalhos para somatórios por responsável
            </div>
          </div>
          <div className="field">
            <label>Inserir após qual etapa</label>
            <select
              className="input" value={afterIdx}
              onChange={e => setAfterIdx(e.target.value)}
              data-testid="retrabalho-after"
            >
              {(obra.etapas || []).map((e, i) => (
                <option key={i} value={i}>{String(i + 1).padStart(2, "0")} · {e.nome}</option>
              ))}
            </select>
          </div>
          {err && <div style={{ color: "#c47a7a", fontSize: 12, padding: "8px 12px", background: "rgba(196,122,122,0.1)", border: "1px solid rgba(196,122,122,0.3)" }} data-testid="retrabalho-err">⚠ {err}</div>}
        </div>
        <div className="modal-foot" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button
            className="btn"
            onClick={submit}
            disabled={busy}
            data-testid="retrabalho-submit"
            style={{ background: "#c47a7a", color: "#0d0d0f", borderColor: "#c47a7a" }}
          >
            {busy ? "Criando…" : "🔧 Criar etapa"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * EtapaObservacoesBlock — bloco destacado com observações da etapa.
 * Usado no card admin da etapa E na cronologia do Signature Book público.
 * Renderiza apenas se observacoes tem conteúdo. Visual: borda lateral dourada,
 * fundo levemente diferente, ícone ⚠️ antes do título, texto em itálico.
 * Aceita whitespace-pre-wrap para preservar quebras de linha do admin.
 */
function EtapaObservacoesBlock({ observacoes, compact = false, testId }) {
  const txt = (observacoes || "").trim();
  if (!txt) return null;
  return (
    <div
      data-testid={testId || "etapa-observacoes-block"}
      style={{
        marginTop: compact ? 8 : 14,
        padding: compact ? "8px 10px 8px 12px" : "14px 16px 14px 18px",
        background: "rgba(184, 147, 90, 0.06)",  // dourado bem suave
        borderLeft: "3px solid var(--gold)",
        borderTop: "1px solid rgba(184, 147, 90, 0.15)",
        borderRight: "1px solid rgba(184, 147, 90, 0.15)",
        borderBottom: "1px solid rgba(184, 147, 90, 0.15)",
      }}
    >
      <div style={{
        fontSize: compact ? 9 : 10,
        letterSpacing: ".25em",
        textTransform: "uppercase",
        color: "var(--gold)",
        fontWeight: 600,
        marginBottom: compact ? 4 : 6,
      }}>
        ⚠️ Observações
      </div>
      <div style={{
        fontSize: compact ? 11 : 13,
        fontStyle: "italic",
        color: "var(--ink-2)",
        lineHeight: 1.55,
        whiteSpace: "pre-wrap",
      }}>
        {txt}
      </div>
    </div>
  );
}

function EtapaEditor({ etapa, prestadores = [], onSave, onCancel, saving }) {
  const [nome, setNome] = useState(etapa.nome || "");
  const [responsavelId, setResponsavelId] = useState(etapa.responsavel_id || "");
  const isRetrab = etapa.kind === "retrabalho";
  const [motivo, setMotivo] = useState(etapa.motivo || "");
  const [respCusto, setRespCusto] = useState(etapa.responsavel_custo || "camasa");
  const [custo, setCusto] = useState(etapa.custo ?? "");
  const [status, setStatus] = useState(etapa.status || "aguardando");
  const [dataInicio, setDataInicio] = useState(etapa.data_inicio || "");
  const [dataFim, setDataFim] = useState(etapa.data_fim || "");
  const [observacoes, setObservacoes] = useState(etapa.observacoes || "");
  const handleSave = () => onSave({
    nome, status,
    data_inicio: dataInicio || null,
    data_fim: dataFim || null,
    // Sempre envia string (vazio limpa o campo). Sem limite de chars no UI.
    observacoes: observacoes || "",
    responsavel_id: responsavelId || "",
    ...(isRetrab ? { motivo, responsavel_custo: respCusto, custo: parseFloat(String(custo).replace(",", ".")) || 0 } : {}),
  });
  const lblSm = { fontSize: 9, color: "var(--ink-3)", letterSpacing: ".15em", textTransform: "uppercase" };
  return (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }} data-testid="etapa-editor">
      <input className="input" value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome" style={{ fontSize: 12 }} data-testid="etapa-editor-nome" disabled={saving} />
      <select className="input" value={status} onChange={e => setStatus(e.target.value)} style={{ fontSize: 11 }} data-testid="etapa-editor-status" disabled={saving}>
        <option value="aguardando">Aguardando</option>
        <option value="em_andamento">Em curso</option>
        <option value="concluida">Concluída</option>
      </select>
      <label style={lblSm}>Responsável</label>
      <select className="input" value={responsavelId} onChange={e => setResponsavelId(e.target.value)} style={{ fontSize: 11 }} data-testid="etapa-editor-responsavel" disabled={saving}>
        <option value="">— sem responsável —</option>
        {prestadores.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
      </select>
      {isRetrab && (
        <>
          <label style={lblSm}>Motivo do retrabalho</label>
          <input className="input" value={motivo} onChange={e => setMotivo(e.target.value)} style={{ fontSize: 11 }} data-testid="etapa-editor-motivo" disabled={saving} />
          <label style={lblSm}>Custo por conta de</label>
          <select className="input" value={respCusto} onChange={e => setRespCusto(e.target.value)} style={{ fontSize: 11 }} data-testid="etapa-editor-resp-custo" disabled={saving}>
            <option value="camasa">CAMASA</option><option value="cliente">Cliente</option><option value="terceiro">Terceiro</option>
          </select>
          <label style={lblSm}>Custo (R$)</label>
          <input type="number" step="0.01" className="input" value={custo} onChange={e => setCusto(e.target.value)} style={{ fontSize: 11 }} data-testid="etapa-editor-custo" disabled={saving} />
        </>
      )}
      <label style={{ fontSize: 9, color: "var(--ink-3)", letterSpacing: ".15em", textTransform: "uppercase" }}>Prazo / Início</label>
      <input type="date" className="input" value={dataInicio || ""} onChange={e => setDataInicio(e.target.value)} style={{ fontSize: 11 }} data-testid="etapa-editor-inicio" disabled={saving} />
      <label style={{ fontSize: 9, color: "var(--ink-3)", letterSpacing: ".15em", textTransform: "uppercase" }}>Prazo / Fim</label>
      <input type="date" className="input" value={dataFim || ""} onChange={e => setDataFim(e.target.value)} style={{ fontSize: 11 }} data-testid="etapa-editor-fim" disabled={saving} />
      <label style={{ fontSize: 9, color: "var(--ink-3)", letterSpacing: ".15em", textTransform: "uppercase" }}>Observações <span style={{ color: "var(--ink-3)", textTransform: "none", letterSpacing: 0 }}>(visível ao cliente no Signature Book)</span></label>
      <textarea
        className="input"
        value={observacoes}
        onChange={e => setObservacoes(e.target.value)}
        placeholder="Ex: Alinhar entrega com içamento previsto para 15/06. Imprevisto externo aguardando liberação."
        rows={4}
        data-testid="etapa-editor-observacoes"
        disabled={saving}
        style={{ fontSize: 11, lineHeight: 1.5, fontFamily: "inherit", resize: "vertical" }}
      />
      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
        <button className="btn btn-sm" style={{ padding: "4px 8px", fontSize: 10 }} onClick={handleSave} data-testid="etapa-editor-save" disabled={saving}>
          {saving ? "Salvando…" : "Salvar"}
        </button>
        <button className="btn btn-ghost btn-sm" style={{ padding: "4px 8px", fontSize: 10 }} onClick={onCancel} disabled={saving}>Cancelar</button>
      </div>
    </div>
  );
}

function ObraMateriais({ obra, canEdit, onReload }) {
  const { api, user } = useAuth();
  const verItens = ["admin", "gestor", "financeiro"].includes(user?.role);
  const mats = obra.materiais || [];
  const [editingMat, setEditingMat] = useState(null);  // material sendo editado
  const [showNewMat, setShowNewMat] = useState(false);  // criar material novo
  const [showVincular, setShowVincular] = useState(false);  // abrir modal de vincular (edit obra)
  return (
    <div className="fade-up">
      <div className="ws-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div className="eyebrow"><Tricolore thin /> Curadoria</div>
          <h1>Materiais da <em>Obra</em></h1>
          <p className="page-sub" style={{ fontSize: 13, marginTop: 8 }}>
            {mats.length} material(is) vinculado(s) · Clique num card para editar nome, acabamento ou subir imagens.
          </p>
        </div>
        {canEdit && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn" onClick={() => setShowVincular(true)} data-testid="btn-vincular-mat">
              <Plus size={13} /> Escolher do Catálogo
            </button>
            <button className="btn btn-ghost" onClick={() => setShowNewMat(true)} data-testid="btn-novo-mat">
              <Plus size={13} /> Novo material
            </button>
          </div>
        )}
      </div>
      {canEdit && <HowToBanner id="obra-materiais" title="Como gerenciar Materiais da obra" steps={[
        { num: 1, title: "Vincule do catálogo", desc: "Clique em <strong>Vincular do catálogo</strong> para escolher quais mármores/quartzitos já cadastrados vão ser usados nesta obra." },
        { num: 2, title: "Ou crie um novo", desc: "Clique em <strong>Novo material</strong> se a pedra ainda não está no catálogo. Ao salvar, ela fica disponível para todas as outras obras também." },
        { num: 3, title: "Clique num card para editar", desc: "Dentro do material você pode subir até <strong>5 fotos reais</strong> da pedra — elas compõem o Signature Book final do cliente." },
        { num: 4, title: "Aparece no PDF automaticamente", desc: "Todos os materiais vinculados aqui aparecem na seção <strong>Matéria-prima selecionada</strong> do Signature Book online e do PDF premium." },
      ]} />}
      {mats.length === 0 ? (
        <div className="empty" data-testid="mats-empty">
          <h3>Nenhum material vinculado</h3>
          <p>Vincule materiais do catálogo para compor o Signature Book desta obra.</p>
          {canEdit && (
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
              <button className="btn" onClick={() => setShowVincular(true)}><Plus size={13} /> Escolher do Catálogo</button>
              <button className="btn btn-ghost" onClick={() => setShowNewMat(true)}><Plus size={13} /> Criar novo</button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 2, background: "var(--line)", border: "1px solid var(--line)" }}>
          {mats.map(m => {
            const imgs = m.imagens || [];
            const imgCount = imgs.length;
            const firstImg = imgs[0];
            return (
              <div
                key={m.id}
                onClick={() => canEdit && setEditingMat(m)}
                style={{ background: "var(--bg-2)", padding: 22, cursor: canEdit ? "pointer" : "default", transition: "background 0.2s" }}
                onMouseEnter={e => canEdit && (e.currentTarget.style.background = "var(--bg-3)")}
                onMouseLeave={e => canEdit && (e.currentTarget.style.background = "var(--bg-2)")}
                data-testid={`mat-card-${m.id}`}
              >
                <div style={{ position: "relative", aspectRatio: "1/1", backgroundImage: firstImg ? `url(${fileUrl(firstImg)})` : "none", backgroundSize: "cover", backgroundPosition: "center", marginBottom: 14, border: "1px solid var(--line-2)" }}>
                  {imgCount > 1 && (
                    <div style={{ position: "absolute", bottom: 6, right: 6, background: "rgba(0,0,0,0.7)", color: "var(--gold)", fontSize: 10, padding: "3px 8px", letterSpacing: ".1em" }}>
                      {imgCount} fotos
                    </div>
                  )}
                  {!firstImg && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-3)", fontSize: 11, letterSpacing: ".2em", textTransform: "uppercase" }}>
                      Sem imagem
                    </div>
                  )}
                </div>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: 18, color: "var(--ink)", fontStyle: "italic" }}>{m.nome}</div>
                <div style={{ fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: "var(--ink-3)", marginTop: 6 }}>{MAT_CAT[m.categoria] || m.categoria}</div>
                <div style={{ fontSize: 11, color: "var(--gold)", marginTop: 4 }}>{m.acabamento}</div>
              </div>
            );
          })}
        </div>
      )}
      {verItens && <ObraMateriaisItens obraId={obra.id} materiais={mats} api={api} canEdit={canEdit} />}
      {editingMat && <MaterialEditor material={editingMat} obraIdLink={obra.id} onClose={() => setEditingMat(null)} onSaved={() => { setEditingMat(null); onReload && onReload(); }} />}
      {showNewMat && <MaterialEditor material={null} obraIdLink={obra.id} onClose={() => setShowNewMat(false)} onSaved={() => { setShowNewMat(false); onReload && onReload(); }} />}
      {showVincular && <EditObraModal obra={obra} onClose={() => setShowVincular(false)} onSaved={() => { setShowVincular(false); onReload && onReload(); }} />}
    </div>
  );
}

function ObraEquipe({ obra }) {
  const { user } = useAuth();
  const canEdit = ["admin", "gestor"].includes(user?.role);
  const prests = obra.prestadores || [];
  const tercs = obra.terceirizados || [];
  return (
    <div className="fade-up">
      <div className="ws-head">
        <div><div className="eyebrow"><Tricolore thin /> Rede</div><h1>Equipe <em>envolvida</em></h1></div>
      </div>
      {canEdit && <HowToBanner id="obra-equipe" title="Como gerenciar a Equipe da obra" steps={[
        { num: 1, title: "Vincule arquiteto", desc: "Volte à <strong>Visão Geral</strong> → <strong>Editar</strong> → selecione o Arquiteto responsável. Ele passa a aparecer nesta aba e no Signature Book." },
        { num: 2, title: "Aloque prestadores", desc: "Em <strong>Editar</strong> → marque os prestadores (marmorista, medidor, instalador, motorista) no chip-grid <strong>Prestadores alocados</strong>." },
        { num: 3, title: "Vincule terceirizados", desc: "Mesmo fluxo para <strong>Terceirizados vinculados</strong> (marmoraria, frete). Eles passam a ver a obra no Portal do Terceirizado e podem enviar fotos." },
        { num: 4, title: "Atribua etapas", desc: "Em <strong>Etapas</strong>, ao editar uma etapa, escolha o prestador responsável por ela. Isso alimenta relatórios de desempenho da rede." },
      ]} />}
      {obra.arquiteto && (
        <div className="panel" style={{ marginBottom: 20 }}>
          <div className="panel-eyebrow">Arquiteto responsável</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12 }}>
            <div className="pava" style={{ width: 52, height: 52, fontSize: 18 }}>{obra.arquiteto.nome.charAt(0)}</div>
            <div>
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 20, color: "var(--ink)" }}>{obra.arquiteto.nome}</div>
              <div style={{ fontSize: 12, color: "var(--gold)", letterSpacing: ".15em" }}>{obra.arquiteto.escritorio}</div>
            </div>
          </div>
        </div>
      )}
      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="panel-eyebrow">Prestadores de serviço</div>
        {prests.length === 0 ? <p style={{ color: "var(--ink-3)" }}>Nenhum prestador vinculado.</p> :
          prests.map(p => (
            <div className="prow" key={p.id} data-testid={`equipe-prestador-${p.id}`}>
              <div className="pava">{(p.nome || "?").charAt(0)}</div>
              <div className="prow-info">
                <div className="prow-name">{p.nome}</div>
                <div className="prow-role">{ESP_LBL[p.especialidade] || p.especialidade}</div>
              </div>
              <span className="pill done">Ativo</span>
            </div>
          ))
        }
      </div>
      <div className="panel" data-testid="equipe-terceirizados">
        <div className="panel-eyebrow">Terceirizados vinculados</div>
        {tercs.length === 0 ? <p style={{ color: "var(--ink-3)" }}>Nenhum terceirizado vinculado.</p> :
          tercs.map(t => (
            <div className="prow" key={t.id} data-testid={`equipe-terceirizado-${t.id}`}>
              <div className="pava">{(t.nome || t.empresa || "?").charAt(0)}</div>
              <div className="prow-info">
                <div className="prow-name">{t.nome || t.empresa}</div>
                <div className="prow-role">
                  {(t.tipo || t.especialidade || "Terceirizado").replace(/_/g, " ")}
                  {t.empresa && t.empresa !== t.nome && <span style={{ color: "var(--ink-3)" }}> · {t.empresa}</span>}
                  {t.cidade && <span style={{ color: "var(--ink-3)" }}> · {t.cidade}</span>}
                </div>
              </div>
              <span className="pill done">Ativo</span>
            </div>
          ))
        }
      </div>
    </div>
  );
}

const DOC_CAT = {
  contrato_orcamento: { label: "Contrato / Orçamento", icon: "📑", color: "#b8935a" },
  medicao: { label: "Medição / Desenhos", icon: "📐", color: "#8a7454" },
  planta: { label: "Planta", icon: "🏗️", color: "#a67c52" },
  nota_fiscal: { label: "Nota Fiscal", icon: "🧾", color: "#c4a678" },
  laudo: { label: "Laudo Técnico", icon: "📋", color: "#967850" },
  ficha_tecnica: { label: "Ficha Técnica", icon: "📄", color: "#b89572" },
  outros: { label: "Outros", icon: "📎", color: "#7a7a7a" },
};
const DOC_EXT_ICON = { pdf: "📄", doc: "📝", docx: "📝", xls: "📊", xlsx: "📊", ppt: "📽️", pptx: "📽️", dwg: "📐", dxf: "📐", zip: "🗜️", txt: "📃", csv: "📊" };
function formatBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function ObraDocumentos({ obra, canEdit, onReload, api }) {
  const { token } = useAuth();
  const docs = obra.documentos || [];
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [uploadCat, setUploadCat] = useState("contrato_orcamento");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadEtapa, setUploadEtapa] = useState("");
  const [filter, setFilter] = useState("todos");
  const etapasNomes = (obra.etapas || []).map(e => e.nome);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = null;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("categoria", uploadCat);
        fd.append("descricao", uploadDesc);
        if (uploadEtapa) fd.append("etapa", uploadEtapa);
        await axios.post(`${API}/obras/${obra.id}/documento`, fd, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        });
      }
      setUploadDesc("");
      setUploadEtapa("");
      onReload && onReload();
    } catch (err) {
      alert("Erro ao enviar: " + (err?.response?.data?.detail || err.message));
    } finally { setUploading(false); }
  };

  const onDelete = async (docId, filename) => {
    if (!window.confirm(`Remover "${filename}"?`)) return;
    try {
      await api("delete", `/obras/${obra.id}/documento/${docId}`);
      onReload && onReload();
    } catch (err) { alert("Erro: " + (err?.response?.data?.detail || err.message)); }
  };

  const onChangeCat = async (docId, newCat) => {
    try {
      await api("patch", `/obras/${obra.id}/documento/${docId}`, { categoria: newCat });
      onReload && onReload();
    } catch (err) { alert("Erro: " + (err?.response?.data?.detail || err.message)); }
  };

  const onChangeEtapa = async (docId, newEtapa) => {
    try {
      await api("patch", `/obras/${obra.id}/documento/${docId}`, { etapa: newEtapa || null });
      onReload && onReload();
    } catch (err) { alert("Erro: " + (err?.response?.data?.detail || err.message)); }
  };

  const filtered = filter === "todos" ? docs : docs.filter(d => d.categoria === filter);
  const cats = Object.keys(DOC_CAT);
  const countsByCat = cats.reduce((acc, c) => { acc[c] = docs.filter(d => d.categoria === c).length; return acc; }, {});

  return (
    <div className="fade-up">
      <div className="ws-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div className="eyebrow"><Tricolore thin /> Arquivo</div>
          <h1><em>Documentos</em> da obra</h1>
          <p className="page-sub" style={{ fontSize: 13, marginTop: 8 }}>
            {docs.length} documento(s) · Contratos, orçamentos, plantas, NFs, laudos e mais. Arquiteto e cliente também visualizam.
          </p>
        </div>
        {canEdit && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <select className="inp" value={uploadCat} onChange={e => setUploadCat(e.target.value)} style={{ minWidth: 180 }} data-testid="doc-upload-cat">
              {cats.map(c => <option key={c} value={c}>{DOC_CAT[c].icon} {DOC_CAT[c].label}</option>)}
            </select>
            <input ref={fileRef} type="file" multiple style={{ display: "none" }}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.dwg,.dxf,.zip,.txt,.csv,.odt,.ods"
              onChange={onFiles} data-testid="doc-file-input" />
            <button className="btn" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-btn-upload">
              <Plus size={13} /> {uploading ? "Enviando…" : "Adicionar Arquivo"}
            </button>
          </div>
        )}
      </div>
      {canEdit && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16, maxWidth: 900, alignItems: "center" }}>
          <input className="inp" type="text" placeholder="Descrição (opcional) — ex: Contrato assinado em 20/04/2026" value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} style={{ flex: 1, minWidth: 280 }} />
          <select
            className="inp"
            value={uploadEtapa}
            onChange={e => setUploadEtapa(e.target.value)}
            data-testid="doc-upload-etapa"
            title="Vincular este documento a uma etapa do cronograma (opcional)"
            style={{ minWidth: 220 }}
          >
            <option value="">— Sem etapa vinculada —</option>
            {etapasNomes.map(n => (<option key={n} value={n}>📎 {n}</option>))}
          </select>
        </div>
      )}

      {canEdit && <HowToBanner id="obra-documentos" title="Como gerenciar Documentos" steps={[
        { num: 1, title: "Escolha a categoria certa", desc: "Antes de enviar, selecione no dropdown: <strong>📑 Contrato / Orçamento</strong>, <strong>📐 Medição / Desenhos</strong>, <strong>🏗️ Planta</strong>, <strong>🧾 NF</strong>, <strong>📋 Laudo</strong> ou <strong>📄 Ficha Técnica</strong>." },
        { num: 2, title: "Adicione uma descrição", desc: "Use o campo de descrição para informação útil, ex: <em>\"Contrato assinado em 20/04/2026\"</em> ou <em>\"Planta baixa rev.3\"</em>. Facilita muito na hora de encontrar." },
        { num: 3, title: "Envie vários de uma vez", desc: "Clique em <strong>Enviar documentos</strong> e selecione múltiplos arquivos. Aceita PDF, DOC, XLS, DWG, ZIP, DXF, TXT, CSV — até 50MB por arquivo." },
        { num: 4, title: "Todos veem conforme permissão", desc: "<strong>Cliente</strong> vê contratos e NFs dele. <strong>Arquiteto</strong> vê plantas e medições. <strong>Terceirizados</strong> (marmoraria, frete) veem fichas técnicas para executar o trabalho." },
      ]} />}

      {/* Filtros */}
      {docs.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
          <button
            onClick={() => setFilter("todos")}
            className={"chip " + (filter === "todos" ? "active" : "")}
            data-testid="doc-filter-todos"
          >Todos <span style={{ opacity: 0.6 }}>· {docs.length}</span></button>
          {cats.filter(c => countsByCat[c] > 0).map(c => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={"chip " + (filter === c ? "active" : "")}
              data-testid={`doc-filter-${c}`}
            >{DOC_CAT[c].icon} {DOC_CAT[c].label} <span style={{ opacity: 0.6 }}>· {countsByCat[c]}</span></button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="empty" data-testid="doc-empty">
          <Folder size={32} color="var(--gold)" style={{ margin: "0 auto 14px", display: "block", opacity: 0.6 }} />
          <h3>{filter === "todos" ? "Nenhum documento ainda" : "Nenhum documento nesta categoria"}</h3>
          <p>{canEdit ? "Envie contratos, orçamentos, plantas ou qualquer documento referente à obra." : "Em breve teremos documentos disponíveis para consulta."}</p>
        </div>
      ) : (
        <div className="docs-grid" data-testid="doc-list">
          {filtered.sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || "")).map(d => {
            const ext = (d.filename || "").split(".").pop().toLowerCase();
            const extIcon = DOC_EXT_ICON[ext] || "📎";
            const cat = DOC_CAT[d.categoria] || DOC_CAT.outros;
            const isPdf = ext === "pdf";
            return (
              <div key={d.id} className="doc-card" data-testid={`doc-card-${d.id}`}>
                <div className="doc-card-icon" style={{ background: `linear-gradient(135deg, ${cat.color}22, ${cat.color}05)` }}>
                  <span style={{ fontSize: 40 }}>{extIcon}</span>
                </div>
                <div className="doc-card-body">
                  <div className="doc-card-cat" style={{ color: cat.color }}>{cat.icon} {cat.label}</div>
                  <div className="doc-card-name" title={d.filename}>{d.filename}</div>
                  {d.descricao && <div className="doc-card-desc">"{d.descricao}"</div>}
                  {d.etapa && (
                    <div
                      data-testid={`doc-etapa-badge-${d.id}`}
                      style={{
                        marginTop: 4, fontSize: 10, letterSpacing: ".08em",
                        textTransform: "uppercase", color: "#b8935a",
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "3px 8px",
                        background: "rgba(184,147,90,0.08)",
                        border: "1px solid rgba(184,147,90,0.25)",
                      }}
                      title={`Vinculado à etapa: ${d.etapa}`}
                    >
                      📎 {d.etapa}
                    </div>
                  )}
                  <div className="doc-card-meta">
                    {formatBytes(d.size_bytes)} · {fmt(d.uploaded_at)}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}
                  </div>
                  <div className="doc-card-actions">
                    <a href={d.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" data-testid={`doc-view-${d.id}`}>
                      {isPdf ? "👁 Visualizar" : "⤓ Abrir"}
                    </a>
                    <a href={d.url} download={d.filename} className="btn btn-ghost btn-sm" data-testid={`doc-download-${d.id}`}>
                      <Download size={12} /> Baixar
                    </a>
                    {canEdit && (
                      <>
                        <select className="inp" value={d.categoria} onChange={e => onChangeCat(d.id, e.target.value)} style={{ fontSize: 10, padding: "4px 6px", width: "auto", flex: 1, minWidth: 0 }} data-testid={`doc-cat-${d.id}`}>
                          {cats.map(c => <option key={c} value={c}>{DOC_CAT[c].label}</option>)}
                        </select>
                        <select className="inp" value={d.etapa || ""} onChange={e => onChangeEtapa(d.id, e.target.value)} style={{ fontSize: 10, padding: "4px 6px", width: "auto", flex: 1, minWidth: 0 }} data-testid={`doc-etapa-${d.id}`} title="Vincular a uma etapa do cronograma">
                          <option value="">— Sem etapa —</option>
                          {etapasNomes.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <button className="btn btn-ghost btn-sm" onClick={() => onDelete(d.id, d.filename)} title="Remover" data-testid={`doc-del-${d.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function ObraMidias({ obra, canEdit, canUpload, onReload, api }) {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [filterEtapa, setFilterEtapa] = useState("");
  const [wizardEtapa, setWizardEtapa] = useState(null);
  const [uploadCategoria, setUploadCategoria] = useState("obra"); // obra | ambientacao
  const [uploadData, setUploadData] = useState(""); // data real das fotos (vazio = hoje)
  const [uploadConcluida, setUploadConcluida] = useState(false);
  const [uploadAntes, setUploadAntes] = useState(false);
  const [selMode, setSelMode] = useState(false);
  const [sel, setSel] = useState([]);
  const [baixandoAlbum, setBaixandoAlbum] = useState(false);
  const toggleSel = (id) => setSel(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const aplicarLote = async (body) => {
    try {
      const r = await api("post", `/obras/${obra.id}/fotos/lote`, { ids: sel, ...body });
      toastSuccess(`${r.atualizadas} mídia(s) atualizada(s)`);
      onReload();
    } catch (e) { toastError("Não foi possível aplicar", { description: pickErrorMsg(e) }); }
  };
  const baixarAlbum = async () => {
    setBaixandoAlbum(true);
    try {
      const r = await axios.get(`${API}/obras/${obra.id}/album-pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Album-${(obra.nome || "obra").replace(/[^A-Za-z0-9_-]+/g, "-")}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      alert(e.response?.status === 404 ? "Marque fotos como Obra Concluída para gerar o álbum." : "Erro ao gerar o álbum: " + e.message);
    } finally { setBaixandoAlbum(false); }
  };
  const [dragActive, setDragActive] = useState(false);
  const fileRef = React.useRef(null);
  const capaRef = React.useRef(null);

  const fotos = obra.fotos || [];
  const videos = obra.videos || [];
  const etapasNomes = (obra.etapas || []).map(e => e.nome);

  const MAX_MB = 30;
  const ALLOWED = /\.(jpe?g|png|webp|heic|heif|mp4|mov|m4v|webm)$/i;
  const IMG_EXT = /\.(jpe?g|png|webp|heic|heif)$/i;
  const MIN_IMG_KB = 20;     // rejeita placeholders corrompidos
  const MIN_IMG_DIM = 400;   // mínimo 400×400 pixels

  // Lê dimensões da imagem no browser antes de enviar (pra dar feedback instantâneo)
  const readImageSize = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
      img.onerror = () => { URL.revokeObjectURL(url); resolve({ w: 0, h: 0 }); };
      img.src = url;
    } catch { resolve({ w: 0, h: 0 }); }
  });

  const doUpload = async (files, extra = {}) => {
    // Valida extensões, tamanho e qualidade ANTES de começar
    const valid = [];
    const rejected = [];
    for (const f of files) {
      if (!ALLOWED.test(f.name)) { rejected.push({ f, reason: "formato não permitido" }); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { rejected.push({ f, reason: `muito grande (${(f.size / 1024 / 1024).toFixed(1)}MB, máx ${MAX_MB}MB)` }); continue; }
      // Validação de qualidade extra para IMAGENS
      if (IMG_EXT.test(f.name)) {
        if (f.size < MIN_IMG_KB * 1024) {
          rejected.push({ f, reason: `imagem muito leve (${Math.round(f.size/1024)}KB). Signature Book premium exige ≥${MIN_IMG_KB}KB` });
          continue;
        }
        const { w, h } = await readImageSize(f);
        if (w && h && (w < MIN_IMG_DIM || h < MIN_IMG_DIM)) {
          rejected.push({ f, reason: `dimensões insuficientes (${w}×${h}px). Mínimo ${MIN_IMG_DIM}×${MIN_IMG_DIM}px` });
          continue;
        }
      }
      valid.push(f);
    }
    if (rejected.length) {
      alert("Algumas fotos não atendem o padrão premium do Signature Book:\n\n" + rejected.map(r => `• ${r.f.name}\n   ${r.reason}`).join("\n\n") + "\n\nEnvie imagens com boa qualidade (≥400×400px, ≥20KB) para manter o resultado premium no PDF.");
    }
    if (!valid.length) return;

    setUploading(true); setProgress(0);
    let i = 0;
    const errors = [];
    let nCamera = 0;
    for (const f of valid) {
      setCurrentFile(f.name);
      const fd = new FormData();
      fd.append("file", f);
      if (extra.etapa) fd.append("etapa", extra.etapa);
      fd.append("destaque", "false");
      fd.append("categoria", uploadCategoria);
      if (uploadData) fd.append("data_captura", uploadData);
      if (uploadConcluida) fd.append("obra_concluida", "true");
      if (uploadAntes) fd.append("antes_obra", "true");
      try {
        const resp = await axios.post(`${API}/obras/${obra.id}/upload`, fd, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000,  // 2min por arquivo (vídeo grande)
          onUploadProgress: (e) => {
            if (e.total) {
              // Progresso granular por arquivo (blend entre arquivos)
              const fileProgress = (e.loaded / e.total);
              const overall = ((i + fileProgress) / valid.length) * 100;
              setProgress(Math.round(overall));
            }
          },
        });
        if (["camera", "arquivo"].includes(resp.data?.data_origem)) nCamera++;
      } catch (e) {
        errors.push(`${f.name}: ${e.response?.data?.detail || e.message}`);
      }
      i++; setProgress(Math.round(i / valid.length * 100));
    }
    setUploading(false); setProgress(0); setCurrentFile("");
    if (errors.length) {
      alert("Falha em alguns uploads:\n\n" + errors.join("\n"));
    } else if (nCamera > 0) {
      toastSuccess(`${nCamera} foto(s) com a data real da foto`, "Você pode ajustar a data em cada foto ou em lote.");
    }
    onReload();
  };

  const onFilesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) doUpload(files);
    e.target.value = "";
  };
  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) doUpload(files);
  };
  const openFilePicker = () => { if (!uploading) fileRef.current?.click(); };

  const uploadCapa = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Valida ANTES de enviar (capa é a imagem principal da obra — precisa ser de qualidade)
    if (!IMG_EXT.test(f.name)) {
      alert("Capa: apenas imagens (JPG, PNG, WEBP).");
      e.target.value = ""; return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      alert(`Capa muito grande (${(f.size / 1024 / 1024).toFixed(1)}MB). Máximo ${MAX_MB}MB.`);
      e.target.value = ""; return;
    }
    if (f.size < MIN_IMG_KB * 1024) {
      alert(`Capa muito leve (${Math.round(f.size / 1024)}KB). A capa é a imagem principal da obra — envie uma foto de qualidade (mínimo ${MIN_IMG_KB}KB).`);
      e.target.value = ""; return;
    }
    const { w, h } = await readImageSize(f);
    if (w && h && (w < MIN_IMG_DIM || h < MIN_IMG_DIM)) {
      alert(`Capa com dimensões insuficientes (${w}×${h}px). Mínimo ${MIN_IMG_DIM}×${MIN_IMG_DIM}px para ficar bonita na visão geral e no Signature Book.`);
      e.target.value = ""; return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await axios.post(`${API}/obras/${obra.id}/capa`, fd, { headers: { Authorization: `Bearer ${token}` } });
      onReload();
    } catch (e) { alert("Erro: " + (e.response?.data?.detail || e.message)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const toggleDestaque = async (foto) => {
    await api("patch", `/obras/${obra.id}/foto/${foto.id}`, { destaque: !foto.destaque });
    onReload();
  };
  const toggleAprovada = async (foto) => {
    await api("patch", `/obras/${obra.id}/foto/${foto.id}`, { aprovada: !foto.aprovada });
    onReload();
  };
  const changeCategoria = async (foto, cat) => {
    await api("patch", `/obras/${obra.id}/foto/${foto.id}`, { categoria: cat });
    onReload();
  };
  const removeFoto = async (foto) => {
    if (!window.confirm("Remover esta mídia? O arquivo será apagado do disco.")) return;
    await api("delete", `/obras/${obra.id}/foto/${foto.id}`);
    onReload();
  };
  const editLegenda = async (foto) => {
    const l = window.prompt("Legenda:", foto.legenda || "");
    if (l === null) return;
    await api("patch", `/obras/${obra.id}/foto/${foto.id}`, { legenda: l });
    onReload();
  };
  const toggleConcluida = async (foto) => {
    await api("patch", `/obras/${obra.id}/foto/${foto.id}`, { obra_concluida: !foto.obra_concluida, ...(!foto.obra_concluida ? { antes_obra: false } : {}) });
    onReload();
  };
  const toggleAntes = async (foto) => {
    await api("patch", `/obras/${obra.id}/foto/${foto.id}`, { antes_obra: !foto.antes_obra, ...(!foto.antes_obra ? { obra_concluida: false } : {}) });
    onReload();
  };
  const changeData = async (foto, data) => {
    try { await api("patch", `/obras/${obra.id}/foto/${foto.id}`, { data_captura: data }); onReload(); }
    catch (e) { alert("Erro: " + (e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || e.message)); }
  };
  const changeEtapa = async (foto, etapa) => {
    await api("patch", `/obras/${obra.id}/foto/${foto.id}`, { etapa });
    onReload();
  };
  const usarComoCapa = async (foto) => {
    await api("post", `/obras/${obra.id}/capa-from-foto/${foto.id}`);
    onReload();
  };
  const substituirArquivo = (foto) => {
    if (!window.confirm(`TROCAR este arquivo?\n\nA foto/vídeo atual será REMOVIDA e substituída pelo novo arquivo.\n\nSe você quer ADICIONAR uma nova mídia à obra (sem apagar essa), use o "Arraste fotos e vídeos aqui" no topo da aba Mídias.\n\nConfirma a troca?`)) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,video/*,.heic,.heif,.webp";
    input.onchange = async () => {
      if (!input.files || !input.files[0]) return;
      const f = input.files[0];
      if (f.size > MAX_MB * 1024 * 1024) {
        window.alert(`Arquivo muito grande (${(f.size / 1024 / 1024).toFixed(1)}MB). Máximo ${MAX_MB}MB.`);
        return;
      }
      if (!ALLOWED.test(f.name)) {
        window.alert("Formato não permitido. Aceitos: JPG, PNG, WEBP, HEIC, MP4, MOV.");
        return;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        await axios.post(`${API}/obras/${obra.id}/foto/${foto.id}/substituir`, fd, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000,
        });
        onReload();
      } catch (e) {
        window.alert("Erro ao substituir arquivo: " + (e?.response?.data?.detail || e.message));
      }
    };
    input.click();
  };

  const visible = filterEtapa ? fotos.filter(f => f.etapa === filterEtapa) : fotos;
  const destaques = fotos.filter(f => f.destaque).length;

  // Detecta se um item é vídeo: por type explícito OU pela extensão da URL.
  // Backend novo separa fotos/videos, mas dados legados podem ter vídeos em obra.fotos.
  const VIDEO_RE = /\.(mp4|mov|m4v|webm|avi)(\?|$)/i;
  const isVideoItem = (item) => {
    if (!item) return false;
    if (item.type === "video" || item.tipo === "video") return true;
    return VIDEO_RE.test(item.url || "");
  };

  // Mídia visível = fotos do filtro + TODOS os vídeos (vídeos não filtram por etapa pra simplificar).
  // Vídeos exibidos depois das fotos.
  const videosVisible = filterEtapa ? videos.filter(v => v.etapa === filterEtapa) : videos;
  const allMedia = [...visible, ...videosVisible];

  // --- Auditor de qualidade das fotos já armazenadas ---
  const [audit, setAudit] = useState(null);     // {total, ok, low, invalid, items:[{foto_id,status,reason,...}]}
  const [auditing, setAuditing] = useState(false);
  const auditMap = React.useMemo(() => {
    const m = {};
    (audit?.items || []).forEach(i => { m[i.foto_id] = i; });
    return m;
  }, [audit]);
  const runAudit = async () => {
    if (auditing) return;
    setAuditing(true);
    try {
      const r = await axios.post(`${API}/obras/${obra.id}/fotos/audit`, {}, { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 });
      setAudit(r.data);
    } catch (e) {
      window.alert("Erro ao auditar: " + (e.response?.data?.detail || e.message));
    } finally {
      setAuditing(false);
    }
  };

  return (
    <div className="fade-up">
      <div className="ws-head">
        <div>
          <div className="eyebrow"><Tricolore thin /> Galeria</div>
          <h1>Mídias da <em>obra</em></h1>
          <p className="page-sub" style={{ fontSize: 13, marginTop: 8 }}>{fotos.length} foto(s) · {videos.length} vídeo(s) · {destaques} marcada(s) como destaque do Signature Book</p>
        </div>
        {canUpload && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input ref={fileRef} type="file" accept="image/*,video/*,.heic,.heif,.webp" multiple style={{ display: "none" }} onChange={onFilesSelect} data-testid="file-input" />
            <button className="btn" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="btn-upload">
              <Plus size={13} /> {uploading ? `Enviando ${progress}%` : "Adicionar Fotos"}
            </button>
            {canEdit && allMedia.length > 0 && (
              <button className={"btn " + (selMode ? "btn-primary" : "btn-ghost")} onClick={() => { setSelMode(!selMode); setSel([]); }} data-testid="btn-selecao-multipla">
                <CheckSquare size={13} /> {selMode ? "Sair da seleção" : "Selecionar várias"}
              </button>
            )}
            {canEdit && fotos.some(f => f.obra_concluida) && (
              <button className="btn btn-ghost" onClick={baixarAlbum} disabled={baixandoAlbum} data-testid="btn-album-pdf" title="Álbum PDF com as fotos marcadas como Obra Concluída">
                <FileText size={13} /> {baixandoAlbum ? "Gerando álbum…" : "Álbum PDF"}
              </button>
            )}
            {canEdit && fotos.length > 0 && (
              <button
                className="btn btn-ghost"
                onClick={runAudit}
                disabled={auditing}
                data-testid="btn-audit"
                title="Verifica se as fotos atendem o padrão Signature Book (≥400×400px, ≥20KB)"
              >
                <ShieldCheck size={13} /> {auditing ? "Auditando…" : "Auditar qualidade"}
              </button>
            )}
            {canEdit && <>
              <input ref={capaRef} type="file" accept="image/*" style={{ display: "none" }} onChange={uploadCapa} />
              <button className="btn btn-ghost" onClick={() => capaRef.current?.click()} disabled={uploading}>
                <ImageIcon size={13} /> Trocar capa
              </button>
            </>}
          </div>
        )}
      </div>

      {audit && canEdit && (
        <div
          data-testid="audit-summary"
          style={{
            marginTop: 14, padding: "14px 18px",
            background: audit.low + audit.invalid > 0 ? "rgba(196,122,122,0.08)" : "rgba(111,160,136,0.08)",
            border: "1px solid " + (audit.low + audit.invalid > 0 ? "rgba(196,122,122,0.35)" : "rgba(111,160,136,0.35)"),
            borderLeft: "3px solid " + (audit.low + audit.invalid > 0 ? "#c47a7a" : "#6fa088"),
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontFamily: "'Fraunces', serif", fontStyle: "italic", fontSize: 16, color: "var(--ink-1)" }}>
              {audit.low + audit.invalid > 0
                ? `${audit.low + audit.invalid} de ${audit.total} fotos abaixo do padrão Signature Book`
                : `Todas as ${audit.total} fotos atendem o padrão premium`}
            </div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4, letterSpacing: ".1em" }}>
              ✓ {audit.ok} OK · ⚠ {audit.low} baixa qualidade · ✗ {audit.invalid} inválidas
            </div>
            {audit.capa && audit.capa.status !== "ok" && (
              <div
                data-testid="audit-capa-warn"
                style={{ marginTop: 10, padding: "8px 12px", background: "rgba(196,122,122,0.12)", borderLeft: "2px solid #c47a7a", fontSize: 12, color: "#c47a7a", lineHeight: 1.5 }}
              >
                <strong>⚠ Imagem de capa problemática:</strong> {audit.capa.reason || "inválida"}
                {audit.capa.width && ` (${audit.capa.width}×${audit.capa.height}px, ${Math.round((audit.capa.size_bytes || 0) / 1024)}KB)`}
                . Clique em <strong>"Trocar capa"</strong> para enviar uma foto decente.
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setAudit(null)} data-testid="btn-audit-clear">Ocultar</button>
        </div>
      )}

      {canUpload && !canEdit && (
        <div style={{ marginBottom: 16, padding: "12px 16px", background: "rgba(184,147,90,0.06)", border: "1px solid rgba(184,147,90,0.25)", borderRadius: 4, fontSize: 12, color: "var(--ink-2)", letterSpacing: "normal", textTransform: "none" }}>
          <strong style={{ color: "var(--gold)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>Portal do Terceirizado:</strong> as mídias que você enviar aqui vão para a <strong>fila de aprovação</strong> do CAMASA. Após aprovadas, aparecerão no Signature Book do cliente.
        </div>
      )}

      {canEdit && <HowToBanner id="obra-midias" title="Como gerenciar Mídias" steps={[
        { num: 1, title: "Escolha a categoria", desc: "Antes de enviar, marque: <strong>Galeria da obra</strong> ou <strong>Ambientação</strong> (detalhes). Para a seção \"A obra concluída\" do Signature Book, marque <strong>Obra Concluída</strong> no envio ou em cada foto." },
        { num: 2, title: "Suba fotos e vídeos", desc: "Arraste para a área pontilhada ou clique em <strong>Enviar mídias</strong>. Aceita JPG, PNG, MP4 — máx 30MB por arquivo." },
        { num: 3, title: "Classifique cada mídia", desc: "Clique na foto para adicionar: <strong>Etapa</strong> (em qual fase foi tirada), <strong>Legenda</strong> e marcar como <strong>★ Destaque</strong> (aparece em maior no PDF)." },
        { num: 4, title: "Troque a capa quando quiser", desc: "Clique em <strong>Trocar capa</strong> para atualizar a imagem principal da obra — aparece em todos os lugares." },
        { num: 5, title: "Fotos de terceirizados", desc: "Quando um terceirizado envia mídia, ela fica <strong>pendente</strong>. Vá em <strong>Aprovação</strong> no menu superior para aprovar antes de publicar para o cliente." },
      ]} />}

      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", padding: "10px 14px", background: "var(--bg-3)", border: "1px solid var(--line)" }}>
          <span style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--gold)", textTransform: "uppercase" }}>Próximos uploads serão:</span>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink-2)", cursor: "pointer", letterSpacing: "normal", textTransform: "none" }}>
            <input type="radio" name="upcat" checked={uploadCategoria === "obra"} onChange={() => setUploadCategoria("obra")} style={{ width: 14, height: 14 }} /> Galeria da obra
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink-2)", cursor: "pointer", letterSpacing: "normal", textTransform: "none" }}>
            <input type="radio" name="upcat" checked={uploadCategoria === "ambientacao"} onChange={() => setUploadCategoria("ambientacao")} style={{ width: 14, height: 14 }} /> Ambientação <span style={{ color: "var(--ink-3)" }}>(máx 6)</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--gold)", cursor: "pointer", letterSpacing: "normal", textTransform: "none" }}>
            <input type="checkbox" checked={uploadConcluida} onChange={e => { setUploadConcluida(e.target.checked); if (e.target.checked) setUploadAntes(false); }} style={{ width: 14, height: 14 }} data-testid="upload-obra-concluida" /> Marcar como Obra Concluída
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#a9a3b5", cursor: "pointer", letterSpacing: "normal", textTransform: "none" }}>
            <input type="checkbox" checked={uploadAntes} onChange={e => { setUploadAntes(e.target.checked); if (e.target.checked) setUploadConcluida(false); }} style={{ width: 14, height: 14 }} data-testid="upload-antes-obra" /> Antes da obra
          </label>
        </div>
      )}

      {canUpload && (
        <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "10px 14px", background: "var(--bg-3)", border: "1px solid var(--line)" }}>
          <span style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--gold)", textTransform: "uppercase" }}>Data das fotos/vídeos:</span>
          <input type="date" value={uploadData} max={new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" })} onChange={e => setUploadData(e.target.value)} data-testid="upload-data-captura" className="upload-date"
            style={{ width: 160, colorScheme: "dark", background: "var(--bg-1)", border: "1px solid var(--line)", color: "var(--ink)", padding: "5px 8px", fontSize: 12 }} />
          <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{uploadData ? "Esta data aparece na cronologia do Signature Book." : "Vazio = data de hoje."}</span>
          {uploadData && <button className="chip" onClick={() => setUploadData("")} data-testid="upload-data-limpar">usar hoje</button>}
        </div>
      )}

      {canUpload && (
        <div
          className="drop-zone"
          onClick={openFilePicker}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openFilePicker(); } }}
          data-testid="drop-zone"
          aria-disabled={uploading}
          style={{
            cursor: uploading ? "progress" : "pointer",
            minHeight: 200,
            border: `2px dashed ${dragActive ? "#b8935a" : "rgba(184,147,90,0.35)"}`,
            background: dragActive
              ? "linear-gradient(135deg, rgba(184,147,90,0.18) 0%, rgba(184,147,90,0.06) 100%)"
              : uploading ? "rgba(184,147,90,0.05)" : "rgba(245,239,224,0.015)",
            transition: "border-color .2s ease, background .2s ease, transform .15s ease",
            transform: dragActive ? "scale(1.01)" : "scale(1)",
            padding: 32,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
            userSelect: "none",
          }}
          onMouseEnter={e => { if (!uploading && !dragActive) e.currentTarget.style.borderColor = "rgba(184,147,90,0.6)"; }}
          onMouseLeave={e => { if (!uploading && !dragActive) e.currentTarget.style.borderColor = "rgba(184,147,90,0.35)"; }}
        >
          {uploading ? (
            <>
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: "var(--gold)", fontStyle: "italic" }}>Enviando… {progress}%</div>
              <div style={{ width: "70%", maxWidth: 420, height: 4, background: "rgba(184,147,90,0.15)", marginTop: 10, position: "relative" }}>
                <div style={{ position: "absolute", top: 0, left: 0, height: "100%", width: `${progress}%`, background: "var(--gold)", transition: "width .3s ease" }} />
              </div>
              {currentFile && <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 10, letterSpacing: ".05em", maxWidth: "80%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{currentFile}</div>}
            </>
          ) : dragActive ? (
            <>
              <ImageIcon size={40} color="#b8935a" strokeWidth={1.5} />
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 24, color: "var(--gold)", marginTop: 10, fontStyle: "italic", letterSpacing: ".01em" }}>Solte para enviar</div>
            </>
          ) : (
            <>
              <ImageIcon size={36} color="#b8935a" strokeWidth={1.5} />
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: "var(--ink)", marginTop: 12, fontStyle: "italic" }}>Arraste fotos e vídeos aqui</div>
              <div style={{ fontSize: 12, color: "var(--gold)", marginTop: 8, letterSpacing: ".15em", textTransform: "uppercase", fontWeight: 600 }}>
                ou clique para escolher arquivos
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 10, letterSpacing: ".08em" }}>
                JPG · PNG · WEBP · HEIC · MP4 · MOV  ·  Máx {MAX_MB}MB por arquivo · múltiplos permitidos
              </div>
              <div style={{ fontSize: 10, color: "rgba(184,147,90,0.7)", marginTop: 6, letterSpacing: ".08em", fontStyle: "italic" }}>
                Padrão Signature Book: imagens ≥ {MIN_IMG_DIM}×{MIN_IMG_DIM}px e ≥ {MIN_IMG_KB}KB
              </div>
            </>
          )}
        </div>
      )}

      {etapasNomes.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 24, marginBottom: 20 }}>
          <button className={"chip " + (filterEtapa === "" ? "active" : "")} onClick={() => setFilterEtapa("")}>Todas</button>
          {etapasNomes.map(n => {
            const etapaObj = (obra.etapas || []).find(e => e.nome === n);
            const cfg = resolveEtapaConfig(etapaObj);
            const nReq = cfg?.shots?.length || 0;
            const isRet = etapaObj?.kind === "retrabalho";
            return (
              <button
                key={n}
                className={"chip " + (filterEtapa === n ? "active" : "")}
                onClick={() => setFilterEtapa(n)}
                title={nReq > 0 ? `${nReq} foto(s) obrigatórias guiadas disponíveis` : undefined}
                data-testid={`midias-chip-${n}`}
                style={isRet ? { borderColor: "#c47a7a", color: filterEtapa === n ? undefined : "#c47a7a" } : undefined}
              >
                {isRet && "🔧 "}{n} {nReq > 0 && <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.7 }}>· {nReq}📷</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* CTA wizard de fotos guiadas — visível quando uma etapa com config está selecionada */}
      {filterEtapa && canUpload && (() => {
        const etapaObj = (obra.etapas || []).find(e => e.nome === filterEtapa);
        const cfg = resolveEtapaConfig(etapaObj);
        if (!cfg || !cfg.shots?.length) return null;
        const isRet = etapaObj?.kind === "retrabalho";
        const accent = isRet ? "#c47a7a" : "#b8935a";
        return (
          <div
            data-testid="midias-wizard-cta"
            style={{
              background: `linear-gradient(135deg, ${isRet ? "rgba(196,122,122,0.10)" : "rgba(184,147,90,0.10)"} 0%, rgba(13,13,15,0.4) 100%)`,
              border: `1px solid ${isRet ? "rgba(196,122,122,0.4)" : "rgba(184,147,90,0.35)"}`,
              padding: "18px 22px",
              marginBottom: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontSize: 10, letterSpacing: ".3em", color: accent, textTransform: "uppercase", fontWeight: 600, marginBottom: 6 }}>
                {isRet ? "🔧 Retrabalho" : "Modo Guiado"} — etapa "{filterEtapa}"
              </div>
              <div style={{ fontSize: 14, color: "#cdc8be", lineHeight: 1.5 }}>
                Wizard à prova de erro: tire as <strong style={{ color: "#f5f0e8" }}>{cfg.shots.length} fotos obrigatórias</strong> uma de cada vez, com descrição clara do que fotografar.
              </div>
            </div>
            <button
              onClick={() => setWizardEtapa(filterEtapa)}
              data-testid="midias-wizard-launch"
              style={{
                background: isRet
                  ? "linear-gradient(135deg, #c47a7a 0%, #d49595 100%)"
                  : "linear-gradient(135deg, #b8935a 0%, #d4b27a 100%)",
                color: "#0d0d0f",
                border: "none",
                padding: "14px 26px",
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.1em",
                cursor: "pointer",
                boxShadow: `0 6px 22px ${isRet ? "rgba(196,122,122,0.35)" : "rgba(184,147,90,0.35)"}`,
                textTransform: "uppercase",
              }}
            >
              🪄 Iniciar Wizard
            </button>
          </div>
        );
      })()}

      {selMode && (
        <MidiasLoteBar total={allMedia.length} selecionadas={sel} onTodas={() => setSel(allMedia.map(m => m.id))}
          onLimpar={() => setSel([])} onAplicar={aplicarLote} onSair={() => { setSelMode(false); setSel([]); }} />
      )}

      {allMedia.length === 0 ? (
        <div className="empty" style={{ marginTop: 30 }}><h3>Nenhuma mídia ainda</h3><p>Envie fotos das etapas desta obra.</p></div>
      ) : (
        <div className="gallery-grid">
          {allMedia.map(f => {
            const isVideo = isVideoItem(f);
            const aud = auditMap[f.id];
            const lowQuality = !isVideo && aud && (aud.status === "low" || aud.status === "invalid");
            return (
            <div key={f.id} className={"gallery-item " + (f.destaque ? "is-destaque" : "")} data-testid={`foto-item-${f.id}`}
              onClickCapture={selMode ? (e) => { if (!e.target.closest(".gi-actions")) { e.stopPropagation(); e.preventDefault(); toggleSel(f.id); } } : undefined}
              style={selMode ? { cursor: "pointer", outline: sel.includes(f.id) ? "2px solid #b8935a" : "none", outlineOffset: 2 } : undefined}>
              {selMode && <SelecaoCheck marcado={sel.includes(f.id)} testid={`foto-sel-${f.id}`} />}
              {isVideo ? (
                <div
                  className="gi-img gi-video"
                  data-testid={`video-thumb-${f.id}`}
                  onClick={() => setLightbox(f)}
                  style={{
                    background: "#0d0d0f",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Thumbnail real do vídeo: força carga + seek pra 0.1s pra renderizar
                      o primeiro frame em todos os browsers (Chrome, Safari, FF). */}
                  <video
                    src={fileUrl(f.url)}
                    preload="auto"
                    muted
                    playsInline
                    disablePictureInPicture
                    onLoadedData={(e) => {
                      // força render do primeiro frame mesmo em browsers preguiçosos:
                      // play() rapido enquanto muted é permitido sem user-gesture
                      const v = e.currentTarget;
                      try { v.currentTime = 0.1; } catch (_) {}
                      const p = v.play();
                      if (p && typeof p.then === "function") {
                        p.then(() => v.pause()).catch(() => {});
                      }
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      pointerEvents: "none",
                    }}
                  />
                  {/* Overlay escuro pra contraste do play icon */}
                  <div style={{
                    position: "absolute", inset: 0,
                    background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.5) 100%)",
                    pointerEvents: "none",
                  }} />
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    background: "rgba(184,147,90,0.92)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 18px rgba(0,0,0,0.6)",
                    position: "relative", zIndex: 2,
                  }}>
                    <div style={{
                      width: 0, height: 0,
                      borderLeft: "18px solid #0d0d0f",
                      borderTop: "12px solid transparent",
                      borderBottom: "12px solid transparent",
                      marginLeft: 5,
                    }} />
                  </div>
                  <div style={{
                    position: "absolute", bottom: 8, left: 8, zIndex: 2,
                    background: "rgba(0,0,0,0.7)", color: "#f5f0e8",
                    padding: "3px 8px", fontSize: 9, letterSpacing: ".15em",
                    textTransform: "uppercase", fontWeight: 600,
                    border: "1px solid rgba(184,147,90,0.4)",
                  }}>
                    ▶ Vídeo
                  </div>
                </div>
              ) : (
                <div className="gi-img" style={{ backgroundImage: `url(${fileUrl(f.url)})` }} onClick={() => setLightbox(f)} />
              )}
              {f.destaque && <div className="gi-badge gold">{isVideo ? "★ no Signature Book" : "★ destaque"}</div>}
              {f.obra_concluida && <div className="gi-badge" style={{ top: f.destaque ? 40 : 10, background: "rgba(13,13,15,0.85)", color: "var(--gold)", borderColor: "var(--gold)" }} data-testid={`foto-concluida-badge-${f.id}`}>✓ Obra concluída</div>}
              {f.antes_obra && <div className="gi-badge" style={{ top: f.destaque ? 40 : 10, background: "rgba(58,55,64,0.92)", color: "#f5f0e8" }} data-testid={`foto-antes-badge-${f.id}`}>Antes da obra</div>}
              {!f.aprovada && <div className="gi-badge warn">aguardando aprovação</div>}
              {lowQuality && (
                <div
                  className="gi-badge"
                  data-testid={`foto-lowquality-${f.id}`}
                  title={aud.reason || "Qualidade insuficiente para Signature Book"}
                  style={{
                    background: "rgba(196,122,122,0.92)", color: "#fff",
                    borderColor: "#c47a7a", top: !f.aprovada ? 40 : 10,
                  }}
                >⚠ {aud.status === "invalid" ? "inválida" : "baixa qualidade"}</div>
              )}
              <div className="gi-body">
                {f.etapa && <div className="gi-etapa">{f.etapa}</div>}
                {f.legenda && <div className="gi-leg">{f.legenda}</div>}
                <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 4, letterSpacing: ".05em" }} data-testid={`foto-data-${f.id}`}>
                  {(f.data_captura || (f.uploaded_at || "").slice(0, 10)).split("-").reverse().join("/")}{f.data_captura ? (f.data_origem === "camera" ? " (câmera)" : "") : " (envio)"}
                </div>
                {lowQuality && (
                  <div style={{ fontSize: 10, color: "#c47a7a", marginTop: 6, fontStyle: "italic", lineHeight: 1.4 }} data-testid={`foto-lowquality-reason-${f.id}`}>
                    {aud.reason} {canEdit && "— considere substituir a foto."}
                  </div>
                )}
                {canEdit && (
                  <div className="gi-actions">
                    <button className="chip" onClick={() => toggleDestaque(f)} title={isVideo ? "Mostrar este vídeo no Signature Book (seção Em movimento)" : "Destaque no SB"} data-testid={`foto-destaque-${f.id}`}>
                      {f.destaque ? <Check size={10} /> : <Plus size={10} />} {isVideo ? "no SB" : "destaque"}
                    </button>
                    <button className="chip" onClick={() => editLegenda(f)} data-testid={`foto-legenda-${f.id}`}>✎ legenda</button>
                    <select className="chip" value={f.etapa || ""} onChange={e => changeEtapa(f, e.target.value)} style={{ cursor: "pointer" }} data-testid={`foto-etapa-${f.id}`}>
                      <option value="">— etapa —</option>
                      {etapasNomes.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    {!isVideo && (
                      <button className="chip" onClick={() => usarComoCapa(f)} title="Usar como capa da obra" data-testid={`foto-capa-${f.id}`}>★ capa</button>
                    )}
                    <button className="chip" onClick={() => substituirArquivo(f)} title="Trocar ESTA foto/vídeo por outro arquivo. Para ADICIONAR nova mídia, use o 'Arraste aqui' no topo." data-testid={`foto-substituir-${f.id}`}>↻ trocar arquivo</button>
                    <select className="chip" value={f.categoria || "obra"} onChange={e => changeCategoria(f, e.target.value)} style={{ cursor: "pointer" }} title="Categoria no PDF" data-testid={`foto-cat-${f.id}`}>
                      <option value="obra">Obra</option>
                      <option value="ambientacao">Ambientação</option>
                    </select>
                    {!isVideo && (
                      <button className="chip" onClick={() => toggleConcluida(f)} title="Mostrar na seção 'A obra concluída' do Signature Book" data-testid={`foto-concluida-${f.id}`}
                        style={f.obra_concluida ? { borderColor: "var(--gold)", color: "var(--gold)" } : undefined}>
                        {f.obra_concluida ? <Check size={10} /> : <Plus size={10} />} obra concluída
                      </button>
                    )}
                    {!isVideo && (
                      <button className="chip" onClick={() => toggleAntes(f)} title="Foto do ambiente antes da reforma (seção Transformação do SB)" data-testid={`foto-antes-${f.id}`}
                        style={f.antes_obra ? { borderColor: "#a9a3b5", color: "#f5f0e8" } : undefined}>
                        {f.antes_obra ? <Check size={10} /> : <Plus size={10} />} antes da obra
                      </button>
                    )}
                    <input type="date" className="chip" value={f.data_captura || (f.uploaded_at || "").slice(0, 10)} max={new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" })}
                      onChange={e => e.target.value && changeData(f, e.target.value)} title="Data da foto/vídeo (cronologia do SB)" data-testid={`foto-data-input-${f.id}`}
                      style={{ cursor: "pointer", colorScheme: "dark" }} />
                    <button className="chip" onClick={() => toggleAprovada(f)} title={f.aprovada ? "Marcar como pendente" : "Aprovar"} data-testid={`foto-aprovada-${f.id}`}>
                      {f.aprovada ? "✓ aprovada" : "⏱ aprovar"}
                    </button>
                    <button className="chip" onClick={() => removeFoto(f)} style={{ color: "#c47a7a" }} data-testid={`foto-remover-${f.id}`}>× remover</button>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)} data-testid="lightbox-close">×</button>
          {isVideoItem(lightbox) ? (
            <video
              src={fileUrl(lightbox.url)}
              controls
              autoPlay
              playsInline
              data-testid="lightbox-video"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "92vw", maxHeight: "82vh", background: "#000" }}
            />
          ) : (
            <img src={fileUrl(lightbox.url)} alt={lightbox.legenda || ""} />
          )}
          {lightbox.legenda && <div className="lightbox-caption">{lightbox.legenda}</div>}
        </div>
      )}

      {wizardEtapa && (
        <FotoWizardEtapa
          obra={obra}
          etapaNome={wizardEtapa}
          isAdmin={canEdit}
          onClose={() => setWizardEtapa(null)}
          onReload={() => { onReload?.(); }}
        />
      )}
    </div>
  );
}

function ObraFinanceiro({ obra, canFin, api, onReload }) {
  const { user } = useAuth();
  const isClientView = ["cliente", "arquiteto"].includes(user?.role);  // visão simplificada sem custos
  const [showLanc, setShowLanc] = useState(false);
  const [editLanc, setEditLanc] = useState(null);
  const [dre, setDre] = useState(null);
  const [aditivosResumo, setAditivosResumo] = useState(null);
  useEffect(() => {
    if (canFin) api("get", `/financeiro/dre/${obra.id}`).then(setDre).catch(() => { });
  }, [api, obra.id, canFin, obra.lancamentos]);
  // Bug-fix 2026-09: Valor Total = orçamento base + aditivos APROVADOS.
  // Sem esse hoist, KPI cards e saldo mostravam só a base — errado quando havia aditivos.
  const reloadAditivos = useCallback(() => {
    api("get", `/obras/${obra.id}/aditivos`).then(setAditivosResumo).catch(() => { });
  }, [api, obra.id]);
  useEffect(() => { reloadAditivos(); }, [reloadAditivos]);

  const lancs = obra.lancamentos || [];
  const receitas = lancs.filter(l => l.tipo === "receita");
  const custos = lancs.filter(l => l.tipo === "custo");
  const totalRec = receitas.reduce((a, l) => a + l.valor, 0);
  const totalCust = custos.reduce((a, l) => a + l.valor, 0);
  const baseValor = obra.valor_total || 0;
  const aditivosAprov = aditivosResumo?.total_aditivos_aprovados || 0;
  const valorTotalObra = (aditivosResumo?.total_aprovado != null) ? aditivosResumo.total_aprovado : baseValor;
  const saldo = valorTotalObra - totalRec;
  // Lançamentos mostrados dependem do papel:
  // - admin/financeiro: veem tudo (receitas + custos detalhados)
  // - cliente/arquiteto: só veem RECEITAS (pagamentos). Custos internos são confidenciais.
  const lancsVisiveis = isClientView ? receitas : lancs;

  const CAT_PUB = { sinal: "Sinal", parcela: "Parcela", complemento: "Complemento", pagamento: "Pagamento", outros: "Pagamento" };
  const displayCategoria = (l) => isClientView ? (CAT_PUB[l.categoria] || "Pagamento") : l.categoria;

  const delLanc = async (lid) => {
    if (!window.confirm("Excluir lançamento?")) return;
    await api("delete", `/obras/${obra.id}/lancamento/${lid}`); onReload();
  };

  return (
    <div className="fade-up">
      <div className="ws-head">
        <div><div className="eyebrow"><Tricolore thin /> DRE</div><h1>Resumo <em>financeiro</em></h1></div>
        {canFin && <button className="btn" onClick={() => setShowLanc(true)}><Plus size={13} />Lançamento</button>}
      </div>
      {canFin && <HowToBanner id="obra-financeiro" title="Como operar o Financeiro desta obra" steps={[
        { num: 1, title: "Lance receitas recebidas", desc: "Clique em <strong>Lançamento</strong>, escolha tipo <strong>Receita</strong> e a categoria: Sinal, Parcela, Complemento etc. Isso atualiza o Saldo a receber em tempo real." },
        { num: 2, title: "Lance custos executados", desc: "Mesma lógica, tipo <strong>Custo</strong> com categoria: Matéria-prima, Beneficiamento, Frete, Colocação, Comissão RT do Arquiteto." },
        { num: 3, title: "Acompanhe a margem", desc: "O painel <strong>Análise de margem</strong> mostra em tempo real a margem líquida em R$ e %. Use o semáforo para tomar decisões rápidas." },
        { num: 4, title: "Dica: lance no dia", desc: "Disciplina de lançamento diário = DRE confiável. Se esperar pro fim do mês, fica caos. Um minuto por movimento vale muito." },
      ]} />}
      <div className="fin-grid" style={{ marginBottom: 32 }}>
        <div className="fin-box">
          <div className="fin-label">Valor total</div>
          <div className="fin-value" data-testid="fin-kpi-total">{brl(valorTotalObra)}</div>
          {aditivosAprov > 0 && (
            <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--ink-3)", marginTop: 4 }}>
              Base {brl(baseValor)} + <span style={{ color: "#7fb0e0", fontWeight: 600 }} data-testid="fin-kpi-aditivos">Aditivos {brl(aditivosAprov)}</span>
            </div>
          )}
        </div>
        <div className="fin-box"><div className="fin-label">Recebido</div><div className="fin-value emerald" data-testid="fin-kpi-recebido">{brl(totalRec)}</div></div>
        <div className="fin-box highlight"><div className="fin-label">Saldo a receber</div><div className="fin-value gold" data-testid="fin-kpi-saldo">{brl(saldo)}</div></div>
      </div>

      <AditivosPanel obraId={obra.id} canFin={canFin} api={api} onReload={() => { onReload && onReload(); reloadAditivos(); }} />
      <BrindesPanel obraId={obra.id} canFin={canFin} api={api} />

      {canFin && dre && (
        <div className="panel" style={{ marginBottom: 24 }}>
          <div className="panel-head"><div><div className="panel-eyebrow">Análise de margem</div><h3 className="panel-title">Lucratividade <em>estimada</em></h3></div>
            <span className={"pill " + (dre.margem_pct > 15 ? "done" : dre.margem_pct > 5 ? "run" : "hold")}>{dre.margem_pct.toFixed(1)}% margem</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, fontSize: 13 }}>
            <div><div className="fin-label">Receitas</div><div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: "var(--verde-2)" }}>{brl(dre.receitas)}</div></div>
            <div><div className="fin-label">Custos totais</div><div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: "#c47a7a" }}>{brl(dre.custos_total)}</div></div>
            <div><div className="fin-label">Margem</div><div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: "var(--gold)" }}>{brl(dre.margem)}</div></div>
          </div>
          <div style={{ marginTop: 24, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Object.entries(dre.custos_por_categoria || {}).map(([k, v]) => (
              <div key={k} style={{ padding: "8px 14px", background: "var(--bg-3)", border: "1px solid var(--line)", fontSize: 12 }}>
                <span style={{ color: "var(--ink-3)", letterSpacing: ".1em", textTransform: "uppercase", fontSize: 10 }}>{k}</span>
                <span style={{ color: "var(--ink)", marginLeft: 8, fontFamily: "'Fraunces',serif" }}>{brl(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-head"><div className="panel-eyebrow">{isClientView ? "Pagamentos" : "Lançamentos"}</div></div>
        {lancsVisiveis.length === 0 ? <p style={{ color: "var(--ink-3)" }}>Nenhum {isClientView ? "pagamento registrado" : "lançamento"}.</p> :
          <div className="tbl-wrap" style={{ border: 0 }}>
            <table className="tbl">
              <thead><tr>
                <th>Data</th>
                {!isClientView && <th>Tipo</th>}
                <th>Categoria</th>
                <th>Descrição</th>
                <th style={{ textAlign: "right" }}>Valor</th>
                {canFin && <th></th>}
              </tr></thead>
              <tbody>
                {lancsVisiveis.map(l => (
                  <tr key={l.id}>
                    <td>{fmt(l.data)}</td>
                    {!isClientView && <td><span className={"pill " + (l.tipo === "receita" ? "done" : "hold")}>{l.tipo}</span></td>}
                    <td>{displayCategoria(l)}</td>
                    <td style={{ color: "var(--ink)" }}>{l.descricao}</td>
                    <td style={{ textAlign: "right", color: "var(--verde-2)", fontFamily: "'Fraunces',serif", fontSize: 15 }}>{brl(l.valor)}</td>
                    {canFin && <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditLanc(l)} data-testid={`lanc-edit-${l.id}`} style={{ fontSize: 10 }}><Pencil size={10} /> Editar</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => delLanc(l.id)} data-testid={`lanc-del-${l.id}`} style={{ marginLeft: 6, fontSize: 10, color: "#c47a7a" }}>Excluir</button>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>
      {(showLanc || editLanc) && <LancamentoModal obraId={obra.id} initial={editLanc} onClose={() => { setShowLanc(false); setEditLanc(null); }} onSaved={() => { setShowLanc(false); setEditLanc(null); onReload(); }} />}
    </div>
  );
}

const LANC_CATS = {
  receita: ["sinal", "parcela", "entrada", "complemento", "aditivo", "final", "outros"],
  custo: ["materia_prima", "beneficiamento", "acabamento", "frete", "colocacao", "retrabalho", "rt_arquiteto", "comissao_vendedor", "outros"],
};
const LANC_METODOS = ["PIX", "Boleto", "Transferência", "Cartão", "Dinheiro", "Cheque"];
// Lançamentos antigos do Financeiro global usavam tipos como "custo_material" — normaliza para receita/custo
const lancTipo = (t) => (t === "receita" ? "receita" : "custo");

/* Cria (POST) ou edita (PATCH) um lançamento. `obras` → mostra seletor de obra (Financeiro global). */
function LancamentoModal({ obraId, obras, initial, onClose, onSaved }) {
  const { api } = useAuth();
  const isEdit = !!initial;
  const [obraSel, setObraSel] = useState(obraId || "");
  const [form, setForm] = useState(() => initial ? {
    tipo: lancTipo(initial.tipo), categoria: initial.categoria || "outros", descricao: initial.descricao || "",
    valor: initial.valor ?? 0, data: initial.data || new Date().toISOString().slice(0, 10),
    pago: initial.pago !== false, metodo_pagamento: initial.metodo_pagamento || "",
  } : { tipo: "receita", categoria: "sinal", descricao: "", valor: 0, data: new Date().toISOString().slice(0, 10), pago: true, metodo_pagamento: "" });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!obraSel) { alert("Selecione uma obra."); return; }
    const valor = parseFloat(String(form.valor).replace(",", "."));
    if (isNaN(valor) || valor < 0) { alert("Valor inválido."); return; }
    setLoading(true);
    try {
      const body = { ...form, valor, descricao: form.descricao || "" };
      if (isEdit) {
        await api("patch", `/obras/${obraSel}/lancamento/${initial.id}`, { ...body, metodo_pagamento: body.metodo_pagamento || "" });
      } else {
        if (!body.metodo_pagamento) delete body.metodo_pagamento;
        await api("post", `/obras/${obraSel}/lancamento`, body);
      }
      onSaved();
    }
    catch (e) { const d = e.response?.data?.detail; alert(typeof d === "string" ? d : "Erro ao salvar lançamento"); }
    finally { setLoading(false); }
  };
  const base = LANC_CATS[form.tipo];
  const cats = base.includes(form.categoria) ? base : [form.categoria, ...base];
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} data-testid={isEdit ? "lanc-edit-modal" : "fin-new-modal"}>
        <div className="modal-head"><h3 className="modal-title">{isEdit ? "Editar" : "Novo"} <em>lançamento</em></h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          {obras && !isEdit && (
            <div className="field"><label>Obra *</label>
              <select value={obraSel} onChange={e => setObraSel(e.target.value)} data-testid="fin-new-obra">
                <option value="">— Selecione —</option>
                {obras.map(o => <option key={o.id} value={o.id}>{o.codigo} · {o.nome} ({o.cliente_nome})</option>)}
              </select>
            </div>
          )}
          <div className="field"><label>Tipo</label>
            <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value, categoria: LANC_CATS[e.target.value][0] })} data-testid="lanc-tipo">
              <option value="receita">Receita</option><option value="custo">Custo</option>
            </select>
          </div>
          <div className="field"><label>Categoria</label>
            <select value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })} data-testid="lanc-categoria">
              {cats.map(c => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div className="field"><label>Descrição</label><input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} data-testid="lanc-descricao" /></div>
          <div className="field"><label>Valor (R$)</label><input type="number" step="0.01" value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })} data-testid="lanc-valor" /></div>
          <div className="field"><label>Data</label><input type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} data-testid="lanc-data" /></div>
          {form.tipo === "receita" && (
            <>
              <div className="field"><label>Status</label>
                <select value={form.pago ? "pago" : "previsto"} onChange={e => setForm({ ...form, pago: e.target.value === "pago" })} data-testid="lanc-pago">
                  <option value="pago">Recebido (já caiu)</option>
                  <option value="previsto">Previsão (a receber)</option>
                </select>
                <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 4 }}>
                  "Previsão" aparece como próximo vencimento no Signature Book, mas não conta no Total Recebido.
                </div>
              </div>
              <div className="field"><label>Forma de pagamento (opcional)</label>
                <select value={form.metodo_pagamento} onChange={e => setForm({ ...form, metodo_pagamento: e.target.value })} data-testid="lanc-metodo">
                  <option value="">— não especificar —</option>
                  {LANC_METODOS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={loading} data-testid="lanc-salvar">{loading ? "Salvando…" : isEdit ? "Salvar alterações" : "Salvar"}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * ClienteSlugEditor — Admin/gestor customiza a URL pública humanizada do SB.
 * PATCH /obras/{id}/cliente-slug. Sanitização e checagem de unicidade no backend.
 */
function ClienteSlugEditor({ obra, api, onReload }) {
  const [editing, setEditing] = useState(false);
  const [valor, setValor] = useState(obra.cliente_slug || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const salvar = async () => {
    setBusy(true); setErr("");
    try {
      await api("patch", `/obras/${obra.id}/cliente-slug`, { cliente_slug: valor.trim() || null });
      setEditing(false);
      onReload?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setBusy(false); }
  };
  if (!editing) {
    return (
      <div style={{ marginTop: 8, fontSize: 11, color: "var(--ink-3)", letterSpacing: ".05em" }}>
        Slug atual: <strong style={{ color: "var(--ink-2)" }}>{obra.cliente_slug || "—"}</strong>{" "}
        <button
          type="button"
          onClick={() => { setValor(obra.cliente_slug || ""); setEditing(true); }}
          data-testid="cliente-slug-edit"
          style={{ marginLeft: 6, background: "transparent", border: "1px solid var(--line)", color: "var(--gold)", fontSize: 10, padding: "2px 8px", letterSpacing: ".15em", textTransform: "uppercase", cursor: "pointer" }}
        >Editar</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }} data-testid="cliente-slug-editor">
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "var(--ink-3)", fontFamily: "monospace" }}>/sb/</span>
        <input
          className="input"
          value={valor}
          onChange={e => setValor(e.target.value)}
          placeholder="luciana-shultz"
          autoFocus
          data-testid="cliente-slug-input"
          style={{ flex: 1, minWidth: 200, fontFamily: "monospace", fontSize: 12 }}
        />
        <button className="btn btn-primary btn-sm" onClick={salvar} disabled={busy} data-testid="cliente-slug-save">{busy ? "…" : "Salvar"}</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
      </div>
      {err && <div style={{ fontSize: 11, color: "#c47a7a" }}>{err}</div>}
      <div style={{ fontSize: 10, color: "var(--ink-3)" }}>
        Acentos, maiúsculas e espaços são normalizados automaticamente. Deixe vazio para regenerar do nome do cliente.
      </div>
    </div>
  );
}

/**
 * NotificarClienteModal — abre em duas situações:
 *  (1) Botão "Notificar Cliente" na aba Visão da obra.
 *  (2) Auto-aberto quando uma etapa é marcada como concluída ou quando uma
 *      série de fotos é aprovada no SB.
 * Renderiza preview editável da mensagem WhatsApp + botão "Enviar via WhatsApp".
 * Listener: window event "camasa:notificar-cliente" { obraId, motivo }.
 */
function NotificarClienteModal() {
  const { api } = useAuth();
  const [obra, setObra] = useState(null);
  const [motivo, setMotivo] = useState("manual");
  const [mensagem, setMensagem] = useState("");

  useEffect(() => {
    const handler = async (ev) => {
      const obraId = ev.detail?.obraId;
      const mot = ev.detail?.motivo || "manual";
      if (!obraId) return;
      try {
        const o = await api("get", `/obras/${obraId}`);
        setObra(o);
        setMotivo(mot);
      } catch { /* ignora */ }
    };
    window.addEventListener("camasa:notificar-cliente", handler);
    return () => window.removeEventListener("camasa:notificar-cliente", handler);
  }, [api]);

  useEffect(() => {
    if (!obra) return;
    const firstName = (obra.cliente_nome || "").split(" ")[0] || "cliente";
    const slug = obra.cliente_slug || obra.public_token;
    const origin = (typeof window !== "undefined" ? window.location.origin : "https://cps.camasa.com.br");
    const url = `${origin}/sb/${slug}`;
    // Data/hora atual no momento da abertura, no fuso de São Paulo
    let dataPart = "", horaPart = "";
    try {
      const agora = new Date().toLocaleString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
      // ex: "22/05/2026, 21:40" ou "22/05/2026 21:40" dependendo do runtime
      const m = agora.match(/(\d{2}\/\d{2}\/\d{4}).*?(\d{2}:\d{2})/);
      if (m) { dataPart = m[1]; horaPart = m[2]; }
    } catch { /* ignora */ }
    const linhaAtualizado = (dataPart && horaPart) ? `📅 Atualizado em ${dataPart} às ${horaPart}` : "";
    const txt = [
      `Olá, ${firstName}! 👋`,
      "",
      `Este é o *Signature Book* oficial da CAMASA para o seu projeto "${obra.nome}" (código ${obraCpsCode(obra)}).`,
      "",
      "Documento premium com todas as etapas, materiais e fotos da obra — além do aceite digital.",
      "",
      linhaAtualizado,
      "",
      `🔗 ${url}`,
      "",
      "🔒 Acesse *somente* este link. Qualquer outro domínio não é da CAMASA.",
      "",
      "Qualquer dúvida, nosso concierge digital (ícone dourado da CAMASA no canto da página) responde 24/7.",
    ].filter((l, i, arr) => !(l === "" && arr[i - 1] === "")).join("\n");
    setMensagem(txt);
  }, [obra]);

  if (!obra) return null;

  // Sanitiza telefone para formato wa.me (só dígitos, prefixa 55 se não tiver)
  const sanitizeWa = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("55")) return digits;
    return "55" + digits;
  };
  const numero = sanitizeWa(obra.cliente_telefone);
  const waUrl = numero
    ? `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`
    : `https://wa.me/?text=${encodeURIComponent(mensagem)}`;

  const motivoLbl = {
    etapa_concluida: "Etapa concluída · gatilho automático",
    serie_aprovada: "Série de fotos aprovada · gatilho automático",
    manual: "Notificação manual",
  }[motivo] || motivo;

  return (
    <div className="modal-back" onClick={() => setObra(null)} data-testid="notificar-cliente-modal">
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, width: "100%" }}>
        <div className="modal-head">
          <div>
            <div className="eyebrow" style={{ color: "#25d366" }}>WhatsApp · {motivoLbl}</div>
            <h3 className="modal-title">Notificar <em>{obra.cliente_nome || "cliente"}</em></h3>
          </div>
          <button className="modal-close" onClick={() => setObra(null)}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 11, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 8 }}>
            Preview da mensagem (editável)
          </div>
          <textarea
            value={mensagem}
            onChange={e => setMensagem(e.target.value)}
            data-testid="notificar-msg-textarea"
            rows={14}
            style={{
              width: "100%", padding: 12, background: "var(--bg-3)", color: "var(--ink)",
              border: "1px solid var(--line)", fontFamily: "var(--font-sans, sans-serif)",
              fontSize: 13, lineHeight: 1.5, resize: "vertical",
            }}
          />
          {!numero && (
            <div style={{ marginTop: 10, padding: 10, background: "rgba(196,122,122,0.08)", border: "1px solid rgba(196,122,122,0.3)", color: "#d49595", fontSize: 12 }}>
              ⚠ O cliente não tem telefone cadastrado. Você poderá escolher o contato no WhatsApp.
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: "var(--ink-3)" }}>
            Destino: <strong style={{ color: "var(--ink-2)" }}>{numero ? `+${numero}` : "(escolher no WhatsApp)"}</strong>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={() => setObra(null)} data-testid="notificar-fechar">Fechar sem enviar</button>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setObra(null)}
            data-testid="notificar-enviar-whatsapp"
            className="btn btn-primary"
            style={{ background: "#25d366", borderColor: "#25d366", color: "#0a0a0a", fontWeight: 600, textDecoration: "none" }}
          >
            <MessageSquare size={13} /> Enviar via WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}



/**
 * ShareBox — sistema seguro de copiar link do Signature Book.
 * Três ações:
 *   1. "Copiar link" → só a URL (útil para colar em qualquer canal).
 *   2. "Copiar mensagem" → texto cordial CAMASA + link, pronto para WhatsApp/email.
 *   3. "Abrir WhatsApp" → abre wa.me com a mensagem pré-preenchida (cliente escolhe destinatário).
 * Feedback visual via badge inline (sem alert, sem bug).
 * Usa navigator.clipboard com fallback para document.execCommand para compat total.
 */
function ShareBox({ url, obra, compact = false }) {
  const [flash, setFlash] = useState("");
  const firstName = (obra?.cliente_nome || "").split(" ")[0] || "cliente";
  // Data/hora atual no fuso America/Sao_Paulo — gerada na renderização (sempre atual).
  const linhaAtualizado = (() => {
    try {
      const agora = new Date().toLocaleString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
      const m = agora.match(/(\d{2}\/\d{2}\/\d{4}).*?(\d{2}:\d{2})/);
      return m ? `📅 Atualizado em ${m[1]} às ${m[2]}` : "";
    } catch { return ""; }
  })();
  const message =
`Olá, ${firstName}! 👋

Este é o *Signature Book* oficial da CAMASA para o seu projeto "${obra?.nome || ""}" (código ${obraCpsCode(obra)}).

Documento premium com todas as etapas, materiais e fotos da obra — além do aceite digital.
${linhaAtualizado ? "\n" + linhaAtualizado + "\n" : ""}
🔗 ${url}

🔒 Acesse *somente* este link. Qualquer outro domínio não é da CAMASA.

Qualquer dúvida, nosso concierge digital (ícone dourado da CAMASA no canto da página) responde 24/7.`;

  const copyText = async (text, label) => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch (e) { ok = false; }
    setFlash(ok ? `✓ ${label} copiado` : "✗ Falha ao copiar — copie manualmente");
    setTimeout(() => setFlash(""), 2400);
  };

  const waUrl = "https://wa.me/?text=" + encodeURIComponent(message);

  const onWhatsAppClick = () => {
    setFlash("✓ WhatsApp aberto em nova aba");
    setTimeout(() => setFlash(""), 2400);
  };

  const btnCls = compact ? "btn btn-ghost btn-sm" : "btn btn-ghost";
  return (
    <div data-testid="sb-share-box">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button
          type="button"
          className={btnCls}
          onClick={() => copyText(url, "Link")}
          data-testid="sb-share-copy-link"
          title="Copia só a URL para colar onde você quiser"
        >
          <Copy size={12} /> Copiar link
        </button>
        <button
          type="button"
          className={btnCls}
          onClick={() => copyText(message, "Mensagem")}
          data-testid="sb-share-copy-message"
          title="Copia mensagem cordial com o link — pronta para colar no WhatsApp/email"
        >
          <Copy size={12} /> Copiar mensagem pronta
        </button>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onWhatsAppClick}
          className={btnCls}
          data-testid="sb-share-whatsapp"
          title="Abre WhatsApp com a mensagem preenchida — você escolhe o contato"
          style={{ borderColor: "#25d366", color: "#25d366", textDecoration: "none", display: "inline-flex", alignItems: "center" }}
        >
          <svg width={13} height={13} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ marginRight: 4 }}>
            <path d="M20.52 3.48A11.86 11.86 0 0 0 12.04 0C5.48 0 .14 5.34.14 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.33-1.66a11.86 11.86 0 0 0 5.71 1.46h.01c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.43-8.42zM12.04 21.8h-.01a9.9 9.9 0 0 1-5.05-1.39l-.36-.22-3.76.99 1-3.66-.24-.38a9.9 9.9 0 0 1-1.51-5.24c0-5.46 4.45-9.9 9.92-9.9 2.65 0 5.14 1.03 7.01 2.9a9.83 9.83 0 0 1 2.9 7c0 5.46-4.45 9.9-9.9 9.9zm5.43-7.42c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.48-1.77-1.66-2.07-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.8.37-.28.3-1.05 1.02-1.05 2.5 0 1.47 1.08 2.9 1.23 3.1.15.2 2.11 3.23 5.12 4.53.72.31 1.27.5 1.71.64.72.23 1.37.2 1.89.12.58-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.18-1.42-.07-.12-.27-.2-.57-.35z"/>
          </svg>
          WhatsApp
        </a>
        {flash && (
          <span
            role="status"
            aria-live="polite"
            data-testid="sb-share-flash"
            style={{
              fontSize: 11,
              letterSpacing: ".15em",
              textTransform: "uppercase",
              color: flash.startsWith("✗") ? "#c47a7a" : "var(--verde-2, #3a5a4a)",
              padding: "4px 10px",
              background: "rgba(58,90,74,0.08)",
              border: "1px solid",
              borderColor: flash.startsWith("✗") ? "#4a2a2a" : "rgba(58,90,74,0.3)",
            }}
          >{flash}</span>
        )}
      </div>
    </div>
  );
}

function SBViewsPanel({ obra }) {
  const { api, user } = useAuth();
  const canSee = ["admin", "financeiro", "gestor"].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expand, setExpand] = useState(false);
  useEffect(() => {
    if (!canSee || !obra?.id) return;
    let alive = true;
    setLoading(true);
    api("get", `/obras/${obra.id}/sb-views`)
      .then(r => { if (alive) setData(r); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [obra?.id, canSee, api]);
  if (!canSee) return null;
  // M1: usa helpers centralizados (./lib/fmt.js)
  const fmtRel = fmtRelativo;
  const fmtAbs = fmtDataHora;
  const total = data?.total || 0;
  const timeline = data?.timeline || [];
  return (
    <div className="panel" style={{ marginTop: 20 }} data-testid="sb-views-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="panel-eyebrow" style={{ color: "var(--gold)" }}>Engajamento</div>
          <h3 className="panel-title" style={{ marginTop: 6 }}>Visualizações <em>do Signature Book</em></h3>
          <p style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 8, lineHeight: 1.6 }}>
            Cada vez que cliente, arquiteto ou alguém com o link abre o SB público, registramos aqui. Repetições da mesma pessoa dentro de 30 min contam como 1.
          </p>
        </div>
        {loading ? (
          <div style={{ color: "var(--ink-3)", fontSize: 12 }}>Carregando…</div>
        ) : (
          <div style={{ display: "flex", gap: 18, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 42, lineHeight: 1, color: "var(--gold)", fontWeight: 300 }} data-testid="sb-views-total">{total}</div>
              <div style={{ fontSize: 10, letterSpacing: ".2em", color: "var(--ink-3)", textTransform: "uppercase", marginTop: 4 }}>Views totais</div>
            </div>
          </div>
        )}
      </div>
      {!loading && total > 0 && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginTop: 18 }}>
            <div style={{ padding: 12, background: "var(--bg-3)", border: "1px solid var(--line)" }}>
              <div style={{ fontSize: 22, color: "var(--ink-1)", fontFamily: "'Fraunces', serif" }}>{data?.last_24h || 0}</div>
              <div style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-3)", textTransform: "uppercase", marginTop: 4 }}>Últimas 24h</div>
            </div>
            <div style={{ padding: 12, background: "var(--bg-3)", border: "1px solid var(--line)" }}>
              <div style={{ fontSize: 22, color: "var(--ink-1)", fontFamily: "'Fraunces', serif" }}>{data?.last_7d || 0}</div>
              <div style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-3)", textTransform: "uppercase", marginTop: 4 }}>Últimos 7 dias</div>
            </div>
            <div style={{ padding: 12, background: "var(--bg-3)", border: "1px solid var(--line)" }}>
              <div style={{ fontSize: 22, color: "var(--ink-1)", fontFamily: "'Fraunces', serif" }}>{data?.last_30d || 0}</div>
              <div style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-3)", textTransform: "uppercase", marginTop: 4 }}>Últimos 30 dias</div>
            </div>
            <div style={{ padding: 12, background: "var(--bg-3)", border: "1px solid var(--line)" }}>
              <div style={{ fontSize: 22, color: "var(--ink-1)", fontFamily: "'Fraunces', serif" }}>{data?.unique_ips || 0}</div>
              <div style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-3)", textTransform: "uppercase", marginTop: 4 }}>Pessoas únicas</div>
            </div>
          </div>
          <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
              Última visualização: <strong style={{ color: "var(--ink-1)" }}>{fmtRel(data?.last_viewed_at)}</strong>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setExpand(e => !e)} data-testid="sb-views-toggle">
              {expand ? "Ocultar histórico" : `Ver histórico (${timeline.length})`}
            </button>
          </div>
          {expand && (
            <div style={{ marginTop: 14, maxHeight: 320, overflowY: "auto", border: "1px solid var(--line)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "var(--bg-3)", color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".15em", fontSize: 10 }}>
                    <th style={{ textAlign: "left", padding: "10px 14px" }}>Quando</th>
                    <th style={{ textAlign: "left", padding: "10px 14px" }}>Dispositivo</th>
                    <th style={{ textAlign: "left", padding: "10px 14px" }}>Origem</th>
                    <th style={{ textAlign: "left", padding: "10px 14px" }}>Visitante</th>
                  </tr>
                </thead>
                <tbody>
                  {timeline.map((v, i) => (
                    <tr key={i} style={{ borderTop: "1px solid var(--line)" }}>
                      <td style={{ padding: "10px 14px", color: "var(--ink-1)" }} title={fmtAbs(v.ts)}>{fmtRel(v.ts)}</td>
                      <td style={{ padding: "10px 14px", color: "var(--ink-2)" }}>{v.device}</td>
                      <td style={{ padding: "10px 14px", color: "var(--ink-3)" }}>{v.source === "web" ? "Página" : v.source === "pdf-view" ? "PDF (visualizar)" : v.source === "pdf-download" ? "PDF (download)" : v.source}</td>
                      <td style={{ padding: "10px 14px", color: "var(--ink-3)", fontFamily: "monospace", fontSize: 10 }}>{v.ip_hash?.slice(0, 8)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {!loading && total === 0 && (
        <p style={{ color: "var(--ink-3)", fontSize: 13, lineHeight: 1.7, marginTop: 16 }}>
          Nenhuma visualização registrada ainda. Assim que o cliente abrir o link público, o contador começa aqui.
        </p>
      )}
    </div>
  );
}

function ObraSignaturePanel({ obra, api, onReload }) {
  // URL pública: prefere slug humanizado (/sb/luciana-shultz) com fallback ao token.
  // window.location.origin resolve para https://cps.camasa.com.br em produção.
  const sbId = obra.cliente_slug || obra.public_token;
  const url = typeof window !== "undefined" ? `${window.location.origin}/sb/${sbId}` : "";
  const [aceiteLocal, setAceiteLocal] = useState(obra.aceite_cliente || null);
  useEffect(() => { setAceiteLocal(obra.aceite_cliente || null); }, [obra.aceite_cliente]);
  const removerAceite = async () => {
    if (!window.confirm("Remover o aceite digital deste cliente? Esta ação é irreversível — o cliente precisará registrar o aceite novamente.")) return;
    try {
      await api("delete", `/obras/${obra.id}/aceite`);
      setAceiteLocal(null); // atualização instantânea no UI
      onReload && onReload();
    } catch (e) {
      alert("Erro ao remover aceite: " + (e?.response?.data?.detail || e.message));
    }
  };
  return (
    <div className="fade-up">
      <div className="ws-head"><div><div className="eyebrow"><Tricolore thin /> Entrega premium</div><h1>Signature <em>Book</em></h1></div></div>
      <HowToBanner id="obra-signature" title="Como entregar o Signature Book ao cliente" steps={[
        { num: 1, title: "Revise tudo antes", desc: "Confirme que as abas <strong>Mídias</strong> (fotos aprovadas), <strong>Materiais</strong> (vinculados com imagens) e <strong>Etapas</strong> (marcadas como concluídas) estão completas. O PDF usa esses dados automaticamente." },
        { num: 2, title: "Teste o link público", desc: "Clique em <strong>Abrir Signature Book</strong> para visualizar como o cliente vai ver. Se algo estiver esquisito, ajuste nas outras abas antes de enviar." },
        { num: 3, title: "Compartilhe com o cliente", desc: "Use <strong>Copiar link</strong> e envie por WhatsApp/email, ou imprima o <strong>QR Code</strong> ao lado para entrega física (caixa premium, cartão, ou na obra)." },
        { num: 4, title: "Baixe o PDF premium", desc: "Clique em <strong>Baixar PDF</strong> para entregar a versão impressa ou arquivada. O PDF é gerado com WeasyPrint e tem identidade visual CAMASA." },
        { num: 5, title: "Aceite digital", desc: "O cliente registra o aceite na própria página pública. Você recebe notificação e o certificado com hash SHA-256 fica salvo como prova legal. Confira em <strong>Aceite digital</strong> abaixo." },
      ]} />
      <div className="panels">
        <div className="panel">
          <div className="panel-eyebrow">Link público</div>
          <h3 className="panel-title" style={{ marginTop: 6 }}>Acesso <em>do cliente</em></h3>
          <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.7, marginTop: 10 }}>
            Cliente e arquiteto podem acessar o Signature Book completo com este link, mesmo sem conta no CPS.
          </p>
          <div style={{ padding: 14, background: "var(--bg-3)", border: "1px solid var(--line)", fontFamily: "monospace", fontSize: 11, color: "var(--gold)", wordBreak: "break-all", margin: "16px 0" }} data-testid="sb-admin-url">{url}</div>
          <ShareBox url={url} obra={obra} />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
            <Link to={`/sb/${sbId}`} className="btn btn-primary" data-testid="sb-admin-open"><ArrowUpRight size={12} />Abrir Signature Book</Link>
          </div>
          <SBWhatsAppBox obra={obra} api={api} url={url} />
          <p style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 12, fontStyle: "italic", lineHeight: 1.5 }}>
            O Signature Book online é a versão oficial e sempre atualizada. Para imprimir, use o botão "Imprimir" dentro da própria página.
          </p>
        </div>
        <div className="panel" style={{ textAlign: "center" }}>
          <div className="panel-eyebrow">QR Code</div>
          <h3 className="panel-title" style={{ marginTop: 6 }}>Para <em>compartilhar</em></h3>
          <div style={{ display: "flex", justifyContent: "center", margin: "24px 0", background: "#fff", padding: 18, border: "1px solid var(--gold-3)" }}>
            <QRCodeSVG value={url} size={180} level="H" />
          </div>
          <p style={{ color: "var(--ink-3)", fontSize: 11, letterSpacing: ".15em", textTransform: "uppercase" }}>Imprima ou envie ao cliente</p>
        </div>
      </div>
      <SBViewsPanel obra={obra} />
      <SBNovidadesPanel obra={obra} api={api} />
      <SBExtrasAdmin obra={obra} api={api} />
      <SBGaleriaAmbientePanel obra={obra} api={api} onReload={onReload} />
      <SBMateriaisAprovadosPanel obra={obra} api={api} onReload={onReload} />
      <SBAceiteCamasaPanel obra={obra} api={api} onReload={onReload} />
      <SBTermoMedicaoPanel obra={obra} api={api} />
      <div className="panel" style={{ marginTop: 20 }} data-testid="aceite-admin-panel">
        <div className="panel-eyebrow" style={{ color: aceiteLocal ? "var(--verde-2)" : "var(--gold)" }}>Aceite digital</div>
        <h3 className="panel-title" style={{ marginTop: 6 }}>{aceiteLocal ? "Aceite " : "Aguardando "}<em>{aceiteLocal ? "confirmado" : "cliente"}</em></h3>
        {aceiteLocal ? (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.8 }}>
              <div><strong>{aceiteLocal.nome}</strong> <span style={{ color: "var(--ink-3)" }}>({aceiteLocal.tipo || "cliente"})</span></div>
              <div style={{ color: "var(--ink-3)", fontSize: 12 }}>
                {fmt(aceiteLocal.data)} · IP {aceiteLocal.ip || "—"}
              </div>
              {aceiteLocal.email && <div style={{ color: "var(--ink-3)", fontSize: 12 }}>E-mail: {aceiteLocal.email}</div>}
              {aceiteLocal.certificate_id && (
                <div style={{ marginTop: 10, fontFamily: "monospace", fontSize: 10, color: "var(--gold)", letterSpacing: "normal", textTransform: "none", wordBreak: "break-all" }}>
                  Cert. N.º {aceiteLocal.certificate_id}
                </div>
              )}
            </div>
            <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {aceiteLocal.certificate_id && (
                <a className="btn btn-ghost btn-sm" href={`${API}/public/aceite/${aceiteLocal.certificate_id}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="aceite-admin-cert-pdf"><Download size={11} /> Baixar certificado</a>
              )}
              {aceiteLocal.certificate_id && (
                <a className="btn btn-ghost btn-sm" href={`/verificar/${aceiteLocal.certificate_id}`} target="_blank" rel="noopener noreferrer">Verificar</a>
              )}
              <button className="btn btn-ghost btn-sm" style={{ color: "#c47a7a", borderColor: "#4a2a2a" }} onClick={removerAceite} data-testid="aceite-remover-btn">
                Remover aceite
              </button>
            </div>
          </div>
        ) : (
          <p style={{ color: "var(--ink-3)", fontSize: 13, lineHeight: 1.7, marginTop: 10 }}>
            O cliente/arquiteto registrará o aceite acessando o Signature Book público.
            <br /><br />
            <span style={{ color: "var(--verde-2)" }}>✓ Pronto para receber aceite — abra o link público e teste.</span>
          </p>
        )}
      </div>
    </div>
  );
}

/* ========== PRESTADORES / ARQUITETOS (CRUD simples) ========== */
function PrestadoresPage() { return <CrudPage title="Prestadores" endpoint="prestadores" icon={HardHat}
  columns={[["nome", "Nome"], ["especialidade", "Função"], ["empresa", "Empresa"], ["cidade", "Cidade"]]}
  fields={[
    { k: "nome", l: "Nome", req: true },
    { k: "especialidade", l: "Especialidade", type: "select", opts: Object.entries(ESP_LBL).map(([v, l]) => ({ v, l })), req: true },
    { k: "empresa", l: "Empresa" },
    { k: "email", l: "Email" }, { k: "telefone", l: "Telefone" }, { k: "cidade", l: "Cidade" },
    { k: "valor_m2", l: "Valor por m² (R$)", type: "number" },
    { k: "notas", l: "Notas", type: "textarea" }
  ]}
  renderCell={(row, k) => k === "especialidade" ? ESP_LBL[row[k]] || row[k] : row[k] || "—"}
  howto={{ title: "Como cadastrar Prestadores", steps: [
    { num: 1, title: "Clique em Cadastrar", desc: "Abre o formulário para adicionar um novo prestador (marmorista, medidor, instalador, motorista etc)." },
    { num: 2, title: "Preencha os dados", desc: "Nome e <strong>Especialidade</strong> são obrigatórios. Informe também valor por m² para facilitar orçamentos automáticos." },
    { num: 3, title: "Vincule à obra", desc: "Depois de cadastrado, entre numa obra → Editar → marque o prestador na seção <strong>Prestadores alocados</strong>." },
    { num: 4, title: "Acompanhe o desempenho", desc: "Na aba <strong>Equipe</strong> de cada obra, veja quem está alocado e qual etapa cada um executa." },
  ]}}
/>; }

function ArquitetosPage() { return <CrudPage title="Arquitetos" endpoint="arquitetos" icon={PenTool}
  columns={[["nome", "Nome"], ["escritorio", "Escritório"], ["cidade", "Cidade"], ["rt_percent", "RT %"]]}
  fields={[
    { k: "nome", l: "Nome", req: true }, { k: "escritorio", l: "Escritório" },
    { k: "email", l: "Email" }, { k: "telefone", l: "Telefone" }, { k: "cidade", l: "Cidade" },
    { k: "rt_percent", l: "RT (%)", type: "number" }, { k: "notas", l: "Notas", type: "textarea" }
  ]}
  renderCell={(row, k) => k === "rt_percent" ? `${row[k] || 0}%` : row[k] || "—"}
  howto={{ title: "Como cadastrar Arquitetos", steps: [
    { num: 1, title: "Cadastre o arquiteto", desc: "Preencha nome, escritório, email e telefone. Informe o <strong>RT %</strong> (comissão/retribuição técnica) para cálculo automático no financeiro." },
    { num: 2, title: "Vincule a uma obra", desc: "Na obra → Editar → selecione o Arquiteto responsável. Isso habilita o link de acesso dele." },
    { num: 3, title: "Gere o Magic Link", desc: "Na Visão Geral da obra clique em <strong>🔗 Link do Arquiteto</strong> para dar acesso sem senha." },
    { num: 4, title: "Arquiteto acompanha online", desc: "Ele visualiza plantas, medições, fotos, etapas e documentos — em tempo real, pelo tablet ou celular." },
  ]}}
/>; }

function TerceirizadosRanking() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    api("get", "/terceirizados-ranking").then(setData).catch(() => setData({ items: [] }));
  }, [api]);

  if (!data) return null;
  const items = data.items || [];
  if (items.length === 0) return null;

  const medalFor = (i) => {
    if (i === 0) return { icon: <Trophy size={16} />, color: "#d4ac5a", lbl: "1º" };
    if (i === 1) return { icon: <Medal size={16} />, color: "#c0c5ce", lbl: "2º" };
    if (i === 2) return { icon: <Award size={16} />, color: "#b97a4a", lbl: "3º" };
    return null;
  };

  const scoreColor = (s) => {
    if (s >= 75) return "var(--verde-2)";
    if (s >= 50) return "var(--gold)";
    if (s >= 25) return "#d4ac5a";
    return "#c47a7a";
  };

  return (
    <div
      className="panel fade-up"
      style={{ marginBottom: 24, padding: 0, border: "1px solid var(--gold-3)", background: "linear-gradient(180deg, rgba(184,147,90,0.04) 0%, transparent 100%)" }}
      data-testid="terceirizados-ranking"
    >
      <div
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "16px 20px", borderBottom: open ? "1px solid var(--line)" : "none",
          cursor: "pointer", userSelect: "none",
        }}
        onClick={() => setOpen(!open)}
        data-testid="ranking-toggle"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Trophy size={18} color="var(--gold)" />
          <div>
            <div className="eyebrow" style={{ marginBottom: 2 }}>Desempenho</div>
            <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 18, margin: 0, fontStyle: "italic" }}>Ranking de Terceirizados</h3>
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".15em", textTransform: "uppercase" }}>{items.length} terceirizados avaliados</span>
          <span style={{ fontSize: 18, color: "var(--gold)" }}>{open ? "−" : "+"}</span>
        </div>
      </div>

      {open && (
        <>
          {/* Pódio top 3 */}
          {items.slice(0, 3).length > 0 && (
            <div style={{ padding: "20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, borderBottom: "1px solid var(--line)" }}>
              {items.slice(0, 3).map((t, i) => {
                const m = medalFor(i);
                return (
                  <div
                    key={t.id}
                    data-testid={`podium-${i + 1}`}
                    style={{
                      padding: "16px 18px",
                      border: `1px solid ${m.color}40`,
                      background: `linear-gradient(135deg, ${m.color}10 0%, transparent 100%)`,
                      position: "relative", overflow: "hidden",
                    }}
                  >
                    <div style={{ position: "absolute", top: 8, right: 10, color: m.color, opacity: 0.8, display: "flex", alignItems: "center", gap: 4 }}>
                      {m.icon}
                      <span style={{ fontFamily: "'Fraunces',serif", fontSize: 13, fontStyle: "italic" }}>{m.lbl}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".15em", marginBottom: 6 }}>{t.tipo || "—"}</div>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: 17, marginBottom: 12 }}>{t.nome}</div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: "var(--ink-3)" }}>Score</span>
                      <span style={{ fontFamily: "'Fraunces',serif", fontSize: 26, color: scoreColor(t.score), fontStyle: "italic" }}>{t.score}</span>
                    </div>
                    <div style={{ display: "flex", gap: 10, fontSize: 11, color: "var(--ink-2)", flexWrap: "wrap" }}>
                      <span>✓ {t.midias_aprovadas}</span>
                      <span style={{ opacity: 0.5 }}>·</span>
                      <span>⏳ {t.midias_pendentes}</span>
                      <span style={{ opacity: 0.5 }}>·</span>
                      <span>✕ {t.midias_rejeitadas}</span>
                      <span style={{ opacity: 0.5 }}>·</span>
                      <span>🏗 {t.obras_ativas}/{t.obras_total}</span>
                    </div>
                    <div style={{ marginTop: 10, height: 4, background: "var(--bg-3)", overflow: "hidden" }}>
                      <div style={{ width: `${t.approval_rate}%`, height: "100%", background: scoreColor(t.score), transition: "width .6s" }} />
                    </div>
                    <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 4 }}>Aprovação: {t.approval_rate}%</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tabela completa */}
          <div className="tbl-wrap" style={{ padding: "0 20px 20px" }}>
            <table className="tbl" data-testid="ranking-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th style={{ textAlign: "center" }}>Obras</th>
                  <th style={{ textAlign: "center" }}>Aprovadas</th>
                  <th style={{ textAlign: "center" }}>Pendentes</th>
                  <th style={{ textAlign: "center" }}>Rejeitadas</th>
                  <th style={{ textAlign: "center" }}>Aprov. %</th>
                  <th style={{ textAlign: "center" }}>NPS</th>
                  <th style={{ textAlign: "center" }}>Score</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t, i) => (
                  <tr key={t.id} data-testid={`ranking-row-${t.id}`}>
                    <td style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "var(--gold)" }}>{i + 1}</td>
                    <td style={{ fontWeight: 500 }}>{t.nome}{!t.tem_acesso && <span style={{ marginLeft: 8, fontSize: 10, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".1em" }}>sem magic-link</span>}</td>
                    <td style={{ color: "var(--ink-2)" }}>{t.tipo || "—"}</td>
                    <td style={{ textAlign: "center", color: "var(--ink-2)" }}>{t.obras_ativas}/{t.obras_total}</td>
                    <td style={{ textAlign: "center", color: "var(--verde-2)" }}>{t.midias_aprovadas}</td>
                    <td style={{ textAlign: "center", color: "var(--gold)" }}>{t.midias_pendentes}</td>
                    <td style={{ textAlign: "center", color: t.midias_rejeitadas > 0 ? "#c47a7a" : "var(--ink-3)" }}>{t.midias_rejeitadas}</td>
                    <td style={{ textAlign: "center", color: "var(--ink-2)" }}>{t.approval_rate}%</td>
                    <td style={{ textAlign: "center", color: t.nps_avg !== null ? "var(--gold)" : "var(--ink-3)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>
                      {t.nps_avg !== null ? `${t.nps_avg}` : "—"}
                      {t.nps_count > 0 && <span style={{ fontSize: 9, color: "var(--ink-3)", fontStyle: "normal", marginLeft: 4 }}>({t.nps_count})</span>}
                    </td>
                    <td style={{ textAlign: "center", fontFamily: "'Fraunces',serif", fontStyle: "italic", color: scoreColor(t.score), fontSize: 16 }}>{t.score}</td>
                    <td style={{ textAlign: "center" }}>
                      {t.alerta ? (
                        <span title={t.alerta} style={{ fontSize: 10, color: "#c47a7a", letterSpacing: ".1em", textTransform: "uppercase" }}>⚠ {t.alerta.split(" ").slice(0, 3).join(" ")}</span>
                      ) : (
                        <span style={{ fontSize: 10, color: "var(--verde-2)", letterSpacing: ".15em", textTransform: "uppercase" }}>OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 12, fontStyle: "italic" }}>
              Score: aprovação (50%) + atividade (30%) + NPS médio das obras (20%). Sem NPS, peso é redistribuído. Alertas automáticos: score &lt; 50 ou ≥ 30 dias sem atividade.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* Lista de etapas-padrão da CAMASA. Carlo pode marcar exatamente quais o parceiro vê no portal.
   Em branco/null = usa fallback regex por tipo (marmoraria, frentista, fornecedor). */
const ETAPAS_PADRAO_CAMASA = [
  "Aprovação do Orçamento",
  "Compra dos Materiais",
  "Frete para Beneficiamento de Superfície",
  "Frete para Marmoraria",
  "Medição in Loco",
  "Aprovação do Projeto",
  "Início da Produção",
  "Frete para Obra",
  "Início da Colocação",
  "Conclusão",
  "Pós-obra",
];

function EtapasVisiveisModal({ terceirizado, onClose, onSaved }) {
  const { api } = useAuth();
  const initial = Array.isArray(terceirizado.etapas_visiveis) ? terceirizado.etapas_visiveis : null;
  const [usaCustom, setUsaCustom] = useState(initial !== null);
  const [selecionadas, setSelecionadas] = useState(initial || []);
  const [customExtra, setCustomExtra] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (et) => {
    setSelecionadas(s => s.includes(et) ? s.filter(x => x !== et) : [...s, et]);
  };
  const salvar = async () => {
    setSaving(true);
    try {
      const extras = customExtra.split(",").map(s => s.trim()).filter(Boolean);
      const finalList = usaCustom ? [...selecionadas, ...extras] : null;
      await api("patch", `/terceirizados/${terceirizado.id}`, { etapas_visiveis: finalList });
      onSaved();
    } catch (e) {
      alert("Erro ao salvar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-back" onClick={onClose} data-testid="etapas-modal-overlay">
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, maxHeight: "85vh", overflow: "auto" }}>
        <div style={{ padding: 22 }}>
          <div className="eyebrow">🎯 Etapas visíveis no Portal</div>
          <h3 className="modal-title">{terceirizado.nome}</h3>
          <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 16 }}>
            Escolha exatamente quais etapas <strong>{terceirizado.nome}</strong> vai ver ao abrir o portal.
            Se você deixar como <em>"Padrão por tipo"</em>, ele usa o filtro padrão da CAMASA para tipo <strong>{terceirizado.tipo}</strong>.
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: 1, padding: 12, border: `1px solid ${!usaCustom ? "var(--gold)" : "var(--line)"}`, background: !usaCustom ? "rgba(184,147,90,0.1)" : "transparent" }}>
              <input type="radio" checked={!usaCustom} onChange={() => setUsaCustom(false)} data-testid="etapas-modo-padrao" />
              <span style={{ fontSize: 13 }}><strong>Padrão por tipo</strong><br/><span style={{ fontSize: 11, color: "var(--ink-3)" }}>Filtro automático CAMASA</span></span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: 1, padding: 12, border: `1px solid ${usaCustom ? "var(--gold)" : "var(--line)"}`, background: usaCustom ? "rgba(184,147,90,0.1)" : "transparent" }}>
              <input type="radio" checked={usaCustom} onChange={() => setUsaCustom(true)} data-testid="etapas-modo-custom" />
              <span style={{ fontSize: 13 }}><strong>Personalizado</strong><br/><span style={{ fontSize: 11, color: "var(--ink-3)" }}>Marcar manualmente</span></span>
            </label>
          </div>

          {usaCustom && (
            <>
              <div style={{ fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 8 }}>
                Etapas padrão CAMASA
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16 }}>
                {ETAPAS_PADRAO_CAMASA.map(et => (
                  <label key={et} style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, cursor: "pointer", background: selecionadas.includes(et) ? "rgba(184,147,90,0.08)" : "transparent" }}>
                    <input
                      type="checkbox"
                      checked={selecionadas.includes(et)}
                      onChange={() => toggle(et)}
                      data-testid={`etapas-cb-${et.replace(/\s+/g, "-").toLowerCase()}`}
                    />
                    <span style={{ fontSize: 13 }}>{et}</span>
                  </label>
                ))}
              </div>
              <div className="field" style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--ink-3)" }}>
                  Etapas custom (separadas por vírgula)
                </label>
                <input
                  type="text"
                  placeholder="Ex: Corte do Piso Lote 170, Beneficiamento Especial"
                  value={customExtra}
                  onChange={e => setCustomExtra(e.target.value)}
                  data-testid="etapas-custom-input"
                  style={{ width: "100%" }}
                />
                <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>
                  Use isso pra etapas que você criou direto numa obra (não estão na lista acima).
                </div>
              </div>
            </>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-ghost" onClick={onClose} data-testid="etapas-cancelar">Cancelar</button>
            <button className="btn btn-primary" onClick={salvar} disabled={saving} data-testid="etapas-salvar">
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TerceirizadosPage() {
  const { api } = useAuth();
  const [etapasModal, setEtapasModal] = useState(null); // {id, nome, etapas_visiveis}
  const genMagic = async (id, nome, force = false) => {
    if (force && !window.confirm(`ROTACIONAR magic link de "${nome}"?\n\nO link antigo será INVALIDADO. Use isso se o link vazou ou você quer revogar o acesso atual.`)) return;
    try {
      const r = await api("post", `/terceirizados/${id}/magic-link${force ? "?force=true" : ""}`);
      const verb = r.rotated ? "🔄 NOVO link gerado" : "🔗 Link atual";
      const msg = `${verb} para ${nome}:\n\n${r.magic_url}\n\n(copiado na área de transferência)`;
      try { await navigator.clipboard.writeText(r.magic_url); } catch {}
      alert(msg);
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };
  const habilitarLogin = async (id, nome, email) => {
    const senha = window.prompt(
      `Habilitar login do FRENTISTA "${nome}"?\n\nEmail (use Enter pra usar "${email || "(vazio)"}"):\n\nDigite a senha desejada (mínimo 4 caracteres) ou Enter para "frente2026":`,
      ""
    );
    if (senha === null) return;
    const body = {};
    if (senha.trim()) body.senha = senha.trim();
    try {
      const r = await api("post", `/terceirizados/${id}/habilitar-login`, body);
      const msg = `✅ LOGIN HABILITADO\n\nFrentista: ${nome}\nLogin: ${r.email}\nSenha: ${r.senha_temporaria}\n\n${r.instrucoes}\n\n(copiado na área de transferência)`;
      try { await navigator.clipboard.writeText(`${r.email} / ${r.senha_temporaria}`); } catch {}
      alert(msg);
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };
  const genDeviceToken = async (id, nome) => {
    if (!window.confirm(`Gerar/regenerar link de tablet para "${nome}"?\n\nSe já existir um link, o antigo será INVALIDADO. Use isso ao parear um novo tablet.`)) return;
    try {
      const r = await api("post", `/terceirizados/${id}/device-token`);
      try { await navigator.clipboard.writeText(r.url); } catch {}
      window.prompt(
        `📱 LINK DO TABLET — ${nome}\n\nAbra este link no tablet do parceiro e salve como atalho na tela inicial (instala como PWA).\n\nO link já foi copiado. Você pode copiar de novo no campo abaixo:`,
        r.url,
      );
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };
  return <>
    <CrudPage title="Terceirizados" endpoint="terceirizados" icon={Users}
    extraSection={<TerceirizadosRanking />}
    columns={[["nome", "Nome"], ["tipo", "Tipo"], ["cidade", "Cidade"], ["email", "Email"]]}
    fields={[
      { k: "nome", l: "Nome", req: true },
      { k: "tipo", l: "Tipo", type: "select", opts: TERC_TIPOS, req: true },
      { k: "email", l: "Email" }, { k: "telefone", l: "Telefone" }, { k: "cidade", l: "Cidade" },
      { k: "notas", l: "Notas", type: "textarea" }
    ]}
    renderCell={(row, k) => row[k] || "—"}
    howto={{ title: "Como funciona o Portal do Terceirizado", steps: [
      { num: 1, title: "Cadastre o terceirizado", desc: "Preencha nome, tipo (Marmoraria, Fornecedor, Frentista, Outros), email e telefone. Clique em <strong>Cadastrar</strong>." },
      { num: 2, title: "Gere o Magic Link", desc: "Na linha do terceirizado, clique em <strong>🔗 Link</strong>. O link é copiado automaticamente e pode ser reutilizado quantas vezes quiser." },
      { num: 3, title: "Envie por WhatsApp", desc: "Compartilhe a URL com o terceirizado. Ele abre no celular/tablet e entra direto — <strong>sem senha</strong>." },
      { num: 4, title: "Frentistas: habilite login", desc: "Para tipo=Frentista, clique <strong>🔑 Login</strong> para criar credenciais (email + senha) que o entregador usa pra logar no painel /frentista do tablet com GPS." },
      { num: 5, title: "Vincule à obra", desc: "Na obra → Editar → marque o terceirizado em <strong>Terceirizados vinculados</strong>. A partir daí ele consegue enviar fotos do canteiro." },
      { num: 6, title: "Aprove mídia enviada", desc: "Toda foto/vídeo que o terceirizado subir aparece em <strong>Aprovação</strong> no menu. Confira e aprove antes de publicar para o cliente." },
      { num: 7, title: "Ajuste etapas visíveis", desc: "Clique em <strong>🎯 Etapas</strong> para escolher exatamente quais etapas esse parceiro vê no portal. Em branco = usa padrão por tipo." },
    ]}}
    rowExtra={(row) => (
      <>
        <button
          className="btn btn-ghost btn-sm"
          style={{ marginRight: 6, borderColor: "var(--gold)", color: "var(--gold)" }}
          onClick={() => genMagic(row.id, row.nome, false)}
          title="Obter magic link (reutilizável — só gera novo se ainda não existe)"
          data-testid={`row-magic-${row.id}`}
        >🔗 Link</button>
        <button
          className="btn btn-ghost btn-sm"
          style={{ marginRight: 6, opacity: 0.6 }}
          onClick={() => genMagic(row.id, row.nome, true)}
          title="Rotacionar: invalida o link antigo e gera um novo (use se o link vazou)"
          data-testid={`row-rotate-${row.id}`}
        >🔄</button>
        <button
          className="btn btn-ghost btn-sm"
          style={{ marginRight: 6, borderColor: "#b8935a", color: "#b8935a" }}
          onClick={() => setEtapasModal({ id: row.id, nome: row.nome, tipo: row.tipo, etapas_visiveis: row.etapas_visiveis })}
          title="Escolher quais etapas este parceiro vê no portal"
          data-testid={`row-etapas-${row.id}`}
        >🎯 Etapas{Array.isArray(row.etapas_visiveis) ? ` (${row.etapas_visiveis.length})` : ""}</button>
        {row.tipo === "frentista" && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginRight: 6, borderColor: "#5fa86c", color: "#5fa86c" }}
            onClick={() => habilitarLogin(row.id, row.nome, row.email)}
            title="Criar usuário de login (email + senha) para o frentista acessar o painel /frentista no tablet"
            data-testid={`row-habilitar-login-${row.id}`}
          >🔑 Login</button>
        )}
        {row.tipo === "marmoraria" && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginRight: 6, borderColor: "#d4b27a", color: "#d4b27a" }}
            onClick={() => genDeviceToken(row.id, row.nome)}
            title="Gerar link permanente para tablet do parceiro (PWA). Abra no tablet e salve como atalho na tela inicial."
            data-testid={`row-device-token-${row.id}`}
          >📱 Gerar Link Tablet</button>
        )}
      </>
    )}
  />
    {etapasModal && (
      <EtapasVisiveisModal
        terceirizado={etapasModal}
        onClose={() => setEtapasModal(null)}
        onSaved={() => { setEtapasModal(null); window.location.reload(); }}
      />
    )}
  </>;
}

function AprovacaoPage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("get", "/fotos/pendentes");
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch { setItems([]); setTotal(0); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const aprovar = async (obra_id, foto_id, aprovar_flag) => {
    try {
      await api("patch", `/obras/${obra_id}/foto/${foto_id}`, { aprovada: !!aprovar_flag });
      load();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  const remover = async (obra_id, foto_id) => {
    if (!window.confirm("Remover esta mídia (excluir arquivo)?")) return;
    try {
      await api("delete", `/obras/${obra_id}/foto/${foto_id}`);
      load();
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <>
      <div className="container">
        <div className="page-head fade-up">
          <div>
            <div className="eyebrow"><Tricolore thin /> Curadoria</div>
            <h1 className="page-title"><em>Aprovação</em> de mídia</h1>
            <p className="page-sub">Mídia enviada por terceirizados e gestores aguardando sua aprovação antes de aparecer no Signature Book.</p>
          </div>
          <div className="kpi-pill"><span className="kpi-num">{total}</span><span className="kpi-lbl">pendentes</span></div>
        </div>

        <HowToBanner id="aprovacao" title="Como usar a Fila de Aprovação" steps={[
          { num: 1, title: "Confira cada mídia", desc: "Aqui chegam fotos/vídeos que <strong>terceirizados e gestores</strong> enviaram do canteiro. Antes de aparecer para o cliente, sua aprovação é necessária." },
          { num: 2, title: "Analise qualidade e contexto", desc: "Verifique se a imagem está nítida, mostra bem o serviço e é apropriada para o Signature Book do cliente." },
          { num: 3, title: "Clique em ✓ Aprovar", desc: "A mídia passa a aparecer para cliente e arquiteto na aba Mídias e entra no Signature Book final." },
          { num: 4, title: "Use × Remover se inadequada", desc: "Mídia com foco errado, borrada ou que vazou informação sensível deve ser removida. O terceirizado pode reenviar depois." },
        ]} />
        {loading ? <div className="empty"><h3>Carregando…</h3></div> :
          items.length === 0 ?
            <div className="empty" data-testid="aprovacao-empty">
              <h3>Tudo em dia</h3>
              <p>Nenhuma mídia pendente de aprovação neste momento.</p>
            </div> :
            <div className="aprov-grid" data-testid="aprovacao-grid">
              {items.map(it => {
                const f = it.foto; const u = f.url || "";
                const isVid = (f.type === "video") || /\.(mp4|mov|webm)$/i.test(u);
                return (
                  <div key={f.id} className="aprov-card">
                    <div className="aprov-media">
                      {isVid ?
                        <video src={u} controls style={{ width: "100%", height: "100%", objectFit: "cover" }} /> :
                        <img src={u} alt={f.legenda || "mídia"} loading="lazy" />
                      }
                    </div>
                    <div className="aprov-body">
                      <div className="aprov-obra">{it.obra_codigo} · {it.obra_nome || it.cliente_nome}</div>
                      {f.etapa && <div className="aprov-etapa">Etapa: {f.etapa}</div>}
                      {f.legenda && <div className="aprov-leg">"{f.legenda}"</div>}
                      <div className="aprov-meta">Por: {f.uploaded_by || "—"} · {fmt(f.uploaded_at)}</div>
                      <div className="aprov-actions">
                        <button className="btn btn-primary btn-sm" onClick={() => aprovar(it.obra_id, f.id, true)} data-testid={`aprov-ok-${f.id}`}>✓ Aprovar</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => remover(it.obra_id, f.id)} data-testid={`aprov-del-${f.id}`}>× Remover</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        }
      </div>
      <Footer />
    </>
  );
}


function MateriaisPage() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEditMat = ["admin", "gestor"].includes(user?.role);
  const canSeeCost = user?.role === "admin" || user?.role === "financeiro";
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("todas");
  const [grupoFilter, setGrupoFilter] = useState("todos");
  const [page, setPage] = useState(1);
  const [compareIds, setCompareIds] = useState([]);  // até 3 materiais selecionados para comparar
  const [showCompare, setShowCompare] = useState(false);
  const PAGE_SIZE = 50;
  const load = useCallback(() => api("get", "/materiais").then(setRows), [api]);
  useEffect(() => { load(); }, [load]);

  const arch = async (id) => { if (!window.confirm("Arquivar este material?")) return; await api("delete", `/materiais/${id}`); load(); };
  const hardDel = async (row) => {
    const c = window.prompt(`EXCLUSÃO PERMANENTE de "${row.nome}".\nDigite EXCLUIR para confirmar:`);
    if (c !== "EXCLUIR") return;
    await api("delete", `/materiais/${row.id}?hard=true`); load();
  };

  const seedCatalogo = async () => {
    if (!window.confirm("Importar o catálogo completo CAMASA (969 materiais)?\nMateriais existentes com mesmo nome serão atualizados.")) return;
    try {
      const r = await api("post", "/materiais/seed-catalogo");
      alert(`Importação concluída.\nTotal: ${r.total}\nCriados: ${r.created}\nAtualizados: ${r.updated}\nIgnorados: ${r.skipped}`);
      load();
    } catch (e) {
      alert("Falha na importação: " + (e?.response?.data?.detail || e.message));
    }
  };

  const [catIA, setCatIA] = useState(false);
  const aiCategorizar = async () => {
    if (!window.confirm("Usar IA para preencher a categoria de manutenção dos materiais que ainda não têm?\nIsso ajuda o Concierge Pós-Obra a recomendar produtos corretos.\nPode levar 1-2 minutos.")) return;
    setCatIA(true);
    try {
      const r = await api("post", "/materiais/ai-categorizar-manutencao", {});
      const linhas = (r.amostra || []).map(x => `• ${x.nome} → ${x.categoria_manutencao}`).join("\n");
      alert(`IA categorizou ${r.atualizados} de ${r.total_pendentes} materiais.\n\nExemplos:\n${linhas}`);
      load();
    } catch (e) {
      alert("Falha ao categorizar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setCatIA(false);
    }
  };

  // Grupos distintos para filtro
  const grupos = useMemo(() => {
    const s = new Set();
    rows.forEach(r => r.grupo && s.add(r.grupo));
    return Array.from(s).sort();
  }, [rows]);

  // Filtragem + busca
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (catFilter !== "todas" && r.categoria !== catFilter) return false;
      if (grupoFilter !== "todos" && r.grupo !== grupoFilter) return false;
      if (q && !(r.nome || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, query, catFilter, grupoFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [query, catFilter, grupoFilter]);

  // M1: usa fmtBRL centralizado (módulo-level, vem de ./lib/fmtMoedaOuTraco)

  return (
    <>
      <div className="container">
        <div className="page-head fade-up" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 20 }}>
          <div>
            <div className="eyebrow"><Tricolore thin /> Catálogo · {rows.length} itens</div>
            <h1 className="page-title">Materiais <em>curados</em></h1>
            <p className="page-sub">Mármores, quartzitos, granitos, compostos e pedras premium — galeria CAMASA.</p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {isAdmin && <button className="btn btn-ghost" onClick={seedCatalogo} data-testid="mat-seed-btn" title="Recarrega os ~969 materiais dos PDFs CAMASA"><Plus size={13} /> Importar Lista</button>}
            {isAdmin && <button className="btn btn-ghost" onClick={aiCategorizar} disabled={catIA} data-testid="mat-ai-cat-btn" title="Usa IA para preencher a categoria de manutenção nos materiais sem essa informação"><Sparkles size={13} /> {catIA ? "Categorizando…" : "Categorizar com IA"}</button>}
            {canEditMat && <button className="btn btn-primary" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="mat-new-btn"><Plus size={13} /> Novo material</button>}
          </div>
        </div>
        {isAdmin && <HowToBanner id="materiais" title="Como gerenciar o Catálogo de Materiais" steps={[
          { num: 1, title: "Catálogo pré-carregado", desc: "O sistema já vem com <strong>969 materiais</strong> oficiais CAMASA (nacionais, importados, granitos, compostos, outros). Use a <strong>busca</strong> e os filtros para encontrar rapidamente." },
          { num: 2, title: "Preço de custo (CONFIDENCIAL)", desc: "O campo <strong>Preço de custo</strong> é visível apenas para <strong>admin</strong> e <strong>financeiro</strong>. Gestores, clientes e terceirizados nunca veem." },
          { num: 3, title: "Suba imagens reais", desc: "Clique em <strong>Editar</strong> e adicione até 5 fotos por material. Fotos reais compõem o Signature Book premium." },
          { num: 4, title: "Vincule às obras", desc: "Dentro de cada obra → aba <strong>Materiais</strong> → <strong>Vincular do catálogo</strong> para selecionar as pedras do projeto." },
        ]} />}

        <div className="mat-filters" style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "18px 0 14px" }}>
          <input
            className="input"
            placeholder={`Buscar em ${rows.length} materiais (ex: Calacatta, Taj Mahal, Neolith…)`}
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ flex: "1 1 320px", minWidth: 240 }}
            data-testid="mat-search"
          />
          <select className="input" value={catFilter} onChange={e => setCatFilter(e.target.value)} data-testid="mat-cat-filter" style={{ flex: "0 1 200px" }}>
            <option value="todas">Todas as categorias</option>
            <option value="marmore">Mármore</option>
            <option value="quartzito">Quartzito</option>
            <option value="granito">Granito</option>
            <option value="quartzo_sinterizado">Quartzo/Composto</option>
            <option value="travertino">Travertino</option>
            <option value="onix">Ônix</option>
            <option value="pedra">Pedra</option>
            <option value="industrializado">Industrializado</option>
          </select>
          <select className="input" value={grupoFilter} onChange={e => setGrupoFilter(e.target.value)} data-testid="mat-grupo-filter" style={{ flex: "0 1 220px" }}>
            <option value="todos">Todos os grupos</option>
            {grupos.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>

        <div style={{ color: "var(--ink-3)", fontSize: 12, letterSpacing: ".15em", textTransform: "uppercase", marginBottom: 10 }}>
          {filtered.length} resultado{filtered.length === 1 ? "" : "s"} · página {page} de {totalPages}
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr>
              <th style={{ width: 32 }}></th>
              <th style={{ width: 70 }}></th>
              <th>Nome</th>
              <th>Categoria</th>
              <th>Grupo</th>
              <th>Acabamento</th>
              {canSeeCost && <th style={{ textAlign: "right", color: "var(--gold)" }} title="Confidencial — apenas admin/financeiro">Custo</th>}
              <th>Imagens</th>
              {canEditMat && <th></th>}
            </tr></thead>
            <tbody>
              {pageRows.length === 0 ? <tr><td colSpan={canEditMat ? 9 : 8} style={{ textAlign: "center", padding: 40, color: "var(--ink-3)" }}>Nenhum material encontrado</td></tr> :
                pageRows.map(r => {
                  const firstImg = r.imagens && r.imagens[0];
                  const selected = compareIds.includes(r.id);
                  const toggleCompare = () => {
                    if (selected) setCompareIds(ids => ids.filter(i => i !== r.id));
                    else if (compareIds.length >= 3) alert("Máximo de 3 materiais para comparação.");
                    else setCompareIds(ids => [...ids, r.id]);
                  };
                  return (
                    <tr key={r.id} data-testid={`mat-row-${r.id}`} style={selected ? { background: "rgba(184,147,90,0.08)" } : undefined}>
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" checked={selected} onChange={toggleCompare} data-testid={`mat-compare-${r.id}`} title="Selecionar para comparar (até 3)" style={{ accentColor: "#b8935a", cursor: "pointer" }} />
                      </td>
                      <td>
                        <div style={{ width: 48, height: 48, backgroundImage: firstImg ? `url(${fileUrl(firstImg)})` : "none", backgroundColor: "var(--bg-3)", backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--line)" }} />
                      </td>
                      <td style={{ color: "var(--ink)" }}>{r.nome}</td>
                      <td>{MAT_CAT[r.categoria] || r.categoria}</td>
                      <td style={{ color: "var(--ink-3)", fontSize: 12 }}>{r.grupo || "—"}</td>
                      <td>{r.acabamento}</td>
                      {canSeeCost && <td style={{ textAlign: "right", color: "var(--gold)", fontVariantNumeric: "tabular-nums" }} data-testid={`mat-custo-${r.id}`}>{fmtBRL(r.preco_custo)}</td>}
                      <td style={{ color: "var(--ink-3)", fontSize: 11, letterSpacing: ".2em", textTransform: "uppercase" }}>{r.imagens?.length || 0}</td>
                      {canEditMat && <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(r); setShowForm(true); }} data-testid={`mat-edit-${r.id}`}><Pencil size={10} /> Editar</button>
                        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} onClick={() => arch(r.id)}>Arquivar</button>
                        {isAdmin && <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6, color: "#c47a7a", borderColor: "#4a2a2a" }} onClick={() => hardDel(r)}>Excluir</button>}
                      </td>}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 24 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="mat-prev-page">‹ Anterior</button>
            <span style={{ color: "var(--ink-3)", fontSize: 12 }}>{page} / {totalPages}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="mat-next-page">Próxima ›</button>
          </div>
        )}
      </div>

      {/* Barra flutuante de comparação */}
      {compareIds.length > 0 && (
        <div data-testid="compare-bar" style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "rgba(13,13,15,0.96)", border: "1px solid var(--gold)", padding: "14px 22px",
          display: "flex", gap: 16, alignItems: "center", zIndex: 60,
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)", backdropFilter: "blur(12px)",
        }}>
          <span style={{ color: "var(--gold)", fontSize: 11, letterSpacing: ".2em", textTransform: "uppercase" }}>
            {compareIds.length} selecionado{compareIds.length === 1 ? "" : "s"} de 3
          </span>
          <button className="btn btn-primary btn-sm" onClick={() => setShowCompare(true)} disabled={compareIds.length < 2} data-testid="compare-open-btn">
            Comparar lado a lado
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setCompareIds([])} data-testid="compare-clear-btn">Limpar</button>
        </div>
      )}

      {showCompare && (
        <MaterialCompareModal
          items={rows.filter(r => compareIds.includes(r.id))}
          onClose={() => setShowCompare(false)}
          canSeeCost={canSeeCost}
        />
      )}

      {showForm && <MaterialEditor material={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />}
      <Footer />
    </>
  );
}

function MaterialCompareModal({ items, onClose, canSeeCost }) {
  // M1: usa fmtBRL módulo-level (centralizado em ./lib)
  return (
    <div className="modal-back" onClick={onClose} data-testid="compare-modal">
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 1100, width: "95vw" }}>
        <div className="modal-head">
          <div>
            <div className="eyebrow"><Tricolore thin /> Comparador</div>
            <h2 className="modal-title">Comparar <em>materiais</em></h2>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 14 }}>
            {items.map(m => {
              const img = m.imagens && m.imagens[0];
              return (
                <div key={m.id} data-testid={`compare-col-${m.id}`} style={{ border: "1px solid var(--line)", background: "var(--bg-2)" }}>
                  <div style={{ aspectRatio: "4/3", backgroundColor: "var(--bg-3)", backgroundImage: img ? `url(${fileUrl(img)})` : "none", backgroundSize: "cover", backgroundPosition: "center", borderBottom: "1px solid var(--line)" }} />
                  <div style={{ padding: 16 }}>
                    <div style={{ fontSize: 10, letterSpacing: ".25em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 6 }}>{MAT_CAT[m.categoria] || m.categoria}</div>
                    <h3 style={{ fontFamily: "var(--font-serif, 'Fraunces', serif)", fontSize: 20, margin: 0, color: "var(--ink)", lineHeight: 1.2 }}>{m.nome}</h3>
                    {m.grupo && <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 6 }}>{m.grupo}</div>}

                    <dl style={{ margin: "16px 0 0", display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 12px", fontSize: 12 }}>
                      <dt style={{ color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".15em", fontSize: 10 }}>Acabamento</dt>
                      <dd style={{ margin: 0, color: "var(--ink)" }}>{m.acabamento || "—"}</dd>

                      <dt style={{ color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".15em", fontSize: 10 }}>Unidade</dt>
                      <dd style={{ margin: 0, color: "var(--ink)" }}>{m.unidade || "m²"}</dd>

                      {m.cor && (<>
                        <dt style={{ color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".15em", fontSize: 10 }}>Cor</dt>
                        <dd style={{ margin: 0, color: "var(--ink)" }}>{m.cor}</dd>
                      </>)}

                      <dt style={{ color: "var(--gold)", textTransform: "uppercase", letterSpacing: ".15em", fontSize: 10 }}>Preço venda</dt>
                      <dd style={{ margin: 0, color: "var(--gold)", fontFamily: "var(--font-serif, 'Fraunces', serif)", fontSize: 18 }}>{fmtBRL(m.preco_m2)}</dd>

                      {canSeeCost && (<>
                        <dt style={{ color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".15em", fontSize: 10 }}>Custo</dt>
                        <dd style={{ margin: 0, color: "var(--ink-2)" }}>{fmtBRL(m.preco_custo)}</dd>
                        {m.preco_custo && m.preco_m2 && m.preco_custo > 0 && (<>
                          <dt style={{ color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".15em", fontSize: 10 }}>Markup</dt>
                          <dd style={{ margin: 0, color: "#6fbf8f" }}>{Math.round(((m.preco_m2 - m.preco_custo) / m.preco_custo) * 100)}%</dd>
                        </>)}
                      </>)}
                    </dl>

                    {m.descricao_care && (
                      <div style={{ marginTop: 16, padding: 12, background: "var(--bg-1)", borderLeft: "2px solid var(--gold)" }}>
                        <div style={{ fontSize: 9, letterSpacing: ".25em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 6 }}>CAMASA Care</div>
                        <div style={{ fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5 }}>{m.descricao_care}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {items.some(m => m.preco_m2) && (
            <div style={{ marginTop: 22, padding: 14, background: "rgba(184,147,90,0.06)", border: "1px solid rgba(184,147,90,0.2)", fontSize: 12, color: "var(--ink-2)" }}>
              <strong style={{ color: "var(--gold)", letterSpacing: ".1em" }}>Resumo rápido:</strong>{" "}
              mais barato = <strong style={{ color: "var(--ink)" }}>{items.filter(i => i.preco_m2).sort((a, b) => a.preco_m2 - b.preco_m2)[0]?.nome || "—"}</strong>
              {" · "}mais caro = <strong style={{ color: "var(--ink)" }}>{items.filter(i => i.preco_m2).sort((a, b) => b.preco_m2 - a.preco_m2)[0]?.nome || "—"}</strong>.
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} data-testid="compare-close-btn">Fechar</button>
        </div>
      </div>
    </div>
  );
}

function MaterialEditor({ material, obraIdLink, onClose, onSaved }) {
  const { api, token, user } = useAuth();
  const canSeeCost = user?.role === "admin" || user?.role === "financeiro";
  const [nome, setNome] = useState(material?.nome || "");
  const [categoria, setCategoria] = useState(material?.categoria || "marmore");
  const [categoriaManutencao, setCategoriaManutencao] = useState(material?.categoria_manutencao || "");
  const [acabamento, setAcabamento] = useState(material?.acabamento || "polido");
  const [cor, setCor] = useState(material?.cor || "");
  const [grupo, setGrupo] = useState(material?.grupo || "");
  const [fornecedor, setFornecedor] = useState(material?.fornecedor || "");
  const [precoCusto, setPrecoCusto] = useState(material?.preco_custo != null ? String(material.preco_custo) : "");
  const [precoVenda, setPrecoVenda] = useState(material?.preco_m2 != null ? String(material.preco_m2) : "");
  const [markupPct, setMarkupPct] = useState(() => {
    // deriva markup inicial a partir do custo/venda se ambos existirem
    const c = material?.preco_custo;
    const v = material?.preco_m2;
    if (c && v && c > 0) return String(Math.round(((v - c) / c) * 100));
    return "";
  });
  const [descricao, setDescricao] = useState(material?.descricao_care || "");
  const [imagens, setImagens] = useState(material?.imagens || []);
  const [matId, setMatId] = useState(material?.id || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Markup simulator: quando admin digita % → atualiza preço-venda automaticamente
  const applyMarkup = (pct) => {
    setMarkupPct(pct);
    const c = parseFloat(String(precoCusto).replace(",", "."));
    const p = parseFloat(String(pct).replace(",", "."));
    if (Number.isFinite(c) && Number.isFinite(p) && c > 0) {
      setPrecoVenda((c * (1 + p / 100)).toFixed(2));
    }
  };
  const onPrecoCustoChange = (val) => {
    setPrecoCusto(val);
    const c = parseFloat(String(val).replace(",", "."));
    const p = parseFloat(String(markupPct).replace(",", "."));
    if (Number.isFinite(c) && Number.isFinite(p) && c > 0) {
      setPrecoVenda((c * (1 + p / 100)).toFixed(2));
    }
  };

  const saveBase = async () => {
    setSaving(true);
    try {
      const payload = { nome, categoria, acabamento, cor, grupo, fornecedor: fornecedor.trim() || null, descricao_care: descricao,
        categoria_manutencao: categoriaManutencao || null };
      const pv = parseFloat(String(precoVenda).replace(",", "."));
      payload.preco_m2 = Number.isFinite(pv) ? pv : null;
      if (canSeeCost) {
        const pc = parseFloat(String(precoCusto).replace(",", "."));
        payload.preco_custo = Number.isFinite(pc) ? pc : null;
      }
      if (matId) {
        await api("patch", `/materiais/${matId}`, payload);
      } else {
        const r = await api("post", "/materiais", payload);
        setMatId(r.id);
        // Se foi criado de dentro de uma obra → auto-vincular ao materiais_ids da obra
        if (obraIdLink) {
          try {
            const o = await api("get", `/obras/${obraIdLink}`);
            const cur = o.materiais_ids || [];
            if (!cur.includes(r.id)) {
              await api("patch", `/obras/${obraIdLink}`, { materiais_ids: [...cur, r.id] });
            }
          } catch (linkErr) {
            // não bloqueia o fluxo: material foi salvo no catálogo
            console.warn("Falha ao auto-vincular material à obra:", linkErr);
          }
        }
        return r.id;
      }
      return matId;
    } catch (e) {
      const status = e?.response?.status;
      const url = e?.config?.url || "";
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 404) msg = `Recurso não encontrado (404). URL chamada: ${url}\n\nIsso pode acontecer se o material foi removido por outro usuário, ou se a sessão precisa ser recarregada (Ctrl+Shift+R / Cmd+Shift+R).`;
      else if (status === 401 || status === 403) msg = `Sem permissão (${status}). Sua sessão pode ter expirado — faça login novamente.`;
      else if (detail) msg = typeof detail === "string" ? detail : JSON.stringify(detail);
      else msg = e.message || "Erro desconhecido";
      alert("Erro ao salvar material:\n\n" + msg);
      return null;
    } finally { setSaving(false); }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    let id = matId;
    if (!id) {
      if (!nome.trim()) { alert("Preencha o nome do material antes de subir imagens."); return; }
      id = await saveBase();
      if (!id) return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await axios.post(`${API}/materiais/${id}/imagem`, fd, { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` } });
        setImagens(r.data.imagens);
      }
    } catch (e) { alert("Erro no upload: " + (e?.response?.data?.detail || e.message)); }
    finally { setUploading(false); }
  };

  const removeImg = async (url) => {
    if (!window.confirm("Remover esta imagem?")) return;
    try {
      const r = await axios.delete(`${API}/materiais/${matId}/imagem`, { params: { url }, headers: { Authorization: `Bearer ${token}` } });
      setImagens(r.data.imagens);
    } catch (e) { alert("Erro: " + (e?.response?.data?.detail || e.message)); }
  };

  const salvar = async () => {
    const id = await saveBase();
    if (id) onSaved();
  };

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }} data-testid="mat-editor">
        <div className="modal-head">
          <div><div className="eyebrow"><Tricolore thin /> Catálogo</div><h2 className="modal-title">{matId ? "Editar" : "Novo"} <em>material</em></h2></div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="field span2"><label>Nome *</label>
              <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Calacatta Gold" data-testid="mat-nome" />
            </div>
            <div className="field"><label>Categoria *</label>
              <select value={categoria} onChange={e => setCategoria(e.target.value)} data-testid="mat-cat">
                {Object.entries(MAT_CAT).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="field"><label>Acabamento *</label>
              <select value={acabamento} onChange={e => setAcabamento(e.target.value)}>
                <option value="polido">Polido</option>
                <option value="escovado">Escovado</option>
                <option value="couro">Couro</option>
                <option value="natural">Natural</option>
              </select>
            </div>
            <div className="field span2" style={{ background: "rgba(184,147,90,0.05)", border: "1px solid rgba(184,147,90,0.25)", padding: 12 }}>
              <label style={{ color: "var(--gold)" }}>
                Categoria de Manutenção · Assistente Pós-Obra
              </label>
              <select
                value={categoriaManutencao}
                onChange={e => setCategoriaManutencao(e.target.value)}
                data-testid="mat-cat-manutencao"
              >
                <option value="">— Sem categoria (AI não recomenda produto) —</option>
                <option value="granito_escuro">Granito Escuro / Preto (Absolute Black, Verde Ubatuba…)</option>
                <option value="granito_medio">Granito Colorido / Médio (verde, vermelho, azul, bege)</option>
                <option value="granito_claro">Granito Claro / Branco (Branco Itaunas, Siena, Dallas…)</option>
                <option value="quartzito">Quartzito Natural (Taj Mahal, Mont Blanc…)</option>
                <option value="marmore">Mármore</option>
                <option value="sinterizado">Lâmina Sinterizada (Dekton, Neolith, Laminam)</option>
                <option value="quartzo">Quartzo / Silestone / Aglomerados</option>
                <option value="limestone">Limestone</option>
                <option value="outro">Outro (AI pede orientação ao cliente)</option>
              </select>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>
                Define qual produto Tenax/Bellinzoni o AI vai recomendar quando o cliente perguntar sobre manutenção após a entrega. Deixe em branco pra não orientar produto específico.
              </div>
            </div>
            <div className="field span2"><label>Cor / notas visuais</label>
              <input value={cor} onChange={e => setCor(e.target.value)} placeholder="Ex: branco com veios dourados" />
            </div>
            <div className="field"><label>Grupo / Catálogo</label>
              <input value={grupo} onChange={e => setGrupo(e.target.value)} placeholder="Ex: Mármores Nacionais" data-testid="mat-grupo" />
            </div>
            <div className="field"><label>Fornecedor</label>
              <input value={fornecedor} onChange={e => setFornecedor(e.target.value)} placeholder="Ex: Marmoraria Itália" data-testid="mat-fornecedor" />
            </div>
            {canSeeCost ? (
              <div className="field span2" data-testid="markup-simulator" style={{ background: "rgba(184,147,90,0.05)", border: "1px solid rgba(184,147,90,0.25)", padding: 16 }}>
                <label style={{ color: "var(--gold)", display: "flex", alignItems: "center", gap: 8 }}>
                  <span>Simulador de preço · R$ / m²</span>
                  <span style={{ fontSize: 9, letterSpacing: ".2em", color: "var(--ink-3)", fontWeight: "normal" }}>CONFIDENCIAL · Admin / Financeiro</span>
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 6 }}>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: ".18em", color: "var(--ink-3)", textTransform: "uppercase", marginBottom: 4 }}>Custo</div>
                    <input type="number" step="0.01" value={precoCusto} onChange={e => onPrecoCustoChange(e.target.value)} placeholder="450.00" data-testid="mat-preco-custo" />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: ".18em", color: "var(--ink-3)", textTransform: "uppercase", marginBottom: 4 }}>Markup (%)</div>
                    <input type="number" step="1" value={markupPct} onChange={e => applyMarkup(e.target.value)} placeholder="80" data-testid="mat-markup" />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: ".18em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 4 }}>Venda (público)</div>
                    <input type="number" step="0.01" value={precoVenda} onChange={e => { setPrecoVenda(e.target.value); const c = parseFloat(String(precoCusto).replace(",", ".")); const v = parseFloat(String(e.target.value).replace(",", ".")); if (Number.isFinite(c) && Number.isFinite(v) && c > 0) setMarkupPct(String(Math.round(((v - c) / c) * 100))); }} placeholder="810.00" data-testid="mat-preco-venda" style={{ borderColor: "var(--gold)", color: "var(--gold)" }} />
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 8, letterSpacing: ".05em" }}>
                  {precoCusto && markupPct ? (
                    <>Margem bruta absoluta: <strong style={{ color: "var(--gold)" }}>R$ {(((parseFloat(String(precoCusto).replace(",", "."))||0) * (parseFloat(String(markupPct).replace(",", "."))||0) / 100)).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> / m². Ajuste o markup e o preço de venda recalcula automaticamente.</>
                  ) : (
                    <>Preencha <strong>Custo</strong> e <strong>Markup</strong> — o preço de venda é calculado automaticamente. Clientes/arquitetos veem só o preço de venda.</>
                  )}
                </div>
              </div>
            ) : (
              <div className="field"><label>Preço de venda (R$/m²)</label>
                <input type="number" step="0.01" value={precoVenda} onChange={e => setPrecoVenda(e.target.value)} placeholder="Ex: 810.00" data-testid="mat-preco-venda" />
              </div>
            )}
            <div className="field span2"><label>Notas de CAMASA Care</label>
              <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={3} placeholder="Orientações específicas de preservação, limpeza, manchas, etc." />
            </div>
          </div>

          <div style={{ marginTop: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--gold)", textTransform: "uppercase" }}>Galeria do material</div>
                <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>{imagens.length} {imagens.length === 1 ? "imagem" : "imagens"} — você pode adicionar quantas quiser</div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="mat-upload-btn">
                {uploading ? "Enviando…" : "+ Adicionar imagens"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => handleUpload(e.target.files)} />
            </div>
            {imagens.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "var(--ink-3)", border: "1px dashed var(--line-2)", fontSize: 12 }}>
                Nenhuma imagem ainda. Clique em <strong style={{ color: "var(--gold)" }}>+ Adicionar imagens</strong> para subir do computador.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 12 }}>
                {imagens.map((u, i) => (
                  <div key={u} style={{ position: "relative", paddingBottom: "100%", background: `center/cover url(${fileUrl(u)})`, border: "1px solid var(--line)" }} data-testid={`mat-img-${i}`}>
                    {i === 0 && <div style={{ position: "absolute", top: 6, left: 6, background: "var(--gold)", color: "var(--bg-1)", padding: "2px 8px", fontSize: 8, letterSpacing: ".2em", textTransform: "uppercase", fontWeight: "bold" }}>CAPA</div>}
                    <button onClick={() => removeImg(u)} style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.7)", color: "#fff", border: "none", width: 24, height: 24, cursor: "pointer" }} title="Remover">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={salvar} disabled={saving || !nome.trim()} data-testid="mat-save">{saving ? "Salvando…" : "Salvar material"}</button>
        </div>
      </div>
    </div>
  );
}

function CrudPage({ title, endpoint, icon: Icon, columns, fields, renderCell, rowExtra, howto, extraSection }) {
  const { api, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const canEdit = ["admin", "gestor"].includes(user?.role);
  const isAdminCrud = user?.role === "admin";

  const load = useCallback(() => api("get", `/${endpoint}`).then(setRows).catch(() => { }), [api, endpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    if (editing) await api("patch", `/${endpoint}/${editing.id}`, form);
    else await api("post", `/${endpoint}`, form);
    setShowForm(false); setEditing(null); load();
  };
  const arch = async (id) => {
    if (!window.confirm("Arquivar este registro? (pode ser restaurado depois)")) return;
    await api("delete", `/${endpoint}/${id}`); load();
  };
  const hardDel = async (row) => {
    const label = row.nome || row.id;
    const c = window.prompt(`EXCLUSÃO PERMANENTE de "${label}".\n\nEsta ação é IRREVERSÍVEL. Digite EXCLUIR para confirmar:`);
    if (c !== "EXCLUIR") { if (c !== null) alert("Operação cancelada."); return; }
    await api("delete", `/${endpoint}/${row.id}?hard=true`); load();
  };

  return (
    <>
      <div className="container">
        <div className="page-head fade-up">
          <div>
            <div className="eyebrow"><Tricolore thin /> Cadastro</div>
            <h1 className="page-title"><em>{title}</em></h1>
            <p className="page-sub">Gestão da rede terceirizada CAMASA.</p>
          </div>
          {canEdit && <button className="btn" onClick={() => { setEditing(null); setShowForm(true); }} data-testid={`btn-new-${endpoint}`}><Plus size={13} />Cadastrar</button>}
        </div>
        {howto && canEdit && <HowToBanner id={`crud-${endpoint}`} title={howto.title} steps={howto.steps} />}
        {extraSection}
        {rows.length === 0 ? <div className="empty"><h3>Nenhum cadastro ainda</h3></div> :
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr>{columns.map(c => <th key={c[0]}>{c[1]}</th>)}{canEdit && <th></th>}</tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    {columns.map(c => <td key={c[0]} style={{ color: c[0] === "nome" ? "var(--ink)" : undefined }}>{renderCell(r, c[0])}</td>)}
                    {canEdit && <td style={{ textAlign: "right" }}>
                      {isAdminCrud && rowExtra && rowExtra(r)}
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(r); setShowForm(true); }} data-testid={`row-edit-${r.id}`}><Pencil size={10} /> Editar</button>
                      <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} onClick={() => arch(r.id)} title="Arquivar" data-testid={`row-arch-${r.id}`}>Arquivar</button>
                      {isAdminCrud && <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6, color: "#c47a7a", borderColor: "#4a2a2a" }} onClick={() => hardDel(r)} title="Excluir permanente" data-testid={`row-del-${r.id}`}>Excluir</button>}
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>
      {showForm && <CrudModal title={title} fields={fields} initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSave={save} />}
      <Footer />
    </>
  );
}

function CrudModal({ title, fields, initial, onClose, onSave }) {
  const [form, setForm] = useState(initial || {});
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try { await onSave(form); } catch (e) { alert(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head"><h3 className="modal-title">{initial ? "Editar" : "Novo"} <em>{title.toLowerCase()}</em></h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          {fields.map(f => (
            <div className="field" key={f.k}>
              <label>{f.l} {f.req && <span style={{ color: "var(--gold)" }}>*</span>}</label>
              {f.type === "textarea" ? <textarea rows={3} value={form[f.k] || ""} onChange={e => setForm({ ...form, [f.k]: e.target.value })} /> :
                f.type === "select" ? <select value={form[f.k] || ""} onChange={e => setForm({ ...form, [f.k]: e.target.value })}>
                  <option value="">—</option>
                  {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select> :
                  <input type={f.type || "text"} value={form[f.k] || ""} onChange={e => setForm({ ...form, [f.k]: e.target.value })} />}
            </div>
          ))}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>
    </div>
  );
}

/* ========== FINANCEIRO GLOBAL ========== */
function Financeiro() {
  const { api } = useAuth();
  const [s, setS] = useState(null);
  const [obras, setObras] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const reload = useCallback(() => {
    api("get", "/dashboard/summary").then(setS);
    api("get", "/obras?include_arquivadas=true").then(setObras);
  }, [api]);
  useEffect(() => { reload(); }, [reload]);
  const changeStatus = async (obra_id, status) => {
    await api("patch", `/obras/${obra_id}`, { status });
    reload();
  };
  if (!s) return <div className="container"><p className="page-sub">Carregando…</p></div>;
  return (
    <>
      <div className="container">
        <div className="page-head fade-up" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 20 }}>
          <div>
            <div className="eyebrow"><Tricolore thin /> Área restrita</div>
            <h1 className="page-title"><em>Financeiro</em></h1>
            <p className="page-sub">Fluxo de caixa consolidado — receita, custos, margens e projeções.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowNew(true)} data-testid="fin-new-lanc"><Plus size={13} /> Novo lançamento</button>
        </div>

        <HowToBanner id="financeiro" title="Como operar o Financeiro" steps={[
          { num: 1, title: "Lançamentos por obra", desc: "Clique em <strong>Novo lançamento</strong>. Selecione: Obra, Tipo (Receita/Custo), Categoria, Valor, Data. A margem da obra é atualizada em tempo real." },
          { num: 2, title: "Categorias padrão", desc: "<strong>Receitas:</strong> Sinal, Pagamento de etapa, Complemento. <strong>Custos:</strong> Matéria-prima, Beneficiamento, Frete, Colocação, Comissão RT Arquiteto." },
          { num: 3, title: "DRE automática", desc: "Cada obra tem um DRE detalhado: receita bruta − custos diretos = margem líquida em R$ e %. Semáforo visual indica rentabilidade." },
          { num: 4, title: "Dashboard consolidado", desc: "Os KPIs do topo mostram valor sob gestão, recebido, a receber e margem média — use para tomada de decisão semanal/mensal." },
          { num: 5, title: "Regra de ouro", desc: "Lance <strong>toda entrada e saída</strong> no mesmo dia. Disciplina de lançamento = precisão de decisão." },
        ]} />
        <div className="fin-grid" style={{ marginBottom: 40 }}>
          <div className="fin-box"><div className="fin-label">Valor sob gestão</div><div className="fin-value">{brl(s.valor_sob_gestao)}</div></div>
          <div className="fin-box"><div className="fin-label">Recebido</div><div className="fin-value emerald">{brl(s.recebido)}</div></div>
          <div className="fin-box highlight"><div className="fin-label">A receber</div><div className="fin-value gold">{brl(s.a_receber)}</div></div>
        </div>
        <div className="fin-grid" style={{ marginBottom: 40 }}>
          <div className="fin-box"><div className="fin-label">Custos totais</div><div className="fin-value rosso">{brl(s.custos)}</div></div>
          <div className="fin-box"><div className="fin-label">Margem estimada</div><div className="fin-value emerald">{brl(s.margem_estimada)}</div></div>
          <div className="fin-box"><div className="fin-label">Margem %</div><div className="fin-value gold">{s.recebido > 0 ? ((s.margem_estimada / s.recebido) * 100).toFixed(1) : 0}%</div></div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Obra</th><th>Cliente</th><th>Total</th><th>Recebido</th><th>Saldo</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {obras.map(o => {
                const rec = (o.lancamentos || []).filter(l => l.tipo === "receita").reduce((a, l) => a + l.valor, 0);
                return (
                  <tr key={o.id} data-testid={`fin-row-${o.id}`}>
                    <td style={{ color: "var(--ink)" }}><Link to={`/obras/${o.id}`} style={{ color: "inherit", textDecoration: "none" }}>{o.nome}</Link></td>
                    <td>{o.cliente_nome}</td>
                    <td>{brl(o.valor_total)}</td>
                    <td className="emerald-text">{brl(rec)}</td>
                    <td className="gold-text">{brl((o.valor_total || 0) - rec)}</td>
                    <td>
                      <select className="input" value={o.status || "em_andamento"} onChange={e => changeStatus(o.id, e.target.value)} style={{ fontSize: 10, padding: "4px 8px", minWidth: 130 }} data-testid={`fin-status-${o.id}`}>
                        {Object.entries(STATUS_OBRA).map(([v, info]) => (<option key={v} value={v}>{info.l}</option>))}
                      </select>
                    </td>
                    <td style={{ textAlign: "right" }}><Link to={`/obras/${o.id}?tab=financeiro`} className="btn btn-ghost btn-sm" style={{ fontSize: 9, padding: "4px 10px" }}>Abrir →</Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {showNew && <LancamentoModal obras={obras} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload(); }} />}
      <Footer />
    </>
  );
}

/* ========== SIGNATURE BOOK PÚBLICO ========== */
function SignatureBookPublic() {
  const { token } = useParams();
  const [obra, setObra] = useState(undefined);
  useEffect(() => {
    // Heurística de identificador: tokens do secrets.token_urlsafe(16) têm ~22 chars
    // sem hífens nem letras minúsculas seguidas (são URL-safe Base64). Slugs humanizados
    // têm hífens e são todos minúsculos. Tentamos ambos para máxima compatibilidade.
    const looksLikeSlug = /^[a-z0-9]+(-[a-z0-9]+)+$/.test(token);
    const primary = looksLikeSlug
      ? `${API}/public/sb-by-slug/${encodeURIComponent(token)}`
      : `${API}/public/signature/${encodeURIComponent(token)}`;
    const fallback = looksLikeSlug
      ? `${API}/public/signature/${encodeURIComponent(token)}`
      : `${API}/public/sb-by-slug/${encodeURIComponent(token)}`;
    const cacheBust = `?_t=${Date.now()}`;
    const headers = { "Cache-Control": "no-cache", "Pragma": "no-cache" };
    axios.get(primary + cacheBust, { headers })
      .then(r => setObra(r.data))
      .catch(() => {
        axios.get(fallback + cacheBust, { headers })
          .then(r => setObra(r.data))
          .catch(() => setObra(null));
      });
  }, [token]);
  // Define título da aba e descrição com identidade CAMASA — evita confusão com outros sites
  useEffect(() => {
    if (obra?.nome) {
      document.title = `CAMASA · Signature Book · ${obra.nome}`;
      // Atualiza/cria meta description para aparecer correto em previews
      const upsertMeta = (name, content) => {
        let m = document.querySelector(`meta[name="${name}"]`);
        if (!m) {
          m = document.createElement("meta");
          m.setAttribute("name", name);
          document.head.appendChild(m);
        }
        m.setAttribute("content", content);
      };
      const upsertOG = (prop, content) => {
        let m = document.querySelector(`meta[property="${prop}"]`);
        if (!m) {
          m = document.createElement("meta");
          m.setAttribute("property", prop);
          document.head.appendChild(m);
        }
        m.setAttribute("content", content);
      };
      const desc = `Documento oficial CAMASA · ${obra.nome} · ${obra.cliente_nome || ""}`;
      upsertMeta("description", desc);
      upsertOG("og:title", `CAMASA · ${obra.nome}`);
      upsertOG("og:description", desc);
      upsertOG("og:type", "article");
      upsertOG("og:site_name", "CAMASA Process System");
      upsertOG("og:url", window.location.href);
      upsertOG("og:image", `${window.location.origin}/cps-logo.jpg`);
      upsertMeta("twitter:card", "summary_large_image");
    }
  }, [obra?.nome, obra?.cliente_nome]);
  if (obra === undefined) return <div className="container"><p className="page-sub">Carregando…</p></div>;
  if (!obra) return <div className="container" style={{ textAlign: "center", paddingTop: 120 }}><h2 className="page-title">Signature Book não encontrado</h2></div>;
  return (
    <>
      <AuthenticityBar obra={obra} />
      <SignatureBookView obra={obra} publicView />
      {/* IMPORTANTE: passa public_token REAL da obra (não o `token` da URL,
          que pode ser um slug humanizado como "diego-volpe"). O backend valida
          contra db.obras.find_one({public_token: ...}) — slug daria 403. */}
      <CamasaAIChat publicToken={obra.public_token} obraNome={obra.nome} clienteNome={obra.cliente_nome} obraStatus={obra.status} />
    </>
  );
}

function SignatureBookDemo() {
  const [obra, setObra] = useState(undefined);
  useEffect(() => {
    document.title = "CAMASA · Signature Book · Demonstração";
    axios.get(`${API}/public/sb-demo`).then(r => setObra(r.data)).catch(() => setObra(null));
  }, []);
  if (obra === undefined) return <div className="container"><p className="page-sub">Carregando…</p></div>;
  if (!obra) return <div className="container" style={{ textAlign: "center", paddingTop: 120 }}><h2 className="page-title">Demonstração indisponível</h2></div>;
  return (
    <>
      <div data-testid="sb-demo-banner" style={{ position: "sticky", top: 0, zIndex: 100, background: "#b8935a", color: "#0d0d0f", textAlign: "center", padding: "10px 16px", fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase", fontWeight: 600 }}>
        Modo demonstração — dados do cliente ocultados para privacidade
      </div>
      <SignatureBookView obra={obra} publicView demo />
    </>
  );
}

/**
 * AuthenticityBar — faixa fixa no topo do SB público que confirma visualmente
 * ao cliente que está no site oficial da CAMASA (cps.camasa.com.br).
 * Previne confusão com outros sites/abas abertos no navegador do cliente.
 */
function AuthenticityBar({ obra }) {
  const [visible, setVisible] = useState(true);
  const host = typeof window !== "undefined" ? window.location.host : "cps.camasa.com.br";
  const isCamasa = host.endsWith("camasa.com.br") || host.endsWith("emergentagent.com");
  if (!visible) return null;
  return (
    <div
      data-testid="authenticity-bar"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        background: "linear-gradient(90deg, #0d0d0f 0%, #1a1816 50%, #0d0d0f 100%)",
        borderBottom: "1px solid #b8935a",
        padding: "10px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: 11,
        letterSpacing: "0.12em",
        color: "#b8935a",
        textTransform: "uppercase",
        boxShadow: "0 2px 16px rgba(0,0,0,0.4)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0, flex: 1 }}>
        <span style={{ color: isCamasa ? "#8fbf6f" : "#c47a7a", fontSize: 14, lineHeight: 1 }} aria-hidden="true">●</span>
        <span style={{ color: "#f5efe0", fontWeight: 600, fontSize: 11, whiteSpace: "nowrap" }}>CAMASA</span>
        <span style={{ color: "#8a7a5a", fontSize: 10, whiteSpace: "nowrap" }}>{host}</span>
        <span style={{ color: "#3a3632", margin: "0 4px" }}>·</span>
        <span style={{ color: "#b8935a", fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          Documento autêntico {obra ? `· ${obraCpsCode(obra)}` : ""}
        </span>
      </div>
      <button
        onClick={() => setVisible(false)}
        aria-label="Fechar barra de autenticidade"
        style={{
          background: "transparent",
          border: "1px solid #3a3632",
          color: "#8a7a5a",
          cursor: "pointer",
          padding: "2px 8px",
          fontSize: 14,
          lineHeight: 1,
          borderRadius: 2,
        }}
        data-testid="authenticity-bar-close"
      >×</button>
    </div>
  );
}

/**
 * CAMASA AI Concierge — widget flutuante de chat (Claude Sonnet 4.5).
 * Aparece como botão dourado no canto inferior direito. Ao clicar abre painel
 * com histórico da conversa. Funciona em modo público (publicToken) ou
 * autenticado (obraId + token JWT auto via axios).
 */
function CamasaAIChat({ publicToken, obraId, obraNome, clienteNome, authToken, obraStatus }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const isPosObra = ["entregue", "concluida", "concluída"].includes((obraStatus || "").toLowerCase());

  // Auto-abrir o chat se o usuário chegou via hash #ai-chat (vindo do e-mail)
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#ai-chat") {
      setOpen(true);
    }
  }, []);

  const [sessionId, setSessionId] = useState(() => {
    const key = `camasa_ai_session_${publicToken || obraId || "default"}`;
    let sid = localStorage.getItem(key);
    if (!sid) {
      sid = "s_" + Math.random().toString(36).slice(2, 14) + Date.now().toString(36);
      localStorage.setItem(key, sid);
    }
    return sid;
  });
  const scrollRef = useRef(null);

  // Carrega histórico ao abrir primeira vez
  useEffect(() => {
    if (!open || messages.length > 0) return;
    const params = new URLSearchParams({ session_id: sessionId });
    if (publicToken) params.set("public_token", publicToken);
    axios.get(`${API}/ai/chat/history?${params.toString()}`)
      .then(r => setMessages(r.data.messages || []))
      .catch(() => { /* silent */ });
  }, [open, sessionId, publicToken, messages.length]);

  // Scroll para fim quando mensagens mudam
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (customText) => {
    const text = (customText != null ? customText : input).trim();
    if (!text || sending) return;
    if (customText == null) setInput("");
    const userMsg = { role: "user", text, at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setSending(true);
    try {
      const payload = { session_id: sessionId, message: text };
      if (publicToken) payload.public_token = publicToken;
      else if (obraId) payload.obra_id = obraId;
      const headers = {};
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      const r = await axios.post(`${API}/ai/chat`, payload, { headers });
      setMessages(prev => [...prev, { role: "assistant", text: r.data.reply, at: new Date().toISOString() }]);
      if (r.data.session_id && r.data.session_id !== sessionId) {
        setSessionId(r.data.session_id);
      }
    } catch (e) {
      setMessages(prev => [...prev, {
        role: "assistant",
        text: "Desculpe, não consegui responder agora. Clique em 'Falar com um consultor' para contato humano.",
        at: new Date().toISOString(),
        error: true,
      }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const resetChat = async () => {
    if (!window.confirm("Apagar TODA a conversa? Isso remove o histórico do servidor e do navegador. Não dá para desfazer.")) return;
    try {
      const params = new URLSearchParams({ session_id: sessionId });
      if (publicToken) params.set("public_token", publicToken);
      const headers = {};
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      await axios.delete(`${API}/ai/chat/history?${params.toString()}`, { headers });
    } catch (e) { /* segue mesmo se falhar — ainda vamos limpar local */ }
    // Limpa localStorage e gera novo session_id
    const key = `camasa_ai_session_${publicToken || obraId || "default"}`;
    localStorage.removeItem(key);
    const newSid = "s_" + Math.random().toString(36).slice(2, 14) + Date.now().toString(36);
    localStorage.setItem(key, newSid);
    setSessionId(newSid);
    setMessages([]);
  };

  const whatsappUrl = "https://wa.me/5511983926920?text=" + encodeURIComponent(
    `Olá, sou ${clienteNome || "cliente CAMASA"} e tenho uma dúvida sobre a obra${obraNome ? " " + obraNome : ""}.`
  );

  return (
    <>
      {/* Botão flutuante — pílula com ícone + label */}
      {!open && (
        <button
          data-testid="ai-chat-open"
          onClick={() => setOpen(true)}
          style={{
            position: "fixed", bottom: 100, right: 24, zIndex: 9999,
            height: 56, padding: "0 22px 0 18px", borderRadius: 28,
            background: "linear-gradient(135deg, #b8935a 0%, #d4b57e 100%)",
            color: "#0d0d0f", border: "none", cursor: "pointer",
            boxShadow: "0 10px 40px rgba(184,147,90,0.5), 0 4px 12px rgba(0,0,0,0.35)",
            display: "flex", alignItems: "center", gap: 10,
            fontFamily: "'Fraunces', Georgia, serif",
            transition: "transform .25s ease, box-shadow .25s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.05)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
          title="Fale com a CAMASA AI — 24/7"
        >
          <span style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: "50%", background: "rgba(13,13,15,0.12)" }}>
            <MessageSquare size={18} strokeWidth={2.2} />
            <Sparkles size={10} strokeWidth={2.5} style={{ position: "absolute", top: 4, right: 4, color: "#0d0d0f" }} />
          </span>
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.05 }}>
            <span style={{ fontSize: 9, letterSpacing: "2.5px", textTransform: "uppercase", fontWeight: 700, fontFamily: "Georgia, serif", opacity: 0.65 }}>Concierge AI</span>
            <span style={{ fontSize: 16, fontStyle: "italic", fontWeight: 500 }}>Tire suas dúvidas</span>
          </span>
          {/* Badge pulsante "online" */}
          <span style={{
            position: "absolute", top: 6, right: 8, width: 10, height: 10, borderRadius: "50%",
            background: "#6fbf8f", border: "2px solid #d4b57e",
            animation: "pulse 2s ease-in-out infinite",
          }} />
        </button>
      )}

      {/* Painel do chat */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          style={{
            position: "fixed", bottom: 100, right: 24, zIndex: 9999,
            width: "min(420px, calc(100vw - 32px))", height: "min(620px, calc(100vh - 148px))",
            background: "#0d0d0f", border: "1px solid rgba(184,147,90,0.35)",
            display: "flex", flexDirection: "column",
            boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
            backdropFilter: "blur(14px)",
          }}
        >
          {/* Header */}
          <div style={{ padding: "18px 20px", borderBottom: "1px solid rgba(184,147,90,0.2)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 9, letterSpacing: ".3em", color: "#b8935a", textTransform: "uppercase", marginBottom: 4 }}>
                {isPosObra ? "Especialista Pós-Obra · Cuidados" : "Concierge 24/7"}
              </div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, color: "#f5efe0", lineHeight: 1.1 }}>
                CAMASA <em style={{ color: "#b8935a" }}>{isPosObra ? "Care" : "AI"}</em>
              </div>
              {clienteNome && <div style={{ fontSize: 11, color: "#8a8a90", marginTop: 4 }}>{clienteNome}{obraNome ? ` · ${obraNome}` : ""}</div>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                data-testid="ai-chat-reset"
                onClick={resetChat}
                title="Apagar histórico desta conversa"
                style={{ background: "none", border: "1px solid rgba(184,147,90,0.3)", color: "#b8935a", fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase", padding: "5px 10px", cursor: "pointer", lineHeight: 1, fontFamily: "inherit" }}
              >
                Apagar
              </button>
              <button data-testid="ai-chat-close" onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "#8a8a90", fontSize: 28, cursor: "pointer", lineHeight: 1, padding: 0 }}>×</button>
            </div>
          </div>

          {/* Mensagens */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>
            {messages.length === 0 && !sending && (
              <div style={{ color: "#8a8a90", fontSize: 13, lineHeight: 1.6, padding: "20px 0" }}>
                <p style={{ margin: 0, marginBottom: 12 }}>
                  Sou o <strong style={{ color: "#b8935a" }}>Concierge CAMASA</strong>. Posso responder sobre:
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, color: "#a8a8ae", fontSize: 12 }}>
                  <li style={{ marginBottom: 6 }}>Etapas, prazos e status da sua obra</li>
                  <li style={{ marginBottom: 6 }}>Cuidados com cada material (CAMASA Care)</li>
                  <li style={{ marginBottom: 6 }}>Processo CAMASA e o que esperar</li>
                </ul>
                <p style={{ margin: "14px 0 0", fontSize: 11, color: "#6a6a70", fontStyle: "italic" }}>
                  Escreva sua pergunta abaixo ↓
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`ai-msg-${m.role}-${i}`} style={{
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "88%",
                padding: "10px 14px",
                background: m.role === "user" ? "rgba(184,147,90,0.15)" : "rgba(245,239,224,0.04)",
                border: `1px solid ${m.role === "user" ? "rgba(184,147,90,0.3)" : "rgba(245,239,224,0.08)"}`,
                color: m.error ? "#c47a7a" : (m.role === "user" ? "#f5efe0" : "#c8c8ce"),
                fontSize: 13, lineHeight: 1.55,
                whiteSpace: "pre-wrap",
              }}>
                {m.text}
              </div>
            ))}
            {sending && (
              <div style={{ alignSelf: "flex-start", color: "#b8935a", fontSize: 12, fontStyle: "italic", letterSpacing: ".05em" }}>
                Concierge digitando…
              </div>
            )}
          </div>

          {/* Ação human-handoff */}
          <a
            href={whatsappUrl} target="_blank" rel="noopener noreferrer"
            data-testid="ai-chat-human"
            style={{ padding: "8px 20px", fontSize: 11, color: "#b8935a", textDecoration: "none", borderTop: "1px solid rgba(184,147,90,0.15)", letterSpacing: ".1em", textAlign: "center", background: "rgba(184,147,90,0.03)" }}
          >
            Prefere falar com um consultor humano? <strong>WhatsApp →</strong>
          </a>

          {/* Quick actions — só no modo Pós-Obra (obra entregue/concluída) */}
          {isPosObra && messages.length < 2 && (
            <div data-testid="ai-chat-quick-actions" style={{ padding: "10px 14px 4px", borderTop: "1px solid rgba(184,147,90,0.15)", background: "rgba(184,147,90,0.05)" }}>
              <div style={{ fontSize: 9, letterSpacing: ".18em", color: "#b8935a", textTransform: "uppercase", marginBottom: 8, fontWeight: 600 }}>
                Cuidados & Manutenção · Ações rápidas
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {[
                  "Como renovar a proteção dos meus materiais?",
                  "Manchei acidentalmente — o que devo fazer?",
                  "Quais produtos posso usar no dia a dia?",
                  "Com que frequência devo fazer manutenção?",
                ].map(q => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    disabled={sending}
                    data-testid={`ai-chat-quick-${q.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                    style={{
                      padding: "6px 10px", fontSize: 11, background: "rgba(184,147,90,0.08)",
                      border: "1px solid rgba(184,147,90,0.3)", color: "#f5efe0",
                      cursor: sending ? "wait" : "pointer", fontFamily: "inherit",
                      letterSpacing: ".03em", borderRadius: 0,
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div style={{ padding: 14, borderTop: "1px solid rgba(184,147,90,0.2)", display: "flex", gap: 8 }}>
            <textarea
              data-testid="ai-chat-input"
              value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKey}
              placeholder="Faça sua pergunta…"
              rows={1}
              style={{
                flex: 1, background: "rgba(245,239,224,0.03)", border: "1px solid rgba(184,147,90,0.2)",
                color: "#f5efe0", padding: "10px 12px", fontSize: 13, resize: "none", fontFamily: "inherit",
                outline: "none",
              }}
              disabled={sending}
            />
            <button
              data-testid="ai-chat-send"
              onClick={send} disabled={!input.trim() || sending}
              style={{
                background: input.trim() && !sending ? "#b8935a" : "rgba(184,147,90,0.3)",
                color: "#0d0d0f", border: "none", padding: "0 16px", cursor: input.trim() && !sending ? "pointer" : "not-allowed",
                fontSize: 13, letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 600,
              }}
            >{sending ? "…" : "Enviar"}</button>
          </div>
        </div>
      )}
    </>
  );
}

function VerificarCertificado() {
  const { cert_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/public/verificar/${cert_id}`).then(r => setData(r.data)).catch(() => setData({ valido: false, motivo: "Erro ao consultar" })).finally(() => setLoading(false));
  }, [cert_id]);
  if (loading) return <div className="container"><p className="page-sub">Verificando…</p></div>;
  const valido = data?.valido && data?.obra_integra;
  const hashHumano = (h) => h ? h.match(/.{1,8}/g).join(" ") : "—";
  return (
    <div className="container" style={{ maxWidth: 720, paddingTop: 60, paddingBottom: 80 }}>
      <div className="eyebrow"><Tricolore thin /> Verificação pública · CAMASA CPS</div>
      <h1 className="page-title" style={{ marginTop: 10 }}>Certificado de <em>Aceite Digital</em></h1>
      <div style={{
        marginTop: 30, padding: 26,
        background: valido ? "rgba(90,122,106,0.08)" : "rgba(139,58,58,0.08)",
        border: "1px solid " + (valido ? "var(--verde-2)" : "#8b3a3a")
      }} data-testid="verify-result">
        <div style={{ fontSize: 48, color: valido ? "var(--verde-2)" : "#c47a7a", lineHeight: 1 }}>{valido ? "✓" : "✕"}</div>
        <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 28, fontWeight: 400, color: "var(--ink)", marginTop: 10 }}>
          {data?.valido ? (data.obra_integra ? <>Certificado <em>autêntico</em></> : <>Certificado válido, mas <em>obra alterada</em></>) : <>Certificado <em>inválido</em></>}
        </h2>
        {!data?.valido && <p style={{ color: "var(--ink-2)", marginTop: 10 }}>{data?.motivo || "Este certificado não pôde ser verificado."}</p>}
        {data?.valido && !data?.obra_integra && (
          <p style={{ color: "#c47a7a", fontSize: 13, lineHeight: 1.7, marginTop: 14 }}>
            Atenção: o aceite é autêntico, mas a obra foi modificada depois da assinatura. O hash atual da obra não corresponde ao momento do aceite.
          </p>
        )}
      </div>

      {data?.certificate_id && (
        <div className="panel" style={{ marginTop: 24 }}>
          <div className="panel-eyebrow">Detalhes do registro</div>
          <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "140px 1fr", gap: 10, fontSize: 13 }}>
            <div style={{ color: "var(--ink-3)" }}>Obra</div>
            <div style={{ color: "var(--ink)" }}>{data.obra_nome} · {data.obra_codigo}</div>
            <div style={{ color: "var(--ink-3)" }}>Signatário</div>
            <div style={{ color: "var(--ink)" }}><strong>{data.signatario.nome}</strong> ({data.signatario.tipo})</div>
            <div style={{ color: "var(--ink-3)" }}>Data do aceite</div>
            <div style={{ color: "var(--ink)" }}>{new Date(data.signatario.data).toLocaleString("pt-BR")}</div>
            <div style={{ color: "var(--ink-3)" }}>Certificado n.º</div>
            <div style={{ color: "var(--ink)", fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>{data.certificate_id}</div>
            <div style={{ color: "var(--ink-3)" }}>Hash SHA-256</div>
            <div style={{ color: "var(--ink)", fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>{hashHumano(data.hash_sha256)}</div>
            <div style={{ color: "var(--ink-3)" }}>Hash atual obra</div>
            <div style={{ color: data.obra_integra ? "var(--verde-2)" : "#c47a7a", fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>
              {hashHumano(data.hash_atual_obra)} {data.obra_integra && "✓ idêntico"}
            </div>
          </div>
          <div style={{ marginTop: 22 }}>
            <a className="btn btn-primary" href={`${API}/public/aceite/${cert_id}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="verify-download-pdf"><Download size={12} /> Baixar certificado PDF</a>
          </div>
        </div>
      )}
      <p style={{ textAlign: "center", fontSize: 11, color: "var(--ink-3)", letterSpacing: ".2em", textTransform: "uppercase", marginTop: 40 }}>
        CAMASA Process System · Verificação pública
      </p>
    </div>
  );
}

function SignatureBookBySlug() {
  const { slug } = useParams();
  const { api, user } = useAuth();
  const [obra, setObra] = useState(undefined);
  useEffect(() => {
    // Busca pela slug via lista de obras (autenticado)
    api("get", "/obras?include_arquivadas=true").then(obras => {
      const o = obras.find(x => x.slug === slug);
      if (o) api("get", `/obras/${o.id}`).then(setObra);
      else setObra(null);
    }).catch(() => setObra(null));
  }, [api, slug]);
  if (obra === undefined) return <div className="container"><p className="page-sub">Carregando…</p></div>;
  if (!obra) return <div className="container" style={{ textAlign: "center", paddingTop: 120 }}><h2 className="page-title">Obra não encontrada</h2></div>;
  return <SignatureBookView obra={obra} />;
}

function PosObraInsightsPage() {
  const { api } = useAuth();
  const [conversas, setConversas] = useState([]);
  const [obraFiltro, setObraFiltro] = useState("");
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (obraFiltro) params.set("obra_id", obraFiltro);
      const arr = await api("get", `/admin/pos-obra-conversas?${params.toString()}`);
      setConversas(Array.isArray(arr) ? arr : []);
    } catch { setConversas([]); }
    finally { setLoading(false); }
  }, [api, obraFiltro]);
  useEffect(() => { load(); }, [load]);

  // Agregações client-side pra Insights
  const insights = useMemo(() => {
    // Stopwords em português (comum + relacionadas a domínio)
    const STOP = new Set(("de da do dos das o a os as um uma uns umas em no na nos nas para pra por " +
      "que se meu minha meus minhas ele ela eles elas eu voce você é foi ser estar já ai aí quer fazer " +
      "com como sobre e ou mas não sim tem ter mais menos onde quando qual quais qque também tambem " +
      "só sao são fazendo sendo isso isto essa esse aquilo aquele aquela pode posso podemos " +
      "quero preciso quer preciso posso quero deve devem faz fazem estão está tá vc").split(/\s+/));
    const wordCount = {};
    const perguntas = [];
    const porObra = {};
    conversas.forEach(c => {
      const q = (c.pergunta || "").toLowerCase();
      // Normaliza acentos e remove pontuação
      const norm = q.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s]/g, " ");
      norm.split(/\s+/).forEach(w => {
        if (w.length < 4 || STOP.has(w)) return;
        wordCount[w] = (wordCount[w] || 0) + 1;
      });
      perguntas.push({ q: c.pergunta || "", when: c.created_at, obra: c.obra_codigo, cliente: c.cliente_nome });
      const k = c.obra_codigo || "—";
      porObra[k] = (porObra[k] || 0) + 1;
    });
    const topTermos = Object.entries(wordCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12);
    const obrasTop = Object.entries(porObra)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
    return { topTermos, obrasTop, total: conversas.length };
  }, [conversas]);

  // Lista única de obras pro dropdown (a partir das conversas)
  const obrasDropdown = useMemo(() => {
    const map = {};
    conversas.forEach(c => { if (c.obra_id) map[c.obra_id] = c.obra_codigo || c.obra_id; });
    return Object.entries(map).sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  }, [conversas]);

  const conversasFiltradas = useMemo(() => {
    if (!busca) return conversas;
    const b = busca.toLowerCase();
    return conversas.filter(c =>
      (c.pergunta || "").toLowerCase().includes(b) ||
      (c.resposta || "").toLowerCase().includes(b) ||
      (c.obra_codigo || "").toLowerCase().includes(b) ||
      (c.cliente_nome || "").toLowerCase().includes(b)
    );
  }, [conversas, busca]);

  return (
    <div style={{ padding: "36px 40px", maxWidth: 1200, margin: "0 auto" }}>
      <div className="page-head">
        <div>
          <div className="eyebrow">Assistente CAMASA Care · pós-entrega</div>
          <h1 className="page-title">Insights Pós-Obra</h1>
          <p className="page-sub">
            Perguntas dos clientes ao Especialista Técnico após a entrega. Use as dúvidas
            recorrentes para antecipar treinamentos, ajustar catálogo Tenax/Bellinzoni ou
            criar conteúdo educativo.
          </p>
        </div>
      </div>

      {/* Cards de insights */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 22 }}>
        <div style={{ padding: 18, background: "var(--bg-2)", border: "1px solid var(--line)" }}>
          <div className="eyebrow">Termos mais mencionados</div>
          {insights.topTermos.length === 0 ? (
            <div style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 8 }}>Nenhuma conversa ainda.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }} data-testid="pos-obra-top-termos">
              {insights.topTermos.map(([w, n]) => (
                <span key={w} style={{
                  padding: "5px 10px", fontSize: 12,
                  background: `rgba(184,147,90, ${Math.min(0.1 + n * 0.08, 0.5)})`,
                  border: "1px solid rgba(184,147,90,0.3)", color: "var(--ink-1)",
                }}>
                  {w} <strong style={{ color: "var(--gold)" }}>·{n}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: 18, background: "var(--bg-2)", border: "1px solid var(--line)" }}>
          <div className="eyebrow">Obras com mais dúvidas</div>
          {insights.obrasTop.length === 0 ? (
            <div style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 8 }}>Nenhuma conversa ainda.</div>
          ) : (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }} data-testid="pos-obra-top-obras">
              {insights.obrasTop.map(([obra, n]) => (
                <div key={obra} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0", borderBottom: "1px dashed var(--line)" }}>
                  <span>{obra}</span>
                  <strong style={{ color: "var(--gold)" }}>{n} pergunta{n === 1 ? "" : "s"}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center" }}>
        <input
          type="search"
          placeholder="Buscar em perguntas/respostas/cliente…"
          value={busca}
          onChange={e => setBusca(e.target.value)}
          data-testid="pos-obra-busca"
          style={{ flex: 1, padding: "8px 12px", background: "var(--bg-2)", border: "1px solid var(--line)", color: "var(--ink-1)", fontFamily: "inherit" }}
        />
        <select
          value={obraFiltro}
          onChange={e => setObraFiltro(e.target.value)}
          data-testid="pos-obra-filtro-obra"
          style={{ padding: "8px 12px", background: "var(--bg-2)", border: "1px solid var(--line)", color: "var(--ink-1)", fontFamily: "inherit" }}
        >
          <option value="">Todas obras ({conversas.length})</option>
          {obrasDropdown.map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: "var(--ink-3)", padding: "0 8px" }}>
          {conversasFiltradas.length} exibida{conversasFiltradas.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Lista de conversas */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>Carregando…</div>
      ) : conversasFiltradas.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--ink-3)", border: "1px dashed var(--line)" }}>
          {conversas.length === 0
            ? "Nenhuma conversa pós-obra ainda. Após uma obra entrar em status ENTREGUE, cada pergunta do cliente ao chat aparece aqui."
            : "Nenhuma conversa bate com o filtro."}
        </div>
      ) : (
        <div data-testid="pos-obra-conversas" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {conversasFiltradas.map((c, idx) => (
            <div key={`${c.session_id}_${idx}`} style={{ padding: 14, background: "var(--bg-2)", border: "1px solid var(--line)", borderLeft: "3px solid var(--gold)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 }}>
                <span>{c.obra_codigo || "—"} · {c.cliente_nome || "cliente"}</span>
                <span>{c.created_at ? new Date(c.created_at).toLocaleString("pt-BR") : "—"}</span>
              </div>
              <div style={{ marginBottom: 6, fontSize: 13, color: "var(--ink-1)" }}>
                <strong style={{ color: "var(--gold)" }}>Q:</strong> {c.pergunta}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-2)", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                <strong style={{ color: "var(--ink-3)" }}>R:</strong> {(c.resposta || "").slice(0, 500)}{(c.resposta || "").length > 500 ? "…" : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function AuditoriaPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [filterAction, setFilterAction] = useState("");
  const [filterEntity, setFilterEntity] = useState("");
  const load = useCallback(() => {
    const params = new URLSearchParams({ limit: "200" });
    if (filterAction) params.set("action", filterAction);
    if (filterEntity) params.set("entity", filterEntity);
    api("get", `/audit?${params.toString()}`).then(setData).catch(() => setData({ items: [], total: 0 }));
  }, [api, filterAction, filterEntity]);
  useEffect(() => { load(); }, [load]);

  const ACTIONS = { archive: "Arquivou", hard_delete: "Excluiu permanente", delete: "Excluiu", create: "Criou", update: "Atualizou", aceite_remove: "Removeu aceite", status_change: "Alterou status" };
  const ENTITIES = { obra: "Obra", arquitetos: "Arquiteto", prestadores: "Prestador", terceirizados: "Terceirizado", materiais: "Material", lancamento: "Lançamento", etapa: "Etapa", foto: "Foto/Vídeo", aceite: "Aceite" };

  const actionColor = (a) => {
    if (a === "hard_delete" || a === "aceite_remove") return "#c47a7a";
    if (a === "archive" || a === "delete") return "#d4ac5a";
    if (a === "create") return "var(--verde-2)";
    return "var(--ink-2)";
  };

  return (
    <>
      <div className="container">
        <div className="page-head fade-up">
          <div>
            <div className="eyebrow"><Tricolore thin /> Área restrita</div>
            <h1 className="page-title">Log de <em>auditoria</em></h1>
            <p className="page-sub">Registro imutável de todas as ações destrutivas do sistema. Somente leitura — jamais editado ou apagado.</p>
          </div>
        </div>


        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20, alignItems: "center" }}>
          <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={{ minWidth: 180, fontSize: 12 }} data-testid="audit-filter-action">
            <option value="">Todas as ações</option>
            {Object.entries(ACTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filterEntity} onChange={e => setFilterEntity(e.target.value)} style={{ minWidth: 180, fontSize: 12 }} data-testid="audit-filter-entity">
            <option value="">Todas as entidades</option>
            {Object.entries(ENTITIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {data && <span style={{ fontSize: 11, letterSpacing: ".2em", color: "var(--ink-3)", textTransform: "uppercase", marginLeft: "auto" }}>{data.total} registros</span>}
        </div>

        {!data ? <p className="page-sub">Carregando…</p> : data.items.length === 0 ? (
          <div className="empty" style={{ padding: 60, textAlign: "center" }}>
            <h3 style={{ fontFamily: "'Fraunces',serif", fontWeight: 400, color: "var(--ink)" }}>Nenhuma ação registrada</h3>
            <p style={{ color: "var(--ink-3)" }}>As ações aparecerão aqui conforme forem executadas.</p>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl" data-testid="audit-table">
              <thead><tr><th style={{ width: 160 }}>Data / Hora</th><th>Usuário</th><th>Ação</th><th>Entidade</th><th>Detalhes</th><th>IP</th></tr></thead>
              <tbody>
                {data.items.map(it => {
                  const meta = it.meta || {};
                  const metaStr = Object.entries(meta).filter(([, v]) => v != null).map(([k, v]) => `${k}: ${typeof v === 'string' ? v.slice(0, 40) : v}`).join(" · ");
                  return (
                    <tr key={it.id} data-testid={`audit-row-${it.id}`}>
                      <td style={{ fontFamily: "monospace", fontSize: 11, color: "var(--ink-2)" }}>{new Date(it.at).toLocaleString("pt-BR")}</td>
                      <td>
                        <div style={{ color: "var(--ink)", fontSize: 12 }}>{it.user_email || "—"}</div>
                        {it.user_role && <div style={{ fontSize: 9, letterSpacing: ".25em", color: "var(--ink-3)", textTransform: "uppercase" }}>{it.user_role}</div>}
                      </td>
                      <td style={{ color: actionColor(it.action), fontSize: 11, letterSpacing: ".15em", textTransform: "uppercase", fontWeight: 600 }}>{ACTIONS[it.action] || it.action}</td>
                      <td style={{ color: "var(--gold)", fontSize: 12 }}>{ENTITIES[it.entity] || it.entity}</td>
                      <td style={{ fontSize: 11, color: "var(--ink-2)", maxWidth: 320, wordBreak: "break-word" }}>{metaStr || "—"}</td>
                      <td style={{ fontFamily: "monospace", fontSize: 10, color: "var(--ink-3)" }}>{it.ip || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}



function FinanceiroResumoSB({ resumo, aceiteSnapshot }) {
  if (!resumo) return null;
  const {
    valor_total, valor_contratado, orcamentos_extras = [], total_orcamentos_extras = 0,
    total_recebido, saldo_a_pagar, pct_recebido, status, pagamentos, proxima_previsao,
    brindes = [],
  } = resumo;
  const quitado = status === "quitado";
  const hasExtras = orcamentos_extras.length > 0;
  const hasBrindes = brindes.length > 0;
  // M1: formatadores centralizados em ./lib/fmt.js
  const fmtDate = fmt;

  return (
    <div className="sb-section" data-testid="sb-financeiro-resumo">
      <div className="sb-h">
        <div className="sb-h-title">Status financeiro</div>
        <div className="sb-h-code">{quitado ? "QUITADO" : "EM ABERTO"}</div>
      </div>

      <div
        style={{
          padding: "24px 28px",
          background: quitado
            ? "linear-gradient(135deg, rgba(95,168,108,0.06) 0%, rgba(184,147,90,0.04) 100%)"
            : "linear-gradient(135deg, rgba(184,147,90,0.08) 0%, rgba(212,178,122,0.04) 100%)",
          border: `1px solid ${quitado ? "rgba(95,168,108,0.35)" : "rgba(184,147,90,0.35)"}`,
          borderLeft: `3px solid ${quitado ? "#5fa86c" : "#b8935a"}`,
          maxWidth: 720,
        }}
      >
        {/* Totais */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 8, columnGap: 24, fontSize: 14, color: "var(--ink-2)" }}>
          <div>Valor total do contrato</div>
          <div data-testid="sb-fin-total" style={{ fontVariantNumeric: "tabular-nums", color: "var(--ink)", fontWeight: 600 }}>{brl(valor_total)}</div>

          <div>Valor recebido</div>
          <div data-testid="sb-fin-recebido" style={{ fontVariantNumeric: "tabular-nums", color: "#5fa86c", fontWeight: 600 }}>{brl(total_recebido)}</div>

          {(
            <>
              <div style={{ borderTop: "1px solid rgba(184,147,90,0.25)", paddingTop: 10, marginTop: 4, gridColumn: "1/-1" }} />
              <div style={{ color: "#b8935a", fontWeight: 600 }}>Saldo a pagar</div>
              <div data-testid="sb-fin-saldo" style={{ fontVariantNumeric: "tabular-nums", color: "#b8935a", fontWeight: 700, fontSize: 18, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>{brl(saldo_a_pagar)}</div>
            </>
          )}
        </div>

        {hasExtras && <SBAditivos itens={orcamentos_extras} base={resumo.valor_base} />}
        {false && (
          <div style={{
            marginTop: 16, padding: "12px 14px", fontSize: 12, color: "var(--ink-3)",
            background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(184,147,90,0.2)",
          }}>
            <div style={{ fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 8 }}>
              Orçamentos complementares (informativo)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 4, columnGap: 16 }}>
              {orcamentos_extras.map((o, i) => (
                <React.Fragment key={o.id || i}>
                  <div data-testid={`sb-fin-extra-nome-${i}`} style={{ color: "var(--ink-3)" }}>{o.nome}</div>
                  <div data-testid={`sb-fin-extra-valor-${i}`} style={{ fontVariantNumeric: "tabular-nums", color: "var(--ink-2)" }}>
                    {brl(o.valor)}
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div style={{ marginTop: 8, fontSize: 10, fontStyle: "italic", color: "var(--ink-3)", lineHeight: 1.5 }}>
              Serviços Adicionais aprovados <strong>somam</strong> ao <strong>Valor total da obra</strong> acima.
            </div>
          </div>
        )}

        {hasBrindes && (
          <div data-testid="sb-fin-brindes" style={{
            marginTop: 16, padding: "14px 16px", fontSize: 12,
            background: "linear-gradient(135deg, rgba(184,147,90,0.10) 0%, rgba(212,178,122,0.05) 100%)",
            border: "1px solid rgba(184,147,90,0.30)", borderRadius: 2,
          }}>
            <div style={{
              fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase",
              color: "#b8935a", marginBottom: 10, fontWeight: 600,
            }}>
              ✦ Cortesias — presente da Camasa
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 12, columnGap: 16 }}>
              {brindes.map((b, i) => {
                const TIPO_LABEL = { peca: "Peça", servico: "Serviço", material: "Material" };
                return (
                  <React.Fragment key={b.id || i}>
                    <div data-testid={`sb-fin-brinde-${i}`} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{
                          fontSize: 9, letterSpacing: ".15em", textTransform: "uppercase",
                          color: "#b8935a", border: "1px solid #b8935a", padding: "1px 7px",
                          background: "rgba(184,147,90,0.10)",
                        }}>Brinde</span>
                        <span style={{ fontSize: 9, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--ink-3)" }}>
                          {TIPO_LABEL[b.tipo] || b.tipo}
                        </span>
                        <span style={{ color: "var(--ink)", fontSize: 13, fontWeight: 500 }}>{b.descricao}</span>
                      </div>
                      {(b.material || b.medidas || b.area_aplicacao) && (
                        <div style={{ display: "flex", gap: 10, fontSize: 11, color: "var(--ink-3)", flexWrap: "wrap" }}>
                          {b.material && <span>{b.material}</span>}
                          {b.medidas && <span>{b.medidas}</span>}
                          {b.area_aplicacao && <span>Local: {b.area_aplicacao}</span>}
                        </div>
                      )}
                    </div>
                    <div data-testid={`sb-fin-brinde-valor-${i}`} style={{ fontVariantNumeric: "tabular-nums", color: "#5fa86c", fontWeight: 600, alignSelf: "center" }}>
                      R$ 0,00
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <div style={{ marginTop: 10, fontSize: 10, fontStyle: "italic", color: "var(--ink-3)", lineHeight: 1.5 }}>
              Cortesias oferecidas gratuitamente — não somam ao valor da obra.
            </div>
          </div>
        )}

        {/* Barra de progresso */}
        <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontSize: 10, letterSpacing: ".25em", textTransform: "uppercase", color: "var(--ink-3)" }}>Progresso de pagamento</span>
          <span data-testid="sb-fin-pct" style={{ fontFamily: "'Fraunces', serif", fontStyle: "italic", fontSize: 20, color: quitado ? "#5fa86c" : "#d4b27a" }}>{pct_recebido}% pago</span>
        </div>
        <div style={{ marginTop: 8, height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" }}>
          <div
            data-testid="sb-fin-progress-bar"
            style={{
              height: "100%",
              width: `${pct_recebido}%`,
              background: quitado
                ? "linear-gradient(90deg, #5fa86c 0%, #7bbf86 100%)"
                : "linear-gradient(90deg, #b8935a 0%, #d4b27a 100%)",
              transition: "width 0.8s ease-out",
            }}
          />
        </div>

        {/* Badge "Quitado" */}
        {quitado && (
          <div data-testid="sb-fin-quitado" style={{
            marginTop: 18, padding: "10px 16px",
            background: "rgba(95,168,108,0.12)", border: "1px solid rgba(95,168,108,0.4)",
            color: "#7bbf86", display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 12, letterSpacing: ".15em", textTransform: "uppercase", fontWeight: 600,
          }}>
            ✓ Totalmente quitado
          </div>
        )}

        {/* Previsão próxima parcela */}
        {!quitado && proxima_previsao && (
          <div data-testid="sb-fin-previsao" style={{
            marginTop: 16, padding: "10px 14px", fontSize: 12, color: "var(--ink-3)",
            background: "rgba(184,147,90,0.04)", border: "1px dashed rgba(184,147,90,0.25)",
          }}>
            <span style={{ fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase", color: "#b8935a", marginRight: 8 }}>
              Próxima previsão
            </span>
            {fmtDate(proxima_previsao.data)} · {proxima_previsao.descricao}
            {proxima_previsao.valor > 0 && <strong style={{ marginLeft: 8, color: "var(--ink)" }}>{brl(proxima_previsao.valor)}</strong>}
          </div>
        )}

        {/* Histórico de pagamentos */}
        {pagamentos && pagamentos.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 10 }}>
              Histórico de pagamentos
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto auto", rowGap: 8, columnGap: 14, fontSize: 12, alignItems: "center" }}>
              {pagamentos.map((p, i) => (
                <React.Fragment key={p.id || i}>
                  <div data-testid={`sb-fin-pag-data-${i}`} style={{ color: "var(--ink-3)", whiteSpace: "nowrap" }}>{fmtDate(p.data)}</div>
                  <div data-testid={`sb-fin-pag-desc-${i}`} style={{ color: "var(--ink-2)" }}>
                    {p.descricao}
                    {p.metodo_pagamento && (
                      <span style={{ marginLeft: 6, fontSize: 10, padding: "2px 6px", border: "1px solid rgba(184,147,90,0.3)", color: "#b8935a", letterSpacing: ".08em", textTransform: "uppercase" }}>
                        {p.metodo_pagamento}
                      </span>
                    )}
                  </div>
                  <div data-testid={`sb-fin-pag-comp-${i}`} style={{ minWidth: 24, textAlign: "center" }}>
                    {p.comprovante_url ? (
                      <a href={p.comprovante_url} target="_blank" rel="noreferrer" title="Ver comprovante" style={{ color: "#b8935a", textDecoration: "none", fontSize: 14 }}>
                        📎
                      </a>
                    ) : null}
                  </div>
                  <div data-testid={`sb-fin-pag-valor-${i}`} style={{ fontVariantNumeric: "tabular-nums", color: "var(--ink)", fontWeight: 600, textAlign: "right", whiteSpace: "nowrap" }}>
                    {brl(p.valor)}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Snapshot congelado do aceite */}
        {aceiteSnapshot && (
          <div data-testid="sb-fin-snapshot" style={{
            marginTop: 22, padding: "14px 18px",
            background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
            fontSize: 12, color: "var(--ink-3)",
          }}>
            <div style={{ fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 6 }}>
              Reconhecido no aceite
            </div>
            Naquela data, o status era:
            <strong style={{ color: "var(--ink)", marginLeft: 6 }}>
              {aceiteSnapshot.status === "quitado"
                ? "totalmente quitado"
                : `saldo de ${brl(aceiteSnapshot.saldo_a_pagar)} a pagar (recebido ${brl(aceiteSnapshot.total_recebido)} de ${brl(aceiteSnapshot.valor_total)})`}
            </strong>
          </div>
        )}
      </div>
    </div>
  );
}


function SignatureBookView({ obra, publicView, demo = false }) {
  // URL pública: prefere slug humanizado (/sb/luciana-shultz), fallback ao token.
  const sbId = obra.cliente_slug || obra.public_token;
  const url = typeof window !== "undefined" ? `${window.location.origin}/sb/${sbId}` : "";
  const mats = obra.materiais || [];
  const fotosDestaque = (obra.fotos || []).filter(f => f.destaque && f.aprovada !== false);
  const fotosPorEtapa = {};
  (obra.fotos || []).forEach(f => {
    if (f.aprovada === false) return;
    if (!f.etapa) return;
    fotosPorEtapa[f.etapa] = fotosPorEtapa[f.etapa] || [];
    fotosPorEtapa[f.etapa].push(f);
  });
  const [aceite, setAceite] = useState(obra.aceite_cliente || null);
  const [showAceite, setShowAceite] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  // Helper que renderiza imagem como <img> (em vez de background-image CSS)
  // garantindo que IMPRIMA NO PDF/PRINT em qualquer browser. As regras antigas
  // de background-image dependiam de "-webkit-print-color-adjust:exact" e eram
  // pintadas como retângulos brancos pelo Chrome/Safari ao imprimir.
  const SBImage = ({ src, ratio = "1/1", alt = "", border = "1px solid var(--gold-3)", onClick, ...rest }) => (
    <div
      onClick={onClick}
      className="sb-img-wrap"
      style={{ aspectRatio: ratio, position: "relative", overflow: "hidden", border, background: "#0a0a0c", cursor: onClick ? "zoom-in" : undefined, ...rest }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </div>
  );

  return (
    <div className="sb fade-up" id="sb-print">
      <div
        className="sb-cover"
        onClick={fotosDestaque[0] ? () => setLightbox(fotosDestaque[0]) : undefined}
        style={fotosDestaque[0] ? { cursor: "zoom-in", position: "relative", overflow: "hidden" } : { position: "relative" }}
        title={fotosDestaque[0] ? "Clique para ampliar a foto de capa" : undefined}
        data-testid="sb-cover"
      >
        {/* Imagem de capa como <img> dedicado — garante que imprima em qualquer browser */}
        {fotosDestaque[0] && (
          <>
            <img
              src={fileUrl(fotosDestaque[0].url)}
              alt=""
              aria-hidden="true"
              className="sb-cover-img"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0, display: "block" }}
            />
            <div className="sb-cover-overlay" style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(13,13,15,0.75), rgba(13,13,15,0.9))", zIndex: 1 }} />
          </>
        )}
        <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <img src="/cps-logo.jpg" alt="CAMASA Process System" style={{ height: 120, width: "auto", maxWidth: 520, objectFit: "contain", filter: "drop-shadow(0 0 24px rgba(184,147,90,0.35))" }} />
        </div>
        <SBFlag />
        <div className="sb-title" style={{ marginTop: 28 }}>Signature Book</div>
        <div className="sb-sub">Documento de entrega premium</div>
        <div className="sb-project">{obra.nome}</div>
        <div className="sb-client" data-testid="sb-client">{obra.cliente_nome} · {obra.cidade}, {obra.estado}</div>
        <div className="sb-auth">Autenticidade · {obraCpsCode(obra)}</div>
        {obra.updated_at && (
          <div style={{ marginTop: 10, fontSize: 11, letterSpacing: ".15em", color: "var(--ink-3)", textTransform: "uppercase" }} data-testid="sb-last-update">
            Última atualização · {(() => {
              try {
                return new Date(obra.updated_at).toLocaleString("pt-BR", {
                  timeZone: "America/Sao_Paulo",
                  day: "2-digit", month: "2-digit", year: "numeric",
                  hour: "2-digit", minute: "2-digit",
                }).replace(", ", " às ");
              } catch { return ""; }
            })()}
          </div>
        )}
        <div style={{ marginTop: 32, fontSize: 10, letterSpacing: ".35em", color: "var(--ink-3)" }}>
          {obra.data_entrega_real ? `ENTREGUE EM ${fmt(obra.data_entrega_real)}` : "EM ANDAMENTO"}
        </div>
        </div>
      </div>

      {!demo && <FinanceiroResumoSB resumo={obra.financeiro_resumo} aceiteSnapshot={aceite?.financeiro_snapshot} />}
      {!demo && <SBCronogramaPagamentos itens={obra.cronograma_pagamentos} />}

      <SBCronologiaMidia
        fotos={obra.fotos}
        series={obra.cronologia_fotografica}
        videos={(obra.videos || []).filter(v => publicView || (v.destaque && v.aprovada !== false))}
      />
      <SBTransformacao fotos={obra.fotos} />
      <SBFichasTecnicas fichas={obra.sb_extras?.fichas_tecnicas} />
      <SBValorizacao v={obra.sb_extras?.valorizacao} />

      <div className="sb-section">
        <div className="sb-h"><div className="sb-h-title">Resumo executivo</div><div className="sb-h-code">{obraCpsCode(obra)}</div></div>
        <p style={{ color: "var(--ink-2)", lineHeight: 1.8, fontSize: 15, maxWidth: 720 }}>
          Este documento registra {obra.status === "concluida" ? "a conclusão dos serviços prestados" : "o progresso"} na {obra.nome.toLowerCase()},
          executada sob os padrões CAMASA de excelência. Cada etapa — da seleção da matéria-prima à entrega — foi conduzida com rigor técnico e materiais selecionados.
        </p>
      </div>

      <div className="sb-section">
        <div className="sb-h"><div className="sb-h-title">Visão geral</div></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <div className="meta-row"><span className="l">Projeto</span><span className="v">{obra.nome}</span></div>
          <div className="meta-row"><span className="l">Cliente</span><span className="v">{obra.cliente_nome}</span></div>
          {obra.arquiteto && <div className="meta-row"><span className="l">Arquiteto</span><span className="v">{obra.arquiteto.nome}</span></div>}
          <div className="meta-row"><span className="l">Local</span><span className="v">{demo ? `${obra.cidade}, ${obra.estado}` : (obra.endereco || obra.cidade)}</span></div>
          <div className="meta-row"><span className="l">Início</span><span className="v">{fmt(obra.data_inicio)}</span></div>
          <div className="meta-row"><span className="l">Entrega</span><span className="v">{fmt(obra.data_entrega_real || obra.data_entrega_prevista)}</span></div>
        </div>
      </div>

      <div className="sb-section">
        <div className="sb-h"><div className="sb-h-title">Ciclo da obra</div><div className="sb-h-code">{(obra.etapas || []).length} etapas</div></div>
        <div className="sb-ciclo-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 2, background: "var(--line)", border: "1px solid var(--line)" }}>
          {(obra.etapas || []).map((e, i) => (
            <div key={i} style={{ background: "var(--bg-2)", padding: 18, borderLeft: e.status === "concluida" ? "2px solid var(--verde-2)" : e.status === "em_andamento" ? "2px solid var(--gold)" : "2px solid transparent" }}>
              <div style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: ".2em" }}>{String(i + 1).padStart(2, "0")}</div>
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 16, color: "var(--ink)", marginTop: 6, fontStyle: "italic", overflowWrap: "break-word" }}>{e.nome}</div>
              <div style={{ fontSize: 10, color: e.status === "concluida" ? "var(--verde-2)" : e.status === "em_andamento" ? "var(--gold)" : "var(--ink-3)", marginTop: 6, letterSpacing: ".15em", textTransform: "uppercase" }}>
                {(() => {
                  if (e.status === "concluida") {
                    const dt = e.data_fim || e.data_conclusao;
                    return dt ? `✓ ${fmt(dt)}` : "✓ concluída";
                  }
                  return e.status === "em_andamento" ? "em andamento" : "aguardando";
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {(() => {
        // Só fotos marcadas pelo admin como "Obra Concluída"
        const concluidas = (obra.fotos || []).filter(f => f.obra_concluida && f.aprovada !== false && f.type !== "video");
        if (concluidas.length === 0) return null;
        const fotosAmbiente = concluidas.filter(f => f.ambiente);
        if (fotosAmbiente.length === 0) {
          return (
            <div className="sb-section" data-testid="sb-obra-concluida">
              <div className="sb-h" style={{ gap: 16, flexWrap: "wrap" }}><div className="sb-h-title">A obra concluída</div><div className="sb-h-code">{concluidas.length} foto(s)</div>{!demo && <a href={`${API}/public/signature/${obra.public_token}/album-pdf`} className="btn btn-ghost btn-sm no-print" data-testid="sb-album-pdf" style={{ marginLeft: "auto" }}>Baixar álbum PDF</a>}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 }}>
                {concluidas.map((f, i) => (
                  <div key={f.id} className="sb-photo-card" style={{ position: "relative", pageBreakInside: "avoid" }}>
                    <SBImage
                      src={fileUrl(f.url)}
                      ratio={i === 0 ? "16/10" : "1/1"}
                      alt={f.legenda || f.etapa || ""}
                      onClick={() => setLightbox(f)}
                    />
                    {f.legenda && <div style={{ padding: "10px 2px", fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 13, color: "var(--ink-2)" }}>{f.legenda}</div>}
                    {f.etapa && !f.legenda && <div style={{ padding: "6px 2px", fontSize: 10, letterSpacing: ".2em", color: "var(--gold)", textTransform: "uppercase" }}>{f.etapa}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        }
        // Galeria por ambiente
        const grupos = {};
        for (const f of fotosAmbiente) {
          if (!grupos[f.ambiente]) grupos[f.ambiente] = [];
          grupos[f.ambiente].push(f);
        }
        const semAmbiente = concluidas.filter(f => !f.ambiente);
        if (semAmbiente.length) grupos["Outros ambientes"] = semAmbiente;
        const nomes = Object.keys(grupos);
        return (
          <div className="sb-section" data-testid="sb-public-galeria-ambiente">
            <div className="sb-h" style={{ gap: 16, flexWrap: "wrap" }}>
              <div className="sb-h-title">A obra concluída</div>
              <div className="sb-h-code">{concluidas.length} fotos · {nomes.length} ambiente(s)</div>
              {!demo && <a href={`${API}/public/signature/${obra.public_token}/album-pdf`} className="btn btn-ghost btn-sm no-print" data-testid="sb-album-pdf" style={{ marginLeft: "auto" }}>Baixar álbum PDF</a>}
            </div>
            {nomes.map(amb => (
              <div key={amb} style={{ marginBottom: 36 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 14, paddingBottom: 8, borderBottom: "1px solid var(--gold-3)" }}>
                  <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontStyle: "italic", color: "var(--gold)" }}>{amb}</div>
                  <div style={{ fontSize: 10, letterSpacing: ".25em", textTransform: "uppercase", color: "var(--ink-3)" }}>{grupos[amb].length} fotos</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12 }}>
                  {grupos[amb].map((f, i) => (
                    <div key={f.id} className="sb-photo-card" style={{ position: "relative", pageBreakInside: "avoid" }}>
                      <SBImage
                        src={fileUrl(f.url)}
                        ratio={i === 0 && grupos[amb].length > 1 ? "4/3" : "1/1"}
                        alt={f.legenda || amb}
                        onClick={() => setLightbox(f)}
                      />
                      {f.legenda && <div style={{ padding: "8px 2px", fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 12, color: "var(--ink-2)" }}>{f.legenda}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Cronologia da obra — auto-gerada do wizard de fotos por etapa+série.
          Itera obra.etapas na ordem real (inclui retrabalhos no meio onde foram inseridos).
          Fix P0 2026-02-27: dedup contra cronologia_fotografica foi removido —
          quando havia séries pendentes, o backend antes retornava elas em
          cronologia_fotografica (fallback), e o dedup zerava as legado. Aceita
          duplicação (raro na prática — só quando a mesma URL vive nos 2 lugares). */}
      {(() => {
        const cronologia = (obra.etapas || []).map(et => {
          const temObs = !!(et.observacoes || "").trim();
          const fotosDaEtapa = [];  // fotos/vídeos agora vivem na Cronologia (SBCronologiaMidia)
          // Mostra a etapa se tiver pelo menos 1 sinal: foto vinculada ou observação.
          // Etapas custom (WhatsApp/Modo Campo) não estão no WIZARD_ETAPA_CONFIG mas
          // ainda assim precisam aparecer quando têm fotos.
          if (fotosDaEtapa.length === 0 && !temObs) return null;
          const series = {};
          fotosDaEtapa.forEach(f => {
            const k = f.serie_nome || "_geral";
            if (!series[k]) series[k] = { nome: k === "_geral" ? "" : k, fotos: [], data: null };
            series[k].fotos.push(f);
            if (!series[k].data && f.uploaded_at) series[k].data = f.uploaded_at;
          });
          return {
            etapa: et.nome,
            isRetrabalho: et.kind === "retrabalho",
            motivo: et.motivo,
            etapaInfo: et,
            series: Object.values(series).sort((a, b) => (a.data || "").localeCompare(b.data || "")),
          };
        }).filter(Boolean);

        if (cronologia.length === 0) return null;

        return (
          <div className="sb-section" data-testid="sb-cronologia">
            <div className="sb-h">
              <div className="sb-h-title">Acompanhamento da Obra</div>
              <div className="sb-h-code">{cronologia.length} etapa(s) documentada(s)</div>
            </div>
            <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.7, maxWidth: 720, marginBottom: 26 }}>
              Registro técnico de cada etapa do processo, com as observações da equipe in loco.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
              {cronologia.map(({ etapa, etapaInfo, series }) => (
                <div key={etapa} data-testid={`sb-cronologia-${etapa}`} style={{ pageBreakInside: "auto", breakInside: "auto" }}>
                  <div style={{
                    display: "flex", alignItems: "baseline", justifyContent: "space-between",
                    borderBottom: "1px solid var(--gold-3)", paddingBottom: 8, marginBottom: 18, flexWrap: "wrap", gap: 8,
                  }}>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontStyle: "italic", color: "var(--ink)" }}>
                      {etapa}
                    </div>
                    {(etapaInfo.data_fim || etapaInfo.data_conclusao) && (
                      <div style={{ fontSize: 10, letterSpacing: ".25em", color: "var(--gold)", textTransform: "uppercase" }}>
                        Concluída em {fmt(etapaInfo.data_fim || etapaInfo.data_conclusao)}
                      </div>
                    )}
                  </div>
                  {/* Observações da etapa — visível ao cliente/arquiteto no SB */}
                  <EtapaObservacoesBlock observacoes={etapaInfo.observacoes} testId={`sb-etapa-obs-${etapa}`} />

                  {series.map((serie, sIdx) => (
                    <div key={sIdx} style={{ marginBottom: 22, pageBreakInside: "avoid" }} className="sb-photo-card">
                      {serie.nome && (
                        <div style={{ fontSize: 11, letterSpacing: ".2em", color: "var(--ink-2)",
                                       textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>
                          📌 {serie.nome}
                        </div>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                        {serie.fotos
                          .sort((a, b) => (a.foto_indice || 99) - (b.foto_indice || 99))
                          .map(f => (
                          <div key={f.id} style={{ pageBreakInside: "avoid" }}>
                            <SBImage
                              src={fileUrl(f.url)}
                              ratio="4/3"
                              alt={f.legenda || ""}
                              onClick={() => setLightbox(f)}
                            />
                            {f.legenda && (
                              <div style={{ padding: "6px 2px", fontSize: 11, letterSpacing: ".1em",
                                             color: "var(--gold)", textTransform: "uppercase" }}>
                                {f.legenda}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Materiais Aprovados pelo Cliente — registro formal pré-produção */}
      {(() => {
        const aprov = obra.materiais_aprovacao || {};
        const aprovados = mats.filter(m => aprov[m.id]?.aprovado);
        if (aprovados.length === 0) return null;
        return (
          <div className="sb-section" data-testid="sb-public-materiais-aprovados">
            <div className="sb-h">
              <div className="sb-h-title">Materiais aprovados pelo cliente</div>
              <div className="sb-h-code">{aprovados.length} aprovação(ões) formal(is)</div>
            </div>
            <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.7, maxWidth: 720, marginBottom: 22 }}>
              Registro oficial dos materiais conferidos e aprovados pelo cliente antes do início da produção,
              com lote, fornecedor e foto da chapa selecionada.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 14 }}>
              {aprovados.map(m => {
                const a = aprov[m.id];
                return (
                  <div key={m.id} className="sb-photo-card" style={{ background: "var(--bg-2)", border: "1px solid var(--gold-3)", padding: 16, pageBreakInside: "avoid" }}>
                    {a.foto_chapa && (
                      <div style={{ marginBottom: 12 }}>
                        <SBImage
                          src={fileUrl(a.foto_chapa)}
                          ratio="16/10"
                          alt={`${m.nome} — Lote ${a.lote || "—"}`}
                          border="1px solid var(--line)"
                          onClick={() => setLightbox({ url: a.foto_chapa, legenda: `${m.nome} — Lote ${a.lote || "—"}` })}
                        />
                      </div>
                    )}
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: 17, color: "var(--ink)", fontStyle: "italic" }}>{m.nome}</div>
                    <div style={{ fontSize: 10, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--ink-3)", marginTop: 6 }}>{MAT_CAT[m.categoria] || m.categoria}</div>
                    <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 10, lineHeight: 1.7 }}>
                      {a.lote && <div><span style={{ color: "var(--ink-3)" }}>Lote:</span> <strong style={{ color: "var(--gold)" }}>{a.lote}</strong></div>}
                      {a.fornecedor && <div><span style={{ color: "var(--ink-3)" }}>Fornecedor:</span> {a.fornecedor}</div>}
                      {a.data_aprovacao && <div><span style={{ color: "var(--ink-3)" }}>Aprovado em:</span> {fmt(a.data_aprovacao)}</div>}
                    </div>
                    <div style={{ marginTop: 12, padding: "8px 0 0", borderTop: "1px solid var(--line)", fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--verde-2)" }}>
                      ✓ Aprovado pelo cliente
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {mats.length > 0 && (
        <div className="sb-section">
          <div className="sb-h"><div className="sb-h-title">Painel de materiais</div><div className="sb-h-code">{mats.length} itens curados</div></div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 2, background: "var(--line)", border: "1px solid var(--line)" }}>
            {mats.map(m => (
              <div key={m.id} className="sb-photo-card" style={{ background: "var(--bg-2)", padding: 22, pageBreakInside: "avoid" }}>
                {m.imagens?.[0] && (
                  <div style={{ marginBottom: 14 }}>
                    <SBImage src={fileUrl(m.imagens[0])} ratio="1/1" alt={m.nome} border="1px solid var(--line-2)" />
                  </div>
                )}
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: 17, color: "var(--ink)", fontStyle: "italic" }}>{m.nome}</div>
                <div style={{ fontSize: 10, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--ink-3)", marginTop: 6 }}>{MAT_CAT[m.categoria]}</div>
                <div style={{ fontSize: 11, color: "var(--gold)", marginTop: 4 }}>{m.acabamento}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="sb-section">
        <div className="sb-h"><div className="sb-h-title">CAMASA Care</div><div className="sb-h-code">Guia de preservação</div></div>
        <div className="care-grid">
          <div className="care-col care-ok">
            <div className="care-col-head"><ShieldCheck size={18} /><h4>Cuidados essenciais</h4></div>
            <ul>
              <li><Droplets size={12} /> Limpeza diária com pano macio úmido e sabão neutro pH 7.</li>
              <li><Sparkles size={12} /> Impermeabilizante específico a cada 6 meses.</li>
              <li><ShieldCheck size={12} /> Descanso de panela em superfícies com calor direto.</li>
              <li><Droplets size={12} /> Secar imediatamente líquidos ácidos (limão, vinho, café).</li>
              <li><Check size={12} /> Inspecionar rejuntes anualmente.</li>
            </ul>
          </div>
          <div className="care-col care-warn">
            <div className="care-col-head"><AlertTriangle size={18} /><h4>O que evitar</h4></div>
            <ul>
              <li><Ban size={12} /> <strong>Nunca</strong> use cloro, amoníaco ou abrasivos.</li>
              <li><Ban size={12} /> Esponjas de aço e escovas de cerdas duras.</li>
              <li><Flame size={12} /> Contato prolongado com óleos concentrados.</li>
              <li><Zap size={12} /> Impactos pontuais (quedas pesadas).</li>
              <li><AlertTriangle size={12} /> Variações bruscas de temperatura (choque térmico).</li>
            </ul>
          </div>
        </div>
      </div>

      {!demo && <SBAvaliacao token={obra.public_token} avaliacao={obra.sb_extras?.avaliacao} />}

      {!demo && <div className="sb-section">
        <div className="sb-h"><div className="sb-h-title">Termo de conformidade</div><div className="sb-h-code">{obraCpsCode(obra)}</div></div>
        <p style={{ color: "var(--ink-2)", lineHeight: 1.9, fontSize: 15, maxWidth: 720 }}>
          Certificamos que o projeto acima foi executado em conformidade com os padrões CAMASA de qualidade,
          envolvendo {mats.length} materiais premium, rede de prestadores certificados e protocolos auditados.
          {obra.financeiro_resumo && (
            <span style={{ display: "block", marginTop: 12, fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>
              Ao registrar o aceite, o cliente reconhece o recebimento das peças entregues e o status financeiro
              apresentado acima como verdadeiro no momento desta assinatura.
            </span>
          )}
        </p>

        {aceite ? (
          <div className="aceite-ok">
            <CheckCircle2 size={32} color="#5a7a6a" />
            <div>
              <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--verde-2)", textTransform: "uppercase" }}>Aceite digital confirmado</div>
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: "var(--ink)", marginTop: 6 }}>{aceite.nome}</div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6, letterSpacing: ".1em" }}>
                {fmt(aceite.data)} · {new Date(aceite.data).toLocaleTimeString("pt-BR")} · IP {aceite.ip || "—"} · {aceite.tipo}
              </div>
            </div>
          </div>
        ) : publicView && (
          <div style={{ marginTop: 24, padding: 24, border: "1px dashed var(--gold-3)", background: "rgba(184,147,90,0.04)" }}>
            <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 8 }}>Aceite digital pendente</div>
            <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.6, margin: "0 0 16px" }}>
              Ao clicar em "Tomar ciência e aceitar a obra", você confirma o recebimento e conformidade dos serviços. Registraremos data, hora e origem da confirmação.
            </p>
            <button className="btn btn-primary" onClick={() => setShowAceite(true)} data-testid="btn-aceite">
              <ShieldCheck size={13} /> Tomar ciência e aceitar a obra
            </button>
          </div>
        )}

        {/* Assinatura institucional CAMASA */}
        {obra.aceite_camasa && (
          <div style={{ marginTop: 18, padding: 20, border: "1px solid var(--gold-3)", background: "rgba(184,147,90,0.05)", display: "flex", alignItems: "center", gap: 14 }} data-testid="sb-public-aceite-camasa">
            <CheckCircle2 size={28} color="var(--gold)" />
            <div>
              <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--gold)", textTransform: "uppercase" }}>Assinatura CAMASA</div>
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 20, color: "var(--ink)", marginTop: 4, fontStyle: "italic" }}>{obra.aceite_camasa.nome}</div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{obra.aceite_camasa.cargo}</div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6, letterSpacing: ".1em" }}>
                {fmt(obra.aceite_camasa.data)} · {new Date(obra.aceite_camasa.data).toLocaleTimeString("pt-BR")}
              </div>
            </div>
          </div>
        )}

        {/* Termo de Medição Final — link público + aceite separado */}
        {obra.medicoes?.termo_final && (
          <SBPublicTermoSection medicoes={obra.medicoes} publicToken={obra.public_token} publicView={publicView} />
        )}

        {/* Status combinado: Obra Entregue e Assinada */}
        {aceite && obra.aceite_camasa && (
          <div style={{ marginTop: 18, padding: 16, background: "linear-gradient(135deg, rgba(90,122,106,0.12) 0%, rgba(184,147,90,0.08) 100%)", border: "1px solid var(--verde-2)", textAlign: "center" }} data-testid="sb-public-fully-signed">
            <div style={{ fontSize: 10, letterSpacing: ".4em", color: "var(--verde-2)", textTransform: "uppercase", marginBottom: 6 }}>Status Final</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontStyle: "italic", color: "var(--ink)" }}>Obra Entregue e Assinada</div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 6, letterSpacing: ".1em" }}>Confirmação dupla — cliente e CAMASA</div>
          </div>
        )}

        <div className="qr-block" style={{ marginTop: 32 }}>
          <div style={{ background: "#fff", padding: 12 }}>
            <QRCodeSVG value={url} size={128} bgColor="#ffffff" fgColor="#0d0d0f" level="H" />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: "var(--ink)", margin: "0 0 8px" }}>Comprovante Digital</h3>
            <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.6, margin: 0 }}>Leia o QR Code para validar este documento online.<br /><span style={{ color: "var(--gold)", fontSize: 11, letterSpacing: ".12em" }}>{url}</span></p>
          </div>
        </div>
      </div>}

      <div style={{ textAlign: "center", padding: "64px 0 40px" }}>
        <Tricolore />
        <div style={{ display: "flex", justifyContent: "center", marginTop: 22 }}>
          <img src="/cps-logo.jpg" alt="CAMASA Process System" style={{ height: 90, width: "auto", maxWidth: 420, objectFit: "contain", filter: "drop-shadow(0 0 20px rgba(184,147,90,0.3))" }} />
        </div>
        <div style={{ fontSize: 10, letterSpacing: ".4em", color: "var(--gold)", marginTop: 18, textTransform: "uppercase" }}>Mármores & Design · Pedras Naturais Premium</div>
        <p style={{ color: "var(--ink-2)", maxWidth: 520, margin: "32px auto", lineHeight: 1.7, fontSize: 14 }}>
          Obrigado por confiar na CAMASA para transformar seu espaço com materiais curados a dedo.
        </p>
        <p style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "var(--gold)", fontSize: 15 }}>"Raro come un sogno."</p>
        <div style={{ display: "flex", gap: 24, justifyContent: "center", fontSize: 11, color: "var(--ink-3)", letterSpacing: ".15em", marginTop: 24, flexWrap: "wrap" }}>
          <span><Phone size={11} style={{ display: "inline", marginRight: 6 }} />(11) 98392-6920</span>
          <span><Mail size={11} style={{ display: "inline", marginRight: 6 }} />carlo@camasa.com.br</span>
        </div>

        {/* Indique um amigo — Círculo CAMASA */}
        <div style={{
          marginTop: 48, padding: "38px 32px",
          background: "linear-gradient(135deg, rgba(37,211,102,0.06) 0%, rgba(168,134,79,0.08) 100%)",
          border: "1px solid rgba(37,211,102,0.3)", textAlign: "center",
        }} className="no-print" data-testid="indique-section">
          <div style={{ fontSize: 10, letterSpacing: ".45em", color: "#25d366", textTransform: "uppercase", marginBottom: 14 }}>Círculo CAMASA</div>
          <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 32, fontWeight: 400, color: "var(--ink)", margin: "0 0 14px", fontStyle: "italic", letterSpacing: "-0.01em" }}>
            Os melhores projetos nascem de <em style={{ color: "var(--gold)", fontStyle: "italic" }}>boas referências</em>
          </h3>
          <p style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.7, maxWidth: 560, margin: "0 auto 26px" }}>
            Se esta obra encantou você, compartilhe a experiência CAMASA.
            Toque no botão, escolha um amigo da sua lista de contatos e envie a indicação — a mensagem já vem pronta.
          </p>
          <a
            href={`https://wa.me/?text=${encodeURIComponent('Olá! Quero te indicar a CAMASA Mármores — curadoria em mármores, quartzitos e pedras naturais premium. Fui cliente e recomendo. Site: www.camasa.com.br · WhatsApp: +55 (11) 98392-6920')}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              padding: "16px 36px", background: "#25d366", color: "#fff",
              textDecoration: "none", fontSize: 13, fontWeight: 600,
              letterSpacing: ".15em", textTransform: "uppercase",
              boxShadow: "0 4px 20px rgba(37,211,102,0.35)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 28px rgba(37,211,102,0.5)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 20px rgba(37,211,102,0.35)"; }}
            data-testid="indique-whatsapp-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.52 3.48A11.93 11.93 0 0012 0C5.37 0 0 5.37 0 12c0 2.12.55 4.18 1.6 6L0 24l6.15-1.61A12 12 0 0012 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52zM12 21.93a9.9 9.9 0 01-5.06-1.39l-.36-.21-3.65.96.97-3.56-.24-.37A9.86 9.86 0 012.07 12C2.07 6.52 6.52 2.07 12 2.07S21.93 6.52 21.93 12 17.48 21.93 12 21.93zm5.43-7.44c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07a8.14 8.14 0 01-2.39-1.48 9.04 9.04 0 01-1.66-2.07c-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.48-.5-.67-.5l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.89 1.22 3.09c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.48 1.69.62.71.23 1.35.19 1.86.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35z"/></svg>
            Indicar um amigo
          </a>
        </div>

        <div style={{ marginTop: 32, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }} className="no-print">
          <button
            className="btn btn-primary"
            onClick={() => window.print()}
            data-testid="sb-print-button"
            title="Imprimir esta página com layout otimizado para papel"
          >Imprimir</button>
        </div>
      </div>
      {showAceite && createPortal(<AceiteModal obra={obra} onClose={() => setShowAceite(false)} onDone={(a) => { setAceite(a); setShowAceite(false); }} />, document.body)}
      {lightbox && createPortal(
        <div className="lightbox" onClick={() => setLightbox(null)} data-testid="sb-lightbox">
          <button className="lightbox-close" onClick={() => setLightbox(null)}>×</button>
          <img src={fileUrl(lightbox.url)} alt={lightbox.legenda || ""} />
          {lightbox.legenda && <div className="lightbox-caption">{lightbox.legenda}</div>}
        </div>,
        document.body,
      )}
    </div>
  );
}

function AceiteModal({ obra, onClose, onDone }) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [tipo, setTipo] = useState("cliente");
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cert, setCert] = useState(null); // após sucesso, guarda {certificate_id, verify_url}
  const submit = async () => {
    if (!nome.trim() || !agree) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/public/signature/${obra.public_token}/aceite`, { nome, tipo, email: email.trim() || null });
      setCert({ certificate_id: r.data.certificate_id, verify_url: r.data.verify_url, aceite: r.data.aceite });
    } catch (e) { alert(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };
  const baixarCertificado = async () => {
    try {
      const resp = await fetch(`${API}/public/aceite/${cert.certificate_id}/pdf`, { credentials: "omit" });
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href; a.download = `certificado-aceite-${cert.certificate_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
    } catch (e) { alert("Erro: " + e.message); }
  };
  const finalizar = () => { onDone(cert.aceite); };

  // Tela de sucesso com certificado
  if (cert) {
    return (
      <div className="modal-back" onClick={finalizar}>
        <div className="modal" onClick={e => e.stopPropagation()} data-testid="aceite-success-modal">
          <div className="modal-head"><h3 className="modal-title">Aceite <em>registrado</em></h3></div>
          <div className="modal-body" style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, color: "var(--verde-2)", marginBottom: 8 }}>✓</div>
            <p style={{ color: "var(--ink)", fontSize: 16, fontFamily: "'Fraunces',serif", fontStyle: "italic", marginTop: 0 }}>Obrigado, {cert.aceite.nome}.</p>
            <p style={{ color: "var(--ink-2)", fontSize: 13, lineHeight: 1.7 }}>
              Seu aceite digital da obra <strong style={{ color: "var(--ink)" }}>{obra.nome}</strong> foi registrado com <strong>validade jurídica</strong> (MP 2.200-2/2001 · Lei 14.063/2020).
            </p>
            <div style={{ padding: 16, background: "var(--bg-3)", border: "1px solid var(--gold-3)", margin: "20px 0", textAlign: "left" }}>
              <div style={{ fontSize: 10, letterSpacing: ".28em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 6 }}>Certificado</div>
              <div style={{ fontFamily: "monospace", fontSize: 12, color: "var(--ink)", wordBreak: "break-all" }}>N.º {cert.certificate_id}</div>
              <div style={{ fontSize: 10, letterSpacing: ".28em", color: "var(--gold)", textTransform: "uppercase", marginTop: 14, marginBottom: 6 }}>Hash SHA-256</div>
              <div style={{ fontFamily: "monospace", fontSize: 10, color: "var(--ink-2)", wordBreak: "break-all" }}>{cert.aceite.hash_sha256}</div>
            </div>
            {email && <p style={{ fontSize: 11, color: "var(--verde-2)", letterSpacing: ".1em" }}>✓ Uma cópia foi enviada para {email}</p>}
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 22, flexWrap: "wrap" }}>
              <button className="btn btn-primary" onClick={baixarCertificado} data-testid="aceite-download-cert"><Download size={13} /> Baixar certificado PDF</button>
              <a className="btn btn-ghost" href={cert.verify_url} target="_blank" rel="noopener noreferrer">Verificar autenticidade</a>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={finalizar} style={{ marginTop: 20, fontSize: 11 }}>Fechar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head"><h3 className="modal-title">Aceite <em>digital</em></h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <p style={{ color: "var(--ink-2)", fontSize: 14, lineHeight: 1.7, marginTop: 0 }}>
            Ao confirmar, você declara que recebeu os serviços da obra <strong style={{ color: "var(--ink)" }}>{obra.nome}</strong> em conformidade
            com o contratado. Registraremos seu nome, data, hora, IP e hash SHA-256 como prova jurídica.
          </p>
          <div className="form-grid">
            <div className="field span2"><label>Seu nome completo *</label>
              <input data-testid="aceite-nome" value={nome} onChange={e => setNome(e.target.value)} placeholder="Como consta no contrato" />
            </div>
            <div className="field span2"><label>Seu e-mail (para receber o certificado)</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="voce@exemplo.com" data-testid="aceite-email" />
            </div>
            <div className="field span2"><label>Você está assinando como</label>
              <select value={tipo} onChange={e => setTipo(e.target.value)}>
                <option value="cliente">Cliente final</option>
                <option value="arquiteto">Arquiteto / Engenheiro responsável</option>
              </select>
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5, cursor: "pointer", marginTop: 18, letterSpacing: "normal", textTransform: "none" }}>
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ marginTop: 3, width: 16, height: 16 }} data-testid="aceite-check" />
            <span>Declaro estar ciente do recebimento da obra em conformidade e autorizo o registro deste aceite digital com <strong style={{ color: "var(--gold)" }}>validade jurídica</strong> (assinatura eletrônica avançada — Lei 14.063/2020, Art. 4º, II).</span>
          </label>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading || !nome.trim() || !agree} data-testid="aceite-confirm">
            {loading ? "Registrando…" : "Confirmar aceite"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <div className="foot">
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <Tricolore thin />
        <span>CAMASA · Process System</span>
      </div>
      <span>Mármores & Design · 2026</span>
    </div>
  );
}

/* ========== OCORRÊNCIAS PAGE (menu principal) ========== */
function OcorrenciasPage() {
  return (
    <div className="container" style={{ maxWidth: 1400, padding: "20px 0" }}>
      <OcorrenciasPanel />
    </div>
  );
}

/* ========== APP ROOT ========== */
function Shell() {
  const location = useLocation();
  const isPublic = location.pathname.startsWith("/sb/") || location.pathname.startsWith("/verificar/") || location.pathname.startsWith("/nps/") || location.pathname.startsWith("/rastreio/") || location.pathname.startsWith("/r/") || location.pathname.startsWith("/frentista") || location.pathname.startsWith("/parceiro") || location.pathname === "/login" || location.pathname === "/forgot-password" || location.pathname === "/reset-password" || location.pathname.startsWith("/magic/");
  return (
    <div className="app marble-bg grain">
      {!isPublic && <Protected><Nav /></Protected>}
      {/* Modal global de notificação WhatsApp — escuta evento 'camasa:notificar-cliente' */}
      {!isPublic && <Protected><NotificarClienteModal /></Protected>}
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/magic/:token" element={<MagicLogin />} />
        <Route path="/sb/demo" element={<SignatureBookDemo />} />
        <Route path="/sb/:token" element={<SignatureBookPublic />} />
        <Route path="/nps/:token" element={<NPSPublicForm />} />
        <Route path="/verificar/:cert_id" element={<VerificarCertificado />} />
        <Route path="/rastreio/:token" element={<RastreioPublic />} />
        <Route path="/r/:token" element={<ReciboPublicView />} />
        <Route path="/frentista" element={<Protected><FrentistaPanel /></Protected>} />
        {/* /parceiro NÃO é Protected — o portal gerencia auth via device-token (?token=xxx) */}
        <Route path="/parceiro" element={<ParceiroPortal />} />
        <Route path="/parceiro2" element={<ParceiroV2Portal />} />
        <Route path="/campo" element={<Protected><ModoCampoHome /></Protected>} />
        <Route path="/campo/foto" element={<Protected><ModoCampoFoto /></Protected>} />
        <Route path="/campo/etapa" element={<Protected><ModoCampoEtapa /></Protected>} />
        <Route path="/campo/aprovacoes" element={<Protected><ModoCampoAprovacoes /></Protected>} />
        <Route path="/aprovar" element={<Protected><ModoCampoAprovacoes /></Protected>} />
        <Route path="/parceiro/obras/:id" element={<Protected><ParceiroObraDetail /></Protected>} />
        <Route path="/admin/fotos-pendentes" element={<Protected><FotosPendentesPanel /></Protected>} />
        <Route path="/recibo/:obraId/:kind/:pid" element={<Protected><ReciboPrintView /></Protected>} />
        {/* Aliases legados / atalhos amigáveis — evita 404 em links antigos */}
        <Route path="/painel" element={<Navigate to="/" replace />} />
        <Route path="/caixa" element={<Navigate to="/cashflow" replace />} />
        <Route path="/concierge" element={<Navigate to="/conversas-ai" replace />} />
        <Route path="/" element={<Protected><Dashboard /></Protected>} />
        <Route path="/obras" element={<Protected><ObrasList /></Protected>} />
        <Route path="/obras/:id" element={<Protected><ObraDetail /></Protected>} />
        <Route path="/ocorrencias" element={<Protected><OcorrenciasPage /></Protected>} />
        <Route path="/prestadores" element={<Protected><PrestadoresPage /></Protected>} />
        <Route path="/arquitetos" element={<Protected><ArquitetosPage /></Protected>} />
        <Route path="/terceirizados" element={<Protected><TerceirizadosPage /></Protected>} />
        <Route path="/materiais" element={<Protected><MateriaisPage /></Protected>} />
        <Route path="/aprovacao" element={<Protected><AprovacaoPage /></Protected>} />
        <Route path="/financeiro" element={<Protected><Financeiro /></Protected>} />
        <Route path="/auditoria" element={<Protected><AuditoriaPage /></Protected>} />
        <Route path="/pos-obra-insights" element={<Protected><PosObraInsightsPage /></Protected>} />
        <Route path="/dre" element={<Protected><DREPage /></Protected>} />
        <Route path="/retrabalhos" element={<Protected><RetrabalhosPage /></Protected>} />
        <Route path="/conversas-ai" element={<Protected><AIConversationsPage /></Protected>} />
        <Route path="/nps" element={<Protected><NPSAdminPage /></Protected>} />
        <Route path="/sobras" element={<Protected><SobrasGlobalPage /></Protected>} />
        <Route path="/cashflow" element={<Protected><CashflowPage /></Protected>} />
        <Route path="/signature/:slug" element={<Protected><SignatureBookBySlug /></Protected>} />
      </Routes>
    </div>
  );
}

// ==================== AI Conversations Dashboard (Admin) ====================

// ==================== DRE Avançado (Admin/Financeiro) ====================
const CUSTO_LBL = {
  materiais: "Matéria-prima",
  beneficiamento: "Beneficiamento",
  frete: "Frete / Logística",
  instalacao: "Instalação",
  rt_arquiteto: "RT Arquiteto",
  comissao: "Comissão vendedor",
  retrabalho: "Retrabalho",
  outros: "Outros",
};
function RetrabalhosPage() {
  const { api, user } = useAuth();
  if (user?.role !== "admin") {
    return <div className="container"><h1 className="page-title">Acesso negado</h1></div>;
  }
  return <RetrabalhosDashboard api={api} />;
}



// M1: fmtBRL módulo-level reusa o centralizado de ./lib (fmtMoedaOuTraco)
const fmtBRL = fmtMoedaOuTraco;

function DREPage() {
  const { api, user } = useAuth();
  const canUse = user?.role === "admin" || user?.role === "financeiro";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedObra, setSelectedObra] = useState(null);

  useEffect(() => {
    if (!canUse) return;
    setLoading(true);
    api("get", "/dre/consolidado").then(setData).finally(() => setLoading(false));
  }, [api, canUse]);

  if (!canUse) return <div className="container"><h1 className="page-title">Acesso negado</h1></div>;

  return (
    <>
      <div className="container">
        <div className="page-head fade-up">
          <div className="eyebrow"><Tricolore thin /> Financeiro · DRE</div>
          <h1 className="page-title">Demonstrativo de <em>Resultado</em></h1>
          <p className="page-sub">Margem líquida por obra em tempo real · receita, custos diretos e RT arquiteto.</p>
        </div>

        <HowToBanner id="dre-page" title="Como usar o DRE" steps={[
          { num: 1, title: "Margem em tempo real", desc: "Cada lançamento que você adiciona no Financeiro (receita ou custo) impacta instantaneamente a margem da obra." },
          { num: 2, title: "Projetada vs. realizada", desc: "<strong>Realizada</strong> = o que já entrou/saiu. <strong>Projetada</strong> = considera o valor contratado total mesmo que ainda falte receber." },
          { num: 3, title: "Clique numa obra", desc: "Abre o DRE detalhado com receitas, custos por categoria (matéria-prima, frete, instalação, RT) e custo teórico dos materiais do catálogo." },
          { num: 4, title: "Ordenação", desc: "Obras são ordenadas pela <strong>margem projetada</strong> — as mais rentáveis primeiro. Atenção às margens baixas/negativas." },
        ]} />

        {loading && <p className="page-sub" style={{ textAlign: "center", padding: 40 }}>Calculando DRE…</p>}
        {!loading && data && (
          <>
            {/* Totais consolidados */}
            <div className="dre-totals" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 34 }}>
              <MetricCard label="Receita realizada" value={fmtBRL(data.total_receita_realizada)} accent="#6fbf8f" testid="dre-rec" />
              <MetricCard label="Custo total" value={fmtBRL(data.total_custo)} accent="#c47a7a" testid="dre-cus" />
              <MetricCard label="Margem bruta" value={fmtBRL(data.total_margem)} accent="#b8935a" testid="dre-marg" />
              <MetricCard label="% Margem" value={`${data.total_margem_pct}%`} accent={data.total_margem_pct >= 30 ? "#6fbf8f" : data.total_margem_pct >= 15 ? "#d4b57e" : "#c47a7a"} testid="dre-marg-pct" />
            </div>

            {/* Tabela de obras */}
            <div style={{ color: "var(--ink-3)", fontSize: 12, letterSpacing: ".15em", textTransform: "uppercase", marginBottom: 10 }}>
              {data.obras_count} obras ativas
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr>
                  <th>Obra</th>
                  <th>Cliente</th>
                  <th style={{ textAlign: "right" }}>Receita realiz.</th>
                  <th style={{ textAlign: "right" }}>Receita projet.</th>
                  <th style={{ textAlign: "right" }}>Custo</th>
                  <th style={{ textAlign: "right" }}>Margem bruta</th>
                  <th style={{ textAlign: "right" }}>% Proj.</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {data.obras.map(o => {
                    const mp = o.margem_projetada_pct;
                    const cor = mp >= 30 ? "#6fbf8f" : mp >= 15 ? "#d4b57e" : "#c47a7a";
                    return (
                      <tr key={o.id} data-testid={`dre-row-${o.id}`}>
                        <td>
                          <div style={{ color: "var(--ink)", fontWeight: 500 }}>{o.nome}</div>
                          <div style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".05em" }}>{o.codigo} · {o.status}</div>
                        </td>
                        <td style={{ color: "var(--ink-2)" }}>{o.cliente_nome}</td>
                        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--ink-2)" }}>{fmtBRL(o.receita_realizada)}</td>
                        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--ink)" }}>{fmtBRL(o.receita_projetada)}</td>
                        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#c47a7a" }}>{fmtBRL(o.custo_total)}</td>
                        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: cor, fontWeight: 600 }}>{fmtBRL(o.margem_projetada)}</td>
                        <td style={{ textAlign: "right", color: cor, fontWeight: 600 }}>{mp}%</td>
                        <td><button className="btn btn-ghost btn-sm" onClick={() => setSelectedObra(o.id)} data-testid={`dre-open-${o.id}`}>Detalhar</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      {selectedObra && <DREObraDetail obraId={selectedObra} onClose={() => setSelectedObra(null)} />}
      <Footer />
    </>
  );
}

function DREObraDetail({ obraId, onClose }) {
  const { api } = useAuth();
  const [dre, setDre] = useState(null);

  useEffect(() => {
    api("get", `/obras/${obraId}/dre`).then(setDre);
  }, [api, obraId]);

  if (!dre) {
    return (
      <div className="modal-back" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="modal-body"><p>Carregando DRE…</p></div>
        </div>
      </div>
    );
  }

  const { obra, receita, custos, margem } = dre;
  const mp = margem.projetada_pct;
  const cor = mp >= 30 ? "#6fbf8f" : mp >= 15 ? "#d4b57e" : "#c47a7a";

  return (
    <div className="modal-back" onClick={onClose} data-testid="dre-detail-modal">
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
        <div className="modal-head">
          <div>
            <div className="eyebrow"><Tricolore thin /> DRE · {obra.codigo}</div>
            <h2 className="modal-title">{obra.nome}</h2>
            <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4, letterSpacing: ".05em" }}>{obra.cliente_nome} · {obra.status}</div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {/* 3 cards top */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 24 }}>
            <MetricCard label="Receita realizada" value={fmtBRL(receita.realizada)} accent="#6fbf8f" />
            <MetricCard label="Custo total" value={fmtBRL(custos.total)} accent="#c47a7a" />
            <MetricCard label={`Margem bruta ${mp}%`} value={fmtBRL(margem.projetada_absoluta)} accent={cor} />
          </div>

          {/* A receber alert */}
          {receita.a_receber > 0 && (
            <div style={{ padding: 14, background: "rgba(212,181,126,0.08)", border: "1px solid rgba(212,181,126,0.3)", marginBottom: 20, fontSize: 13, color: "var(--ink-2)" }}>
              <strong style={{ color: "#d4b57e" }}>A receber:</strong> {fmtBRL(receita.a_receber)} · valor contratado {fmtBRL(receita.valor_contratado)}.
            </div>
          )}

          {/* Receitas */}
          <h3 style={{ fontSize: 12, letterSpacing: ".25em", color: "var(--ink-3)", textTransform: "uppercase", margin: "12px 0 10px" }}>Receitas</h3>
          <table className="tbl" style={{ marginBottom: 24 }}>
            <thead><tr><th>Data</th><th>Descrição</th><th>Categoria</th><th style={{ textAlign: "right" }}>Valor</th></tr></thead>
            <tbody>
              {receita.detalhe.length === 0 ? <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--ink-3)", padding: 16 }}>Sem receitas registradas</td></tr> :
                receita.detalhe.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontSize: 12, color: "var(--ink-3)" }}>{r.data}</td>
                    <td style={{ color: "var(--ink)" }}>{r.descricao}</td>
                    <td style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".05em", textTransform: "uppercase" }}>{r.categoria}</td>
                    <td style={{ textAlign: "right", color: "#6fbf8f", fontVariantNumeric: "tabular-nums" }}>{fmtBRL(r.valor)}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>

          {/* Custos por bucket */}
          <h3 style={{ fontSize: 12, letterSpacing: ".25em", color: "var(--ink-3)", textTransform: "uppercase", margin: "12px 0 10px" }}>Custos por categoria</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginBottom: 24 }}>
            {Object.entries(custos.por_categoria).map(([k, v]) => {
              if (!v) return null;
              const pct = custos.total ? (v / custos.total * 100) : 0;
              return (
                <div key={k} data-testid={`dre-bucket-${k}`} style={{ padding: "12px 14px", background: "var(--bg-2)", border: "1px solid var(--line)" }}>
                  <div style={{ fontSize: 10, letterSpacing: ".2em", color: "var(--ink-3)", textTransform: "uppercase", marginBottom: 4 }}>{CUSTO_LBL[k] || k}</div>
                  <div style={{ color: "#c47a7a", fontVariantNumeric: "tabular-nums", fontSize: 16, fontFamily: "'Fraunces', serif" }}>{fmtBRL(v)}</div>
                  <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 2 }}>{pct.toFixed(1)}% do custo</div>
                </div>
              );
            })}
          </div>

          {/* Materiais teóricos vs realizados */}
          {custos.materiais_detalhe.length > 0 && (
            <>
              <h3 style={{ fontSize: 12, letterSpacing: ".25em", color: "var(--ink-3)", textTransform: "uppercase", margin: "12px 0 10px" }}>
                Custo teórico dos materiais <span style={{ color: "var(--gold)", fontSize: 10 }}>· referência do catálogo</span>
              </h3>
              <table className="tbl" style={{ marginBottom: 20 }}>
                <thead><tr><th>Material</th><th>Categoria</th><th style={{ textAlign: "right" }}>Custo/m²</th></tr></thead>
                <tbody>
                  {custos.materiais_detalhe.map(m => (
                    <tr key={m.id}>
                      <td style={{ color: "var(--ink)" }}>{m.nome}</td>
                      <td style={{ color: "var(--ink-3)", fontSize: 12 }}>{MAT_CAT[m.categoria] || m.categoria}</td>
                      <td style={{ textAlign: "right", color: "var(--gold)", fontVariantNumeric: "tabular-nums" }}>{fmtBRL(m.preco_custo)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={2} style={{ color: "var(--ink-3)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".15em" }}>Total teórico</td>
                    <td style={{ textAlign: "right", color: "var(--gold)", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{fmtBRL(custos.materiais_teorico)}</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}


function AIConversationsPage() {
  const { api, user } = useAuth();
  const canUse = user?.role === "admin" || user?.role === "gestor" || user?.role === "financeiro";
  const [stats, setStats] = useState(null);
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("todas"); // todas | falhas | sucesso
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canUse) return;
    setLoading(true);
    Promise.all([
      api("get", "/ai/stats"),
      api("get", "/ai/conversations"),
    ]).then(([s, c]) => { setStats(s); setConvs(c); }).finally(() => setLoading(false));
  }, [api, canUse]);

  const reload = () => {
    setLoading(true);
    Promise.all([
      api("get", "/ai/stats"),
      api("get", "/ai/conversations"),
    ]).then(([s, c]) => { setStats(s); setConvs(c); }).finally(() => setLoading(false));
  };

  const delOne = async (sessionId) => {
    if (!window.confirm("Apagar PERMANENTEMENTE esta conversa do histórico?")) return;
    await api("delete", `/ai/conversations/${sessionId}`);
    reload();
  };

  const delByObra = async (obraId, obraCodigo) => {
    if (!window.confirm(`Apagar TODAS as conversas da obra ${obraCodigo}?\n\nIrreversível. Use para limpar testes antes de enviar o Signature Book ao cliente.`)) return;
    const r = await api("delete", `/ai/conversations?obra_id=${obraId}`);
    alert(`${r.deleted} conversa(s) apagada(s).`);
    reload();
  };

  const delAll = async () => {
    const c = window.prompt("APAGAR TODAS as conversas do Concierge de TODAS as obras.\n\nIrreversível. Digite APAGAR para confirmar:");
    if (c !== "APAGAR") return;
    const r = await api("delete", `/ai/conversations?all=true`);
    alert(`${r.deleted} conversa(s) apagada(s).`);
    reload();
  };

  if (!canUse) {
    return <div className="container"><h1 className="page-title">Acesso negado</h1></div>;
  }

  const filtered = convs.filter(c => {
    if (filter === "falhas") return c.has_failure;
    if (filter === "sucesso") return !c.has_failure;
    return true;
  });

  const roleLbl = (r) => ({
    public: "Público (SB)", admin: "Admin", cliente: "Cliente", arquiteto: "Arquiteto",
    financeiro: "Financeiro", gestor: "Gestor", terceirizado: "Terceirizado",
  }[r] || r);

  // M1: usa fmtDataCurta centralizado (./lib/fmt.js)
  const fmtDate = fmtDataCurta;

  return (
    <>
      <div className="container">
        <div className="page-head fade-up" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 20 }}>
          <div>
            <div className="eyebrow"><Tricolore thin /> Concierge · CAMASA AI</div>
            <h1 className="page-title">Conversas do <em>Concierge</em></h1>
            <p className="page-sub">Engajamento em tempo real · detecção de falhas · tópicos que seus clientes mais perguntam.</p>
          </div>
          {user?.role === "admin" && convs.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={delAll} data-testid="ai-del-all" style={{ color: "#c47a7a", borderColor: "#4a2a2a" }}>
              × Apagar TODAS as conversas
            </button>
          )}
        </div>

        <HowToBanner id="ai-convs" title="Como usar este painel" steps={[
          { num: 1, title: "Engajamento", desc: "Veja quantos clientes estão ativamente usando o Concierge. Mais conversas = mais valor percebido." },
          { num: 2, title: "Falhas detectadas", desc: "Sessões em <strong>vermelho</strong> tiveram respostas em que a IA recusou ou disse 'consulte um consultor'. Use para tunar o FAQ ou treinar novos tópicos." },
          { num: 3, title: "Tópicos populares", desc: "Palavras-chave mostram o que seus clientes mais querem saber. Oportunidade de criar conteúdo/material sobre esses temas." },
          { num: 4, title: "Clique em uma conversa", desc: "Abre a conversa inteira para você entender o contexto e, se necessário, entrar em contato proativo com o cliente." },
        ]} />

        {/* Métricas */}
        {loading && <p className="page-sub" style={{ textAlign: "center", padding: 40 }}>Carregando…</p>}
        {!loading && stats && (
          <div className="ai-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 32 }}>
            <MetricCard label="Sessões" value={stats.total_sessions} accent="#f5efe0" testid="ai-m-sessions" />
            <MetricCard label="Mensagens" value={stats.total_messages} accent="#b8935a" testid="ai-m-messages" />
            <MetricCard label="Taxa de falha" value={`${stats.failure_rate}%`} accent={stats.failure_rate > 30 ? "#c47a7a" : "#6fbf8f"} testid="ai-m-fail" />
            <MetricCard label="Clientes engajados" value={(stats.top_obras || []).length} accent="#f5efe0" testid="ai-m-obras" />
          </div>
        )}

        {/* Top tópicos */}
        {!loading && stats?.top_keywords?.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Tópicos mais perguntados</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {stats.top_keywords.map(k => (
                <div key={k.term} data-testid={`ai-kw-${k.term}`} style={{ padding: "8px 14px", border: "1px solid rgba(184,147,90,0.3)", background: "rgba(184,147,90,0.06)", fontSize: 12, letterSpacing: ".05em" }}>
                  <span style={{ color: "var(--ink)" }}>{k.term}</span>
                  <span style={{ color: "var(--gold)", marginLeft: 8, fontWeight: 600 }}>× {k.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filtros */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {[{ k: "todas", l: "Todas" }, { k: "falhas", l: "Com falha" }, { k: "sucesso", l: "Sucesso" }].map(f => (
            <button key={f.k} className={"btn btn-sm " + (filter === f.k ? "btn-primary" : "btn-ghost")} onClick={() => setFilter(f.k)} data-testid={`ai-filter-${f.k}`}>
              {f.l} ({f.k === "todas" ? convs.length : f.k === "falhas" ? convs.filter(c => c.has_failure).length : convs.filter(c => !c.has_failure).length})
            </button>
          ))}
        </div>

        {/* Lista */}
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr>
              <th>Quando</th>
              <th>Obra / Cliente</th>
              <th>Origem</th>
              <th style={{ textAlign: "center" }}>Msgs</th>
              <th>Primeira pergunta</th>
              <th style={{ textAlign: "center" }}>Status</th>
              <th></th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "var(--ink-3)" }}>Nenhuma conversa</td></tr> :
                filtered.map(c => (
                  <tr key={c.session_id} data-testid={`ai-row-${c.session_id}`} style={c.has_failure ? { background: "rgba(196,122,122,0.05)" } : undefined}>
                    <td style={{ fontSize: 12, color: "var(--ink-2)", whiteSpace: "nowrap" }}>{fmtDate(c.updated_at)}</td>
                    <td>
                      <div style={{ color: "var(--ink)", fontWeight: 500 }}>{c.cliente_nome || "—"}</div>
                      <div style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".05em" }}>{c.obra_codigo || "—"}</div>
                    </td>
                    <td style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".1em", textTransform: "uppercase" }}>{roleLbl(c.user_role)}</td>
                    <td style={{ textAlign: "center", color: "var(--gold)", fontVariantNumeric: "tabular-nums" }}>{c.total_messages}</td>
                    <td style={{ fontSize: 13, color: "var(--ink-2)", maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.preview}>{c.preview || "—"}</td>
                    <td style={{ textAlign: "center" }}>
                      {c.has_failure ? <span style={{ color: "#c47a7a", fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase" }}>Falha</span> : <span style={{ color: "#6fbf8f", fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase" }}>OK</span>}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setSelected(c.session_id)} data-testid={`ai-open-${c.session_id}`}>Ver</button>
                      {user?.role === "admin" && (
                        <button className="btn btn-ghost btn-sm" onClick={() => delOne(c.session_id)} data-testid={`ai-del-${c.session_id}`} style={{ marginLeft: 6, color: "#c47a7a", borderColor: "#4a2a2a" }}>×</button>
                      )}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>

      {selected && <AIConversationDetail sessionId={selected} onClose={() => setSelected(null)} />}
      <Footer />
    </>
  );
}

function MetricCard({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} style={{ padding: "20px 22px", background: "var(--bg-2)", border: "1px solid var(--line)" }}>
      <div style={{ fontSize: 10, letterSpacing: ".25em", color: "var(--ink-3)", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: 34, color: accent, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function AIConversationDetail({ sessionId, onClose }) {
  const { api } = useAuth();
  const [conv, setConv] = useState(null);

  useEffect(() => {
    api("get", `/ai/conversations/${sessionId}`).then(setConv);
  }, [api, sessionId]);

  // Monta mensagem pré-preenchida usando a primeira pergunta do cliente
  const buildContactMsg = () => {
    if (!conv) return "";
    const firstQ = (conv.messages || []).find(m => m.role === "user")?.text || "";
    const short = firstQ.slice(0, 120);
    const nome = (conv.cliente_nome || "").split(" ")[0];
    const base = `Olá${nome ? " " + nome : ""}! Sou ${conv.obra_nome ? "da equipe CAMASA" : "consultor CAMASA"} e vi que você conversou com o nosso Concierge sobre${short ? `:\n\n"${short}${firstQ.length > 120 ? "..." : ""}"\n\n` : " a sua obra. "}Gostaria de te ajudar pessoalmente. Posso esclarecer alguma dúvida?`;
    return base;
  };

  const phoneClean = (conv?.cliente_telefone || "").replace(/\D/g, "");
  const hasPhone = phoneClean.length >= 10;
  // Adiciona prefixo BR se só tiver 10 ou 11 dígitos (DDD + número)
  const waPhone = phoneClean.length <= 11 ? `55${phoneClean}` : phoneClean;
  const waUrl = hasPhone ? `https://wa.me/${waPhone}?text=${encodeURIComponent(buildContactMsg())}` : null;
  const mailUrl = conv?.cliente_email ? `mailto:${conv.cliente_email}?subject=${encodeURIComponent("CAMASA · Acompanhamento da sua obra")}&body=${encodeURIComponent(buildContactMsg())}` : null;

  return (
    <div className="modal-back" onClick={onClose} data-testid="ai-conv-detail-modal">
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-head">
          <div>
            <div className="eyebrow"><Tricolore thin /> Conversa</div>
            <h2 className="modal-title">{conv?.cliente_nome || "—"} <em style={{ color: "var(--ink-3)", fontSize: 14 }}>· {conv?.obra_codigo}</em></h2>
            {conv && (
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4, letterSpacing: ".05em" }}>
                {conv.cliente_telefone && <span>📱 {conv.cliente_telefone}</span>}
                {conv.cliente_telefone && conv.cliente_email && <span style={{ margin: "0 8px", color: "var(--line)" }}>·</span>}
                {conv.cliente_email && <span>✉ {conv.cliente_email}</span>}
              </div>
            )}
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {!conv ? <p>Carregando…</p> :
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {(conv.messages || []).map((m, i) => (
                <div key={i} data-testid={`ai-det-${m.role}-${i}`} style={{
                  alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: "85%",
                  padding: "10px 14px",
                  background: m.role === "user" ? "rgba(184,147,90,0.15)" : "rgba(245,239,224,0.04)",
                  border: `1px solid ${m.role === "user" ? "rgba(184,147,90,0.3)" : "rgba(245,239,224,0.08)"}`,
                  color: m.role === "user" ? "var(--ink)" : "var(--ink-2)",
                  fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap",
                }}>
                  <div style={{ fontSize: 9, letterSpacing: ".2em", color: "var(--ink-3)", textTransform: "uppercase", marginBottom: 4 }}>
                    {m.role === "user" ? "Cliente" : "Concierge"} · {m.at ? new Date(m.at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : ""}
                  </div>
                  {m.text}
                </div>
              ))}
            </div>
          }
        </div>
        <div className="modal-foot" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {waUrl && (
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="ai-contact-wa" style={{ background: "#25d366", color: "#0d0d0f" }}>
              Contatar no WhatsApp →
            </a>
          )}
          {mailUrl && (
            <a href={mailUrl} className="btn btn-ghost" data-testid="ai-contact-email">
              Enviar e-mail
            </a>
          )}
          {!waUrl && !mailUrl && conv && (
            <span style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".05em", alignSelf: "center" }}>
              Cliente sem telefone/e-mail cadastrado na obra.
            </span>
          )}
          <button className="btn btn-ghost" onClick={onClose} style={{ marginLeft: "auto" }}>Fechar</button>
        </div>
      </div>
    </div>
  );
}



export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
        <Toaster
          position="top-center"
          closeButton
          richColors
          expand
          toastOptions={{ style: { fontSize: 15 } }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
